// Page content translations for all languages
// This file contains translations for all content pages

export const pageTranslations = {
  en: {
    // =====================
    // SMART METERING PAGE
    // =====================
    smartMeteringPage: {
      metaTitle: 'Smart Metering Solutions | IoT for Utilities | 0g Baltics',
      metaDesc: 'Deploy smart metering infrastructure with LoRaWAN, Sigfox, and NB-IoT. Remote data collection for water, gas, electricity, and heat meters.',
      heroBadge: 'Smart Metering',
      heroTitle: 'Smart Metering Infrastructure for Utilities',
      heroSubtitle: 'Deploy remote meter reading infrastructure using LoRaWAN, Sigfox, NB-IoT, and cellular technologies. Reduce operational costs and improve data accuracy for water, gas, electricity, and heat utilities.',
      ctaPrimary: 'Request a Consultation',
      ctaSecondary: 'Learn More',
      // Section: The Challenge
      challengeBadge: 'The Challenge',
      challengeTitle: 'Why Utilities Need Smart Metering',
      challengeDesc: 'Traditional meter reading is expensive, error-prone, and doesn\'t provide real-time visibility into consumption patterns.',
      challenge1: 'Manual meter reading costs',
      challenge1Desc: 'Field visits are expensive and time-consuming',
      challenge2: 'Inaccurate billing',
      challenge2Desc: 'Estimation errors lead to customer disputes',
      challenge3: 'Leak detection delays',
      challenge3Desc: 'Problems discovered too late cause waste',
      challenge4: 'Limited data',
      challenge4Desc: 'Monthly readings don\'t reveal consumption patterns',
      // Section: Our Solution
      solutionBadge: 'Our Solution',
      solutionTitle: 'IoT-Based Smart Metering',
      solutionDesc: 'We deploy smart metering infrastructure that connects existing meters to the cloud, providing real-time data without replacing your meter fleet.',
      solutionFeature1: 'Retrofit existing meters',
      solutionFeature2: 'Multiple connectivity options',
      solutionFeature3: 'Real-time consumption data',
      solutionFeature4: 'Leak and anomaly detection',
      solutionFeature5: 'Billing integration',
      solutionFeature6: 'Customer portal',
      // Section: Technologies
      techBadge: 'Technologies',
      techTitle: 'Connectivity Options',
      techDesc: 'We select the right technology based on your infrastructure, coverage requirements, and budget.',
      lorawanTitle: 'LoRaWAN',
      lorawanDesc: 'Long range, low power. Ideal for dense urban deployments.',
      sigfoxTitle: 'Sigfox',
      sigfoxDesc: 'Simple integration, nationwide coverage in Baltics.',
      nbiotTitle: 'NB-IoT',
      nbiotDesc: 'Cellular-based. Good for areas with mobile coverage.',
      cellularTitle: 'Cellular (4G/LTE)',
      cellularDesc: 'For high-frequency data transmission requirements.',
      // Section: Use Cases
      useCasesBadge: 'Applications',
      useCasesTitle: 'Smart Metering Use Cases',
      waterMetering: 'Water Metering',
      waterMeteringDesc: 'Monitor consumption, detect leaks, and reduce non-revenue water losses.',
      gasMetering: 'Gas Metering',
      gasMeteringDesc: 'Remote reading of gas meters for residential and commercial customers.',
      electricityMetering: 'Electricity Metering',
      electricityMeteringDesc: 'Sub-metering for commercial buildings and industrial facilities.',
      heatMetering: 'Heat Metering',
      heatMeteringDesc: 'District heating consumption monitoring and billing.',
      // CTA Section
      ctaTitle: 'Ready to modernize your metering infrastructure?',
      ctaDesc: 'Contact us to discuss your smart metering project.',
      ctaButton: 'Get in Touch',
      // Section: Network Quality
      networkBadge: 'Critical Infrastructure',
      networkTitle: 'Network Reliability is Everything',
      networkDesc: 'Many smart metering projects fail not because of the meters, but because of unreliable connectivity.',
      networkLocation1: 'Underground pits',
      networkLocation2: 'Inside buildings',
      networkLocation3: 'In basements',
      networkLocation4: 'Remote locations',
      networkWarning: 'Utilities require carrier-grade infrastructure designed for decades of reliable operation.',
      // Section: Carrier Grade
      carrierBadge: 'Infrastructure Standards',
      carrierTitle: 'What Does "Carrier-Grade" Mean?',
      carrierDesc: 'Carrier-grade infrastructure meets the reliability standards required by telecom operators and critical infrastructure providers.',
      carrierFeature1: 'Network uptime guarantee',
      carrierFeature1Desc: '99.9%+ availability',
      carrierFeature2: 'Professional-grade hardware',
      carrierFeature2Desc: 'Industrial-rated equipment',
      carrierFeature3: 'Long-term support',
      carrierFeature3Desc: '10+ year lifecycle',
      carrierFeature4: 'Security compliance',
      carrierFeature4Desc: 'Enterprise-grade security',
      // Section: LoRaWAN
      lorawanSectionBadge: 'Primary Technology',
      lorawanSectionTitle: 'LoRaWAN for Smart Metering',
      lorawanSectionDesc: 'LoRaWAN is the leading LPWAN technology for smart metering deployments.',
      lorawanAdvantage1: 'Long range (up to 15km)',
      lorawanAdvantage2: '10+ year battery life',
      lorawanAdvantage3: 'Deep indoor penetration',
      lorawanAdvantage4: 'Low infrastructure cost',
      // Section: Cellular
      cellularSectionBadge: 'Alternative Technology',
      cellularSectionTitle: 'Cellular IoT for Smart Metering',
      cellularSectionDesc: 'NB-IoT and LTE-M provide cellular-based connectivity for areas with good mobile coverage.',
    },

    // =====================
    // INDOOR AIR QUALITY PAGE
    // =====================
    iaqPage: {
      metaTitle: 'Indoor Air Quality Monitoring | CO2 Sensors | 0g Baltics',
      metaDesc: 'Professional indoor air quality monitoring with CO2, temperature, humidity, and PM sensors. Solutions for schools, offices, and healthcare facilities.',
      heroBadge: 'Indoor Air Quality',
      heroTitle: 'Indoor Air Quality Monitoring Solutions',
      heroSubtitle: 'Deploy professional IAQ monitoring systems to ensure healthy indoor environments in schools, offices, healthcare facilities, and public buildings.',
      // Technology badges
      techCO2: 'CO2',
      techTemperature: 'Temperature',
      techHumidity: 'Humidity',
      techPM25: 'PM2.5',
      techTVOC: 'TVOC',
      techOccupancy: 'Occupancy',
      // Section: Why IAQ Matters
      whyBadge: 'Why It Matters',
      whyTitle: 'The Importance of Indoor Air Quality',
      whyDesc: 'People spend 90% of their time indoors. Poor air quality affects health, productivity, and cognitive performance.',
      whyPoint1: 'CO2 levels above 1000 ppm reduce cognitive function',
      whyPoint2: 'Poor ventilation increases virus transmission',
      whyPoint3: 'Students in well-ventilated classrooms perform better',
      whyPoint4: 'Building occupants report fewer sick days',
      // Why outcomes list
      outcome1: 'Healthier indoor environments for occupants',
      outcome2: 'Better ventilation decisions based on real data',
      outcome3: 'Support for ESG and smart building strategies',
      outcome4: 'Improved workplace and classroom comfort',
      outcome5: 'Reduced operational blind spots',
      // Key Decision Drivers
      keyDrivers: 'Key Decision Drivers',
      driverHealth: 'Health',
      driverComfort: 'Comfort',
      driverVentilation: 'Ventilation',
      driverEfficiency: 'Efficiency',
      driverCompliance: 'Compliance',
      // Section: What We Monitor
      monitorBadge: 'Parameters',
      monitorTitle: 'What We Monitor',
      typicalRange: 'Typical range',
      co2: 'CO2 (Carbon Dioxide)',
      co2Desc: 'Primary indicator of ventilation adequacy',
      temperature: 'Temperature',
      temperatureDesc: 'Thermal comfort for occupants',
      humidity: 'Humidity',
      humidityDesc: 'Optimal range 40-60% for health',
      pm: 'Particulate Matter (PM)',
      pmDesc: 'PM2.5 and PM10 for air quality assessment',
      tvoc: 'TVOC',
      tvocDesc: 'Volatile organic compounds detection',
      // Section: Sensors - TEKTELIC
      sensorsBadge: 'Hardware',
      sensorsTitle: 'Professional-Grade Sensors',
      sensorsDesc: 'We deploy TEKTELIC and Connected Inventions sensors for reliable, long-term monitoring.',
      tektelicBreezeDesc: 'Smart Room Sensor for monitoring CO2, temperature, humidity, barometric pressure, and ambient light. NDIR CO2 sensor with ±30 ppm + 3% accuracy and 5+ years battery life.',
      tektelicBreezeVDesc: 'Extends BREEZE with PIR occupancy detection for smart room functionality. Ideal for correlating air quality with space utilization for demand-controlled ventilation.',
      tektelicBreezeDDesc: 'CO2 Smart Room Sensor with E-Ink Display. Combines LoRaWAN, BLE and E-Ink for low power consumption. Display is wireless and can be wall or table mounted.',
      whenTektelic: 'When to choose TEKTELIC',
      tektelicRelevant: 'TEKTELIC sensors are especially relevant when your project needs:',
      tektelicBenefit1: 'LoRaWAN smart building infrastructure',
      tektelicBenefit2: 'Scalable enterprise deployments',
      tektelicBenefit3: 'Carrier-grade reliability',
      tektelicBenefit4: 'Integration with building IoT systems',
      tektelicBenefit5: 'Optional BLE e-Ink displays',
      tektelicBenefit6: 'Professional support and documentation',
      bestFitTektelic: 'Best fit environments for TEKTELIC',
      corpOffices: 'Corporate offices',
      schoolsUnis: 'Schools & universities',
      publicBuildings: 'Public buildings',
      healthcareFacilities: 'Healthcare facilities',
      // Section: Connected Inventions
      connectedBadge: 'Long-Life Monitoring',
      connectedTitle: 'Connected Inventions AirWits Devices',
      connectedDesc: '0g Baltics also uses Connected Inventions devices for specific indoor environment monitoring needs. The AirWits product family offers purpose-built sensors optimized for long battery life, low maintenance, and cost-effective deployment across building portfolios.',
      airwitsBasicDesc: 'Temperature and humidity monitoring with ±0.2°C accuracy and up to 10 years battery life.',
      airwitsCO2Desc: 'CO2, temperature and humidity monitoring for ventilation assessment and comfort optimization.',
      airwitsCO2PlusDesc: 'Advanced CO2 monitoring with e-ink display showing real-time air quality status and color-coded indicators.',
      airwitsPMDesc: 'PM1.0 / PM2.5 / PM10 particulate monitoring with temperature and humidity for air quality assessment.',
      flexSenseDesc: 'Modular multi-sensor platform for flexible deployment with customizable sensor combinations for various use cases.',
      airwitsIAQDesc: 'TVOC monitoring with temperature and humidity for detecting pollutant events and poor air conditions.',
      airwitsEcoSenseDesc: 'Solar-powered, battery-free or ultra-low-maintenance option for deployment scenarios requiring minimal intervention.',
      whenConnected: 'When to choose Connected Inventions',
      connectedUseful: 'Connected Inventions devices are especially useful when your project values:',
      connectedBenefit1: 'Up to 10 years battery life',
      connectedBenefit2: 'Low total cost of ownership',
      connectedBenefit3: 'Sigfox or LoRaWAN options',
      connectedBenefit4: 'Simple large-scale deployment',
      connectedBenefit5: 'Cost-efficient portfolio monitoring',
      connectedBenefit6: 'Maintenance-free operation',
      // Section: Device Selection
      deviceSelectionBadge: 'Solution Architecture',
      deviceSelectionTitle: 'Choosing the Right IAQ Device for the Right Building',
      deviceSelectionDesc: '0g Baltics does not push a single device for every project. Instead, we select the right architecture depending on budget, network availability, required parameters, battery expectations, reporting needs, and integration requirements.',
      tableBuilding: 'Building Type',
      tableNeed: 'Project Need',
      tableSensor: 'Recommended Sensor',
      tableWhy: 'Why It Fits',
      // Table rows
      officePortfolio: 'Office Portfolio',
      officeNeed: 'CO2, temperature, humidity across floors',
      officeWhy: 'LoRaWAN scalability, occupancy correlation, BLE display option',
      classroomSchool: 'Classroom / School',
      classroomNeed: 'Ventilation monitoring, student comfort',
      classroomWhy: 'Real-time CO2 visibility, long battery life, easy installation',
      uniCampus: 'University Campus',
      uniNeed: 'Multi-building IAQ strategy',
      uniSensor: 'Mixed: TEKTELIC + AirWits',
      uniWhy: 'LoRaWAN infrastructure + cost-effective expansion',
      healthcareClinics: 'Healthcare / Clinics',
      healthcareNeed: 'Sensitive environment monitoring',
      healthcareIaqSensor: 'AirWits PM + AirWits IAQ',
      healthcareWhy: 'PM and TVOC critical for patient environments',
      residentialAreas: 'Residential Common Areas',
      residentialNeed: 'Basic comfort and ventilation',
      residentialSensor: 'AirWits / AirWits CO2',
      residentialWhy: 'Cost-efficient, low maintenance, long battery',
      municipalBuildings: 'Municipal Buildings',
      municipalNeed: 'Public space monitoring, compliance',
      municipalWhy: 'Carrier-grade reliability, audit trails',
      industrialIndoor: 'Industrial Indoor Spaces',
      industrialNeed: 'Process control, worker comfort',
      industrialSensor: 'AirWits PM + CO2',
      industrialWhy: 'Particulate monitoring for industrial environments',
      // Section: IoT vs Manual
      iotVsManualBadge: 'Operational Advantage',
      iotVsManualTitle: 'Why IoT-Based IAQ Monitoring Beats Manual Spot Checks',
      iotVsManualDesc: 'Traditional indoor air quality assessment relies on periodic manual inspections with handheld devices. A technician visits each space, takes a measurement, records the result, and moves on. This approach has fundamental limitations that IoT-based monitoring eliminates.',
      iotVsManualDesc2: 'Connected sensors provide continuous data instead of occasional snapshots. They generate alerts instead of waiting for complaints. They create historical trends for analysis and benchmarking. And they scale across portfolios without proportionally increasing labor costs.',
      continuous247: 'Continuous 24/7 Monitoring',
      continuous247Desc: 'Data every 15-30 minutes, not once per quarter',
      realTimeAlerts: 'Real-Time Alerts',
      realTimeAlertsDesc: 'Instant notification when thresholds are exceeded',
      historicalTrends: 'Historical Trends',
      historicalTrendsDesc: 'Analyze patterns over weeks, months, and seasons',
      portfolioBenchmark: 'Portfolio Benchmarking',
      portfolioBenchmarkDesc: 'Compare buildings and identify outliers',
      betterDecisions: 'Better Decisions',
      betterDecisionsDesc: 'Data-driven maintenance and ventilation optimization',
      scalableOps: 'Scalable Operations',
      scalableOpsDesc: 'Monitor hundreds of spaces without additional staff',
      // Section: IAQ Energy
      iaqEnergyBadge: 'Building Performance',
      iaqEnergyTitle: 'Balance Health, Comfort and Energy Efficiency',
      iaqEnergyDesc1: 'Indoor air quality monitoring is not only about health—it is also about building performance and operational efficiency. Buildings account for approximately 40% of global energy consumption, with HVAC systems representing a significant portion. Intelligent ventilation based on actual indoor conditions can reduce energy waste while maintaining healthy air quality.',
      co2Ventilation: 'CO2 and Occupancy-Based Ventilation',
      co2VentilationDesc: 'Monitoring CO2 and occupancy can support smarter ventilation decisions. When spaces are unoccupied or lightly used, ventilation can be reduced. When CO2 rises, fresh air supply can be increased. This demand-controlled approach optimizes energy use while maintaining air quality.',
      tempHumidityInsights: 'Temperature and Humidity Insights',
      tempHumidityInsightsDesc: 'Temperature and humidity trends help understand building comfort and HVAC performance. Identifying zones with persistent issues enables targeted improvements rather than system-wide adjustments. Better data leads to more efficient operations.',
      iaqEnergyDesc2: 'IAQ data supports better facility management by providing objective measurements that inform maintenance priorities, renovation decisions, and system upgrades. Smart buildings need measurement before optimization—you cannot improve what you do not measure.',
      iaqEnergyNote: 'While IAQ monitoring provides data that can inform energy optimization strategies, specific energy savings depend on many factors including building systems, climate, and operational practices. We focus on providing accurate measurement and actionable insights rather than overpromising specific ROI figures.',
      // Section: Standards
      standardsBadge: 'Regulatory Framework',
      standardsTitle: 'Standards, Guidelines and IAQ Best Practice',
      standardsDesc: 'Indoor air quality projects are often driven by health expectations, ESG and smart building policies, school and workplace comfort goals, and ventilation and risk-management guidelines. Understanding the relevant frameworks helps define monitoring strategy, ventilation targets, and building-performance expectations.',
      whoGuidelines: 'WHO Air Quality Guidelines',
      whoGuidelinesDesc: 'The World Health Organization provides guidelines on air quality including particulate matter thresholds. These recommendations are relevant for understanding PM-related health impacts and setting monitoring objectives in buildings concerned with occupant health.',
      ashraeStandard: 'ASHRAE Standard 241',
      ashraeStandardDesc: 'ASHRAE 241 addresses control of infectious aerosols and provides guidance for healthier indoor environments. This standard is particularly relevant for healthcare, educational, and commercial buildings seeking to reduce airborne transmission risks.',
      enStandard: 'EN 16798 Family',
      enStandardDesc: 'In Europe, the EN 16798 standards are relevant to indoor environmental quality, ventilation requirements, and inspection frameworks. These provide guidance for designing and operating buildings to meet indoor environment quality expectations.',
      standardsImportant: 'IAQ monitoring supports evidence-based building management and can help organizations document conditions, identify issues, and prioritize corrective actions. While sensors provide data, compliance with specific standards depends on how that data is used within broader building management and ventilation strategies.',
      iaqHelps: 'IAQ monitoring helps with:',
      iaqHelp1: 'Documenting indoor conditions over time',
      iaqHelp2: 'Identifying spaces requiring intervention',
      iaqHelp3: 'Supporting ventilation optimization efforts',
      iaqHelp4: 'Providing audit trails for compliance',
      iaqHelp5: 'Benchmarking across building portfolios',
      iaqHelp6: 'Informing renovation and upgrade decisions',
      // Section: Use Cases
      useCasesBadge: 'Applications',
      useCasesTitle: 'Where Indoor Air Quality Monitoring Delivers Value',
      useCasesDesc: 'IAQ monitoring provides operational benefits across a wide range of building types. Each environment has specific requirements that drive sensor selection and deployment strategy.',
      ucOffices: 'Offices',
      ucOfficesDesc: 'Improve workplace comfort and environment quality. Monitor CO2 levels to ensure adequate ventilation, optimize temperature for productivity, and demonstrate commitment to employee well-being.',
      ucSchools: 'Schools and Universities',
      ucSchoolsDesc: 'Support classroom ventilation and create better learning environments. Research shows that elevated CO2 levels in classrooms correlate with reduced cognitive performance and increased absenteeism.',
      ucHealthcare: 'Healthcare and Clinics',
      ucHealthcareDesc: 'Monitor sensitive indoor environments where air quality directly affects patient outcomes. PM and TVOC monitoring is particularly relevant in clinical settings, waiting rooms, and patient care areas.',
      ucMunicipal: 'Municipal Buildings',
      ucMunicipalDesc: 'Bring visibility to public indoor environments including libraries, community centers, and government offices. Support transparency and demonstrate responsible public facility management.',
      ucCommercial: 'Commercial Real Estate',
      ucCommercialDesc: 'Benchmark and monitor multiple buildings consistently across portfolios. Provide tenants with healthy building credentials and support ESG reporting requirements.',
      ucIndustrial: 'Industrial Indoor Spaces',
      ucIndustrialDesc: 'Monitor indoor conditions where comfort, process control, or worker safety matter. Track particulates in manufacturing environments and ensure compliance with workplace health standards.',
      // CTA
      ctaTitle: 'Improve air quality in your buildings',
      ctaButton: 'Request a Demo',
      // Section: Why 0g Baltics
      whyUsBadge: 'Your Partner',
      whyUsTitle: 'Why 0g Baltics',
      whyUsDesc: 'We bring practical IoT deployment expertise to indoor air quality monitoring projects across the Baltic region.',
      whyUsFeature1: 'Baltics Market Focus',
      whyUsFeature1Desc: 'Local presence and understanding of regional requirements',
      whyUsFeature2: 'Practical IoT Expertise',
      whyUsFeature2Desc: 'Real-world deployment experience across building types',
      whyUsFeature3: 'Multi-Connectivity',
      whyUsFeature3Desc: 'LoRaWAN, Sigfox, and hybrid architectures',
      // Section: Sectors
      sectorsBadge: 'Industries',
      sectorsTitle: 'IAQ Monitoring by Sector',
      sectorSchools: 'Schools & Education',
      sectorSchoolsDesc: 'Improve learning environments with better air quality',
      sectorOffices: 'Offices & Workplaces',
      sectorOfficesDesc: 'Boost productivity and reduce sick days',
      sectorHealthcare: 'Healthcare Facilities',
      sectorHealthcareDesc: 'Critical air quality for patient safety',
      sectorPublic: 'Public Buildings',
      sectorPublicDesc: 'Municipal buildings and government facilities',
      // FAQ Section
      faqBadge: 'FAQ',
      faqTitle: 'Frequently Asked Questions',
      faqDesc: 'Everything you need to know about indoor air quality monitoring',
      faq1Q: 'What is indoor air quality monitoring?',
      faq1A: 'IAQ monitoring uses sensors to continuously measure CO2, temperature, humidity, PM, and VOC in indoor spaces.',
      faq2Q: 'What parameters should I monitor?',
      faq2A: 'Start with CO2 and temperature. Add humidity, PM, or VOC based on your specific needs.',
      faq3Q: 'How many sensors do I need?',
      faq3A: 'Typically one sensor per room or zone. Large spaces may need multiple sensors.',
      faq4Q: 'What is the battery life?',
      faq4A: 'Professional sensors typically operate 5-10 years on battery power.',
    },

    // =====================
    // SMART CITY PAGE
    // =====================
    smartCityPage: {
      metaTitle: 'Smart City IoT Infrastructure | 0g Baltics',
      metaDesc: 'Build smart city infrastructure with LoRaWAN networks, environmental monitoring, smart lighting, and connected urban services.',
      heroBadge: 'Smart Cities',
      heroTitle: 'Smart City IoT Infrastructure',
      heroSubtitle: 'We help municipalities deploy IoT infrastructure for smarter, more efficient cities. From environmental monitoring to connected services.',
      // Section: Solutions
      solutionsBadge: 'Solutions',
      solutionsTitle: 'Smart City Applications',
      envMonitoring: 'Environmental Monitoring',
      envMonitoringDesc: 'Air quality, noise levels, and weather monitoring across the city.',
      smartLighting: 'Smart Lighting',
      smartLightingDesc: 'Connected street lighting with dimming and fault detection.',
      wasteManagement: 'Waste Management',
      wasteManagementDesc: 'Smart bins with fill-level sensors and route optimization.',
      parking: 'Smart Parking',
      parkingDesc: 'Real-time parking availability and guidance systems.',
      flooding: 'Flood Monitoring',
      floodingDesc: 'Water level sensors for flood early warning systems.',
      traffic: 'Traffic Monitoring',
      trafficDesc: 'Vehicle counting and traffic flow analysis.',
      // What Is section
      whatIsBadge: 'Understanding the Foundation',
      whatIsTitle: 'What is Smart City IoT Infrastructure?',
      whatIsDesc: 'Smart city infrastructure is the digital backbone of connected urban services.',
      dataCollected: 'Urban data collected includes:',
      traffic: 'Traffic patterns',
      airQuality: 'Air quality',
      parking: 'Parking availability',
      water: 'Water consumption',
      energy: 'Energy usage',
      assets: 'Public assets',
      // Why Projects Fail section
      failBadge: 'Common Challenges',
      failTitle: 'Why Many Smart City Projects Fail',
      failDesc: 'Many smart city initiatives begin with isolated pilot projects without building a long-term infrastructure strategy.',
      commonProblems: 'Common problems include:',
      problem1: 'Fragmented connectivity with multiple incompatible systems',
      problem2: 'Unreliable networks that cannot support growing device counts',
      problem3: 'Pilot projects that cannot scale to city-wide deployment',
      problem4: 'Data silos that prevent unified city intelligence',
      solutionText: 'The solution:',
      solutionDesc: 'Successful smart city strategies start with a shared connectivity infrastructure capable of supporting many services—not isolated pilots that cannot grow.',
      // Connectivity Foundation section
      connectivityBadge: 'Network Technologies',
      connectivityTitle: 'The Foundation of Smart Cities: Reliable IoT Connectivity',
      connectivityDesc: 'Smart cities require networks capable of supporting thousands of devices distributed across large urban areas. Different technologies serve different use cases, and the most successful deployments combine multiple connectivity options.',
      lorawanName: 'LoRaWAN',
      lorawanDesc: 'Long-range, low-power for city-wide coverage',
      nbiotName: 'NB-IoT',
      nbiotDesc: 'Cellular-based for deep penetration',
      ltemName: 'LTE-M',
      ltemDesc: 'Mobile assets and higher bandwidth',
      bleName: 'BLE Networks',
      bleDesc: 'Indoor and proximity applications',
      satelliteName: 'Satellite',
      satelliteDesc: 'Remote and backup connectivity',
      connectivityNote: '0g Baltics designs multi-connectivity architectures ensuring that each device uses the most efficient communication technology. This approach maximizes reliability while minimizing operational costs.',
      // Carrier Grade section
      carrierBadge: 'Infrastructure Standards',
      carrierTitle: 'Why Cities Need Carrier-Grade Infrastructure',
      carrierDesc1: 'Urban infrastructure must operate continuously for many years—often decades. Smart city connectivity must therefore be designed as carrier-grade infrastructure, not consumer-grade solutions that cannot meet municipal reliability requirements.',
      carrierDesc2: 'These principles come from the telecommunications industry and ensure that city services remain operational over time, regardless of weather conditions, network load, or system changes.',
      carrierProvide: 'Carrier-grade networks provide:',
      highAvailability: 'High Availability',
      highAvailabilityDesc: '99.9%+ uptime targets',
      secureCommunications: 'Secure Communications',
      secureCommunicationsDesc: 'Encrypted data transport',
      scalableArchitecture: 'Scalable Architecture',
      scalableArchitectureDesc: 'Grows with city needs',
      professionalMonitoring: 'Professional Monitoring',
      professionalMonitoringDesc: '24/7 network operations',
      longTermReliability: 'Long-term Reliability',
      longTermReliabilityDesc: '10+ year infrastructure',
      redundancy: 'Redundancy',
      redundancyDesc: 'Failover capabilities',
      infrastructureLayers: 'Infrastructure Layers',
      layer5: 'Layer 5',
      layer5Name: 'City Services',
      layer5Desc: 'Parking, lighting, environment',
      layer4: 'Layer 4',
      layer4Name: 'City Platform',
      layer4Desc: 'Data management and analytics',
      layer3: 'Layer 3',
      layer3Name: 'Network Servers',
      layer3Desc: 'Device and data management',
      layer2: 'Layer 2',
      layer2Name: 'Gateways',
      layer2Desc: 'Professional base stations',
      layer1: 'Layer 1',
      layer1Name: 'Devices',
      layer1Desc: 'Sensors and endpoints',
      // LoRaWAN for Cities section
      lorawanBadge: 'LPWAN Technology',
      lorawanTitle: 'LoRaWAN Networks for Urban IoT',
      lorawanMainDesc: 'LoRaWAN is one of the most widely adopted technologies for smart city deployments worldwide. Its technical characteristics make it ideally suited for connecting thousands of sensors across urban environments.',
      lorawanFeature1Title: 'Long-range connectivity',
      lorawanFeature1Desc: 'Single gateways can cover several kilometers in urban environments, reducing infrastructure investment.',
      lorawanFeature2Title: 'Excellent penetration',
      lorawanFeature2Desc: 'LoRa signals reach sensors inside buildings, underground, and in challenging locations throughout the city.',
      lorawanFeature3Title: 'Low power consumption',
      lorawanFeature3Desc: 'Battery-powered sensors can operate for many years without maintenance or replacement.',
      lorawanFeature4Title: 'Large network capacity',
      lorawanFeature4Desc: 'A small number of gateways can connect thousands of sensors across the city.',
      lorawanNote: '0g Baltics deploys carrier-grade LoRaWAN infrastructure using professional TEKTELIC gateways and secure network architecture designed for municipal-grade reliability.',
      stat1Value: '15+ km',
      stat1Label: 'Range in rural areas',
      stat2Value: '5+ km',
      stat2Label: 'Range in urban areas',
      stat3Value: '10+ years',
      stat3Label: 'Sensor battery life',
      stat4Value: '10,000+',
      stat4Label: 'Devices per gateway',
      // Multi-Service Infrastructure section
      multiServiceBadge: 'Unified Infrastructure',
      multiServiceTitle: 'One Infrastructure, Many Urban Services',
      multiServiceDesc: 'When a city deploys a shared IoT infrastructure, many services can operate on the same network. This approach reduces costs, simplifies operations, and enables cities to scale innovation over time.',
      servicesShare: 'Services that can share infrastructure:',
      smartParking: 'Smart parking',
      airQualityMonitoring: 'Air quality monitoring',
      smartWaterMetering: 'Smart water metering',
      wasteManagementService: 'Waste management',
      municipalAssetTracking: 'Municipal asset tracking',
      smartStreetLighting: 'Smart street lighting',
      smartBuildingMonitoring: 'Smart building monitoring',
      energyManagement: 'Energy management',
      // Innovation Ecosystem section
      innovationBadge: 'Economic Development',
      innovationTitle: 'New Services and Economic Development Enabled by LPWAN Networks',
      innovationDesc: 'A city-wide LPWAN network is not only technical infrastructure. It becomes an innovation platform that enables economic activity and new digital services. Cities that deploy shared IoT infrastructure often see the emergence of new businesses and services.',
      organizationsTitle: 'Organizations that can leverage city IoT infrastructure:',
      localStartups: 'Local startups',
      techCompanies: 'Technology companies',
      universities: 'Universities',
      researchLabs: 'Research laboratories',
      publicServiceProviders: 'Public service providers',
      privateOperators: 'Private operators',
      innovationBarrier: 'Instead of building their own connectivity infrastructure, these organizations can rely on the city\'s shared IoT network. This significantly reduces the barrier to innovation.',
      innovationAnalogy: 'Think of it like fiber broadband: IoT infrastructure becomes a foundation for long-term economic development, enabling services that cannot exist without reliable connectivity.',
      newServicesTitle: 'New services that can emerge on city IoT infrastructure:',
      smartLogistics: 'Smart logistics services',
      smartLogisticsDesc: 'Last-mile delivery optimization',
      envMonitoringStartups: 'Environmental monitoring startups',
      envMonitoringStartupsDesc: 'Urban air and noise analytics',
      equipmentTracking: 'Equipment tracking services',
      equipmentTrackingDesc: 'Construction and industrial assets',
      sharedMobility: 'Shared mobility monitoring',
      sharedMobilityDesc: 'Bikes, scooters, vehicles',
      energyOptimization: 'Energy optimization services',
      energyOptimizationDesc: 'Building and grid management',
      smartBuildingProviders: 'Smart building providers',
      smartBuildingProvidersDesc: 'Commercial property services',
      // City Intelligence section
      intelligenceBadge: 'Urban Analytics',
      intelligenceTitle: 'From Sensors to City Intelligence',
      intelligenceDesc: 'Smart city platforms collect data from thousands of sensors deployed across urban areas. This data helps city operators understand urban systems and make better decisions about infrastructure, services, and planning.',
      dataHelps: 'Data helps cities understand:',
      trafficPatterns: 'Traffic patterns and congestion',
      envConditions: 'Environmental conditions and air quality',
      infrastructureUsage: 'Infrastructure usage and maintenance needs',
      energyConsumption: 'Energy consumption across facilities',
      assetUtilization: 'Public asset utilization',
      urbanTrends: 'Trends for urban planning',
      intelligenceConclusion: 'Over time, cities can optimize operations and make better planning decisions based on objective data rather than assumptions.',
      // Deployment Strategy section
      deploymentBadge: 'Implementation Approach',
      deploymentTitle: 'A Practical Deployment Strategy for Cities',
      deploymentDesc: 'Successful smart city deployments follow a progressive approach that reduces risk while enabling long-term scalability.',
      step01: '01',
      step01Title: 'Deploy Reliable IoT Connectivity Infrastructure',
      step01Desc: 'Establish carrier-grade network coverage across priority areas. Install professional gateways and configure secure network architecture that can support thousands of devices.',
      step02: '02',
      step02Title: 'Connect Priority Services',
      step02Desc: 'Deploy initial use cases such as smart parking, air quality monitoring, or water metering. Focus on services that deliver clear value and demonstrate infrastructure capabilities.',
      step03: '03',
      step03Title: 'Expand Gradually to New Services',
      step03Desc: 'Add new services and departments over time. The shared infrastructure supports additional use cases without requiring new network investment.',
      step04: '04',
      step04Title: 'Use Data Analytics to Improve Operations',
      step04Desc: 'Leverage collected data to optimize urban services, inform planning decisions, and demonstrate value to citizens and stakeholders.',
      // Why 0g Baltics section
      whyBadge: 'Your Partner',
      whyTitle: 'Why Cities Work with 0g Baltics',
      whyDesc: '0g Baltics provides the infrastructure expertise cities need to build successful smart city deployments. Our goal is to help cities build reliable digital infrastructure that supports long-term innovation.',
      service1Title: 'Carrier-grade IoT connectivity',
      service1Desc: 'Networks designed for municipal reliability requirements',
      service2Title: 'LoRaWAN infrastructure deployment',
      service2Desc: 'Professional network design, installation, and management',
      service3Title: 'Multi-connectivity architecture',
      service3Desc: 'Hybrid networks combining LoRaWAN, cellular, and more',
      service4Title: 'Smart city consulting',
      service4Desc: 'Strategic guidance for infrastructure planning',
      service5Title: 'Integration with urban platforms',
      service5Desc: 'Connect sensors to city management systems',
      relatedSolutions: 'Related Solutions from 0g Baltics',
      relatedConnectivity: 'Multi-Network Connectivity',
      relatedConnectivityDesc: 'LoRaWAN, NB-IoT, Sigfox solutions',
      relatedTektelic: 'TEKTELIC Infrastructure',
      relatedTektelicDesc: 'Carrier-grade LoRaWAN gateways',
      relatedMetering: 'Smart Metering',
      relatedMeteringDesc: 'Water, energy, gas metering',
      relatedAssetTracking: 'Asset Tracking',
      relatedAssetTrackingDesc: 'Municipal equipment monitoring',
      relatedAirQuality: 'Air Quality Monitoring',
      relatedAirQualityDesc: 'Environmental IoT sensors',
      // CTA section
      ctaTitle: 'Build Your Smart City Infrastructure',
      ctaDesc: 'Contact 0g Baltics to design scalable smart city connectivity and infrastructure. We help municipalities and cities build the digital foundation for long-term urban innovation.',
      ctaButton: 'Talk to our team',
    },

    // =====================
    // SMART LOCKERS PAGE
    // =====================
    smartLockersPage: {
      metaTitle: 'Smart Lockers | Automated Parcel & Tool Lockers | 0g Baltics',
      metaDesc: 'Smart locker solutions for parcel delivery, tool management, and public services. Automated, connected, and scalable.',
      heroBadge: 'Smart Lockers',
      heroTitle: 'Smart Locker Solutions',
      heroSubtitle: 'Automated locker infrastructure for logistics, industrial operations, and public services.',
      // Use case badges
      parcelDelivery: 'Parcel Delivery',
      toolManagement: 'Tool Management',
      beachLockers: 'Beach Lockers',
      retailPickup: 'Retail Pickup',
      keyManagement: 'Key Management',
      // Types
      parcelLockers: 'Parcel Lockers',
      parcelLockersDesc: 'Automated parcel delivery and pickup for last-mile logistics.',
      toolLockers: 'Tool Management Lockers',
      toolLockersDesc: 'Industrial tool distribution and tracking with access control.',
      libraryLockers: 'Library Lockers',
      libraryLockersDesc: 'Automated book pickup and return for public libraries.',
      discoverToolTracking: 'Discover our tool-tracking solutions',
      // What Are Smart Lockers
      whatAreBadge: 'Understanding the Technology',
      whatAreTitle: 'What Are Smart Lockers?',
      whatAreDesc1: 'Smart lockers are connected storage systems that allow items to be securely stored and retrieved using digital authentication. Unlike traditional lockers that rely on physical keys or combination locks, smart lockers integrate with cloud platforms and provide multiple access methods.',
      whatAreDesc2: 'Smart lockers replace manual storage systems with automated secure access, providing complete visibility into usage patterns, inventory levels, and access history. This makes them ideal for high-volume operations where tracking and accountability are important.',
      accessMethods: 'Users can access lockers using:',
      accessMobile: 'Mobile applications',
      accessPIN: 'PIN codes',
      accessRFID: 'RFID cards',
      accessQR: 'QR codes',
      accessBadge: 'Employee badges',
      accessPayment: 'Payment systems',
      cloudCapabilities: 'Cloud platform capabilities:',
      capAccessControl: 'Access Control',
      capAccessControlDesc: 'Manage who can access which lockers and when',
      capNotifications: 'Notifications',
      capNotificationsDesc: 'SMS, email, and push notifications for users',
      capUsageTracking: 'Usage Tracking',
      capUsageTrackingDesc: 'Complete history of all locker transactions',
      capAvailability: 'Locker Availability',
      capAvailabilityDesc: 'Real-time visibility of empty compartments',
      capAnalytics: 'Analytics',
      capAnalyticsDesc: 'Usage patterns and operational insights',
      // Smart Locker Models
      modelsBadge: 'Hardware Options',
      modelsTitle: 'Smart Locker Models',
      modelsDesc: '0g Baltics offers several locker architectures depending on the use case. Each model allows flexible configuration of compartment sizes to match the items being stored.',
      towerLockers: 'Tower Lockers',
      towerLockersDesc: 'Vertical locker systems designed for parcel delivery and logistics hubs. Compact footprint with maximum storage capacity.',
      towerFeature1: 'High capacity',
      towerFeature2: 'Small footprint',
      towerFeature3: 'Outdoor ready',
      storeLockers: 'Store Lockers',
      storeLockersDesc: 'Large modular lockers suitable for retail pickup and commercial environments. Wall-mounted or freestanding configurations.',
      storeFeature1: 'Modular design',
      storeFeature2: 'Retail integration',
      storeFeature3: 'High visibility',
      slingLockers: 'Sling Lockers',
      slingLockersDesc: 'Compact lockers ideal for small spaces, offices and indoor environments. Perfect for corporate and campus deployments.',
      slingFeature1: 'Space efficient',
      slingFeature2: 'Indoor design',
      slingFeature3: 'Easy install',
      batteryPowered: 'Battery Powered',
      batteryPoweredDesc: 'Lockers powered by batteries, enabling deployment without electrical wiring. Ideal for remote and temporary locations.',
      batteryFeature1: 'No wiring',
      batteryFeature2: 'Flexible placement',
      batteryFeature3: 'Low cost install',
      // Retail Pickup Lockers
      retailTitle: 'Retail Pickup Lockers',
      retailDesc: 'Retailers use lockers to allow customers to collect online orders quickly. Click-and-collect services are increasingly popular, with lockers enabling 24/7 pickup without requiring staff assistance.',
      retailAdvantages: 'Advantages:',
      retailAdv1: '24/7 pickup availability',
      retailAdv2: 'Reduced store queues',
      retailAdv3: 'Faster customer service',
      retailAdv4: 'Automated pickup logistics',
      retailAdv5: 'Integration with e-commerce',
      retailNote: 'Retail lockers are increasingly used in supermarkets, shopping malls, and e-commerce fulfillment centers.',
      // Beach Lockers
      beachTitle: 'Beach and Leisure Lockers',
      beachDesc: 'Smart lockers are ideal for leisure environments where visitors need secure storage for personal belongings. Battery-powered options enable deployment without infrastructure investment.',
      idealLocations: 'Ideal locations:',
      beachLoc1: 'Beaches and coastal areas',
      beachLoc2: 'Parks and recreation areas',
      beachLoc3: 'Sports facilities',
      beachLoc4: 'Tourism locations',
      beachLoc5: 'Event venues',
      beachNote: 'Users access lockers through mobile apps or payment systems, enabling monetization for operators.',
      // Key Management
      keyTitle: 'Key Management Lockers',
      keyDesc: 'Connected lockers can manage physical keys for fleet vehicles, property management, and equipment access. Each transaction is tracked digitally, creating an audit trail that improves operational security and accountability.',
      keyFeature1: 'Fleet vehicle key management',
      keyFeature2: 'Property and facility access',
      keyFeature3: 'Equipment key distribution',
      keyFeature4: 'Contractor and visitor access',
      keyFeature5: 'Complete digital audit trail',
      // Battery Powered
      batteryTitle: 'Battery Powered Lockers',
      batteryDesc: 'Battery powered lockers allow installation in locations without electrical infrastructure. This dramatically reduces installation costs and enables flexible deployment in locations where wiring would be impractical.',
      batteryFeat1: 'No electrical wiring required',
      batteryFeat2: 'Fast and easy installation',
      batteryFeat3: 'Flexible location placement',
      batteryFeat4: 'Lower total installation cost',
      batteryFeat5: 'Ideal for temporary deployments',
      batteryUseCases: 'Common use cases:',
      batteryUseCasesDesc: 'Beaches, parks, temporary installations, remote areas, and locations where electrical infrastructure is expensive or unavailable.',
      // Software Platform
      platformBadge: 'Cloud Management',
      platformTitle: 'Locker Management Platform',
      platformDesc: 'Smart lockers are managed through a cloud platform that provides complete visibility and control over locker networks. Operators can monitor status, manage users, and analyze usage patterns from anywhere.',
      platformCapabilities: 'Platform capabilities:',
      platAvailability: 'Availability Monitoring',
      platAvailabilityDesc: 'Real-time compartment status',
      platUserMgmt: 'User Management',
      platUserMgmtDesc: 'Access control and permissions',
      platNotifications: 'Notifications',
      platNotificationsDesc: 'SMS, email, push alerts',
      platAnalytics: 'Usage Analytics',
      platAnalyticsDesc: 'Trends and patterns',
      platRemoteConfig: 'Remote Configuration',
      platRemoteConfigDesc: 'Update settings remotely',
      platSecurityLogs: 'Security Logs',
      platSecurityLogsDesc: 'Complete audit trail',
      // API and Integrations
      apiBadge: 'Open Architecture',
      apiTitle: 'Open APIs and Integrations',
      apiDesc: 'Locker platforms can integrate with existing business systems through APIs, allowing lockers to become part of a broader digital infrastructure rather than standalone solutions.',
      intEcommerce: 'E-commerce',
      intEcommerceDesc: 'Order management',
      intIdentity: 'Identity',
      intIdentityDesc: 'SSO and LDAP',
      intPayment: 'Payment',
      intPaymentDesc: 'Card and mobile',
      intLogistics: 'Logistics',
      intLogisticsDesc: 'TMS integration',
      intSmartCity: 'Smart City',
      intSmartCityDesc: 'Urban platforms',
      intERP: 'ERP',
      intERPDesc: 'Enterprise systems',
      // Smart Lockers for Cities
      citiesBadge: 'Urban Infrastructure',
      citiesTitle: 'Smart Locker Infrastructure for Cities',
      citiesDesc: 'Cities can deploy locker networks as part of smart city infrastructure. Parcel lockers reduce delivery traffic, public storage lockers serve citizens and tourists, and equipment lockers support municipal operations.',
      municipalApps: 'Municipal locker applications:',
      cityApp1: 'Parcel delivery networks reducing traffic',
      cityApp2: 'Public storage for citizens and tourists',
      cityApp3: 'Beach and leisure area services',
      cityApp4: 'Municipal equipment management',
      cityApp5: 'City vehicle and facility key access',
      learnSmartCity: 'Learn more about Smart City Infrastructure',
      networksInfra: 'Locker networks become infrastructure',
      networksInfraDesc: 'When cities deploy locker networks, they create shared infrastructure that serves multiple purposes—reducing last-mile delivery traffic, providing citizen services, and supporting municipal operations.',
      cityBenefit1: 'Reduce delivery vehicle traffic',
      cityBenefit2: 'Enable 24/7 citizen services',
      cityBenefit3: 'Support tourism and leisure',
      cityBenefit4: 'Improve municipal efficiency',
      // Why 0g Baltics
      whyBadge: 'Your Partner',
      whyTitle: 'Why Deploy Smart Lockers with 0g Baltics',
      whyDesc: '0g Baltics combines robust hardware, IoT connectivity, cloud platforms, and open integrations to deliver locker systems designed for multiple industries and large-scale deployments.',
      whyFeature1: 'Robust Hardware',
      whyFeature1Desc: 'Industrial-grade lockers built for high-volume operations',
      whyFeature2: 'IoT Connectivity',
      whyFeature2Desc: 'LoRaWAN, cellular, and WiFi options for any environment',
      whyFeature3: 'Cloud Platform',
      whyFeature3Desc: 'Complete management, analytics, and notification system',
      whyFeature4: 'Open Integrations',
      whyFeature4Desc: 'APIs for e-commerce, logistics, identity, and payment systems',
      whyFeature5: 'Multi-Industry Experience',
      whyFeature5Desc: 'Logistics, retail, industrial, and public sector deployments',
      relatedSolutions: 'Related Solutions',
      assetTrackingLabel: 'Asset Tracking',
      assetTrackingDesc: 'Track tools and equipment beyond the locker',
      connectivityLabel: 'IoT Connectivity',
      connectivityDesc: 'LoRaWAN, NB-IoT, and cellular options',
      smartCityLabel: 'Smart City Infrastructure',
      smartCityDesc: 'Urban IoT deployments',
      // FAQ
      faqBadge: 'FAQ',
      faqTitle: 'Frequently Asked Questions',
      faq1Q: 'What are smart lockers?',
      faq1A: 'Smart lockers are connected storage systems that allow items to be securely stored and retrieved using digital authentication such as PIN codes, mobile apps, RFID cards, or QR codes. Unlike traditional lockers with physical keys, smart lockers connect to cloud platforms that manage access control, send notifications, and provide usage analytics.',
      faq2Q: 'How do parcel lockers work?',
      faq2A: 'When a parcel is delivered to a locker, the recipient receives an SMS or email notification with a unique PIN code. They visit the locker location, enter the PIN on the touchscreen, and the compartment containing their parcel opens automatically. Parcels are typically held for 7 days before being returned to sender. Anyone with the PIN can collect—no ID verification required.',
      faq3Q: 'What industries use smart lockers?',
      faq3A: 'Smart lockers are used across many industries: logistics and e-commerce (parcel delivery), retail (click-and-collect pickup), manufacturing and construction (tool management), hospitality and tourism (beach and leisure lockers), corporate offices (mail and package rooms), healthcare (specimen and medication storage), and municipalities (public storage and city services).',
      faq4Q: 'Can lockers operate without electricity?',
      faq4A: 'Yes. Battery-powered lockers can operate for extended periods without electrical infrastructure. This enables deployment in locations like beaches, parks, temporary installations, and remote areas where wiring would be expensive or impractical. Battery lockers communicate via cellular or LoRaWAN networks.',
      faq5Q: 'How secure are smart lockers?',
      faq5A: 'Smart lockers provide multiple layers of security: digital access control ensures only authorized users can open compartments, every transaction is logged creating a complete audit trail, compartments use robust locking mechanisms, and cloud platforms enable remote monitoring and alerts. For high-security applications, lockers can integrate with biometric authentication.',
      faq6Q: 'Can locker systems integrate with existing platforms?',
      faq6A: 'Yes. Modern locker platforms provide APIs that enable integration with e-commerce platforms (for order management), identity systems (SSO, LDAP), payment gateways, logistics software (TMS), smart city platforms, and enterprise ERP systems. This allows lockers to become part of broader business workflows rather than standalone systems.',
      // CTA
      ctaTitle: 'Deploy smart lockers for your organization',
      ctaButton: 'Learn More',
    },

    // =====================
    // BUILDING ENERGY PAGE
    // =====================
    buildingEnergyPage: {
      metaTitle: 'Building Energy Efficiency | IoT Solutions | 0g Baltics',
      metaDesc: 'Reduce building energy consumption with IoT monitoring and smart controls. HVAC optimization, lighting control, and energy analytics.',
      heroBadge: 'Energy Efficiency',
      heroTitle: 'Building Energy Efficiency Solutions',
      heroSubtitle: 'Monitor and optimize energy consumption in commercial buildings with IoT sensors and smart controls.',
      // Hero badges
      heroBadge1: 'Energy Monitoring',
      heroBadge2: 'HVAC Optimization',
      heroBadge3: 'Occupancy Sensing',
      heroBadge4: 'Analytics',
      heroBadge5: 'Cost Reduction',
      // Problem
      problemBadge: 'The Challenge',
      problemTitle: 'Buildings Waste Energy',
      problemDesc: 'Commercial buildings account for 40% of energy consumption. Most operate inefficiently due to lack of visibility and control.',
      // HVAC Section
      hvacBadge: 'Largest Opportunity',
      hvacTitle: 'HVAC Optimization',
      hvacDesc: 'HVAC systems represent 40-60% of building energy use. Smart controls can reduce this significantly.',
      hvacParadoxTitle: 'The Paradox of Current Regulation Systems',
      hvacParadoxDesc: 'Traditional HVAC regulation systems, paradoxically, often lead to higher energy consumption.',
      hvacProblem1: 'On/off cycling creates temperature oscillations',
      hvacProblem2: 'Fixed schedules ignore actual usage patterns',
      hvacProblem3: 'No correlation between CO2 and ventilation',
      hvacProblem4: 'Systems compensate with oversized equipment',
      harmonySolution: 'Smart Solution',
      harmonyTitle: 'TEKTELIC Harmony',
      harmonyDesc: 'Intelligent thermostat combining CO2 monitoring, temperature/humidity sensing, and HVAC control.',
      harmonyBenefit: 'By correlating CO2 levels with ventilation needs and actual occupancy, the Harmony optimizes HVAC operation to reduce energy consumption by 20-40%.',
      // Invisibility section
      invisibilityBadge: 'The Core Challenge',
      invisibilityTitle: 'Energy Consumption Is Often Invisible',
      invisibilityDesc: 'Many organizations do not have clear visibility into how their buildings actually consume energy.',
      invisibility1: 'Which equipment consumes the most energy',
      invisibility2: 'When consumption peaks occur',
      invisibility3: 'Where inefficiencies exist',
      invisibility4: 'Whether consumption is increasing',
      withoutMonitoring: 'Without Monitoring',
      withMonitoring: 'With IoT Monitoring',
      // Stats
      stat1: 'of global energy consumed by buildings',
      stat2: 'typical energy waste in commercial buildings',
      stat3: 'savings achievable with smart monitoring',
      stat4: 'typical payback period',
      // IoT Monitoring Section
      iotMonitoringBadge: 'Technology Foundation',
      iotMonitoringTitle: 'IoT Energy Monitoring Systems',
      iotMonitoringDesc: 'IoT sensors allow continuous monitoring of building energy usage at granular levels. Unlike traditional utility metering that provides only aggregate monthly data, smart sensors capture detailed information about how, when, and where energy is consumed.',
      iotDevicesMeasure: 'IoT devices can measure:',
      measureElectricity: 'Electricity',
      measureElectricityDesc: 'Total and circuit-level consumption',
      measureWater: 'Water',
      measureWaterDesc: 'Usage patterns and leak detection',
      measureTemperature: 'Temperature',
      measureTemperatureDesc: 'Zone-by-zone thermal data',
      measureHumidity: 'Humidity',
      measureHumidityDesc: 'Comfort and system performance',
      measureOccupancy: 'Occupancy',
      measureOccupancyDesc: 'Space utilization patterns',
      measureEquipment: 'Equipment',
      measureEquipmentDesc: 'Runtime and operational status',
      iotDataTransmission: 'This data is transmitted wirelessly via LoRaWAN or cellular networks to digital platforms where operators can analyze consumption patterns and identify optimization opportunities.',
      // Energy Analytics Section
      analyticsBadge: 'Data-Driven Decisions',
      analyticsTitle: 'Energy Analytics and Optimization',
      analyticsDesc: 'Once energy data is collected, analytics tools help identify inefficiencies and optimization opportunities. Modern energy management platforms apply algorithms to detect patterns, anomalies, and correlations that would be impossible to identify manually.',
      typicalInsights: 'Typical insights include:',
      insight1: 'Abnormal energy consumption indicating equipment problems',
      insight2: 'Equipment running outside scheduled operating hours',
      insight3: 'Inefficient heating or cooling system operation',
      insight4: 'Poor building scheduling wasting energy',
      insight5: 'Consumption trends requiring attention',
      analyticsConclusion: 'Organizations can use these insights to prioritize improvements, verify the impact of changes, and continuously optimize building operations.',
      // Occupancy Section
      occupancyBadge: 'Demand-Driven Control',
      occupancyTitle: 'Occupancy-Based Energy Management',
      occupancyDesc: 'Buildings are often heated, cooled, and lit even when spaces are completely empty. Conference rooms prepared for meetings that don\'t happen, offices conditioned on weekends, entire floors lit overnight—this represents significant energy waste.',
      occupancyDesc2: 'Occupancy sensors allow buildings to adapt energy usage to actual utilization patterns. When spaces are unoccupied, lighting can dim or turn off, HVAC can reduce output, and equipment can enter standby modes. When people arrive, systems respond automatically.',
      highImpactApps: 'High-impact applications:',
      appMeetingRooms: 'Meeting rooms',
      appClassrooms: 'Classrooms',
      appIndividualOffices: 'Individual offices',
      appPublicBuildings: 'Public buildings',
      appCommonAreas: 'Common areas',
      appParkingFacilities: 'Parking facilities',
      // Sensors Section
      sensorsBadge: 'Sensor Infrastructure',
      sensorsTitle: 'Sensors for Energy Efficient Buildings',
      sensorsDesc: 'Energy optimization relies on multiple sensor types working together to create a complete picture of building conditions. Each sensor provides specific data that contributes to overall energy intelligence.',
      sensorTemp: 'Temperature Sensors',
      sensorTempDesc: 'Monitor thermal conditions across zones to optimize heating and cooling. Identify hot and cold spots indicating HVAC imbalances.',
      sensorHumidity: 'Humidity Sensors',
      sensorHumidityDesc: 'Track moisture levels affecting comfort and system performance. Optimize dehumidification and prevent mold conditions.',
      sensorCO2: 'CO2 Sensors',
      sensorCO2Desc: 'Measure air quality and ventilation adequacy. Enable demand-controlled ventilation that balances air quality and energy.',
      sensorOccupancy: 'Occupancy Sensors',
      sensorOccupancyDesc: 'Detect space utilization in real time. Enable automatic adjustment of lighting, HVAC, and equipment based on presence.',
      sensorEnergy: 'Energy Meters',
      sensorEnergyDesc: 'Measure electricity consumption at building, floor, or circuit level. Identify high-consumption equipment and usage patterns.',
      sensorWater: 'Water Meters',
      sensorWaterDesc: 'Track water consumption and detect leaks. Identify abnormal usage indicating plumbing problems or waste.',
      learnMoreIAQ: 'Learn more about Indoor Air Quality Monitoring',
      // Standards Section
      standardsBadge: 'Regulatory Context',
      standardsTitle: 'Energy Efficiency Standards and Regulations',
      standardsDesc: 'Many countries now require improved building energy performance. Regulatory frameworks are becoming stricter, and organizations increasingly need monitoring systems to support compliance and sustainability reporting.',
      epbdTitle: 'EU Energy Performance of Buildings Directive (EPBD)',
      epbdDesc: 'Requires member states to establish minimum energy performance requirements for buildings and implement energy certification systems. The directive is pushing toward nearly zero-energy buildings.',
      epcTitle: 'Energy Performance Certificates (EPC)',
      epcDesc: 'Buildings must be rated for energy efficiency, with certificates required for sale or rental in many jurisdictions. Better ratings can increase property value and rental attractiveness.',
      esgTitle: 'ESG and Sustainability Reporting',
      esgDesc: 'Corporate sustainability reporting increasingly requires detailed energy consumption data. Monitoring systems provide the verifiable data needed for ESG disclosures and sustainability certifications.',
      monitoringSupports: 'Energy monitoring supports:',
      support1: 'Regulatory compliance documentation',
      support2: 'Energy Performance Certificate improvements',
      support3: 'ESG and sustainability reporting',
      support4: 'Carbon footprint calculation',
      support5: 'Green building certification (LEED, BREEAM)',
      support6: 'Audit and verification processes',
      // ROI Section
      roiBadge: 'Business Impact',
      roiTitle: 'The Business Case for Energy Monitoring',
      roiDesc: 'Energy monitoring provides measurable, verifiable benefits. Organizations that implement comprehensive monitoring systems typically see significant returns through reduced energy costs, improved operations, and avoided equipment failures.',
      typicalBenefits: 'Typical Benefits',
      benefit1: '10-25% reduction in energy costs',
      benefit2: 'Extended equipment lifetime',
      benefit3: 'Reduced maintenance costs',
      benefit4: 'Improved occupant comfort',
      benefit5: 'Better operational visibility',
      benefit6: 'Data for strategic decisions',
      investmentTitle: 'Investment Considerations',
      investment1: 'Low-cost wireless sensors',
      investment2: 'Minimal installation disruption',
      investment3: 'Scalable deployment approach',
      investment4: 'Cloud platform—no IT infrastructure',
      investment5: 'Measurable ROI within 2-3 years',
      investment6: 'Ongoing operational savings',
      roiNote: 'Even small improvements matter: A 10% reduction in energy consumption for a building with €100,000 annual energy costs generates €10,000 in annual savings—compounding year after year.',
      // Why 0g Baltics Section
      whyBadge: 'Your Partner',
      whyTitle: 'Why Work with 0g Baltics',
      whyDesc: '0g Baltics provides comprehensive energy monitoring solutions combining professional IoT sensors, reliable connectivity infrastructure, and powerful analytics platforms. Our goal is to help organizations reduce energy consumption and improve building performance.',
      whyFeature1: 'IoT Sensors for Buildings',
      whyFeature1Desc: 'Temperature, humidity, occupancy, energy, and environmental monitoring',
      whyFeature2: 'Energy Monitoring Systems',
      whyFeature2Desc: 'Real-time consumption tracking and analytics',
      whyFeature3: 'LoRaWAN Connectivity',
      whyFeature3Desc: 'Reliable, low-power wireless infrastructure',
      whyFeature4: 'Data Platforms and Analytics',
      whyFeature4Desc: 'Insights and optimization recommendations',
      whyFeature5: 'Smart Building Consulting',
      whyFeature5Desc: 'Strategy and implementation guidance',
      relatedSolutions: 'Related Solutions',
      relatedIAQ: 'Indoor Air Quality',
      relatedIAQDesc: 'CO2, temperature, humidity monitoring',
      relatedSmartCity: 'Smart City Infrastructure',
      relatedSmartCityDesc: 'Urban building deployments',
      relatedConnectivity: 'IoT Connectivity',
      relatedConnectivityDesc: 'LoRaWAN and cellular options',
      relatedMetering: 'Smart Metering',
      relatedMeteringDesc: 'Water and energy metering',
      // CTA
      ctaTitle: 'Improve Your Building Energy Efficiency',
      ctaDesc: 'Contact 0g Baltics to deploy smart energy monitoring systems for your buildings. Whether you manage offices, universities, or industrial facilities, we can help you reduce consumption and optimize operations.',
      ctaButton: 'Talk to our team',
    },

    // =====================
    // OFFICE SPACE PAGE
    // =====================
    officeSpacePage: {
      metaTitle: 'Office Space Management | Occupancy Analytics | 0g Baltics',
      metaDesc: 'Optimize office space utilization with IoT occupancy sensors. Desk booking, meeting room management, and space analytics.',
      heroBadge: 'Office Management',
      heroTitle: 'Smart Office Space Management',
      heroSubtitle: 'Optimize workspace utilization with occupancy sensors, desk booking systems, and analytics.',
      // Hero badges
      heroBadge1: 'Occupancy Sensing',
      heroBadge2: 'Desk Monitoring',
      heroBadge3: 'Meeting Room Analytics',
      heroBadge4: 'Hybrid Work',
      // What Is Section
      whatIsBadge: 'Understanding the Challenge',
      whatIsTitle: 'What Is Office Space Management?',
      whatIsDesc: 'Office space management is the practice of understanding, monitoring, and optimizing how physical workplace spaces are used.',
      driversTitle: 'Key drivers for smart office management:',
      driver1: 'Hybrid work models',
      driver2: 'Flexible office layouts',
      driver3: 'Shared desk policies',
      driver4: 'Meeting room optimization',
      visibilityTitle: 'IoT sensors provide visibility into:',
      visibility1: 'Real-time occupancy',
      visibility1Desc: 'Which spaces are currently in use',
      visibility2: 'Usage patterns',
      visibility2Desc: 'Peak hours, quiet periods, daily trends',
      visibility3: 'Space efficiency',
      visibility3Desc: 'Utilization rates across zones',
      visibility4: 'Environmental conditions',
      visibility4Desc: 'Temperature, air quality, comfort',
      // Hidden Costs Section
      costBadge: 'The Business Problem',
      costTitle: 'The Hidden Cost of Empty Office Space',
      costDesc: 'Studies consistently show that many companies have 30-60% unused office capacity at any given time.',
      costsTitle: 'Operational costs for empty space:',
      cost1: 'Rent and lease costs',
      cost2: 'Heating expenses',
      cost3: 'Cooling and electricity',
      cost4: 'Lighting costs',
      stat1: 'typical unused office capacity',
      stat2: 'cost per sqm per year (EU average)',
      stat3: 'potential savings with optimization',
      stat4: 'typical ROI for smart office systems',
      // Smart Sensors Section
      sensorsBadge: 'Sensing Technology',
      sensorsTitle: 'Smart Office Sensors',
      sensorsDesc: 'Different types of IoT sensors serve different purposes in office space management. All sensors collect anonymous usage data—no personal identification or surveillance—providing aggregate insights that respect employee privacy.',
      occupancySensors: 'Occupancy Sensors',
      occupancySensorsDesc: 'Ceiling-mounted sensors detect presence and count people in areas such as open floors, common areas, and large meeting rooms. Provide zone-level occupancy data.',
      deskSensors: 'Desk Sensors',
      deskSensorsDesc: 'Small sensors mounted under desks detect whether individual workstations are occupied. Enable precise desk-by-desk utilization tracking for hot-desking environments.',
      meetingRoomSensors: 'Meeting Room Sensors',
      meetingRoomSensorsDesc: 'Dedicated sensors for meeting rooms track actual usage vs. calendar bookings, detect no-shows, and identify ghost meetings that waste valuable space.',
      bestFor: 'Best for:',
      openPlanOffices: 'Open plan offices',
      commonAreas: 'Common areas',
      cafeterias: 'Cafeterias',
      largeMeetingRooms: 'Large meeting rooms',
      hotDeskingAreas: 'Hot-desking areas',
      assignedDesks: 'Assigned desks',
      focusBooths: 'Focus booths',
      phoneRooms: 'Phone rooms',
      conferenceRooms: 'Conference rooms',
      huddleSpaces: 'Huddle spaces',
      videoCallRooms: 'Video call rooms',
      executiveSuites: 'Executive suites',
      // IAQ Section
      iaqBadge: 'Environmental Intelligence',
      iaqTitle: 'Indoor Air Quality Monitoring',
      iaqDesc: 'Smart offices should monitor more than just occupancy. Environmental conditions directly impact employee comfort, productivity, and well-being. Poor air quality, uncomfortable temperatures, or high humidity create suboptimal working conditions.',
      iaqDesc2: 'Integrating environmental sensors with occupancy data provides a complete picture of workplace conditions, enabling facility managers to optimize both space utilization and comfort.',
      co2Levels: 'CO2 Levels',
      co2LevelsDesc: 'Indicates ventilation adequacy and air freshness',
      temperature: 'Temperature',
      temperatureDesc: 'Thermal comfort monitoring across zones',
      humidity: 'Humidity',
      humidityDesc: 'Impacts comfort and building health',
      ventilation: 'Ventilation',
      ventilationDesc: 'Air quality and circulation assessment',
      impactTitle: 'Impact on workplace performance',
      impactStat1: 'productivity increase with improved air quality',
      impactStat2: 'reduction in sick days with better ventilation',
      impactStat3: 'cognitive improvement with lower CO2 levels',
      learnMoreIAQ: 'Learn more about IAQ Monitoring',
      // Analytics Platform Section
      analyticsBadge: 'Data-Driven Insights',
      analyticsTitle: 'Office Analytics Platform',
      analyticsDesc: 'All sensors connect to a cloud-based analytics platform that transforms raw data into actionable insights. Facility managers gain complete visibility into how their office spaces are actually used, enabling evidence-based decisions.',
      platformCapabilities: 'Platform capabilities:',
      realTimeDashboards: 'Real-time Dashboards',
      realTimeDashboardsDesc: 'Live view of current occupancy across all zones',
      occupancyHeatmaps: 'Occupancy Heatmaps',
      occupancyHeatmapsDesc: 'Visual representation of high and low usage areas',
      meetingRoomAnalytics: 'Meeting Room Analytics',
      meetingRoomAnalyticsDesc: 'Booking vs actual usage, no-show rates',
      historicalTrends: 'Historical Trends',
      historicalTrendsDesc: 'Daily, weekly, monthly usage patterns',
      spaceEfficiencyMetrics: 'Space Efficiency Metrics',
      spaceEfficiencyMetricsDesc: 'Utilization rates and optimization opportunities',
      // Hybrid Workplace Section
      hybridBadge: 'Modern Work Models',
      hybridTitle: 'Hybrid Workplace Management',
      hybridDesc: 'Office analytics becomes even more critical in hybrid work environments where employees split time between office and remote work. Understanding actual usage patterns helps organizations right-size their real estate and design spaces that support flexible work.',
      deskBookingSystems: 'Desk Booking Systems',
      deskBookingSystemsDesc: 'Allow employees to reserve desks in advance, with real-time availability showing which spaces are free. Occupancy sensors validate bookings and release no-shows automatically.',
      meetingRoomAvailability: 'Meeting Room Availability',
      meetingRoomAvailabilityDesc: 'Show real-time availability based on actual occupancy rather than calendar bookings alone. Identify and release ghost meetings, making more rooms available for productive use.',
      spacePlanning: 'Space Planning',
      spacePlanningDesc: 'Use historical data to plan office layouts that match actual demand. Determine optimal ratios of desks to employees, collaboration spaces, and quiet zones.',
      // Benefits Section
      benefitsBadge: 'Business Impact',
      benefitsTitle: 'Benefits for Companies',
      benefitsDesc: 'Smart office space management delivers measurable benefits across multiple dimensions—from direct cost savings to improved employee experience and sustainability outcomes.',
      reducedCosts: 'Reduced Real Estate Costs',
      reducedCostsDesc: 'Identify underutilized space and right-size your office footprint. Many organizations reduce leased space by 15-30% after implementing analytics.',
      betterExperience: 'Better Employee Experience',
      betterExperienceDesc: 'Help employees find available desks and meeting rooms easily. Reduce friction and frustration in the workplace.',
      higherProductivity: 'Higher Productivity',
      higherProductivityDesc: 'Optimize office layouts based on how people actually work. Create the right mix of collaboration and focus spaces.',
      dataDrivenPlanning: 'Data-Driven Planning',
      dataDrivenPlanningDesc: 'Replace assumptions with facts. Make real estate and workplace decisions based on actual usage data.',
      sustainabilityImprovements: 'Sustainability Improvements',
      sustainabilityImprovementsDesc: 'Reduce energy waste by conditioning only occupied spaces. Support ESG goals with measurable efficiency gains.',
      operationalVisibility: 'Operational Visibility',
      operationalVisibilityDesc: 'Understand your workplace like never before. Facility managers gain tools to optimize and improve continuously.',
      // CTA
      ctaTitle: 'Optimize Your Workplace',
      ctaDesc: 'Contact 0g Baltics to design smart office solutions that improve efficiency and reduce costs. Whether you manage corporate headquarters, university campuses, or commercial buildings, we can help.',
      ctaButton: 'Talk to our team',
      // Use Cases Section
      useCasesBadge: 'Applications',
      useCasesTitle: 'Use Cases',
      useCasesDesc: 'Smart office solutions serve organizations across industries and sizes, from corporate headquarters to educational institutions.',
      corporateHQ: 'Corporate Headquarters',
      corporateHQDesc: 'Large enterprises use office analytics to optimize multi-floor headquarters, manage hot-desking programs, and support hybrid work policies across thousands of employees.',
      techCompanies: 'Technology Companies',
      techCompaniesDesc: 'Tech firms with flexible work cultures rely on desk booking and occupancy data to create dynamic workspaces that adapt to changing team needs.',
      governmentBuildings: 'Government Buildings',
      governmentBuildingsDesc: 'Public sector organizations use analytics to optimize taxpayer-funded facilities and demonstrate efficient use of public resources.',
      universities: 'Universities',
      universitiesDesc: 'Educational institutions monitor lecture halls, libraries, and study spaces to optimize scheduling and understand student usage patterns.',
      coworkingSpaces: 'Coworking Spaces',
      coworkingSpacesDesc: 'Flexible workspace operators use real-time occupancy to manage desk availability, optimize layouts, and provide accurate capacity data to members.',
      commercialRealEstate: 'Commercial Real Estate',
      commercialRealEstateDesc: 'Property managers offer building analytics as a tenant amenity, providing occupancy insights that help businesses optimize their leased spaces.',
      // IoT Connectivity Section
      iotConnectivityBadge: 'Network Infrastructure',
      iotConnectivityTitle: 'IoT Connectivity for Smart Buildings',
      iotConnectivityDesc: '0g Baltics uses low-power IoT networks designed for building environments. These networks enable wireless sensor deployment without complex wiring, with battery life measured in years rather than months.',
      connectivityOptions: 'Supported connectivity options:',
      lorawanName: 'LoRaWAN',
      lorawanDesc: 'Long range, low power, ideal for large buildings',
      nbiotName: 'NB-IoT',
      nbiotDesc: 'Cellular-based, deep indoor penetration',
      ltemName: 'LTE-M',
      ltemDesc: 'Mobile assets and higher bandwidth needs',
      wifiName: 'Wi-Fi',
      wifiDesc: 'Integration with existing IT infrastructure',
      keyAdvantages: 'Key advantages:',
      longBatteryLife: 'Long Battery Life',
      longBatteryLifeDesc: '5-10 years typical sensor operation',
      lowMaintenance: 'Low Maintenance',
      lowMaintenanceDesc: 'Set and forget installation',
      secureCommunication: 'Secure Communication',
      secureCommunicationDesc: 'Encrypted data transmission',
      scalableDeployment: 'Scalable Deployment',
      scalableDeploymentDesc: 'From pilot to building-wide rollout',
      learnMoreConnectivity: 'Learn more about IoT Connectivity',
      // Why 0g Baltics Section
      whyBadge: 'Your Partner',
      whyTitle: 'Why 0g Baltics',
      whyDesc: '0g Baltics delivers complete smart office solutions—from IoT sensors to cloud analytics platforms. We combine hardware expertise, connectivity infrastructure, and analytics capabilities into integrated solutions for workplace optimization.',
      service1: 'IoT Sensors',
      service1Desc: 'Occupancy, desk, meeting room, and environmental sensors',
      service2: 'Connectivity Infrastructure',
      service2Desc: 'LoRaWAN, cellular, and hybrid network options',
      service3: 'Cloud Platforms',
      service3Desc: 'Analytics dashboards and reporting tools',
      service4: 'System Integration',
      service4Desc: 'Connect with existing building and IT systems',
      service5: 'Smart Building Experience',
      service5Desc: 'Expertise from smart city and building projects',
      relatedSolutions: 'Related Solutions',
      relatedSmartCity: 'Smart City Infrastructure',
      relatedSmartCityDesc: 'Urban and building IoT networks',
      relatedSmartLockers: 'Smart Lockers',
      relatedSmartLockersDesc: 'Secure storage solutions',
      relatedAssetTracking: 'Asset Tracking',
      relatedAssetTrackingDesc: 'Equipment and resource monitoring',
      relatedConnectivity: 'IoT Connectivity',
      relatedConnectivityDesc: 'LoRaWAN and cellular networks',
    },

    // =====================
    // ASSET TRACKING PAGE
    // =====================
    assetTrackingPage: {
      metaTitle: 'Asset Tracking Solutions | GPS & IoT | 0g Baltics',
      metaDesc: 'Track assets indoors and outdoors with LoRaWAN, GPS, BLE, and cellular connectivity. Solutions for logistics, healthcare, and industry.',
      heroBadge: 'Asset Tracking',
      heroTitle: 'Asset Tracking Solutions',
      heroSubtitle: 'Track equipment, vehicles, and inventory with indoor and outdoor positioning solutions.',
      // Types
      indoorTracking: 'Indoor Tracking',
      indoorTrackingDesc: 'BLE and LoRaWAN-based positioning for warehouses and facilities.',
      outdoorTracking: 'Outdoor Tracking',
      outdoorTrackingDesc: 'GPS and cellular tracking for vehicles and mobile assets.',
      hybridTracking: 'Hybrid Tracking',
      hybridTrackingDesc: 'Seamless indoor-outdoor tracking with automatic switching.',
      // Industries
      industriesBadge: 'Industries',
      logistics: 'Logistics',
      logisticsDesc: 'Track shipments, containers, and fleet vehicles.',
      healthcare: 'Healthcare',
      healthcareDesc: 'Medical equipment tracking in hospitals and clinics.',
      manufacturing: 'Manufacturing',
      manufacturingDesc: 'Tool and work-in-progress tracking on the factory floor.',
      // CTA
      ctaTitle: 'Start tracking your assets',
      ctaButton: 'Request a Demo',
      // What Is Section
      whatIsBadge: 'Understanding the Technology',
      whatIsTitle: 'What is IoT Asset Tracking?',
      whatIsDesc: 'IoT asset tracking enables companies to monitor the location, status and usage of physical assets in real time.',
      assetsTitle: 'Assets that can be tracked include:',
      asset1: 'Construction equipment',
      asset2: 'Logistics containers',
      asset3: 'Vehicles and trailers',
      asset4: 'Industrial tools',
      asset5: 'Medical equipment',
      asset6: 'Temperature-sensitive cargo',
      // Why Section
      whyBadge: 'Business Impact',
      whyTitle: 'Why Companies Deploy Asset Tracking',
      whyDesc: 'Organizations deploy asset tracking to solve critical operational problems that directly impact profitability.',
      benefit1: 'Asset theft prevention',
      benefit1Desc: 'Reduce theft by 60%+',
      benefit2: 'Utilization optimization',
      benefit2Desc: 'Increase asset use by 25%+',
      benefit3: 'Maintenance scheduling',
      benefit3Desc: 'Prevent costly breakdowns',
      benefit4: 'Location visibility',
      benefit4Desc: 'Know where assets are 24/7',
      // Multi-Connectivity Section
      networkBadge: 'Network Architecture',
      networkTitle: 'Why Multi-Connectivity is Essential',
      networkDesc: 'Most IoT deployments fail when they rely on a single connectivity technology. Assets don\'t stay in one place—they move between industrial campuses, urban environments, logistics routes, and remote locations where network coverage varies dramatically.',
      networkDesc2: '0g Baltics designs multi-connectivity architectures that combine multiple technologies. Devices automatically select the best available network depending on the environment, ensuring continuous asset visibility regardless of location.',
      lorawanName: 'LoRaWAN',
      lorawanDesc: 'Long-range, low-power for campus and regional coverage',
      nbiotName: 'NB-IoT / LTE-M',
      nbiotDesc: 'Cellular connectivity for urban and mobile assets',
      bleName: 'BLE Beacons',
      bleDesc: 'Indoor positioning and proximity tracking',
      satelliteName: 'Satellite',
      satelliteDesc: 'Global coverage for remote and out-of-network locations',
      // Tracking Devices Section
      devicesBadge: 'Hardware Solutions',
      devicesTitle: 'Professional Asset Tracking Devices',
      devicesDesc: '0g Baltics integrates professional-grade tracking devices from leading manufacturers such as Digital Matter. These devices provide GPS tracking, satellite connectivity, cellular connectivity, BLE gateway capabilities, and multi-year battery life—designed for industrial environments and global deployments.',
      gpsTracking: 'GPS Tracking',
      gpsTrackingDesc: 'High-precision outdoor positioning with GNSS',
      satelliteConnectivity: 'Satellite Connectivity',
      satelliteConnectivityDesc: 'Iridium satellite for remote locations',
      cellularNetworks: 'Cellular Networks',
      cellularNetworksDesc: '4G LTE, LTE-M, NB-IoT connectivity',
      multiYearBattery: 'Multi-Year Battery',
      multiYearBatteryDesc: 'Up to 10+ years with smart power management',
      compactTracker: 'Compact Asset Tracker',
      compactTrackerDesc: 'Rugged battery-powered GPS tracker for indoor/outdoor asset tracking with magnetic activation.',
      longLifeTracker: 'Long-Life Tracker',
      longLifeTrackerDesc: 'GPS tracker with the longest battery life on the market—up to 10+ years for set-and-forget deployments.',
      globalSatelliteTracker: 'Global Satellite Tracker',
      globalSatelliteTrackerDesc: 'Rugged wired tracker with Iridium satellite and 4G/2G connectivity for critical global deployments.',
      // Campus Tracking Section
      campusBadge: 'On-Site Visibility',
      campusTitle: 'On-Campus Asset Tracking',
      campusDesc: 'Large industrial sites operate like small cities. Factories, airports, industrial parks, universities, and energy facilities contain thousands of assets that move daily. Without tracking, organizations lose visibility the moment equipment leaves a central warehouse.',
      campusDesc2: 'Using LoRaWAN gateways and BLE beacons, organizations can track equipment across entire campuses with meter-level accuracy. Indoor/outdoor positioning eliminates blind spots, while zone-based alerts ensure assets stay where they belong.',
      campusDeployments: 'Typical Campus Deployments',
      campusSite1: 'Manufacturing plants',
      campusSite2: 'Airport facilities',
      campusSite3: 'Hospital campuses',
      campusSite4: 'University grounds',
      campusSite5: 'Energy installations',
      campusSite6: 'Logistics hubs',
      multiSiteBadge: 'Multi-Site Operations',
      multiSiteTitle: 'Campus-to-Campus Visibility',
      multiSiteDesc: 'Assets often move between multiple sites. Construction machinery rotates between projects. Mobile generators travel to temporary installations. Maintenance equipment serves multiple facilities. Without tracking, inter-site movements create visibility gaps.',
      multiSiteDesc2: 'IoT tracking enables organizations to monitor asset movement between facilities, understand transit times, and improve utilization across the entire organization—not just individual sites.',
      realImpact: 'Real Impact',
      realImpactDesc: 'Companies typically see 25% improvement in asset utilization when they gain visibility across multiple sites, reducing the need for duplicate equipment purchases.',
      // Global Tracking Section
      globalBadge: 'Worldwide Coverage',
      globalTitle: 'Global Asset Tracking',
      globalDesc: 'For logistics environments, assets travel across large distances—between countries, continents, and oceans. Shipping containers move from factories in Asia to distribution centers in Europe. High-value equipment travels to remote project sites. Temperature-sensitive pharmaceuticals require continuous monitoring during global transit.',
      globalDesc2: 'Global tracking systems combine cellular networks (available in 190+ countries), satellite connectivity (for ocean crossings and remote areas), and mobile gateways (for real-time in-transit monitoring). This ensures reliable tracking virtually anywhere on Earth.',
      globalStat1: '190+',
      globalStat1Label: 'Countries with cellular coverage',
      globalStat2: '100%',
      globalStat2Label: 'Global satellite coverage',
      globalStat3: '130',
      globalStat3Label: 'Countries with active deployments',
      globalStat4: '2.5M+',
      globalStat4Label: 'Devices deployed worldwide',
      // Cold Chain Section
      coldChainBadge: 'Temperature-Sensitive Logistics',
      coldChainTitle: 'Cold Chain Monitoring with IoT',
      coldChainDesc: 'Temperature-sensitive logistics require continuous monitoring throughout the supply chain. Pharmaceuticals, vaccines, fresh food, and biological samples can be ruined by even brief temperature excursions. Traditional monitoring with manual checks and paper logs is inadequate for modern compliance requirements.',
      coldChainDesc2: 'IoT sensors and BLE beacons continuously monitor temperature, humidity, and movement. Mobile gateways in vehicles collect sensor data and transmit it to cloud platforms in real time. If thresholds are exceeded, alerts are generated immediately—enabling intervention before product loss occurs.',
      tempLogging: 'Continuous Temperature Logging',
      tempLoggingDesc: 'Sensor readings every 15 minutes with cloud storage',
      thresholdAlerts: 'Real-Time Threshold Alerts',
      thresholdAlertsDesc: 'Instant notifications when limits are exceeded',
      chainCustody: 'Complete Chain of Custody',
      chainCustodyDesc: 'Audit trail from origin to destination',
      regulatoryCompliance: 'Regulatory Compliance',
      regulatoryComplianceDesc: 'GDP, HACCP, and pharma standards supported',
      // Asset Recovery Section
      securityBadge: 'Security & Protection',
      securityTitle: 'Asset Recovery and Theft Detection',
      securityDesc: 'High-value assets represent significant capital investment. Construction equipment, specialized tools, and vehicles are prime targets for theft. When theft occurs, rapid recovery is critical—both to minimize financial loss and to maintain project schedules.',
      securityDesc2: 'GPS tracking devices with covert installation options enable instant location of stolen assets. Geofencing creates virtual boundaries—when an asset leaves an authorized zone, alerts are triggered immediately. Anti-theft mode activates high-frequency GPS updates for real-time tracking during recovery operations.',
      theftPrevention: 'Theft Prevention Features',
      theftFeature1: 'Geofencing with instant breach alerts',
      theftFeature2: 'After-hours movement detection',
      theftFeature3: 'Covert device installation',
      theftFeature4: 'RF beaconing for last-mile recovery',
      theftFeature5: 'Law enforcement coordination support',
      analyticsBadge: 'Operational Intelligence',
      analyticsTitle: 'AI and Analytics',
      analyticsDesc: 'Asset tracking generates massive amounts of operational data. Without analytics, this data remains unused. Advanced platforms transform raw location and sensor data into actionable business intelligence.',
      utilizationStats: 'Utilization Statistics',
      utilizationStatsDesc: 'Understand asset usage patterns',
      predictiveMaintenance: 'Predictive Maintenance',
      predictiveMaintenanceDesc: 'Anticipate service needs',
      fleetOptimization: 'Fleet Optimization',
      fleetOptimizationDesc: 'Improve operational efficiency',
      customReporting: 'Custom Reporting',
      customReportingDesc: 'Business intelligence exports',
      // Final CTA Section
      finalCtaTitle: 'Deploy Asset Tracking with 0g Baltics',
      finalCtaDesc: '0g Baltics designs and deploys advanced IoT asset tracking solutions across the Baltic region. From single-site pilots to enterprise-wide deployments, we provide the hardware, connectivity, and expertise to transform your asset management.',
      finalCtaButton: 'Talk to an Expert',
    },

    // =====================
    // TOOL TRACKING PAGE
    // =====================
    toolTrackingPage: {
      metaTitle: 'Tool Tracking | Industrial Tool Management | 0g Baltics',
      metaDesc: 'Track industrial tools with RFID, smart lockers, and IoT sensors. Reduce tool loss, improve utilization, and automate inventory.',
      heroBadge: 'Tool Tracking',
      heroTitle: 'Industrial Tool Tracking Solutions',
      heroSubtitle: 'Reduce tool loss and improve utilization with smart tracking and automated distribution systems.',
      // Features
      rfidTracking: 'RFID Tracking',
      rfidTrackingDesc: 'Tag and track every tool with RFID technology.',
      smartLockers: 'Smart Lockers',
      smartLockersDesc: 'Automated tool distribution with access control.',
      inventory: 'Inventory Management',
      inventoryDesc: 'Know exactly what tools you have and where they are.',
      analytics: 'Usage Analytics',
      analyticsDesc: 'Understand tool utilization and optimize your inventory.',
      // CTA
      ctaTitle: 'Stop losing tools',
      ctaButton: 'Learn More',
      // BLE Tracking Section
      bleSectionBadge: 'BLE Asset Tracking',
      bleSectionTitle: 'Tool Tracking: Know Where Every Tool Is',
      bleSectionDesc: 'Attach BLE sensors to your tools and equipment. Get real-time visibility on location, usage, and maintenance status.',
      // ROI Stats
      stat1: 'Reduction in tool losses',
      stat2: 'Increase in utilization',
      stat3: 'Less time searching',
      stat4: 'Lower replacement costs',
      // Why Track
      whyTrackTitle: 'Why Track Your Tools?',
      whyTrack1: 'Eliminate Theft & Loss',
      whyTrack1Desc: 'BLE sensors alert you when tools leave authorized zones.',
      whyTrack2: 'Find Tools Instantly',
      whyTrack2Desc: 'Open the app and locate any tool on a map in seconds.',
      whyTrack3: 'Automate Inventory',
      whyTrack3Desc: 'BLE gateways detect tools automatically in warehouses.',
      whyTrack4: 'Ensure Compliance',
      whyTrack4Desc: 'Track inspection dates. Get alerts before they expire.',
      // How It Works
      howItWorks: 'How It Works',
      howStep1: 'Attach BLE sensor (magnet, adhesive, or hidden)',
      howStep2: 'Smartphones & gateways detect tools automatically',
      howStep3: 'Track location, receive alerts, schedule maintenance',
      // Buttons
      requestDemo: 'Request a Demo',
      viewSolutions: 'View Solutions',
      scheduleDemo: 'Schedule a Demo',
      // BLE Trackers Section
      bleTrackersTitle: 'Industrial-Grade BLE Tracking Sensors',
      coinBeaconName: 'Coin Beacon',
      coinBeaconDesc: 'Ultra-compact tracker for small tools. 3+ year battery.',
      proAssetTagName: 'Pro Asset Tag',
      proAssetTagDesc: 'Professional-grade for power tools. BLE + LPWAN positioning.',
      industrialTrackerName: 'Industrial Tracker',
      industrialTrackerDesc: 'Heavy-duty with GPS, BLE & LTE for global tracking of high-value equipment.',
      learnConnectivity: 'Learn more about our connectivity technologies',
      // Distribution Section
      officialPartner: 'Official Partner',
      distributionTitle: 'Tools & Equipment Distribution',
      distributionDesc: 'Automated locker systems for secure distribution of tools, PPE, and consumables. Badge identification, full traceability, and real-time inventory management.',
      secureAccess: 'Secure Access',
      secureAccessDesc: 'Badge authentication for every transaction',
      fullTraceability: 'Full Traceability',
      fullTraceabilityDesc: 'Know who took what, when, and why',
      availability247: '24/7 Availability',
      availability247Desc: 'Self-service access anytime',
      realtimeDashboard: 'Real-time Dashboard',
      realtimeDashboardDesc: 'Monitor inventory and usage patterns',
      // Products
      xtowerTitle: 'PPE Dispenser X\'TOWER®',
      xtowerDesc: 'Secure and optimize your consumables with automated carousel distribution. Reduce storage space by 90%.',
      xstoreTitle: 'Store & Room Management: X\'STORE®',
      xstoreDesc: 'Secure and track your materials in a dedicated storage room. Full visibility on inventory levels.',
      xlockerTitle: 'Industrial Supplies Dispenser X\'LOCKER®',
      xlockerDesc: 'Secure and optimize your sensitive materials and tools.',
      xslingTitle: 'Wall-mounted Sling & Harness Rack X\'SLING®',
      xslingDesc: 'Ensure safety and operational status of your sensitive equipment.',
      xweightTitle: 'Small Consumables Weighing Dispenser: X\'WEIGHT®',
      xweightDesc: 'Secure and optimize the distribution of screws, bolts and small consumables.',
      comingSoon: 'Coming Soon',
      // Why It Matters Section
      whyMattersTitle: 'Why Tool Tracking & Smart Distribution Matter',
      whyMattersDesc: 'In industrial environments, unmanaged tools cost companies thousands of euros annually in losses, wasted time, and inefficiencies.',
      trackEveryTool: 'Track Every Tool',
      trackEveryToolDesc1: 'Without visibility, tools disappear. Workers hoard equipment "just in case." Theft goes unnoticed until inventory counts reveal massive gaps.',
      trackEveryToolDesc2: 'BLE tracking gives you real-time location data on every asset. Know who has what, where it is, and when it was last used. Reduce losses by 40% and eliminate the daily hunt for missing equipment.',
      simplifyDistribution: 'Simplify Distribution',
      simplifyDistributionDesc1: 'Manual tool cribs create bottlenecks. Workers wait in line. Inventory records are inaccurate. PPE expires unnoticed. Compliance becomes a nightmare.',
      simplifyDistributionDesc2: 'Smart lockers provide 24/7 self-service access with badge authentication. Every transaction is logged automatically. Inventory updates in real-time. Compliance is built-in.',
      readyTransform: 'Ready to Transform Your Tool Management?',
      readyTransformDesc: 'Contact us for a personalized demo and see how our solutions can reduce losses and improve operational efficiency.',
    },

    // =====================
    // CONNECTIVITY PAGE
    // =====================
    connectivityPage: {
      metaTitle: 'IoT Connectivity | LoRaWAN, Sigfox, NB-IoT | 0g Baltics',
      metaDesc: 'Deploy IoT connectivity infrastructure with LoRaWAN, Sigfox, NB-IoT, and cellular technologies. Network planning and deployment services.',
      // Hero Section
      heroBadge: 'Connectivity Infrastructure',
      heroTitle: 'IoT Connectivity Infrastructure',
      heroSubtitle: 'Choosing the right connectivity is essential for successful IoT deployments. 0g Baltics designs multi-network infrastructures combining LoRaWAN, Sigfox, NB-IoT, BLE and Satellite connectivity.',
      heroDesc: 'Every IoT project has different requirements in terms of coverage, power consumption, data frequency and infrastructure costs. Our role is to select and combine the most efficient connectivity technologies to ensure reliability, scalability and long-term sustainability.',
      // Multi-Connectivity Section
      multiBadge: 'Our Philosophy',
      multiTitle: 'Why Multi-Network IoT Matters',
      multiDesc: '0g Baltics never designs mono-connectivity solutions. We understand that different devices, use cases, and environments require different network technologies to achieve optimal performance.',
      multiPoint1: 'Different devices require different networks',
      multiPoint2: 'Infrastructure must remain flexible',
      multiPoint3: 'Cost optimization is essential',
      multiPoint4: 'Redundancy improves reliability',
      multiPoint5: 'Device longevity must be preserved',
      commitmentTitle: 'Our Commitment',
      commitmentDesc: '0g Baltics solutions are never based on a single connectivity technology. We design hybrid infrastructures that combine multiple networks to reduce operational costs while maximizing security and equipment longevity.',
      // LoRaWAN Section
      lorawanBadge: 'Long Range IoT',
      lorawanTitle: 'LoRaWAN Infrastructure',
      lorawanDesc: 'LoRaWAN (Long Range Wide Area Network) is the foundation of many large-scale IoT deployments. This technology enables long-range communication with minimal power consumption, making it ideal for smart city and utility applications.',
      lorawanAdvantages: 'Key Advantages',
      lorawanAdv1: 'Long range connectivity (up to 15km)',
      lorawanAdv2: 'Extremely low power consumption',
      lorawanAdv3: 'Private network infrastructure',
      lorawanAdv4: 'Ideal for smart cities and utilities',
      lorawanAdv5: 'Scalable deployments',
      lorawanNote: 'We deploy carrier-grade LoRaWAN infrastructure using Tektelic gateways for maximum reliability.',
      lorawanUseCases: 'Typical Use Cases',
      lorawanUse1Title: 'Smart Metering',
      lorawanUse1Desc: 'Remote reading of water, gas, and electricity meters',
      lorawanUse2Title: 'Environment Monitoring',
      lorawanUse2Desc: 'Air quality, temperature, humidity sensors',
      lorawanUse3Title: 'Asset Tracking',
      lorawanUse3Desc: 'Location tracking for vehicles and equipment',
      lorawanUse4Title: 'Smart Buildings',
      lorawanUse4Desc: 'Occupancy, energy, and facility management',
      lorawanUse5Title: 'City Infrastructure',
      lorawanUse5Desc: 'Street lighting, parking, waste management',
      // Sigfox Section
      sigfoxBadge: 'Global IoT Network',
      sigfoxTitle: 'Sigfox Connectivity',
      sigfoxDesc: 'Sigfox is a global LPWAN (Low Power Wide Area Network) technology designed for simple, low-cost IoT devices that transmit small amounts of data. Its ultra-low power consumption enables battery life measured in years rather than months.',
      sigfoxAdvantages: 'Key Advantages',
      sigfoxAdv1: 'Ultra low power consumption',
      sigfoxAdv2: 'Battery life up to 10+ years',
      sigfoxAdv3: 'Simple device integration',
      sigfoxAdv4: 'Global IoT network coverage',
      sigfoxWhen: 'When to Use Sigfox',
      sigfoxWhenDesc: 'Sigfox is the most efficient solution when devices need to send small amounts of data infrequently, and battery life is the primary constraint. The global network coverage eliminates the need for private infrastructure investment.',
      sigfoxUseCases: 'Typical Use Cases',
      sigfoxUse1Title: 'Asset Tracking',
      sigfoxUse1Desc: 'Long-term tracking with multi-year battery life',
      sigfoxUse2Title: 'Environment Sensors',
      sigfoxUse2Desc: 'Temperature, humidity, pressure monitoring',
      sigfoxUse3Title: 'Alarm Systems',
      sigfoxUse3Desc: 'Intrusion detection and security alerts',
      sigfoxUse4Title: 'Utility Monitoring',
      sigfoxUse4Desc: 'Leak detection and consumption alerts',
      // NB-IoT Section
      nbiotBadge: 'Cellular IoT',
      nbiotTitle: 'NB-IoT Cellular Connectivity',
      nbiotDesc: 'NB-IoT (Narrowband Internet of Things) leverages existing cellular infrastructure to provide reliable, secure connectivity for IoT devices. This technology excels in environments where deep indoor coverage and carrier-grade reliability are essential.',
      nbiotAdvantages: 'Key Advantages',
      nbiotAdv1: 'Telecom operator infrastructure',
      nbiotAdv2: 'Deep indoor coverage',
      nbiotAdv3: 'High reliability and security',
      nbiotAdv4: 'Wide area coverage',
      nbiotNote: 'NB-IoT is particularly beneficial for large infrastructure projects where devices are deployed in challenging environments such as underground utilities, basements, or industrial facilities.',
      nbiotUseCases: 'Typical Use Cases',
      nbiotUse1Title: 'Smart Metering',
      nbiotUse1Desc: 'Utility meters in challenging locations',
      nbiotUse2Title: 'Industrial Sensors',
      nbiotUse2Desc: 'Factory and plant monitoring',
      nbiotUse3Title: 'Smart Parking',
      nbiotUse3Desc: 'Underground parking occupancy',
      nbiotUse4Title: 'Smart Infrastructure',
      nbiotUse4Desc: 'Bridge and tunnel monitoring',
      // BLE Section
      bleBadge: 'Indoor Positioning',
      bleTitle: 'Bluetooth Low Energy',
      bleDesc: 'Bluetooth Low Energy (BLE) technology enables precise indoor positioning and asset tracking with minimal power consumption. Its compatibility with smartphones and widespread adoption makes it ideal for user-facing applications.',
      bleAdvantages: 'Key Advantages',
      bleAdv1: 'Very low power consumption',
      bleAdv2: 'Indoor positioning capabilities',
      bleAdv3: 'High accuracy (sub-meter)',
      bleAdv4: 'Smartphone compatibility',
      bleUseCases: 'Typical Use Cases',
      bleUse1Title: 'Indoor Asset Tracking',
      bleUse1Desc: 'Real-time location of equipment and inventory',
      bleUse2Title: 'Hospital Equipment Tracking',
      bleUse2Desc: 'Medical device location and management',
      bleUse3Title: 'Smart Office Occupancy',
      bleUse3Desc: 'Workspace utilization monitoring',
      bleUse4Title: 'Indoor Location Services',
      bleUse4Desc: 'Navigation and wayfinding applications',
      bleLearnMore: 'Learn more about our hospital asset tracking solutions powered by BLE technology.',
      // Satellite Section
      satelliteBadge: 'Global Coverage',
      satelliteTitle: 'Satellite IoT Connectivity',
      satelliteDesc: 'Satellite connectivity complements terrestrial networks by providing coverage in remote areas where cellular or LPWAN infrastructure is unavailable. This technology ensures continuous monitoring of assets and infrastructure regardless of location.',
      satelliteAdv1Title: 'Global Coverage',
      satelliteAdv1Desc: 'Connectivity anywhere on Earth, including oceans and remote regions',
      satelliteAdv2Title: 'Remote Area Connectivity',
      satelliteAdv2Desc: 'Monitor assets in locations without terrestrial infrastructure',
      satelliteAdv3Title: 'Network Redundancy',
      satelliteAdv3Desc: 'Backup connectivity when primary networks fail',
      satelliteUse1Title: 'Remote Infrastructure Monitoring',
      satelliteUse1Desc: 'Pipelines, power lines, and facilities in isolated areas',
      satelliteUse2Title: 'Logistics Tracking',
      satelliteUse2Desc: 'Container and fleet tracking across borders',
      satelliteUse3Title: 'Industrial Assets',
      satelliteUse3Desc: 'Heavy equipment and machinery in remote sites',
      satelliteNote: '0g Baltics integrates satellite connectivity solutions compatible with various satellite infrastructures including Starlink when project requirements demand it.',
      // Strategy Section
      strategyBadge: 'Our Methodology',
      strategyTitle: 'How 0g Baltics Selects the Right Network',
      strategyDesc: 'Every IoT deployment begins with a thorough analysis of requirements. Our systematic approach ensures the selected connectivity technologies align with project objectives and constraints.',
      step1: '01',
      step1Title: 'Infrastructure Assessment',
      step1Desc: 'Evaluate existing infrastructure and coverage requirements',
      step2: '02',
      step2Title: 'Device Requirements',
      step2Desc: 'Analyze data frequency, payload size, and communication needs',
      step3: '03',
      step3Title: 'Battery Optimization',
      step3Desc: 'Calculate power consumption and target device lifespan',
      step4: '04',
      step4Title: 'Coverage Study',
      step4Desc: 'Map coverage areas and identify connectivity gaps',
      step5: '05',
      step5Title: 'Deployment Architecture',
      step5Desc: 'Design hybrid network topology for optimal performance',
      strategyNote: 'Our objective is always to reduce operational costs while ensuring long-term reliability and device longevity.',
      // Industries Section
      industriesBadge: 'Applications',
      industriesTitle: 'Connectivity for Real-World IoT Deployments',
      industriesDesc: '0g Baltics delivers multi-network IoT connectivity across diverse industries, each with unique requirements and challenges.',
      industrySmartCities: 'Smart Cities',
      industryUtilities: 'Utilities',
      industryLogistics: 'Logistics',
      industryHealthcare: 'Healthcare',
      industryRealEstate: 'Real Estate',
      industryIndustrial: 'Industrial',
      industriesExplore: 'Explore our device ecosystem and consulting services to learn more.',
      // CTA Section
      ctaTitle: 'Design Your IoT Connectivity Infrastructure',
      ctaDesc: 'Ready to deploy a multi-network IoT solution? Our team will help you select the right connectivity technologies for your specific requirements.',
      ctaButton: 'Discuss Your Connectivity Strategy',
    },

    // =====================
    // TEKTELIC PAGE
    // =====================
    tektelicPage: {
      metaTitle: 'TEKTELIC | LoRaWAN Gateways & Sensors | 0g Baltics',
      metaDesc: 'TEKTELIC carrier-grade LoRaWAN gateways and sensors. Authorized partner for the Baltic region.',
      // Hero Section
      heroBadge: 'Authorized Partner',
      heroTitle: 'Carrier-Grade LoRaWAN Infrastructure with',
      heroTitleHighlight: 'Tektelic',
      heroSubtitle: 'Build scalable IoT networks for cities, utilities and industrial deployments using Tektelic gateways and 0g Baltics infrastructure expertise.',
      heroDesc: '0g Baltics designs and deploys LoRaWAN infrastructure across the Baltics and Europe using Tektelic carrier-grade gateways. Our deployments support smart city infrastructure, metering networks, environmental monitoring and industrial IoT applications.',
      ctaPrimary: 'Discuss Your LoRaWAN Project',
      ctaSecondary: 'Explore Starter Kits',
      // Why Tektelic Section
      whyBadge: 'Technology Partner',
      whyTitle: 'Why Tektelic LoRaWAN Gateways',
      whyDesc: 'Tektelic gateways are deployed worldwide in large-scale LoRaWAN networks, trusted by telecommunications operators, cities, and enterprises for their reliability and performance.',
      feature1Title: 'Carrier-Grade Reliability',
      feature1Desc: 'Built to telecom standards for 24/7 operation in demanding environments',
      feature2Title: 'Industrial Grade Hardware',
      feature2Desc: 'Designed for outdoor deployment with IP67 ratings and wide temperature range',
      feature3Title: 'Scalable Architecture',
      feature3Desc: 'From single gateway pilots to city-wide network deployments',
      feature4Title: 'Advanced Network Management',
      feature4Desc: 'Comprehensive tools for monitoring, configuration, and maintenance',
      feature5Title: 'Secure Infrastructure',
      feature5Desc: 'End-to-end encryption and secure boot for enterprise deployments',
      feature6Title: 'High-Performance RF Design',
      feature6Desc: 'Optimized radio frequency design for maximum coverage and reliability',
      // LoRaWAN for Cities Section
      citiesBadge: 'Smart City Infrastructure',
      citiesTitle: 'LoRaWAN Infrastructure for Cities and Utilities',
      citiesDesc: 'LoRaWAN technology enables cities and utilities to deploy cost-effective IoT infrastructure that supports multiple applications simultaneously. A single LoRaWAN network can serve smart metering, environmental monitoring, asset tracking, and more.',
      networkEfficiency: 'Network Efficiency',
      networkEfficiencyDesc: 'A single LoRaWAN infrastructure can support multiple applications simultaneously, reducing deployment costs and simplifying network management across different city departments.',
      useCasesTitle: 'Use Cases',
      useCase1Title: 'Smart Metering',
      useCase1Desc: 'Water, gas, and electricity',
      useCase2Title: 'Air Quality Monitoring',
      useCase2Desc: 'CO2, particulates, pollutants',
      useCase3Title: 'Parking Monitoring',
      useCase3Desc: 'Occupancy and guidance',
      useCase4Title: 'Asset Tracking',
      useCase4Desc: 'Vehicles and equipment',
      useCase5Title: 'Waste Management',
      useCase5Desc: 'Fill-level monitoring',
      useCase6Title: 'Environment Monitoring',
      useCase6Desc: 'Temperature, humidity',
      useCase7Title: 'Smart Buildings',
      useCase7Desc: 'Energy and occupancy',
      // Multi-Network Section
      multiNetworkBadge: 'Multi-Network Strategy',
      multiNetworkTitle: 'Designed for Hybrid IoT Infrastructure',
      multiNetworkDesc: '0g Baltics never builds mono-connectivity deployments. LoRaWAN is often the backbone of IoT infrastructure, but many deployments combine LoRaWAN with Sigfox, NB-IoT, BLE or satellite connectivity to optimize costs, coverage and reliability.',
      benefit1Title: 'Cost Optimization',
      benefit1Desc: 'Select the most efficient connectivity for each use case',
      benefit2Title: 'Network Redundancy',
      benefit2Desc: 'Backup connectivity for critical applications',
      benefit3Title: 'Device Longevity',
      benefit3Desc: 'Optimize battery life with the right network choice',
      benefit4Title: 'Deployment Flexibility',
      benefit4Desc: 'Adapt infrastructure to changing requirements',
      learnMultiNetwork: 'Learn about our multi-network approach',
      connectivityTechnologies: 'Connectivity Technologies',
      lorawanName: 'LoRaWAN',
      lorawanDesc: 'Long-range backbone infrastructure',
      sigfoxName: 'Sigfox',
      sigfoxDesc: 'Ultra-low power simple messages',
      nbiotName: 'NB-IoT',
      nbiotDesc: 'Cellular deep indoor coverage',
      bleName: 'BLE',
      bleDesc: 'Indoor positioning and tracking',
      satelliteName: 'Satellite',
      satelliteDesc: 'Remote area connectivity',
      // Starter Kits Section
      kitsBadge: 'Quick Start',
      kitsTitle: 'LoRaWAN Starter Kits',
      kitsDesc: 'Start your LoRaWAN deployment quickly with tested infrastructure packages designed for different use cases and scales.',
      pilotKitTitle: 'Pilot Deployment Kit',
      pilotKitDesc: 'Ideal for organizations starting their first LoRaWAN pilot project',
      pilotIncludes: 'Includes:',
      pilotItem1: '1 Tektelic LoRaWAN gateway',
      pilotItem2: 'Network configuration',
      pilotItem3: 'Device integration support',
      pilotItem4: 'Basic dashboard access',
      pilotUseCases: 'Typical use cases:',
      pilotTag1: 'Proof of concept',
      pilotTag2: 'Small campus',
      pilotTag3: 'Innovation projects',
      smartCityKitTitle: 'Smart City Infrastructure Kit',
      smartCityKitDesc: 'Designed for municipalities and utilities launching smart city IoT infrastructure',
      smartCityIncludes: 'Includes:',
      smartCityItem1: 'Multiple Tektelic gateways',
      smartCityItem2: 'Network architecture design',
      smartCityItem3: 'Coverage planning',
      smartCityItem4: 'Integration with city platforms',
      smartCityUseCases: 'Typical applications:',
      smartCityTag1: 'Smart metering',
      smartCityTag2: 'Environment monitoring',
      smartCityTag3: 'Smart parking',
      smartCityTag4: 'Public infrastructure',
      industrialKitTitle: 'Industrial IoT Network Kit',
      industrialKitDesc: 'Designed for logistics, utilities and industrial environments',
      industrialIncludes: 'Includes:',
      industrialItem1: 'Industrial gateways',
      industrialItem2: 'Asset tracking devices',
      industrialItem3: 'Network optimization',
      industrialItem4: 'Integration support',
      industrialUseCases: 'Typical applications:',
      industrialTag1: 'Asset tracking',
      industrialTag2: 'Warehouse monitoring',
      industrialTag3: 'Energy infrastructure',
      requestKitInfo: 'Request Kit Information',
      popular: 'POPULAR',
      // Device Ecosystem Section
      devicesBadge: 'Device Compatibility',
      devicesTitle: 'Compatible IoT Devices',
      devicesDesc: 'Our LoRaWAN infrastructure works with a wide ecosystem of certified devices from leading manufacturers, ensuring you can select the best sensors for your specific application requirements.',
      viewDeviceEcosystem: 'View our full device ecosystem',
      device1Name: 'Adeunis',
      device1Desc: 'Industrial sensors',
      device2Name: 'ATIM',
      device2Desc: 'Industrial IoT devices',
      device3Name: 'Digital Matter',
      device3Desc: 'Asset trackers',
      device4Name: 'Environment sensors',
      device4Desc: 'Air quality, temperature',
      device5Name: 'Metering devices',
      device5Desc: 'Water, gas, electricity',
      device6Name: 'Connected Inventions',
      device6Desc: 'Smart sensors',
      // Services Section
      servicesBadge: 'Our Services',
      servicesTitle: 'Infrastructure Design and Deployment',
      servicesDesc: 'From initial planning to large-scale rollouts, 0g Baltics provides comprehensive LoRaWAN infrastructure services.',
      service1: 'Network Design',
      service2: 'Gateway Deployment',
      service3: 'Coverage Optimization',
      service4: 'Device Selection',
      service5: 'ROI Analysis',
      service6: 'Pilot Deployments',
      service7: 'Large Scale Rollouts',
      learnConsulting: 'Learn more about our consulting services',
      // Who We Work With Section
      clientsBadge: 'Our Clients',
      clientsTitle: 'Who Deploys LoRaWAN Infrastructure',
      client1Title: 'Cities & Municipalities',
      client1Desc: 'Smart city infrastructure',
      client2Title: 'Utilities',
      client2Desc: 'Metering and monitoring',
      client3Title: 'Industrial Companies',
      client3Desc: 'Asset tracking and monitoring',
      client4Title: 'Logistics Companies',
      client4Desc: 'Fleet and cargo tracking',
      client5Title: 'IoT Integrators',
      client5Desc: 'Solution development',
      // CTA Section
      ctaTitle: 'Start Your LoRaWAN Infrastructure Project',
      ctaDesc: 'Discuss your infrastructure project with our IoT specialists. We\'ll help you design and deploy the right LoRaWAN solution for your requirements.',
      ctaButton: 'Contact Our LoRaWAN Experts',
    },

    // =====================
    // COMMON ELEMENTS
    // =====================
    common: {
      learnMore: 'Learn more',
      contactUs: 'Contact us',
      getStarted: 'Get started',
      requestDemo: 'Request a demo',
      downloadBrochure: 'Download brochure',
      viewCaseStudy: 'View case study',
      keyBenefits: 'Key benefits',
      features: 'Features',
      useCases: 'Use cases',
      industries: 'Industries',
      technologies: 'Technologies',
      readMore: 'Read more',
      backToHome: 'Back to home',
    },
    realEstatePage: {
      metaTitle: 'IoT Solutions for Real Estate & Infrastructure | Smart Buildings in the Baltics | 0g Baltics',
      metaDesc: 'Turn real estate assets into smart, connected, data-driven buildings. IoT for offices, hotels, residential, retail, industrial parks and infrastructure in Latvia, Lithuania and Estonia — energy, leak detection, air quality, occupancy, smart lockers and more.',
      ogTitle: 'Smart Buildings & Infrastructure — IoT Solutions by 0g Baltics',
      ogDesc: 'Reduce operating costs, improve tenant experience and de-risk assets with turnkey IoT for real estate across the Baltics.',
      heroBadge: 'Real Estate & Infrastructure',
      heroTitle: 'Smart Buildings. Connected Infrastructure. Measurable Results.',
      heroSubtitle: 'Turn every building and site into a connected, data-driven asset — from energy and leak detection to air quality, occupancy and access.',
      heroDesc: 'A single, multi-connectivity IoT stack for real estate developers, owners, operators and asset managers across Latvia, Lithuania and Estonia.',
      ctaDemo: 'Book a 30-minute demo',
      ctaExplore: 'Explore solutions',
      ctaPilot: 'Start a pilot',
      trustReady: 'Turnkey & pilot-ready',
      trustMulti: 'Multi-connectivity (LoRaWAN · NB-IoT · BLE · Satellite)',
      trustLocal: 'Local support in the Baltics',
      trustScalable: 'From 1 building to full portfolios',
      trustIntegration: 'BMS, PropTech & ERP integrations',
      solutionsBadge: 'Solutions overview',
      solutionsTitle: 'One platform, every building use case',
      solutionsDesc: 'Deploy only what you need today — and extend tomorrow. All solutions run on the same 0g Baltics IoT backbone, so each new use case reuses your existing network, devices and dashboards.',
      learnMore: 'Learn more',
      solEnergy: 'Energy & sub-metering',
      solEnergyDesc: 'Sub-metered electricity, heat, gas and water per floor, tenant or asset — with real-time consumption, benchmarking and cost allocation.',
      solLeak: 'Water leak detection',
      solLeakDesc: 'Wireless leak and flow sensors in risers, plant rooms and units to catch issues in minutes, not weeks.',
      solAir: 'Indoor air quality',
      solAirDesc: 'CO₂, temperature, humidity, PM2.5 and TVOC monitoring for healthy, compliant and productive indoor spaces.',
      solOccupancy: 'Occupancy & space usage',
      solOccupancyDesc: 'Desk, room and zone occupancy analytics to rightsize space, cut unused capacity and plan fit-outs with data.',
      solCleaning: 'Cleaning & washroom analytics',
      solCleaningDesc: 'On-demand cleaning based on real footfall and washroom usage — fewer hours, better standards.',
      solSafety: 'Safety & environment',
      solSafetyDesc: 'Panic buttons, temperature, noise and environmental alerts for staff, tenants and critical rooms.',
      solLockers: 'Parcel & smart lockers',
      solLockersDesc: 'Building-integrated lockers for residents, employees and tenants — parcels, keys, cleaning tools and more.',
      solParking: 'Parking occupancy',
      solParkingDesc: 'Real-time outdoor and indoor parking occupancy, guidance and utilization analytics for garages and lots.',
      solAsset: 'Asset & equipment tracking',
      solAssetDesc: 'Track HVAC units, tools, FM equipment and valuable assets across one building or a full portfolio.',
      solESG: 'ESG & carbon reporting',
      solESGDesc: 'Automated, verifiable energy and environmental data for GRESB, CSRD, EU Taxonomy and internal ESG reporting.',
      detailEnergyBadge: 'Energy monitoring',
      detailEnergyTitle: 'See, benchmark and cut energy across every building',
      detailEnergyDesc: 'Most portfolios still rely on one main meter per site. We add wireless sub-meters and environmental sensors so you can see consumption floor-by-floor, tenant-by-tenant, and link it to outdoor and indoor conditions — exactly where savings come from.',
      detailEnergyPoint1: 'Wireless electricity, heat, gas and water sub-metering per zone or tenant',
      detailEnergyPoint2: 'Live dashboards, benchmarks and anomaly alerts',
      detailEnergyPoint3: 'Tenant cost allocation and energy pass-through',
      detailEnergyPoint4: 'Ready for BREEAM, LEED, GRESB and CSRD data needs',
      statEnergySaving: 'Typical energy savings on monitored sites',
      statPayback: 'Years typical ROI',
      statMonitoring: 'Continuous monitoring',
      statAlerts: 'Real-time anomaly alerts',
      detailLeakBadge: 'Leak detection',
      detailLeakTitle: 'Protect your buildings from the #1 avoidable damage cost',
      detailLeakDesc: 'Water damage is one of the costliest, most preventable losses in real estate. Our wireless sensors monitor risers, plant rooms, bathrooms and vulnerable zones 24/7 and trigger alerts the moment water appears where it should not.',
      detailLeakPoint1: 'Battery-powered wireless leak, flow and pressure sensors',
      detailLeakPoint2: 'Instant alerts to FM, security and owners — SMS, email, BMS',
      detailLeakPoint3: 'Automatic valve shut-off integration available',
      leakProblem: 'The hidden cost of unmonitored buildings',
      leakProblemDesc: 'A single undetected leak can cost tens of thousands of euros in repairs, downtime and insurance premiums.',
      leakRisk1: 'Structural and finish damage',
      leakRisk2: 'Tenant disruption and complaints',
      leakRisk3: 'Rising insurance premiums and excess',
      detailAirBadge: 'Indoor air quality',
      detailAirTitle: 'Healthy, productive indoor environments — measured',
      detailAirDesc: 'Indoor air quality directly impacts productivity, tenant satisfaction, absenteeism and compliance. We deploy discreet, battery-powered sensors that continuously measure the key parameters of healthy indoor air.',
      airTemp: 'Temperature',
      airHumidity: 'Humidity',
      airBenefits: 'What you gain',
      airBenefit1: 'Healthier, more productive tenants and staff',
      airBenefit2: 'Better HVAC control and energy savings',
      airBenefit3: 'Evidence for WELL, BREEAM and tenant reporting',
      airBenefit4: 'Early detection of ventilation issues',
      detailOccupancyBadge: 'Occupancy',
      detailOccupancyTitle: 'Design space around real usage',
      detailOccupancyDesc: 'Measure how desks, rooms and whole floors are really used — and use that data to rightsize leases, plan fit-outs, improve cleaning and optimise HVAC schedules.',
      occupancyUse1: 'Desk, room and zone occupancy analytics',
      occupancyUse2: 'Meeting room usage and no-show detection',
      occupancyUse3: 'HVAC and lighting automation based on presence',
      occupancyUse4: 'Data for lease, fit-out and space strategy decisions',
      detailLockersBadge: 'Smart lockers',
      detailLockersTitle: 'Smart lockers built into every building',
      detailLockersDesc: 'Modular locker solutions for residents, employees, tenants and visitors — integrated with access control and our IoT platform.',
      lockerUse1: 'Parcel delivery for residential and office buildings',
      lockerUse2: 'Key and access device management',
      lockerUse3: 'Tool and equipment distribution for FM teams',
      lockerUse4: 'Click & collect for retail and mixed-use',
      segmentsBadge: 'Use cases by segment',
      segmentsTitle: 'Built for every real estate and infrastructure profile',
      segmentsDesc: 'From Class A offices to industrial parks, every segment has specific priorities. We configure the same IoT stack to match each one.',
      segOffice: 'Offices',
      segOfficeDesc: 'Energy, occupancy, IAQ and smart access for landlords and corporate occupiers.',
      segHotel: 'Hotels & hospitality',
      segHotelDesc: 'Room comfort, energy, minibar and back-of-house monitoring for branded and boutique properties.',
      segResidential: 'Residential & multi-family',
      segResidentialDesc: 'Sub-metering, leak protection, parcel lockers and resident comfort for BTR and condos.',
      segRetail: 'Retail & shopping centres',
      segRetailDesc: 'Footfall, cold chain, HVAC and common-area energy monitoring with tenant cost allocation.',
      segBanks: 'Banks & public buildings',
      segBanksDesc: 'Secure environmental monitoring, energy efficiency and compliance reporting for regulated spaces.',
      segParks: 'Industrial & logistics parks',
      segParksDesc: 'Multi-tenant sub-metering, asset tracking, gate and access monitoring across large sites.',
      segDevelopers: 'Developers',
      segDevelopersDesc: 'Pre-fitted IoT stack that boosts asset value, ESG rating and tenant appeal from day one.',
      segAssetManagers: 'Asset & property managers',
      segAssetManagersDesc: 'Portfolio-wide dashboards, benchmarks and automated ESG data for every asset under management.',
      platformBadge: 'Connectivity & platform',
      platformTitle: 'One connected stack — from sensor to dashboard',
      platformDesc: 'We design, deploy and operate the complete IoT chain for your buildings: devices, connectivity, data platform, dashboards, alerts and integrations. No vendor patchwork, one partner for the entire lifecycle.',
      platformDevices: 'Industrial-grade devices',
      platformConnectivity: 'Multi-network connectivity',
      platformDashboard: 'Unified dashboards',
      platformAlerts: 'Smart alerting',
      platformIntegration: 'BMS & PropTech integrations',
      platformSupport: 'Local Baltic support',
      techTitle: 'The right network for every use case',
      techLoRaWAN: 'Long-range, low-power — ideal for sub-metering, leak, IAQ and occupancy across full buildings.',
      techNBIoT: 'Deep indoor cellular coverage — perfect for critical assets and remote single sites.',
      techBLE: 'Indoor micro-location and asset tracking inside buildings and large sites.',
      learnMoreConnectivity: 'See all connectivity options',
      bmsBadge: 'BMS & BACnet integration',
      bmsTitle: 'Plugs into your existing BMS — no rip & replace',
      bmsDesc: 'Our wireless IoT layer augments your existing Building Management System with real-time, granular data. Sensors ship pre-configured, gateways run locally, and your BMS receives structured data over the protocols it already speaks.',
      bmsProtocols: 'Supported protocols',
      bmsBACnet: 'BACnet/IP',
      bmsBACnetDesc: 'Native BACnet/IP connector — points map directly into Niagara, Honeywell, Siemens and other BMS platforms.',
      bmsModbus: 'Modbus TCP',
      bmsModbusDesc: 'Standard Modbus TCP output for legacy controllers, HVAC plant rooms and industrial automation.',
      bmsRest: 'REST API & MQTT',
      bmsRestDesc: 'Open REST APIs, webhooks and MQTT for PropTech platforms, custom dashboards and data lakes.',
      bmsLoRaWAN: 'LoRaWAN®',
      bmsLoRaWANDesc: 'Carrier-grade LoRaWAN network with 5–10+ year battery life and full-building coverage from a single gateway.',
      bmsEcosystemTitle: 'A complete sensor ecosystem',
      bmsEcosystemDesc: 'Leverage a battle-tested TEKTELIC device portfolio — comfort, occupancy, leak, CO₂, smart AC, cold chain, staff safety — all on the same network.',
      bmsItWorks: 'It just works',
      bmsItWorksDesc: 'Out-of-the-box devices, minimal engineering effort, low total cost of ownership and a partner that handles the lifecycle — design, deployment, operation and maintenance.',
      bmsBenefit1: 'No rip-and-replace of existing BMS',
      bmsBenefit2: '5–10 year device battery life',
      bmsBenefit3: 'One gateway covers a full building',
      bmsBenefit4: 'Niagara / OpenBlue / Honeywell ready',
      fmBadge: 'Facility Management IoT Suite',
      fmTitle: 'LoRaWAN® Solutions for Smart Building Management',
      fmDesc: 'Running modern facilities is more challenging than ever — rising energy costs, higher tenant expectations, stricter ESG compliance. Our LoRaWAN® IoT suite delivers real-time insights that reduce operating costs, improve tenant comfort and automate compliance across your entire portfolio.',
      fmStat1: 'Cleaning cost reduction in 60 days',
      fmStat2: 'Energy savings via HVAC & occupancy optimization',
      fmStat3: 'Automated ESG reporting — effortless compliance',
      fmStat4: 'Continuous monitoring of air, leaks & comfort',
      fmChallengesTitle: 'Solving your building management challenges',
      fmChallengesDesc: 'Every building has its own set of operational challenges. Our solutions are built around your priorities to help you manage your portfolio with confidence.',
      fmCh1Title: 'Comfort & Air Quality Complaints',
      fmCh1Desc: 'Poor temperature, CO₂ and humidity control lead to unhappy tenants and lower productivity.',
      fmCh2Title: 'Hidden Risks & Incidents',
      fmCh2Desc: 'Leaks, equipment failures or environmental issues escalate quickly without early detection.',
      fmCh3Title: 'Hard-to-Prove ESG & Compliance',
      fmCh3Desc: 'Sustainability and regulatory standards are tough to meet without continuous, verifiable data.',
      fmCh4Title: 'Slow, Manual Workflows',
      fmCh4Desc: 'Walk-throughs and reactive maintenance waste time and inflate operating costs.',
      fmCh5Title: 'Scaling Beyond a Pilot',
      fmCh5Desc: 'Many IoT projects stall after testing. Expanding across multiple buildings often feels complex and expensive.',
      fmCh6Title: 'Integration & Data Silos',
      fmCh6Desc: 'Data is spread across disconnected systems, making it hard to get a single view and act fast.',
      fmAppsTitle: 'Ready-to-Use Applications',
      fmAppsDesc: 'Start using your IoT solution in minutes. Scan a QR code, connect any device and manage everything from a user-friendly dashboard.',
      fmApp1Name: 'BREEZE',
      fmApp1Desc: 'Real-time indoor air quality dashboards & alerts.',
      fmApp2Name: 'LeapX',
      fmApp2Desc: 'Quick, seamless activation of IoT gateways and devices.',
      fmApp3Name: 'TEMPO',
      fmApp3Desc: 'Smart meeting room booking & analytics with Outlook® / Google integration.',
      fmApp4Name: 'LOCUS',
      fmApp4Desc: 'Visualization and RTLS tools for Facility Management teams.',
      fmBmsTitle: 'Wireless Integration with Your Building Management System (BMS)',
      fmBmsDesc1: '0g Baltics makes it easy to bring real-time insights from air quality, occupancy, energy, leaks, and cold chain monitoring directly into your Building Management System (BMS).',
      fmBmsDesc2: 'Our wireless solutions eliminate complex wiring, reduce installation costs, and scale effortlessly across any facility. With support for industry-standard protocols such as BACnet, Modbus, and Niagara, integration is seamless — giving you one complete view of your buildings to enable smarter decisions, lower costs, and healthier environments.',
      fmBenefitsTitle: 'Efficiency, comfort and compliance — all in one solution',
      fmBen1Title: 'Lower Operating Costs',
      fmBen1Desc: 'Reduce cleaning, maintenance and energy expenses with real-time insights that drive efficiency.',
      fmBen2Title: 'Smarter Energy Use',
      fmBen2Desc: 'Cut energy waste by optimising HVAC, lighting and space usage based on real demand.',
      fmBen3Title: 'Healthier, Comfortable Spaces',
      fmBen3Desc: 'Maintain air quality, temperature and humidity at optimal levels for tenant well-being and productivity.',
      fmBen4Title: 'Effortless Compliance',
      fmBen4Desc: 'Automatically collect and report ESG and regulatory data — no manual checks or paperwork.',
      fmBen5Title: 'Reliable Safety Monitoring',
      fmBen5Desc: 'Detect leaks, environmental risks or unusual activity instantly to prevent costly incidents.',
      fmBen6Title: 'Scalable Simplicity',
      fmBen6Desc: 'Start with one building and expand portfolio-wide without adding complexity.',
      relatedBadge: 'Linked solutions',
      relatedTitle: 'Related solutions you can combine',
      relatedPakuskapis: 'Our turnkey parcel locker network for residential and commercial buildings in Latvia.',
      relatedSmartLockers: 'Smart lockers',
      relatedSmartLockersDesc: 'Full modular locker range for parcels, keys, tools and click & collect.',
      relatedIAQ: 'Indoor air quality',
      relatedIAQDesc: 'Continuous IAQ monitoring for healthier, compliant and productive indoor spaces.',
      relatedEnergy: 'Building energy efficiency',
      relatedEnergyDesc: 'Energy monitoring, HVAC optimisation and ESG-ready reporting for buildings.',
      faqBadge: 'FAQ',
      faqTitle: 'Questions real estate teams ask us',
      faq1Q: 'Do we need to replace our existing meters or BMS?',
      faq1A: 'No. Our sensors are non-intrusive and battery-powered, and integrate with your existing meters, BMS and ERP. You keep what works and extend visibility where you have blind spots.',
      faq2Q: 'Can we start with one building before rolling out across a portfolio?',
      faq2A: 'Yes — most clients start with a single pilot building, validate ROI over 3–6 months, then scale to the rest of the portfolio reusing the same platform and processes.',
      faq3Q: 'How is the data protected and where is it hosted?',
      faq3A: 'Data is encrypted in transit and at rest, hosted in EU data centres, and access is role-based. We are fully GDPR-compliant and can integrate with your SSO.',
      faq4Q: 'Which networks do you use in the Baltics?',
      faq4A: 'We combine LoRaWAN, NB-IoT, LTE-M, BLE and satellite depending on the building, the use case and the coverage available — so you are never locked into a single technology.',
      faq5Q: 'Can we integrate IoT data with our BMS, PropTech or ERP?',
      faq5A: 'Yes. We provide REST APIs, webhooks and prebuilt connectors for common BMS, PropTech platforms, accounting and ERP systems.',
      faq6Q: 'How does this support our ESG and CSRD reporting?',
      faq6A: 'Every measured data point (energy, water, IAQ, occupancy) is stored with timestamps and metadata and can be exported for GRESB, BREEAM, LEED, CSRD and EU Taxonomy reporting.',
      faq7Q: 'What does a typical pilot project look like?',
      faq7A: 'A typical pilot covers 1 building with 20–100 devices over 3–6 months, with clearly defined KPIs (e.g. energy savings, leak events prevented, occupancy insights). We take care of design, deployment and training.',
      ctaTitle: 'Let us turn your buildings into data-driven assets',
      ctaDesc: 'Book a 30-minute call with our real estate IoT team. We will map your priorities, show similar deployments and propose a concrete pilot.',
    },

  },

  // =====================
  // LATVIAN (LV)
  // =====================
  lv: {
    smartMeteringPage: {
      metaTitle: 'Viedās mērīšanas risinājumi | IoT komunālajiem uzņēmumiem | 0g Baltics',
      metaDesc: 'Izvietojiet viedās mērīšanas infrastruktūru ar LoRaWAN, Sigfox un NB-IoT. Attālināta datu vākšana ūdens, gāzes, elektrības un siltuma skaitītājiem.',
      heroBadge: 'Viedā mērīšana',
      heroTitle: 'Viedās mērīšanas infrastruktūra komunālajiem uzņēmumiem',
      heroSubtitle: 'Izvietojiet attālinātas skaitītāju nolasīšanas infrastruktūru, izmantojot LoRaWAN, Sigfox, NB-IoT un mobilās tehnoloģijas.',
      ctaPrimary: 'Pieprasīt konsultāciju',
      ctaSecondary: 'Uzzināt vairāk',
      challengeBadge: 'Izaicinājums',
      challengeTitle: 'Kāpēc komunālajiem uzņēmumiem nepieciešama viedā mērīšana',
      challengeDesc: 'Tradicionālā skaitītāju nolasīšana ir dārga, kļūdaina un nenodrošina reāllaika redzamību patēriņa modeļos.',
      challenge1: 'Manuālās nolasīšanas izmaksas',
      challenge1Desc: 'Lauka vizītes ir dārgas un laikietilpīgas',
      challenge2: 'Neprecīza rēķinu izrakstīšana',
      challenge2Desc: 'Novērtējuma kļūdas rada klientu strīdus',
      challenge3: 'Noplūdes noteikšanas aizkavēšanās',
      challenge3Desc: 'Problēmas tiek atklātas par vēlu',
      challenge4: 'Ierobežoti dati',
      challenge4Desc: 'Ikmēneša nolasījumi neatklāj patēriņa modeļus',
      solutionBadge: 'Mūsu risinājums',
      solutionTitle: 'IoT balstīta viedā mērīšana',
      solutionDesc: 'Mēs izvietojam viedās mērīšanas infrastruktūru, kas savieno esošos skaitītājus ar mākoni.',
      solutionFeature1: 'Modernizēt esošos skaitītājus',
      solutionFeature2: 'Vairākas savienojamības iespējas',
      solutionFeature3: 'Reāllaika patēriņa dati',
      solutionFeature4: 'Noplūžu un anomāliju noteikšana',
      solutionFeature5: 'Rēķinu integrācija',
      solutionFeature6: 'Klientu portāls',
      techBadge: 'Tehnoloģijas',
      techTitle: 'Savienojamības iespējas',
      techDesc: 'Mēs izvēlamies pareizo tehnoloģiju, pamatojoties uz jūsu infrastruktūru un budžetu.',
      lorawanTitle: 'LoRaWAN',
      lorawanDesc: 'Liels attālums, zems enerģijas patēriņš. Ideāli piemērots blīviem pilsētas izvietojumiem.',
      sigfoxTitle: 'Sigfox',
      sigfoxDesc: 'Vienkārša integrācija, valsts mēroga pārklājums Baltijā.',
      nbiotTitle: 'NB-IoT',
      nbiotDesc: 'Balstīts uz mobilo tīklu. Labs zonām ar mobilo pārklājumu.',
      cellularTitle: 'Mobilais (4G/LTE)',
      cellularDesc: 'Augstas frekvences datu pārraides prasībām.',
      useCasesBadge: 'Pielietojumi',
      useCasesTitle: 'Viedās mērīšanas lietojumi',
      waterMetering: 'Ūdens mērīšana',
      waterMeteringDesc: 'Uzraugiet patēriņu, atklājiet noplūdes un samaziniet ūdens zudumus.',
      gasMetering: 'Gāzes mērīšana',
      gasMeteringDesc: 'Attālināta gāzes skaitītāju nolasīšana mājsaimniecībām un uzņēmumiem.',
      electricityMetering: 'Elektrības mērīšana',
      electricityMeteringDesc: 'Apakšmērīšana komerciālām ēkām un rūpnieciskām iekārtām.',
      heatMetering: 'Siltuma mērīšana',
      heatMeteringDesc: 'Centralizētās apkures patēriņa uzraudzība un rēķinu izrakstīšana.',
      ctaTitle: 'Gatavi modernizēt savu mērīšanas infrastruktūru?',
      ctaDesc: 'Sazinieties ar mums, lai apspriestu savu viedās mērīšanas projektu.',
      ctaButton: 'Sazināties',
      // Section: Network Quality
      networkBadge: 'Kritiskā infrastruktūra',
      networkTitle: 'Tīkla uzticamība ir viss',
      networkDesc: 'Daudzi viedās mērīšanas projekti cieš neveiksmi nevis skaitītāju dēļ, bet neuzticamas savienojamības dēļ.',
      networkLocation1: 'Pazemes bedres',
      networkLocation2: 'Ēku iekšienē',
      networkLocation3: 'Pagrabos',
      networkLocation4: 'Attālās vietās',
      networkWarning: 'Komunālajiem uzņēmumiem nepieciešama operatora līmeņa infrastruktūra, kas paredzēta gadu desmitiem ilgai uzticamai darbībai.',
      // Section: Carrier Grade
      carrierBadge: 'Infrastruktūras standarti',
      carrierTitle: 'Ko nozīmē "Operatora līmenis"?',
      carrierDesc: 'Operatora līmeņa infrastruktūra atbilst telekomunikāciju operatoru un kritiskās infrastruktūras prasībām.',
      carrierFeature1: 'Tīkla darbspējas garantija',
      carrierFeature1Desc: '99.9%+ pieejamība',
      carrierFeature2: 'Profesionāla aprīkojuma',
      carrierFeature2Desc: 'Industriāla klases iekārtas',
      carrierFeature3: 'Ilgtermiņa atbalsts',
      carrierFeature3Desc: '10+ gadu dzīves cikls',
      carrierFeature4: 'Drošības atbilstība',
      carrierFeature4Desc: 'Uzņēmumu līmeņa drošība',
      // Section: LoRaWAN
      lorawanSectionBadge: 'Primārā tehnoloģija',
      lorawanSectionTitle: 'LoRaWAN viedajai mērīšanai',
      lorawanSectionDesc: 'LoRaWAN ir vadošā LPWAN tehnoloģija viedās mērīšanas izvietojumiem.',
      lorawanAdvantage1: 'Liels attālums (līdz 15km)',
      lorawanAdvantage2: '10+ gadu akumulatora darbība',
      lorawanAdvantage3: 'Dziļa iekštelpu iespiešanās',
      lorawanAdvantage4: 'Zemas infrastruktūras izmaksas',
      // Section: Cellular
      cellularSectionBadge: 'Alternatīvā tehnoloģija',
      cellularSectionTitle: 'Mobilais IoT viedajai mērīšanai',
      cellularSectionDesc: 'NB-IoT un LTE-M nodrošina uz mobilo tīklu balstītu savienojamību zonās ar labu mobilo pārklājumu.',
    },

    iaqPage: {
      metaTitle: 'Iekštelpu gaisa kvalitātes monitorings | CO2 sensori | 0g Baltics',
      metaDesc: 'Profesionāls iekštelpu gaisa kvalitātes monitorings ar CO2, temperatūras, mitruma un PM sensoriem.',
      heroBadge: 'Iekštelpu gaisa kvalitāte',
      heroTitle: 'Iekštelpu gaisa kvalitātes monitoringa risinājumi',
      heroSubtitle: 'Izvietojiet profesionālas IAQ uzraudzības sistēmas, lai nodrošinātu veselīgu iekštelpu vidi skolās, birojos un veselības aprūpes iestādēs.',
      // Technology badges
      techCO2: 'CO2',
      techTemperature: 'Temperatūra',
      techHumidity: 'Mitrums',
      techPM25: 'PM2.5',
      techTVOC: 'TVOC',
      techOccupancy: 'Aizņemtība',
      whyBadge: 'Kāpēc tas ir svarīgi',
      whyTitle: 'Iekštelpu gaisa kvalitātes nozīme',
      whyDesc: 'Cilvēki pavada 90% laika telpās. Slikta gaisa kvalitāte ietekmē veselību, produktivitāti un kognitīvo veiktspēju.',
      // Why outcomes list
      outcome1: 'Veselīgāka iekštelpu vide iemītniekiem',
      outcome2: 'Labāki ventilācijas lēmumi, balstoties uz reāliem datiem',
      outcome3: 'Atbalsts ESG un viedo ēku stratēģijām',
      outcome4: 'Uzlabots komforts darba vietās un klasēs',
      outcome5: 'Samazinātas darbības aklo zonu',
      // Key Decision Drivers
      keyDrivers: 'Galvenie lēmumu pieņemšanas faktori',
      driverHealth: 'Veselība',
      driverComfort: 'Komforts',
      driverVentilation: 'Ventilācija',
      driverEfficiency: 'Efektivitāte',
      driverCompliance: 'Atbilstība',
      monitorBadge: 'Parametri',
      monitorTitle: 'Ko mēs uzraugām',
      typicalRange: 'Tipiskais diapazons',
      co2: 'CO2 (oglekļa dioksīds)',
      co2Desc: 'Galvenais ventilācijas pietiekamības rādītājs',
      temperature: 'Temperatūra',
      temperatureDesc: 'Termiskais komforts iemītniekiem',
      humidity: 'Mitrums',
      humidityDesc: 'Optimālais diapazons 40-60% veselībai',
      pm: 'Daļiņas (PM)',
      pmDesc: 'PM2.5 un PM10 gaisa kvalitātes novērtējumam',
      tvoc: 'TVOC',
      tvocDesc: 'Gaistošo organisko savienojumu noteikšana',
      // TEKTELIC section
      sensorsBadge: 'Aparatūra',
      sensorsTitle: 'Profesionālas klases sensori',
      sensorsDesc: 'Mēs izvietojam TEKTELIC un Connected Inventions sensorus uzticamam, ilgtermiņa monitoringam.',
      tektelicBreezeDesc: 'Viedais telpas sensors CO2, temperatūras, mitruma, barometriskā spiediena un apkārtējā apgaismojuma uzraudzībai. NDIR CO2 sensors ar ±30 ppm + 3% precizitāti un 5+ gadu akumulatora darbību.',
      tektelicBreezeVDesc: 'Paplašina BREEZE ar PIR aizņemtības noteikšanu viedai telpas funkcionalitātei. Ideāli piemērots gaisa kvalitātes korelācijai ar telpas izmantošanu pieprasījuma vadītai ventilācijai.',
      tektelicBreezeDDesc: 'CO2 viedais telpas sensors ar E-Ink displeju. Apvieno LoRaWAN, BLE un E-Ink zemam enerģijas patēriņam. Displejs ir bezvadu un var tikt uzstādīts uz sienas vai galda.',
      whenTektelic: 'Kad izvēlēties TEKTELIC',
      tektelicRelevant: 'TEKTELIC sensori ir īpaši piemēroti, kad jūsu projektam nepieciešams:',
      tektelicBenefit1: 'LoRaWAN viedās ēkas infrastruktūra',
      tektelicBenefit2: 'Mērogojami uzņēmumu izvietojumi',
      tektelicBenefit3: 'Operatora līmeņa uzticamība',
      tektelicBenefit4: 'Integrācija ar ēkas IoT sistēmām',
      tektelicBenefit5: 'Izvēles BLE e-Ink displeji',
      tektelicBenefit6: 'Profesionāls atbalsts un dokumentācija',
      bestFitTektelic: 'Labākā piemērotība TEKTELIC vidēm',
      corpOffices: 'Korporatīvie biroji',
      schoolsUnis: 'Skolas un universitātes',
      publicBuildings: 'Publiskās ēkas',
      healthcareFacilities: 'Veselības aprūpes iestādes',
      // Connected Inventions section
      connectedBadge: 'Ilgmūžīgs monitorings',
      connectedTitle: 'Connected Inventions AirWits ierīces',
      connectedDesc: '0g Baltics izmanto arī Connected Inventions ierīces specifiskām iekštelpu vides uzraudzības vajadzībām. AirWits produktu saime piedāvā mērķtiecīgi izstrādātus sensorus, kas optimizēti ilgam akumulatora darbības laikam, zemai uzturēšanai un rentablam izvietojumam ēku portfeļos.',
      airwitsBasicDesc: 'Temperatūras un mitruma uzraudzība ar ±0.2°C precizitāti un līdz 10 gadu akumulatora darbību.',
      airwitsCO2Desc: 'CO2, temperatūras un mitruma uzraudzība ventilācijas novērtējumam un komforta optimizācijai.',
      airwitsCO2PlusDesc: 'Uzlabots CO2 monitorings ar e-ink displeju, kas rāda reāllaika gaisa kvalitātes statusu un krāsu kodētus indikatorus.',
      airwitsPMDesc: 'PM1.0 / PM2.5 / PM10 daļiņu uzraudzība ar temperatūru un mitrumu gaisa kvalitātes novērtējumam.',
      flexSenseDesc: 'Modulāra daudzjutekļu platforma elastīgam izvietojumam ar pielāgojamām sensoru kombinācijām dažādiem lietojumiem.',
      airwitsIAQDesc: 'TVOC uzraudzība ar temperatūru un mitrumu piesārņojuma notikumu un sliktas gaisa kvalitātes noteikšanai.',
      airwitsEcoSenseDesc: 'Ar saules enerģiju darbināma, bez baterijām vai ļoti zemas uzturēšanas opcija izvietojuma scenārijiem, kas prasa minimālu iejaukšanos.',
      whenConnected: 'Kad izvēlēties Connected Inventions',
      connectedUseful: 'Connected Inventions ierīces ir īpaši noderīgas, kad jūsu projekts novērtē:',
      connectedBenefit1: 'Līdz 10 gadu akumulatora darbība',
      connectedBenefit2: 'Zemas kopējās īpašumtiesību izmaksas',
      connectedBenefit3: 'Sigfox vai LoRaWAN opcijas',
      connectedBenefit4: 'Vienkārša liela mēroga izvietošana',
      connectedBenefit5: 'Rentabls portfeļa monitorings',
      connectedBenefit6: 'Bezmaksas uzturēšanas darbība',
      // Device Selection section
      deviceSelectionBadge: 'Risinājumu arhitektūra',
      deviceSelectionTitle: 'Pareizās IAQ ierīces izvēle pareizajai ēkai',
      deviceSelectionDesc: '0g Baltics nepiedāvā vienu ierīci katram projektam. Tā vietā mēs izvēlamies pareizo arhitektūru atkarībā no budžeta, tīkla pieejamības, nepieciešamajiem parametriem, akumulatora gaidām, pārskatu vajadzībām un integrācijas prasībām.',
      tableBuilding: 'Ēkas tips',
      tableNeed: 'Projekta vajadzība',
      tableSensor: 'Ieteicamais sensors',
      tableWhy: 'Kāpēc tas der',
      officePortfolio: 'Biroju portfelis',
      officeNeed: 'CO2, temperatūra, mitrums visos stāvos',
      officeWhy: 'LoRaWAN mērogojamība, aizņemtības korelācija, BLE displeja opcija',
      classroomSchool: 'Klase / Skola',
      classroomNeed: 'Ventilācijas uzraudzība, studentu komforts',
      classroomWhy: 'Reāllaika CO2 redzamība, ilgs akumulatora darbības laiks, vienkārša uzstādīšana',
      uniCampus: 'Universitātes kampuss',
      uniNeed: 'Daudzu ēku IAQ stratēģija',
      uniSensor: 'Jaukts: TEKTELIC + AirWits',
      uniWhy: 'LoRaWAN infrastruktūra + rentabla paplašināšana',
      healthcareClinics: 'Veselības aprūpe / Klīnikas',
      healthcareNeed: 'Jutīgas vides uzraudzība',
      healthcareIaqSensor: 'AirWits PM + AirWits IAQ',
      healthcareWhy: 'PM un TVOC kritisks pacientu vidēm',
      residentialAreas: 'Dzīvojamās koplietošanas zonas',
      residentialNeed: 'Pamata komforts un ventilācija',
      residentialSensor: 'AirWits / AirWits CO2',
      residentialWhy: 'Rentabls, zema uzturēšana, ilgs akumulators',
      municipalBuildings: 'Pašvaldību ēkas',
      municipalNeed: 'Publisko telpu uzraudzība, atbilstība',
      municipalWhy: 'Operatora līmeņa uzticamība, audita pieraksti',
      industrialIndoor: 'Rūpnieciskās iekštelpas',
      industrialNeed: 'Procesa kontrole, darbinieku komforts',
      industrialSensor: 'AirWits PM + CO2',
      industrialWhy: 'Daļiņu uzraudzība rūpnieciskām vidēm',
      // IoT vs Manual section
      iotVsManualBadge: 'Darbības priekšrocība',
      iotVsManualTitle: 'Kāpēc IoT balstīts IAQ monitorings pārspēj manuālās pārbaudes',
      iotVsManualDesc: 'Tradicionālais iekštelpu gaisa kvalitātes novērtējums balstās uz periodiskām manuālām pārbaudēm ar pārnēsājamām ierīcēm. Tehniķis apmeklē katru telpu, veic mērījumu, pieraksta rezultātu un turpina. Šai pieejai ir būtiski ierobežojumi, ko IoT balstīts monitorings novērš.',
      iotVsManualDesc2: 'Savienotie sensori nodrošina nepārtrauktus datus, nevis gadījuma momentuzņēmumus. Tie ģenerē brīdinājumus, nevis gaida sūdzības. Tie veido vēsturiskās tendences analīzei un salīdzināšanai. Un tie mērogojas portfeļos bez proporcionāla darbaspēka izmaksu pieauguma.',
      continuous247: 'Nepārtraukta 24/7 uzraudzība',
      continuous247Desc: 'Dati ik 15-30 minūtes, nevis reizi ceturksnī',
      realTimeAlerts: 'Reāllaika brīdinājumi',
      realTimeAlertsDesc: 'Tūlītējs paziņojums, kad sliekšņi tiek pārsniegti',
      historicalTrends: 'Vēsturiskās tendences',
      historicalTrendsDesc: 'Analizējiet modeļus nedēļu, mēnešu un sezonu garumā',
      portfolioBenchmark: 'Portfeļa salīdzināšana',
      portfolioBenchmarkDesc: 'Salīdziniet ēkas un identificējiet izņēmumus',
      betterDecisions: 'Labāki lēmumi',
      betterDecisionsDesc: 'Datu vadīta uzturēšana un ventilācijas optimizācija',
      scalableOps: 'Mērogojamas operācijas',
      scalableOpsDesc: 'Uzraugiet simtiem telpu bez papildu personāla',
      // IAQ Energy section
      iaqEnergyBadge: 'Ēkas veiktspēja',
      iaqEnergyTitle: 'Līdzsvarojiet veselību, komfortu un energoefektivitāti',
      iaqEnergyDesc1: 'Iekštelpu gaisa kvalitātes uzraudzība nav tikai par veselību—tā ir arī par ēkas veiktspēju un darbības efektivitāti. Ēkas veido aptuveni 40% no globālā enerģijas patēriņa, un HVAC sistēmas pārstāv ievērojamu daļu. Inteliģenta ventilācija, balstoties uz faktiskajiem iekštelpu apstākļiem, var samazināt enerģijas izšķērdēšanu, saglabājot veselīgu gaisa kvalitāti.',
      co2Ventilation: 'CO2 un aizņemtības balstīta ventilācija',
      co2VentilationDesc: 'CO2 un aizņemtības uzraudzība var atbalstīt gudrākus ventilācijas lēmumus. Kad telpas nav aizņemtas vai ir maz izmantotas, ventilāciju var samazināt. Kad CO2 pieaug, svaigā gaisa padevi var palielināt. Šī pieprasījuma vadītā pieeja optimizē enerģijas izmantošanu, saglabājot gaisa kvalitāti.',
      tempHumidityInsights: 'Temperatūras un mitruma ieskati',
      tempHumidityInsightsDesc: 'Temperatūras un mitruma tendences palīdz izprast ēkas komfortu un HVAC veiktspēju. Zonu ar pastāvīgām problēmām identificēšana ļauj veikt mērķtiecīgus uzlabojumus, nevis visas sistēmas pielāgojumus. Labāki dati ved pie efektīvākām operācijām.',
      iaqEnergyDesc2: 'IAQ dati atbalsta labāku iekārtu pārvaldību, nodrošinot objektīvus mērījumus, kas informē par uzturēšanas prioritātēm, renovācijas lēmumiem un sistēmu uzlabojumiem. Viedām ēkām vajadzīgi mērījumi pirms optimizācijas—jūs nevarat uzlabot to, ko nemērāt.',
      iaqEnergyNote: 'Lai gan IAQ uzraudzība nodrošina datus, kas var informēt par enerģijas optimizācijas stratēģijām, konkrētie enerģijas ietaupījumi ir atkarīgi no daudziem faktoriem, tostarp ēkas sistēmām, klimata un darbības prakses. Mēs koncentrējamies uz precīzu mērījumu un izmantojamu ieskatu nodrošināšanu, nevis pārsolām konkrētus ROI skaitļus.',
      // Standards section
      standardsBadge: 'Regulatīvais ietvars',
      standardsTitle: 'Standarti, vadlīnijas un IAQ labākā prakse',
      standardsDesc: 'Iekštelpu gaisa kvalitātes projektus bieži virza veselības gaidas, ESG un viedo ēku politikas, skolu un darbavietu komforta mērķi un ventilācijas un riska pārvaldības vadlīnijas. Attiecīgo ietvaru izpratne palīdz definēt uzraudzības stratēģiju, ventilācijas mērķus un ēkas veiktspējas gaidas.',
      whoGuidelines: 'PVO gaisa kvalitātes vadlīnijas',
      whoGuidelinesDesc: 'Pasaules Veselības organizācija sniedz vadlīnijas par gaisa kvalitāti, tostarp daļiņu sliekšņus. Šie ieteikumi ir būtiski PM saistīto veselības ietekmes izpratnei un uzraudzības mērķu noteikšanai ēkās, kas rūpējas par iemītnieku veselību.',
      ashraeStandard: 'ASHRAE standarts 241',
      ashraeStandardDesc: 'ASHRAE 241 risina infekciozos aerosolus un sniedz vadlīnijas veselīgākām iekštelpu vidēm. Šis standarts ir īpaši būtisks veselības aprūpes, izglītības un komerciālām ēkām, kas cenšas samazināt gaisā pārnēsātu transmisijas riskus.',
      enStandard: 'EN 16798 saime',
      enStandardDesc: 'Eiropā EN 16798 standarti ir būtiski iekštelpu vides kvalitātei, ventilācijas prasībām un pārbaudes ietvariem. Tie sniedz vadlīnijas ēku projektēšanai un ekspluatācijai, lai atbilstu iekštelpu vides kvalitātes gaidām.',
      standardsImportant: 'IAQ uzraudzība atbalsta uz pierādījumiem balstītu ēku pārvaldību un var palīdzēt organizācijām dokumentēt apstākļus, identificēt problēmas un noteikt korektīvo darbību prioritātes. Lai gan sensori nodrošina datus, atbilstība konkrētiem standartiem ir atkarīga no tā, kā šie dati tiek izmantoti plašākās ēku pārvaldības un ventilācijas stratēģijās.',
      iaqHelps: 'IAQ uzraudzība palīdz ar:',
      iaqHelp1: 'Iekštelpu apstākļu dokumentēšanu laika gaitā',
      iaqHelp2: 'Telpu identificēšanu, kurās nepieciešama iejaukšanās',
      iaqHelp3: 'Ventilācijas optimizācijas centienu atbalstīšanu',
      iaqHelp4: 'Audita pierakstu nodrošināšanu atbilstībai',
      iaqHelp5: 'Salīdzināšanu starp ēku portfeļiem',
      iaqHelp6: 'Renovācijas un uzlabojumu lēmumu informēšanu',
      // Use Cases section
      useCasesBadge: 'Pielietojumi',
      useCasesTitle: 'Kur iekštelpu gaisa kvalitātes uzraudzība sniedz vērtību',
      useCasesDesc: 'IAQ uzraudzība nodrošina darbības priekšrocības plašam ēku tipu lokam. Katrai videi ir specifiskas prasības, kas nosaka sensoru izvēli un izvietošanas stratēģiju.',
      ucOffices: 'Biroji',
      ucOfficesDesc: 'Uzlabojiet darbavietas komfortu un vides kvalitāti. Uzraugiet CO2 līmeņus, lai nodrošinātu atbilstošu ventilāciju, optimizējiet temperatūru produktivitātei un demonstrējiet apņemšanos par darbinieku labklājību.',
      ucSchools: 'Skolas un universitātes',
      ucSchoolsDesc: 'Atbalstiet klases ventilāciju un radiet labākas mācību vides. Pētījumi rāda, ka paaugstināts CO2 līmenis klasēs korelē ar samazinātu kognitīvo veiktspēju un palielinātu kavējumu.',
      ucHealthcare: 'Veselības aprūpe un klīnikas',
      ucHealthcareDesc: 'Uzraugiet jutīgas iekštelpu vides, kur gaisa kvalitāte tieši ietekmē pacientu iznākumus. PM un TVOC uzraudzība ir īpaši būtiska klīniskajos iestatījumos, uzgaidāmajās telpās un pacientu aprūpes zonās.',
      ucMunicipal: 'Pašvaldību ēkas',
      ucMunicipalDesc: 'Nodrošiniet redzamību publiskajās iekštelpu vidēs, tostarp bibliotēkās, kopienas centros un valdības birojos. Atbalstiet caurskatāmību un demonstrējiet atbildīgu publisko iestāžu pārvaldību.',
      ucCommercial: 'Komerciālais nekustamais īpašums',
      ucCommercialDesc: 'Salīdziniet un uzraugiet vairākas ēkas konsekventi visā portfelī. Nodrošiniet īrniekiem veselīgas ēkas akreditācijas un atbalstiet ESG ziņošanas prasības.',
      ucIndustrial: 'Rūpnieciskās iekštelpas',
      ucIndustrialDesc: 'Uzraugiet iekštelpu apstākļus, kur komforts, procesa kontrole vai darbinieku drošība ir svarīga. Izsekojiet daļiņas ražošanas vidēs un nodrošiniet atbilstību darba veselības standartiem.',
      // CTA
      ctaTitle: 'Uzlabojiet gaisa kvalitāti savās ēkās',
      ctaButton: 'Pieprasīt demonstrāciju',
      // Section: Why 0g Baltics
      whyUsBadge: 'Jūsu partneris',
      whyUsTitle: 'Kāpēc 0g Baltics',
      whyUsDesc: 'Mēs sniedzam praktisku IoT izvietošanas pieredzi iekštelpu gaisa kvalitātes monitoringa projektos visā Baltijas reģionā.',
      whyUsFeature1: 'Baltijas tirgus fokuss',
      whyUsFeature1Desc: 'Vietējā klātbūtne un reģionālo prasību izpratne',
      whyUsFeature2: 'Praktiska IoT pieredze',
      whyUsFeature2Desc: 'Reālās pasaules izvietošanas pieredze dažādos ēku tipos',
      whyUsFeature3: 'Daudzējāda savienojamība',
      whyUsFeature3Desc: 'LoRaWAN, Sigfox un hibrīdarhitektūras',
      // Section: Sectors
      sectorsBadge: 'Nozares',
      sectorsTitle: 'IAQ monitorings pa nozarēm',
      sectorSchools: 'Skolas un izglītība',
      sectorSchoolsDesc: 'Uzlabojiet mācību vidi ar labāku gaisa kvalitāti',
      sectorOffices: 'Biroji un darba vietas',
      sectorOfficesDesc: 'Palieliniet produktivitāti un samaziniet slimības dienas',
      sectorHealthcare: 'Veselības aprūpes iestādes',
      sectorHealthcareDesc: 'Kritiska gaisa kvalitāte pacientu drošībai',
      sectorPublic: 'Publiskās ēkas',
      sectorPublicDesc: 'Pašvaldību ēkas un valdības iestādes',
      // FAQ Section
      faqBadge: 'BUJ',
      faqTitle: 'Bieži uzdotie jautājumi',
      faqDesc: 'Viss, kas jums jāzina par iekštelpu gaisa kvalitātes monitoringu',
      faq1Q: 'Kas ir iekštelpu gaisa kvalitātes monitorings?',
      faq1A: 'IAQ monitorings izmanto sensorus, lai nepārtraukti mērītu CO2, temperatūru, mitrumu, PM un VOC iekštelpās.',
      faq2Q: 'Kādus parametrus man vajadzētu uzraudzīt?',
      faq2A: 'Sāciet ar CO2 un temperatūru. Pievienojiet mitrumu, PM vai VOC atbilstoši savām vajadzībām.',
      faq3Q: 'Cik sensoru man nepieciešams?',
      faq3A: 'Parasti viens sensors katrai telpai vai zonai. Lielām telpām var būt nepieciešami vairāki sensori.',
      faq4Q: 'Kāds ir akumulatora kalpošanas laiks?',
      faq4A: 'Profesionālie sensori parasti darbojas 5-10 gadus ar baterijām.',
    },

    smartCityPage: {
      metaTitle: 'Viedās pilsētas IoT infrastruktūra | 0g Baltics',
      metaDesc: 'Veidojiet viedās pilsētas infrastruktūru ar LoRaWAN tīkliem, vides monitoringu un savienotiem pilsētas pakalpojumiem.',
      heroBadge: 'Viedās pilsētas',
      heroTitle: 'Viedās pilsētas IoT infrastruktūra',
      heroSubtitle: 'Mēs palīdzam pašvaldībām izvietot IoT infrastruktūru gudrākām, efektīvākām pilsētām.',
      solutionsBadge: 'Risinājumi',
      solutionsTitle: 'Viedās pilsētas lietojumi',
      envMonitoring: 'Vides monitorings',
      envMonitoringDesc: 'Gaisa kvalitāte, trokšņa līmenis un laika apstākļu uzraudzība visā pilsētā.',
      smartLighting: 'Viedais apgaismojums',
      smartLightingDesc: 'Savienots ielu apgaismojums ar aptumšošanu un kļūmju noteikšanu.',
      wasteManagement: 'Atkritumu apsaimniekošana',
      wasteManagementDesc: 'Viedās tvertnes ar pildījuma līmeņa sensoriem un maršrutu optimizāciju.',
      parking: 'Autostāvvietu pieejamība',
      parkingDesc: 'Reāllaika autostāvvietu pieejamība un vadības sistēmas.',
      flooding: 'Plūdu monitorings',
      floodingDesc: 'Ūdens līmeņa sensori plūdu agrīnās brīdināšanas sistēmām.',
      traffic: 'Satiksmes modeļi',
      trafficDesc: 'Transportlīdzekļu skaitīšana un satiksmes plūsmas analīze.',
      // What Is section
      whatIsBadge: 'Pamatu izpratne',
      whatIsTitle: 'Kas ir viedās pilsētas IoT infrastruktūra?',
      whatIsDesc: 'Viedās pilsētas infrastruktūra ir savienoto pilsētas pakalpojumu digitālais mugurkauls.',
      dataCollected: 'Savāktie pilsētas dati ietver:',
      airQuality: 'Gaisa kvalitāte',
      water: 'Ūdens patēriņš',
      energy: 'Enerģijas lietojums',
      assets: 'Publiskie aktīvi',
      // Why Projects Fail section
      failBadge: 'Biežākie izaicinājumi',
      failTitle: 'Kāpēc daudzi viedās pilsētas projekti cieš neveiksmi',
      failDesc: 'Daudzas viedās pilsētas iniciatīvas sākas ar izolētiem pilotprojektiem bez ilgtermiņa infrastruktūras stratēģijas.',
      commonProblems: 'Biežākās problēmas:',
      problem1: 'Sadrumstalota savienojamība ar vairākām nesavietojamām sistēmām',
      problem2: 'Neuzticami tīkli, kas nevar atbalstīt pieaugošu ierīču skaitu',
      problem3: 'Pilotprojekti, kas nav mērogojami visas pilsētas izvietojumam',
      problem4: 'Datu silos, kas novērš vienotu pilsētas intelektu',
      solutionText: 'Risinājums:',
      solutionDesc: 'Veiksmīgas viedās pilsētas stratēģijas sākas ar kopīgu savienojamības infrastruktūru, kas spēj atbalstīt daudzus pakalpojumus—nevis izolētus pilotprojektus, kas nevar augt.',
      // Connectivity Foundation section
      connectivityBadge: 'Tīkla tehnoloģijas',
      connectivityTitle: 'Viedo pilsētu pamats: Uzticama IoT savienojamība',
      connectivityDesc: 'Viedām pilsētām nepieciešami tīkli, kas spēj atbalstīt tūkstošiem ierīču, kas izvietotas plašās pilsētu teritorijās. Dažādas tehnoloģijas kalpo dažādiem lietojumiem, un veiksmīgākie izvietojumi apvieno vairākas savienojamības iespējas.',
      lorawanName: 'LoRaWAN',
      lorawanDesc: 'Liels diapazons, zems enerģijas patēriņš visas pilsētas pārklājumam',
      nbiotName: 'NB-IoT',
      nbiotDesc: 'Šūnu bāzēts dziļai iekļūšanai',
      ltemName: 'LTE-M',
      ltemDesc: 'Mobiliem aktīviem un augstākai joslas platībai',
      bleName: 'BLE tīkli',
      bleDesc: 'Iekštelpu un tuvuma lietojumiem',
      satelliteName: 'Satelīts',
      satelliteDesc: 'Attālinātai un rezerves savienojamībai',
      connectivityNote: '0g Baltics projektē daudzveidu savienojamības arhitektūras, nodrošinot, ka katra ierīce izmanto visefektīvāko sakaru tehnoloģiju. Šī pieeja maksimizē uzticamību, vienlaikus samazinot darbības izmaksas.',
      // Carrier Grade section
      carrierBadge: 'Infrastruktūras standarti',
      carrierTitle: 'Kāpēc pilsētām nepieciešama operatora līmeņa infrastruktūra',
      carrierDesc1: 'Pilsētas infrastruktūrai jādarbojas nepārtraukti daudzus gadus—bieži vien gadu desmitus. Tāpēc viedās pilsētas savienojamībai jābūt projektētai kā operatora līmeņa infrastruktūrai, nevis patērētāju līmeņa risinājumiem, kas neatbilst pašvaldību uzticamības prasībām.',
      carrierDesc2: 'Šie principi nāk no telekomunikāciju nozares un nodrošina, ka pilsētas pakalpojumi paliek funkcionāli laika gaitā neatkarīgi no laika apstākļiem, tīkla slodzes vai sistēmu izmaiņām.',
      carrierProvide: 'Operatora līmeņa tīkli nodrošina:',
      highAvailability: 'Augsta pieejamība',
      highAvailabilityDesc: '99.9%+ darbspējas mērķi',
      secureCommunications: 'Droši sakari',
      secureCommunicationsDesc: 'Šifrēta datu pārraide',
      scalableArchitecture: 'Mērogojama arhitektūra',
      scalableArchitectureDesc: 'Aug līdzi pilsētas vajadzībām',
      professionalMonitoring: 'Profesionāla uzraudzība',
      professionalMonitoringDesc: '24/7 tīkla darbības',
      longTermReliability: 'Ilgtermiņa uzticamība',
      longTermReliabilityDesc: '10+ gadu infrastruktūra',
      redundancy: 'Redundance',
      redundancyDesc: 'Pārslēgšanās iespējas',
      infrastructureLayers: 'Infrastruktūras slāņi',
      layer5: '5. slānis',
      layer5Name: 'Pilsētas pakalpojumi',
      layer5Desc: 'Stāvvietas, apgaismojums, vide',
      layer4: '4. slānis',
      layer4Name: 'Pilsētas platforma',
      layer4Desc: 'Datu pārvaldība un analītika',
      layer3: '3. slānis',
      layer3Name: 'Tīkla serveri',
      layer3Desc: 'Ierīču un datu pārvaldība',
      layer2: '2. slānis',
      layer2Name: 'Vārtejas',
      layer2Desc: 'Profesionālas bāzes stacijas',
      layer1: '1. slānis',
      layer1Name: 'Ierīces',
      layer1Desc: 'Sensori un galapunkti',
      // LoRaWAN for Cities section
      lorawanBadge: 'LPWAN tehnoloģija',
      lorawanTitle: 'LoRaWAN tīkli pilsētu IoT',
      lorawanMainDesc: 'LoRaWAN ir viena no visplašāk pieņemtajām tehnoloģijām viedo pilsētu izvietojumiem visā pasaulē. Tās tehniskās īpašības padara to ideāli piemērotu tūkstošiem sensoru savienošanai pilsētu vidēs.',
      lorawanFeature1Title: 'Liela diapazona savienojamība',
      lorawanFeature1Desc: 'Vienas vārtejas var aptvert vairākus kilometrus pilsētu vidēs, samazinot infrastruktūras investīcijas.',
      lorawanFeature2Title: 'Izcila iekļūšana',
      lorawanFeature2Desc: 'LoRa signāli sasniedz sensorus ēku iekšienē, pazemē un sarežģītās vietās visā pilsētā.',
      lorawanFeature3Title: 'Zems enerģijas patēriņš',
      lorawanFeature3Desc: 'Ar baterijām darbināmi sensori var darboties daudzus gadus bez apkopes vai nomaiņas.',
      lorawanFeature4Title: 'Liela tīkla kapacitāte',
      lorawanFeature4Desc: 'Neliels skaits vārteju var savienot tūkstošiem sensoru visā pilsētā.',
      lorawanNote: '0g Baltics izvieto operatora līmeņa LoRaWAN infrastruktūru, izmantojot profesionālas TEKTELIC vārtejas un drošu tīkla arhitektūru, kas paredzēta pašvaldību līmeņa uzticamībai.',
      stat1Value: '15+ km',
      stat1Label: 'Diapazons lauku apvidos',
      stat2Value: '5+ km',
      stat2Label: 'Diapazons pilsētu apvidos',
      stat3Value: '10+ gadi',
      stat3Label: 'Sensora baterijas kalpošanas laiks',
      stat4Value: '10 000+',
      stat4Label: 'Ierīces uz vienu vārteju',
      // Multi-Service Infrastructure section
      multiServiceBadge: 'Vienota infrastruktūra',
      multiServiceTitle: 'Viena infrastruktūra, daudzi pilsētas pakalpojumi',
      multiServiceDesc: 'Kad pilsēta izvieto kopīgu IoT infrastruktūru, daudzi pakalpojumi var darboties vienā tīklā. Šī pieeja samazina izmaksas, vienkāršo darbības un ļauj pilsētām laika gaitā paplašināt inovācijas.',
      servicesShare: 'Pakalpojumi, kas var dalīt infrastruktūru:',
      smartParking: 'Viedā autostāvvieta',
      airQualityMonitoring: 'Gaisa kvalitātes monitorings',
      smartWaterMetering: 'Viedā ūdens uzskaite',
      wasteManagementService: 'Atkritumu apsaimniekošana',
      municipalAssetTracking: 'Pašvaldības aktīvu izsekošana',
      smartStreetLighting: 'Viedais ielu apgaismojums',
      smartBuildingMonitoring: 'Viedo ēku monitorings',
      energyManagement: 'Enerģijas pārvaldība',
      // Innovation Ecosystem section
      innovationBadge: 'Ekonomiskā attīstība',
      innovationTitle: 'Jauni pakalpojumi un ekonomiskā attīstība, ko nodrošina LPWAN tīkli',
      innovationDesc: 'Pilsētas mēroga LPWAN tīkls nav tikai tehniska infrastruktūra. Tas kļūst par inovāciju platformu, kas veicina ekonomisko aktivitāti un jaunus digitālos pakalpojumus. Pilsētas, kas izvieto kopīgu IoT infrastruktūru, bieži redz jaunu uzņēmumu un pakalpojumu rašanos.',
      organizationsTitle: 'Organizācijas, kas var izmantot pilsētas IoT infrastruktūru:',
      localStartups: 'Vietējie jaunuzņēmumi',
      techCompanies: 'Tehnoloģiju uzņēmumi',
      universities: 'Universitātes',
      researchLabs: 'Pētniecības laboratorijas',
      publicServiceProviders: 'Publisko pakalpojumu sniedzēji',
      privateOperators: 'Privātie operatori',
      innovationBarrier: 'Tā vietā, lai veidotu savu savienojamības infrastruktūru, šīs organizācijas var paļauties uz pilsētas kopīgo IoT tīklu. Tas ievērojami samazina barjeru inovācijām.',
      innovationAnalogy: 'Domājiet par to kā par optisko platjoslu: IoT infrastruktūra kļūst par pamatu ilgtermiņa ekonomiskajai attīstībai, nodrošinot pakalpojumus, kas nevar pastāvēt bez uzticamas savienojamības.',
      newServicesTitle: 'Jauni pakalpojumi, kas var rasties pilsētas IoT infrastruktūrā:',
      smartLogistics: 'Viedie loģistikas pakalpojumi',
      smartLogisticsDesc: 'Pēdējās jūdzes piegādes optimizācija',
      envMonitoringStartups: 'Vides monitoringa jaunuzņēmumi',
      envMonitoringStartupsDesc: 'Pilsētas gaisa un trokšņa analītika',
      equipmentTracking: 'Aprīkojuma izsekošanas pakalpojumi',
      equipmentTrackingDesc: 'Būvniecības un rūpnieciskie aktīvi',
      sharedMobility: 'Koplietošanas mobilitātes monitorings',
      sharedMobilityDesc: 'Velosipēdi, skūteri, transportlīdzekļi',
      energyOptimization: 'Enerģijas optimizācijas pakalpojumi',
      energyOptimizationDesc: 'Ēku un tīkla pārvaldība',
      smartBuildingProviders: 'Viedo ēku pakalpojumu sniedzēji',
      smartBuildingProvidersDesc: 'Komerciālo īpašumu pakalpojumi',
      // City Intelligence section
      intelligenceBadge: 'Pilsētas analītika',
      intelligenceTitle: 'No sensoriem līdz pilsētas intelektam',
      intelligenceDesc: 'Viedās pilsētas platformas apkopo datus no tūkstošiem sensoru, kas izvietoti pilsētu teritorijās. Šie dati palīdz pilsētas operatoriem izprast pilsētas sistēmas un pieņemt labākus lēmumus par infrastruktūru, pakalpojumiem un plānošanu.',
      dataHelps: 'Dati palīdz pilsētām izprast:',
      trafficPatterns: 'Satiksmes modeļus un sastrēgumus',
      envConditions: 'Vides apstākļus un gaisa kvalitāti',
      infrastructureUsage: 'Infrastruktūras lietojumu un apkopes vajadzības',
      energyConsumption: 'Enerģijas patēriņu dažādās ēkās',
      assetUtilization: 'Publisko aktīvu izmantošanu',
      urbanTrends: 'Tendences pilsētu plānošanai',
      intelligenceConclusion: 'Laika gaitā pilsētas var optimizēt darbības un pieņemt labākus plānošanas lēmumus, balstoties uz objektīviem datiem, nevis pieņēmumiem.',
      // Deployment Strategy section
      deploymentBadge: 'Ieviešanas pieeja',
      deploymentTitle: 'Praktiska izvietošanas stratēģija pilsētām',
      deploymentDesc: 'Veiksmīgi viedās pilsētas izvietojumi seko pakāpeniskai pieejai, kas samazina risku, vienlaikus nodrošinot ilgtermiņa mērogojamību.',
      step01: '01',
      step01Title: 'Izvietojiet uzticamu IoT savienojamības infrastruktūru',
      step01Desc: 'Izveidojiet operatora līmeņa tīkla pārklājumu prioritārajās zonās. Uzstādiet profesionālas vārtejas un konfigurējiet drošu tīkla arhitektūru, kas var atbalstīt tūkstošiem ierīču.',
      step02: '02',
      step02Title: 'Savienojiet prioritāros pakalpojumus',
      step02Desc: 'Izvietojiet sākotnējos lietojuma gadījumus, piemēram, viedo autostāvvietu, gaisa kvalitātes monitoringu vai ūdens uzskaiti. Koncentrējieties uz pakalpojumiem, kas sniedz skaidru vērtību un demonstrē infrastruktūras iespējas.',
      step03: '03',
      step03Title: 'Pakāpeniski paplašiniet uz jauniem pakalpojumiem',
      step03Desc: 'Laika gaitā pievienojiet jaunus pakalpojumus un nodaļas. Kopīgā infrastruktūra atbalsta papildu lietojuma gadījumus bez jaunu tīkla investīciju nepieciešamības.',
      step04: '04',
      step04Title: 'Izmantojiet datu analītiku darbību uzlabošanai',
      step04Desc: 'Izmantojiet savāktos datus, lai optimizētu pilsētas pakalpojumus, informētu plānošanas lēmumus un demonstrētu vērtību iedzīvotājiem un ieinteresētajām pusēm.',
      // Why 0g Baltics section
      whyBadge: 'Jūsu partneris',
      whyTitle: 'Kāpēc pilsētas sadarbojas ar 0g Baltics',
      whyDesc: '0g Baltics nodrošina infrastruktūras ekspertīzi, kas pilsētām nepieciešama veiksmīgu viedās pilsētas izvietojumu veidošanai. Mūsu mērķis ir palīdzēt pilsētām veidot uzticamu digitālo infrastruktūru, kas atbalsta ilgtermiņa inovācijas.',
      service1Title: 'Operatora līmeņa IoT savienojamība',
      service1Desc: 'Tīkli, kas projektēti pašvaldību uzticamības prasībām',
      service2Title: 'LoRaWAN infrastruktūras izvietošana',
      service2Desc: 'Profesionāla tīkla projektēšana, uzstādīšana un pārvaldība',
      service3Title: 'Daudzveidu savienojamības arhitektūra',
      service3Desc: 'Hibrīdi tīkli, kas apvieno LoRaWAN, šūnu un citas tehnoloģijas',
      service4Title: 'Viedās pilsētas konsultācijas',
      service4Desc: 'Stratēģiskas vadlīnijas infrastruktūras plānošanai',
      service5Title: 'Integrācija ar pilsētas platformām',
      service5Desc: 'Savienojiet sensorus ar pilsētas pārvaldības sistēmām',
      relatedSolutions: 'Saistītie 0g Baltics risinājumi',
      relatedConnectivity: 'Daudztīklu savienojamība',
      relatedConnectivityDesc: 'LoRaWAN, NB-IoT, Sigfox risinājumi',
      relatedTektelic: 'TEKTELIC infrastruktūra',
      relatedTektelicDesc: 'Operatora līmeņa LoRaWAN vārtejas',
      relatedMetering: 'Viedā uzskaite',
      relatedMeteringDesc: 'Ūdens, enerģijas, gāzes uzskaite',
      relatedAssetTracking: 'Aktīvu izsekošana',
      relatedAssetTrackingDesc: 'Pašvaldības aprīkojuma monitorings',
      relatedAirQuality: 'Gaisa kvalitātes monitorings',
      relatedAirQualityDesc: 'Vides IoT sensori',
      // CTA section
      ctaTitle: 'Veidojiet savu viedās pilsētas infrastruktūru',
      ctaDesc: 'Sazinieties ar 0g Baltics, lai projektētu mērogojamu viedās pilsētas savienojamību un infrastruktūru. Mēs palīdzam pašvaldībām un pilsētām veidot digitālo pamatu ilgtermiņa pilsētu inovācijām.',
      ctaButton: 'Runājiet ar mūsu komandu',
    },

    smartLockersPage: {
      metaTitle: 'Viedie skapīši | Automatizēti sūtījumu un instrumentu skapīši | 0g Baltics',
      metaDesc: 'Viedo skapīšu risinājumi sūtījumu piegādei, instrumentu pārvaldībai un publiskajiem pakalpojumiem.',
      heroBadge: 'Viedie skapīši',
      heroTitle: 'Viedo skapīšu risinājumi',
      heroSubtitle: 'Automatizēta skapīšu infrastruktūra loģistikai, rūpnieciskām operācijām un publiskajiem pakalpojumiem.',
      // Use case badges
      parcelDelivery: 'Sūtījumu piegāde',
      toolManagement: 'Instrumentu pārvaldība',
      beachLockers: 'Pludmales skapīši',
      retailPickup: 'Mazumtirdzniecības saņemšana',
      keyManagement: 'Atslēgu pārvaldība',
      parcelLockers: 'Sūtījumu skapīši',
      parcelLockersDesc: 'Automatizēta sūtījumu piegāde un saņemšana pēdējās jūdzes loģistikai.',
      toolLockers: 'Instrumentu pārvaldības skapīši',
      toolLockersDesc: 'Rūpniecisko instrumentu izplatīšana un izsekošana ar piekļuves kontroli.',
      libraryLockers: 'Bibliotēkas skapīši',
      libraryLockersDesc: 'Automatizēta grāmatu saņemšana un atgriešana publiskajām bibliotēkām.',
      discoverToolTracking: 'Atklājiet mūsu instrumentu izsekošanas risinājumus',
      // What Are Smart Lockers
      whatAreBadge: 'Tehnoloģijas izpratne',
      whatAreTitle: 'Kas ir viedie skapīši?',
      whatAreDesc1: 'Viedie skapīši ir savienotas uzglabāšanas sistēmas, kas ļauj droši uzglabāt un izgūt priekšmetus, izmantojot digitālo autentifikāciju. Atšķirībā no tradicionālajiem skapīšiem, kas paļaujas uz fiziskām atslēgām vai kombināciju slēdzenēm, viedie skapīši integrējas ar mākoņa platformām un nodrošina vairākas piekļuves metodes.',
      whatAreDesc2: 'Viedie skapīši aizstāj manuālas uzglabāšanas sistēmas ar automatizētu drošu piekļuvi, nodrošinot pilnīgu redzamību par lietošanas modeļiem, inventāra līmeņiem un piekļuves vēsturi. Tas padara tos ideālus liela apjoma operācijām, kur svarīga ir izsekošana un atbildība.',
      accessMethods: 'Lietotāji var piekļūt skapīšiem, izmantojot:',
      accessMobile: 'Mobilās lietotnes',
      accessPIN: 'PIN kodus',
      accessRFID: 'RFID kartes',
      accessQR: 'QR kodus',
      accessBadge: 'Darbinieku caurlaides',
      accessPayment: 'Maksājumu sistēmas',
      cloudCapabilities: 'Mākoņa platformas iespējas:',
      capAccessControl: 'Piekļuves kontrole',
      capAccessControlDesc: 'Pārvaldiet, kas var piekļūt kuriem skapīšiem un kad',
      capNotifications: 'Paziņojumi',
      capNotificationsDesc: 'SMS, e-pasta un push paziņojumi lietotājiem',
      capUsageTracking: 'Lietošanas izsekošana',
      capUsageTrackingDesc: 'Pilnīga visu skapīšu transakciju vēsture',
      capAvailability: 'Skapīšu pieejamība',
      capAvailabilityDesc: 'Reāllaika redzamība par tukšajiem nodalījumiem',
      capAnalytics: 'Analītika',
      capAnalyticsDesc: 'Lietošanas modeļi un operacionālie ieskati',
      // Smart Locker Models
      modelsBadge: 'Aparatūras opcijas',
      modelsTitle: 'Viedo skapīšu modeļi',
      modelsDesc: '0g Baltics piedāvā vairākas skapīšu arhitektūras atkarībā no lietošanas gadījuma. Katrs modelis ļauj elastīgi konfigurēt nodalījumu izmērus atbilstoši uzglabājamajiem priekšmetiem.',
      towerLockers: 'Torņa skapīši',
      towerLockersDesc: 'Vertikālas skapīšu sistēmas, kas paredzētas sūtījumu piegādei un loģistikas centriem. Kompakts nospiedums ar maksimālu uzglabāšanas ietilpību.',
      towerFeature1: 'Liela ietilpība',
      towerFeature2: 'Mazs nospiedums',
      towerFeature3: 'Ārtelpām gatavs',
      storeLockers: 'Veikala skapīši',
      storeLockersDesc: 'Lieli modulāri skapīši, kas piemēroti mazumtirdzniecības saņemšanai un komerciālām vidēm. Sienas montēšanas vai brīvi stāvošas konfigurācijas.',
      storeFeature1: 'Modulārs dizains',
      storeFeature2: 'Mazumtirdzniecības integrācija',
      storeFeature3: 'Augsta redzamība',
      slingLockers: 'Sling skapīši',
      slingLockersDesc: 'Kompakti skapīši, ideāli mazām telpām, birojiem un iekštelpām. Perfekti korporatīviem un kampusa izvietojumiem.',
      slingFeature1: 'Telpas efektīvi',
      slingFeature2: 'Iekštelpu dizains',
      slingFeature3: 'Vienkārša uzstādīšana',
      batteryPowered: 'Ar baterijām darbināmi',
      batteryPoweredDesc: 'Skapīši, ko darbina baterijas, ļaujot izvietot bez elektriskās instalācijas. Ideāli attālām un pagaidu vietām.',
      batteryFeature1: 'Bez vadiem',
      batteryFeature2: 'Elastīga izvietošana',
      batteryFeature3: 'Zemas uzstādīšanas izmaksas',
      // Retail Pickup Lockers
      retailTitle: 'Mazumtirdzniecības saņemšanas skapīši',
      retailDesc: 'Mazumtirgotāji izmanto skapīšus, lai ļautu klientiem ātri saņemt tiešsaistes pasūtījumus. Klikšķini-un-saņem pakalpojumi kļūst arvien populārāki, skapīši nodrošina 24/7 saņemšanu bez personāla palīdzības.',
      retailAdvantages: 'Priekšrocības:',
      retailAdv1: '24/7 saņemšanas pieejamība',
      retailAdv2: 'Samazinātas veikala rindas',
      retailAdv3: 'Ātrāka klientu apkalpošana',
      retailAdv4: 'Automatizēta saņemšanas loģistika',
      retailAdv5: 'Integrācija ar e-komerciju',
      retailNote: 'Mazumtirdzniecības skapīši arvien vairāk tiek izmantoti lielveikalos, tirdzniecības centros un e-komercijas izpildes centros.',
      // Beach Lockers
      beachTitle: 'Pludmales un atpūtas skapīši',
      beachDesc: 'Viedie skapīši ir ideāli atpūtas vidēm, kur apmeklētājiem nepieciešama droša personīgo mantu uzglabāšana. Ar baterijām darbināmas opcijas ļauj izvietot bez infrastruktūras ieguldījumiem.',
      idealLocations: 'Ideālās atrašanās vietas:',
      beachLoc1: 'Pludmales un piekrastes zonas',
      beachLoc2: 'Parki un atpūtas zonas',
      beachLoc3: 'Sporta objekti',
      beachLoc4: 'Tūrisma vietas',
      beachLoc5: 'Pasākumu norises vietas',
      beachNote: 'Lietotāji piekļūst skapīšiem caur mobilajām lietotnēm vai maksājumu sistēmām, ļaujot operatoriem monetizēt.',
      // Key Management
      keyTitle: 'Atslēgu pārvaldības skapīši',
      keyDesc: 'Savienotie skapīši var pārvaldīt fiziskas atslēgas autoparka transportlīdzekļiem, īpašumu pārvaldībai un aprīkojuma piekļuvei. Katra transakcija tiek digitāli reģistrēta, veidojot audita pierakstu, kas uzlabo operacionālo drošību un atbildību.',
      keyFeature1: 'Autoparka transportlīdzekļu atslēgu pārvaldība',
      keyFeature2: 'Īpašuma un objekta piekļuve',
      keyFeature3: 'Aprīkojuma atslēgu izplatīšana',
      keyFeature4: 'Darbuzņēmēju un apmeklētāju piekļuve',
      keyFeature5: 'Pilnīgs digitālais audita pieraksts',
      // Battery Powered
      batteryTitle: 'Ar baterijām darbināmi skapīši',
      batteryDesc: 'Ar baterijām darbināmi skapīši ļauj uzstādīt vietās bez elektriskās infrastruktūras. Tas dramatiski samazina uzstādīšanas izmaksas un ļauj elastīgi izvietot vietās, kur vadu instalācija būtu nepraktiska.',
      batteryFeat1: 'Nav nepieciešama elektriskā instalācija',
      batteryFeat2: 'Ātra un vienkārša uzstādīšana',
      batteryFeat3: 'Elastīga atrašanās vietas izvēle',
      batteryFeat4: 'Zemākas kopējās uzstādīšanas izmaksas',
      batteryFeat5: 'Ideāli pagaidu izvietojumiem',
      batteryUseCases: 'Bieži lietošanas gadījumi:',
      batteryUseCasesDesc: 'Pludmales, parki, pagaidu instalācijas, attālas vietas un vietas, kur elektriskā infrastruktūra ir dārga vai nepieejama.',
      // Software Platform
      platformBadge: 'Mākoņa pārvaldība',
      platformTitle: 'Skapīšu pārvaldības platforma',
      platformDesc: 'Viedos skapīšus pārvalda caur mākoņa platformu, kas nodrošina pilnīgu redzamību un kontroli pār skapīšu tīkliem. Operatori var uzraudzīt statusu, pārvaldīt lietotājus un analizēt lietošanas modeļus no jebkuras vietas.',
      platformCapabilities: 'Platformas iespējas:',
      platAvailability: 'Pieejamības uzraudzība',
      platAvailabilityDesc: 'Reāllaika nodalījumu statuss',
      platUserMgmt: 'Lietotāju pārvaldība',
      platUserMgmtDesc: 'Piekļuves kontrole un atļaujas',
      platNotifications: 'Paziņojumi',
      platNotificationsDesc: 'SMS, e-pasta, push brīdinājumi',
      platAnalytics: 'Lietošanas analītika',
      platAnalyticsDesc: 'Tendences un modeļi',
      platRemoteConfig: 'Attālā konfigurācija',
      platRemoteConfigDesc: 'Attālināti atjaunināt iestatījumus',
      platSecurityLogs: 'Drošības žurnāli',
      platSecurityLogsDesc: 'Pilnīgs audita pieraksts',
      // API and Integrations
      apiBadge: 'Atvērta arhitektūra',
      apiTitle: 'Atvērti API un integrācijas',
      apiDesc: 'Skapīšu platformas var integrēties ar esošajām biznesa sistēmām caur API, ļaujot skapīšiem kļūt par plašākas digitālās infrastruktūras daļu, nevis atsevišķiem risinājumiem.',
      intEcommerce: 'E-komercija',
      intEcommerceDesc: 'Pasūtījumu pārvaldība',
      intIdentity: 'Identitāte',
      intIdentityDesc: 'SSO un LDAP',
      intPayment: 'Maksājumi',
      intPaymentDesc: 'Karte un mobilie',
      intLogistics: 'Loģistika',
      intLogisticsDesc: 'TMS integrācija',
      intSmartCity: 'Viedā pilsēta',
      intSmartCityDesc: 'Pilsētvides platformas',
      intERP: 'ERP',
      intERPDesc: 'Uzņēmuma sistēmas',
      // Smart Lockers for Cities
      citiesBadge: 'Pilsētvides infrastruktūra',
      citiesTitle: 'Viedo skapīšu infrastruktūra pilsētām',
      citiesDesc: 'Pilsētas var izvietot skapīšu tīklus kā daļu no viedās pilsētas infrastruktūras. Sūtījumu skapīši samazina piegādes satiksmi, publiskie uzglabāšanas skapīši apkalpo iedzīvotājus un tūristus, un aprīkojuma skapīši atbalsta pašvaldības operācijas.',
      municipalApps: 'Pašvaldību skapīšu pielietojumi:',
      cityApp1: 'Sūtījumu piegādes tīkli, kas samazina satiksmi',
      cityApp2: 'Publiskā uzglabāšana iedzīvotājiem un tūristiem',
      cityApp3: 'Pludmales un atpūtas zonu pakalpojumi',
      cityApp4: 'Pašvaldības aprīkojuma pārvaldība',
      cityApp5: 'Pilsētas transportlīdzekļu un objektu atslēgu piekļuve',
      learnSmartCity: 'Uzzināt vairāk par viedās pilsētas infrastruktūru',
      networksInfra: 'Skapīšu tīkli kļūst par infrastruktūru',
      networksInfraDesc: 'Kad pilsētas izvieto skapīšu tīklus, tās rada koplietojamu infrastruktūru, kas kalpo vairākiem mērķiem—samazina pēdējās jūdzes piegādes satiksmi, nodrošina iedzīvotāju pakalpojumus un atbalsta pašvaldības operācijas.',
      cityBenefit1: 'Samazina piegādes transportlīdzekļu satiksmi',
      cityBenefit2: 'Nodrošina 24/7 iedzīvotāju pakalpojumus',
      cityBenefit3: 'Atbalsta tūrismu un atpūtu',
      cityBenefit4: 'Uzlabo pašvaldības efektivitāti',
      // Why 0g Baltics
      whyBadge: 'Jūsu partneris',
      whyTitle: 'Kāpēc izvietot viedos skapīšus ar 0g Baltics',
      whyDesc: '0g Baltics apvieno izturīgu aparatūru, IoT savienojamību, mākoņa platformas un atvērtas integrācijas, lai piegādātu skapīšu sistēmas, kas paredzētas vairākām nozarēm un liela mēroga izvietojumiem.',
      whyFeature1: 'Izturīga aparatūra',
      whyFeature1Desc: 'Rūpnieciskas klases skapīši, kas būvēti liela apjoma operācijām',
      whyFeature2: 'IoT savienojamība',
      whyFeature2Desc: 'LoRaWAN, mobilie un WiFi risinājumi jebkurai videi',
      whyFeature3: 'Mākoņa platforma',
      whyFeature3Desc: 'Pilnīga pārvaldība, analītika un paziņojumu sistēma',
      whyFeature4: 'Atvērtas integrācijas',
      whyFeature4Desc: 'API e-komercijai, loģistikai, identitātei un maksājumu sistēmām',
      whyFeature5: 'Daudznozaru pieredze',
      whyFeature5Desc: 'Loģistikas, mazumtirdzniecības, rūpniecības un publiskā sektora izvietojumi',
      relatedSolutions: 'Saistītie risinājumi',
      assetTrackingLabel: 'Aktīvu izsekošana',
      assetTrackingDesc: 'Izsekojiet instrumentus un aprīkojumu ārpus skapīša',
      connectivityLabel: 'IoT savienojamība',
      connectivityDesc: 'LoRaWAN, NB-IoT un mobilie risinājumi',
      smartCityLabel: 'Viedās pilsētas infrastruktūra',
      smartCityDesc: 'Pilsētvides IoT izvietojumi',
      // FAQ
      faqBadge: 'BUJ',
      faqTitle: 'Bieži uzdotie jautājumi',
      faq1Q: 'Kas ir viedie skapīši?',
      faq1A: 'Viedie skapīši ir savienotas uzglabāšanas sistēmas, kas ļauj droši uzglabāt un izgūt priekšmetus, izmantojot digitālo autentifikāciju, piemēram, PIN kodus, mobilās lietotnes, RFID kartes vai QR kodus. Atšķirībā no tradicionālajiem skapīšiem ar fiziskām atslēgām, viedie skapīši savienojas ar mākoņa platformām, kas pārvalda piekļuves kontroli, sūta paziņojumus un nodrošina lietošanas analītiku.',
      faq2Q: 'Kā darbojas sūtījumu skapīši?',
      faq2A: 'Kad sūtījums tiek piegādāts skapītī, saņēmējs saņem SMS vai e-pasta paziņojumu ar unikālu PIN kodu. Viņi apmeklē skapīša atrašanās vietu, ievada PIN uz skārienekrāna, un nodalījums ar viņu sūtījumu automātiski atveras. Sūtījumus parasti glabā 7 dienas pirms atgriešanas sūtītājam. Ikviens ar PIN var saņemt—ID pārbaude nav nepieciešama.',
      faq3Q: 'Kuras nozares izmanto viedos skapīšus?',
      faq3A: 'Viedie skapīši tiek izmantoti daudzās nozarēs: loģistikā un e-komercijā (sūtījumu piegāde), mazumtirdzniecībā (klikšķini-un-saņem saņemšana), ražošanā un būvniecībā (instrumentu pārvaldība), viesmīlībā un tūrismā (pludmales un atpūtas skapīši), korporatīvajos birojos (pasta un paciņu telpas), veselības aprūpē (paraugu un medikamentu uzglabāšana) un pašvaldībās (publiskā uzglabāšana un pilsētas pakalpojumi).',
      faq4Q: 'Vai skapīši var darboties bez elektrības?',
      faq4A: 'Jā. Ar baterijām darbināmi skapīši var darboties ilgāku laiku bez elektriskās infrastruktūras. Tas ļauj izvietot tādās vietās kā pludmales, parki, pagaidu instalācijas un attālas vietas, kur vadu instalācija būtu dārga vai nepraktiska. Bateriju skapīši komunicē caur mobilajiem vai LoRaWAN tīkliem.',
      faq5Q: 'Cik droši ir viedie skapīši?',
      faq5A: 'Viedie skapīši nodrošina vairākus drošības līmeņus: digitālā piekļuves kontrole nodrošina, ka tikai autorizēti lietotāji var atvērt nodalījumus, katra transakcija tiek reģistrēta, veidojot pilnīgu audita pierakstu, nodalījumi izmanto izturīgus bloķēšanas mehānismus, un mākoņa platformas ļauj attālināti uzraudzīt un brīdināt. Augsta līmeņa drošības lietojumiem skapīšus var integrēt ar biometrisko autentifikāciju.',
      faq6Q: 'Vai skapīšu sistēmas var integrēties ar esošajām platformām?',
      faq6A: 'Jā. Modernas skapīšu platformas nodrošina API, kas ļauj integrēties ar e-komercijas platformām (pasūtījumu pārvaldībai), identitātes sistēmām (SSO, LDAP), maksājumu vārtejām, loģistikas programmatūru (TMS), viedās pilsētas platformām un uzņēmuma ERP sistēmām. Tas ļauj skapīšiem kļūt par plašāku biznesa darbplūsmu daļu, nevis atsevišķām sistēmām.',
      ctaTitle: 'Izvietojiet viedos skapīšus savā organizācijā',
      ctaButton: 'Uzzināt vairāk',
    },

    buildingEnergyPage: {
      metaTitle: 'Ēku energoefektivitāte | IoT risinājumi | 0g Baltics',
      metaDesc: 'Samaziniet ēku enerģijas patēriņu ar IoT monitoringu un viedajām vadības ierīcēm.',
      heroBadge: 'Energoefektivitāte',
      heroTitle: 'Ēku energoefektivitātes risinājumi',
      heroSubtitle: 'Uzraugiet un optimizējiet enerģijas patēriņu komerciālās ēkās ar IoT sensoriem un viedajām vadības ierīcēm.',
      // Hero badges
      heroBadge1: 'Enerģijas monitorings',
      heroBadge2: 'HVAC optimizācija',
      heroBadge3: 'Noslogojuma noteikšana',
      heroBadge4: 'Analītika',
      heroBadge5: 'Izmaksu samazināšana',
      // Problem
      problemBadge: 'Izaicinājums',
      problemTitle: 'Ēkas izšķērdē enerģiju',
      problemDesc: 'Komerciālās ēkas veido 40% no enerģijas patēriņa. Lielākā daļa darbojas neefektīvi.',
      hvacBadge: 'Lielākā iespēja',
      hvacTitle: 'HVAC optimizācija',
      hvacDesc: 'HVAC sistēmas veido 40-60% no ēku enerģijas patēriņa.',
      hvacParadoxTitle: 'Pašreizējo regulēšanas sistēmu paradokss',
      hvacParadoxDesc: 'Tradicionālās HVAC regulēšanas sistēmas paradoksāli bieži izraisa augstāku enerģijas patēriņu.',
      hvacProblem1: 'Ieslēgšanas/izslēgšanas cikli rada temperatūras svārstības',
      hvacProblem2: 'Fiksēti grafiki ignorē faktiskos lietošanas modeļus',
      hvacProblem3: 'Nav korelācijas starp CO2 un ventilāciju',
      hvacProblem4: 'Sistēmas kompensē ar pārmērīgi lielu aprīkojumu',
      harmonySolution: 'Viedais risinājums',
      harmonyTitle: 'TEKTELIC Harmony',
      harmonyDesc: 'Inteliģents termostats, kas apvieno CO2 monitoringu, temperatūras/mitruma sensorus un HVAC vadību.',
      harmonyBenefit: 'Korelējot CO2 līmeņus ar ventilācijas vajadzībām un faktisko noslogojumu, Harmony optimizē HVAC darbību, samazinot enerģijas patēriņu par 20-40%.',
      // Invisibility section
      invisibilityBadge: 'Galvenais izaicinājums',
      invisibilityTitle: 'Enerģijas patēriņš bieži nav redzams',
      invisibilityDesc: 'Daudzām organizācijām nav skaidras redzamības par to, kā viņu ēkas patiesībā patērē enerģiju.',
      invisibility1: 'Kurš aprīkojums patērē visvairāk enerģijas',
      invisibility2: 'Kad notiek patēriņa maksimumi',
      invisibility3: 'Kur pastāv neefektivitāte',
      invisibility4: 'Vai patēriņš pieaug',
      withoutMonitoring: 'Bez monitoringa',
      withMonitoring: 'Ar IoT monitoringu',
      // Stats
      stat1: 'no globālās enerģijas patērē ēkas',
      stat2: 'tipiska enerģijas izšķērdēšana komerciālajās ēkās',
      stat3: 'ietaupījumi, kas sasniedzami ar viedo monitoringu',
      stat4: 'tipiskais atmaksāšanās periods',
      // IoT Monitoring Section
      iotMonitoringBadge: 'Tehnoloģiju pamati',
      iotMonitoringTitle: 'IoT enerģijas monitoringa sistēmas',
      iotMonitoringDesc: 'IoT sensori ļauj nepārtraukti uzraudzīt ēku enerģijas patēriņu detalizētā līmenī. Atšķirībā no tradicionālās komunālo pakalpojumu uzskaitēs, kas sniedz tikai mēneša kopējos datus, viedie sensori uztver detalizētu informāciju par to, kā, kad un kur enerģija tiek patērēta.',
      iotDevicesMeasure: 'IoT ierīces var mērīt:',
      measureElectricity: 'Elektrība',
      measureElectricityDesc: 'Kopējais un ķēdes līmeņa patēriņš',
      measureWater: 'Ūdens',
      measureWaterDesc: 'Lietošanas modeļi un noplūžu noteikšana',
      measureTemperature: 'Temperatūra',
      measureTemperatureDesc: 'Termālie dati pa zonām',
      measureHumidity: 'Mitrums',
      measureHumidityDesc: 'Komforts un sistēmas veiktspēja',
      measureOccupancy: 'Noslogojums',
      measureOccupancyDesc: 'Telpu izmantošanas modeļi',
      measureEquipment: 'Aprīkojums',
      measureEquipmentDesc: 'Darbības laiks un operacionālais statuss',
      iotDataTransmission: 'Šie dati tiek pārsūtīti bezvadu režīmā caur LoRaWAN vai mobilajiem tīkliem uz digitālajām platformām, kur operatori var analizēt patēriņa modeļus un identificēt optimizācijas iespējas.',
      // Energy Analytics Section
      analyticsBadge: 'Uz datiem balstīti lēmumi',
      analyticsTitle: 'Enerģijas analītika un optimizācija',
      analyticsDesc: 'Kad enerģijas dati ir savākti, analītikas rīki palīdz identificēt neefektivitāti un optimizācijas iespējas. Modernas enerģijas pārvaldības platformas izmanto algoritmus, lai noteiktu modeļus, anomālijas un korelācijas, kuras būtu neiespējami identificēt manuāli.',
      typicalInsights: 'Tipiskās atziņas ietver:',
      insight1: 'Neparasts enerģijas patēriņš, kas norāda uz aprīkojuma problēmām',
      insight2: 'Aprīkojums, kas darbojas ārpus plānotā darba laika',
      insight3: 'Neefektīva apkures vai dzesēšanas sistēmas darbība',
      insight4: 'Slikta ēkas plānošana, kas izšķērdē enerģiju',
      insight5: 'Patēriņa tendences, kurām nepieciešama uzmanība',
      analyticsConclusion: 'Organizācijas var izmantot šīs atziņas, lai noteiktu uzlabojumu prioritātes, pārbaudītu izmaiņu ietekmi un nepārtraukti optimizētu ēku darbību.',
      // Occupancy Section
      occupancyBadge: 'Pieprasījuma vadīta kontrole',
      occupancyTitle: 'Uz noslogojumu balstīta enerģijas pārvaldība',
      occupancyDesc: 'Ēkas bieži tiek apsildītas, dzesētas un apgaismotas pat tad, kad telpas ir pilnīgi tukšas. Konferenču zāles, kas sagatavotas sanāksmēm, kas nenotiek, biroji, kas kondicionēti nedēļas nogalēs, veseli stāvi, kas apgaismoti nakti—tas ir būtisks enerģijas izšķērdējums.',
      occupancyDesc2: 'Noslogojuma sensori ļauj ēkām pielāgot enerģijas patēriņu faktiskajiem izmantošanas modeļiem. Kad telpas nav noslogotas, apgaismojums var aptumšoties vai izslēgties, HVAC var samazināt jaudu, un aprīkojums var pāriet gaidīšanas režīmā. Kad cilvēki ierodas, sistēmas automātiski reaģē.',
      highImpactApps: 'Augstas ietekmes lietojumi:',
      appMeetingRooms: 'Sanāksmju telpas',
      appClassrooms: 'Klases',
      appIndividualOffices: 'Individuālie biroji',
      appPublicBuildings: 'Publiskās ēkas',
      appCommonAreas: 'Koplietošanas zonas',
      appParkingFacilities: 'Autostāvvietas',
      // Sensors Section
      sensorsBadge: 'Sensoru infrastruktūra',
      sensorsTitle: 'Sensori energoefektīvām ēkām',
      sensorsDesc: 'Enerģijas optimizācija balstās uz vairāku veidu sensoriem, kas strādā kopā, lai radītu pilnīgu priekšstatu par ēkas apstākļiem. Katrs sensors nodrošina specifiskus datus, kas veicina kopējo enerģijas intelektu.',
      sensorTemp: 'Temperatūras sensori',
      sensorTempDesc: 'Uzrauga termiskos apstākļus zonās, lai optimizētu apkuri un dzesēšanu. Identificē karstos un aukstos punktus, kas norāda uz HVAC nelīdzsvarotību.',
      sensorHumidity: 'Mitruma sensori',
      sensorHumidityDesc: 'Seko mitruma līmeņiem, kas ietekmē komfortu un sistēmas veiktspēju. Optimizē gaisa žāvēšanu un novērš pelējuma apstākļus.',
      sensorCO2: 'CO2 sensori',
      sensorCO2Desc: 'Mēra gaisa kvalitāti un ventilācijas pietiekamību. Iespējo pieprasījuma vadītu ventilāciju, kas līdzsvaro gaisa kvalitāti un enerģiju.',
      sensorOccupancy: 'Noslogojuma sensori',
      sensorOccupancyDesc: 'Nosaka telpu izmantošanu reāllaikā. Iespējo automātisku apgaismojuma, HVAC un aprīkojuma pielāgošanu atkarībā no klātbūtnes.',
      sensorEnergy: 'Enerģijas skaitītāji',
      sensorEnergyDesc: 'Mēra elektrības patēriņu ēkas, stāva vai ķēdes līmenī. Identificē augsta patēriņa aprīkojumu un lietošanas modeļus.',
      sensorWater: 'Ūdens skaitītāji',
      sensorWaterDesc: 'Seko ūdens patēriņam un nosaka noplūdes. Identificē neparastu lietošanu, kas norāda uz santehnikas problēmām vai izšķērdēšanu.',
      learnMoreIAQ: 'Uzziniet vairāk par iekštelpu gaisa kvalitātes monitoringu',
      // Standards Section
      standardsBadge: 'Regulējošais konteksts',
      standardsTitle: 'Energoefektivitātes standarti un noteikumi',
      standardsDesc: 'Daudzās valstīs tagad pieprasa uzlabotu ēku energoefektivitāti. Regulējošie ietvari kļūst stingrāki, un organizācijām arvien vairāk nepieciešamas monitoringa sistēmas, lai atbalstītu atbilstību un ilgtspējības ziņojumus.',
      epbdTitle: 'ES Ēku energoefektivitātes direktīva (EPBD)',
      epbdDesc: 'Pieprasa dalībvalstīm noteikt minimālās ēku energoefektivitātes prasības un ieviest energosertifikācijas sistēmas. Direktīva virza uz gandrīz nulles enerģijas ēkām.',
      epcTitle: 'Energoefektivitātes sertifikāti (EPC)',
      epcDesc: 'Ēkām jābūt novērtētām pēc energoefektivitātes, un sertifikāti nepieciešami pārdošanai vai īrei daudzās jurisdikcijās. Labāki reitingi var palielināt īpašuma vērtību un īres pievilcību.',
      esgTitle: 'ESG un ilgtspējības ziņojumi',
      esgDesc: 'Korporatīvie ilgtspējības ziņojumi arvien vairāk pieprasa detalizētus enerģijas patēriņa datus. Monitoringa sistēmas nodrošina pārbaudāmus datus, kas nepieciešami ESG atklāsmēm un ilgtspējības sertifikātiem.',
      monitoringSupports: 'Enerģijas monitorings atbalsta:',
      support1: 'Regulējošās atbilstības dokumentāciju',
      support2: 'Energoefektivitātes sertifikātu uzlabojumus',
      support3: 'ESG un ilgtspējības ziņojumus',
      support4: 'Oglekļa pēdas nospieduma aprēķinu',
      support5: 'Zaļo ēku sertifikāciju (LEED, BREEAM)',
      support6: 'Audita un verifikācijas procesus',
      // ROI Section
      roiBadge: 'Biznesa ietekme',
      roiTitle: 'Biznesa pamatojums enerģijas monitoringam',
      roiDesc: 'Enerģijas monitorings nodrošina izmērāmus, pārbaudāmus ieguvumus. Organizācijas, kas ievieš visaptverošas monitoringa sistēmas, parasti redz būtisku atdevi caur samazinātām enerģijas izmaksām, uzlabotām operācijām un novērstām aprīkojuma avārijām.',
      typicalBenefits: 'Tipiskie ieguvumi',
      benefit1: '10-25% enerģijas izmaksu samazinājums',
      benefit2: 'Pagarināts aprīkojuma kalpošanas laiks',
      benefit3: 'Samazinātas uzturēšanas izmaksas',
      benefit4: 'Uzlabots iemītnieku komforts',
      benefit5: 'Labāka operacionālā redzamība',
      benefit6: 'Dati stratēģiskiem lēmumiem',
      investmentTitle: 'Investīciju apsvērumi',
      investment1: 'Zemas izmaksas bezvadu sensori',
      investment2: 'Minimāli uzstādīšanas traucējumi',
      investment3: 'Mērogojama ieviešanas pieeja',
      investment4: 'Mākoņplatforma—nav IT infrastruktūras',
      investment5: 'Izmērāma ROI 2-3 gados',
      investment6: 'Nepārtraukti operacionāli ietaupījumi',
      roiNote: 'Pat nelieli uzlabojumi ir svarīgi: 10% enerģijas patēriņa samazinājums ēkai ar €100 000 ikgadējām enerģijas izmaksām rada €10 000 ikgadējos ietaupījumos—kas uzkrājas gadu pēc gada.',
      // Why 0g Baltics Section
      whyBadge: 'Jūsu partneris',
      whyTitle: 'Kāpēc strādāt ar 0g Baltics',
      whyDesc: '0g Baltics nodrošina visaptverošus enerģijas monitoringa risinājumus, apvienojot profesionālus IoT sensorus, uzticamu savienojamības infrastruktūru un jaudīgas analītikas platformas. Mūsu mērķis ir palīdzēt organizācijām samazināt enerģijas patēriņu un uzlabot ēku veiktspēju.',
      whyFeature1: 'IoT sensori ēkām',
      whyFeature1Desc: 'Temperatūras, mitruma, noslogojuma, enerģijas un vides monitorings',
      whyFeature2: 'Enerģijas monitoringa sistēmas',
      whyFeature2Desc: 'Reāllaika patēriņa izsekošana un analītika',
      whyFeature3: 'LoRaWAN savienojamība',
      whyFeature3Desc: 'Uzticama, zemas jaudas bezvadu infrastruktūra',
      whyFeature4: 'Datu platformas un analītika',
      whyFeature4Desc: 'Atziņas un optimizācijas ieteikumi',
      whyFeature5: 'Viedo ēku konsultācijas',
      whyFeature5Desc: 'Stratēģijas un ieviešanas vadlīnijas',
      relatedSolutions: 'Saistītie risinājumi',
      relatedIAQ: 'Iekštelpu gaisa kvalitāte',
      relatedIAQDesc: 'CO2, temperatūras, mitruma monitorings',
      relatedSmartCity: 'Viedās pilsētas infrastruktūra',
      relatedSmartCityDesc: 'Pilsētas ēku ieviešanas',
      relatedConnectivity: 'IoT savienojamība',
      relatedConnectivityDesc: 'LoRaWAN un mobilie risinājumi',
      relatedMetering: 'Viedā uzskaite',
      relatedMeteringDesc: 'Ūdens un enerģijas uzskaite',
      // CTA
      ctaTitle: 'Uzlabojiet savas ēkas energoefektivitāti',
      ctaDesc: 'Sazinieties ar 0g Baltics, lai izvietotu viedas enerģijas monitoringa sistēmas savām ēkām. Neatkarīgi no tā, vai pārvaldāt birojus, universitātes vai rūpnieciskos objektus, mēs varam palīdzēt jums samazināt patēriņu un optimizēt darbību.',
      ctaButton: 'Sarunājiet ar mūsu komandu',
    },

    officeSpacePage: {
      metaTitle: 'Biroja telpu pārvaldība | Noslogojuma analītika | 0g Baltics',
      metaDesc: 'Optimizējiet biroja telpu izmantošanu ar IoT noslogojuma sensoriem.',
      heroBadge: 'Biroja pārvaldība',
      heroTitle: 'Viedā biroja telpu pārvaldība',
      heroSubtitle: 'Optimizējiet darba vietu izmantošanu ar noslogojuma sensoriem, galdu rezervēšanas sistēmām un analītiku.',
      // Hero badges
      heroBadge1: 'Noslogojuma noteikšana',
      heroBadge2: 'Galdu uzraudzība',
      heroBadge3: 'Sanāksmju telpu analītika',
      heroBadge4: 'Hibrīda darbs',
      // What Is Section
      whatIsBadge: 'Izaicinājuma izpratne',
      whatIsTitle: 'Kas ir biroja telpu pārvaldība?',
      whatIsDesc: 'Biroja telpu pārvaldība ir prakse, lai saprastu, uzraudzītu un optimizētu, kā tiek izmantotas fiziskās darba vietas.',
      driversTitle: 'Galvenie viedās biroja pārvaldības virzītājspēki:',
      driver1: 'Hibrīda darba modeļi',
      driver2: 'Elastīgi biroja izkārtojumi',
      driver3: 'Koplietošanas darba vietu politikas',
      driver4: 'Sanāksmju telpu optimizācija',
      visibilityTitle: 'IoT sensori nodrošina redzamību:',
      visibility1: 'Reāllaika noslogojums',
      visibility1Desc: 'Kuras telpas pašlaik tiek izmantotas',
      visibility2: 'Lietojuma modeļi',
      visibility2Desc: 'Maksimuma stundas, klusu periodi, ikdienas tendences',
      visibility3: 'Telpu efektivitāte',
      visibility3Desc: 'Izmantošanas rādītāji pa zonām',
      visibility4: 'Vides apstākļi',
      visibility4Desc: 'Temperatūra, gaisa kvalitāte, komforts',
      // Hidden Costs Section
      costBadge: 'Biznesa problēma',
      costTitle: 'Tukšas biroja telpas slēptās izmaksas',
      costDesc: 'Pētījumi pastāvīgi rāda, ka daudziem uzņēmumiem jebkurā brīdī ir 30-60% neizmantota biroja jauda.',
      costsTitle: 'Darbības izmaksas par tukšu telpu:',
      cost1: 'Īres un nomas izmaksas',
      cost2: 'Apkures izdevumi',
      cost3: 'Dzesēšana un elektrība',
      cost4: 'Apgaismojuma izmaksas',
      stat1: 'tipiskā neizmantotā biroja jauda',
      stat2: 'izmaksas par m² gadā (ES vidēji)',
      stat3: 'potenciālie ietaupījumi ar optimizāciju',
      stat4: 'tipiskā ROI viedajām biroja sistēmām',
      // Smart Sensors Section
      sensorsBadge: 'Sensoru tehnoloģija',
      sensorsTitle: 'Viedie biroja sensori',
      sensorsDesc: 'Dažādi IoT sensoru veidi kalpo dažādiem mērķiem biroja telpu pārvaldībā. Visi sensori vāc anonīmus lietošanas datus—bez personas identifikācijas vai novērošanas—sniedzot apkopotas atziņas, kas respektē darbinieku privātumu.',
      occupancySensors: 'Noslogojuma sensori',
      occupancySensorsDesc: 'Pie griestiem montēti sensori nosaka klātbūtni un skaita cilvēkus tādās zonās kā atvērti stāvi, koplietošanas zonas un lielas sanāksmju telpas. Nodrošina zonas līmeņa noslogojuma datus.',
      deskSensors: 'Galdu sensori',
      deskSensorsDesc: 'Mazi sensori, kas montēti zem galdiem, nosaka, vai atsevišķas darba vietas ir aizņemtas. Iespējo precīzu galda-pa-galdam izmantošanas izsekošanu koplietošanas vidēs.',
      meetingRoomSensors: 'Sanāksmju telpu sensori',
      meetingRoomSensorsDesc: 'Specializēti sensori sanāksmju telpām izseko faktisko lietojumu pret kalendāra rezervācijām, nosaka neieradušos un identificē tukšās sapulces, kas izšķērdē vērtīgu telpu.',
      bestFor: 'Vislabāk piemērots:',
      openPlanOffices: 'Atvērtā plānojuma biroji',
      commonAreas: 'Koplietošanas zonas',
      cafeterias: 'Kafejnīcas',
      largeMeetingRooms: 'Lielas sanāksmju telpas',
      hotDeskingAreas: 'Koplietošanas darba vietu zonas',
      assignedDesks: 'Piešķirtie galdi',
      focusBooths: 'Fokusa kabīnes',
      phoneRooms: 'Telefona kabīnes',
      conferenceRooms: 'Konferenču telpas',
      huddleSpaces: 'Nelielas sapulču telpas',
      videoCallRooms: 'Video zvanu telpas',
      executiveSuites: 'Vadības kabineti',
      // IAQ Section
      iaqBadge: 'Vides intelekts',
      iaqTitle: 'Iekštelpu gaisa kvalitātes uzraudzība',
      iaqDesc: 'Viediem birojiem jāuzrauga vairāk nekā tikai noslogojums. Vides apstākļi tieši ietekmē darbinieku komfortu, produktivitāti un labsajūtu. Slikta gaisa kvalitāte, nekomfortablas temperatūras vai augsts mitrums rada neoptimālus darba apstākļus.',
      iaqDesc2: 'Vides sensoru integrēšana ar noslogojuma datiem sniedz pilnu priekšstatu par darba vietas apstākļiem, ļaujot ēku vadītājiem optimizēt gan telpu izmantošanu, gan komfortu.',
      co2Levels: 'CO2 līmeņi',
      co2LevelsDesc: 'Norāda ventilācijas pietiekamību un gaisa svaigumu',
      temperature: 'Temperatūra',
      temperatureDesc: 'Termālā komforta uzraudzība pa zonām',
      humidity: 'Mitrums',
      humidityDesc: 'Ietekmē komfortu un ēkas veselību',
      ventilation: 'Ventilācija',
      ventilationDesc: 'Gaisa kvalitātes un cirkulācijas novērtējums',
      impactTitle: 'Ietekme uz darba vietas veiktspēju',
      impactStat1: 'produktivitātes pieaugums ar uzlabotu gaisa kvalitāti',
      impactStat2: 'slimības dienu samazinājums ar labāku ventilāciju',
      impactStat3: 'kognitīvo funkciju uzlabojums ar zemāku CO2 līmeni',
      learnMoreIAQ: 'Uzziniet vairāk par IAQ uzraudzību',
      // Analytics Platform Section
      analyticsBadge: 'Uz datiem balstītas atziņas',
      analyticsTitle: 'Biroja analītikas platforma',
      analyticsDesc: 'Visi sensori ir savienoti ar mākoņbāzētu analītikas platformu, kas pārveido neapstrādātus datus praktiskās atziņās. Ēku vadītāji iegūst pilnīgu redzamību par to, kā viņu biroja telpas faktiski tiek izmantotas, ļaujot pieņemt uz pierādījumiem balstītus lēmumus.',
      platformCapabilities: 'Platformas iespējas:',
      realTimeDashboards: 'Reāllaika paneļi',
      realTimeDashboardsDesc: 'Tiešsaistes skats uz pašreizējo noslogojumu visās zonās',
      occupancyHeatmaps: 'Noslogojuma kartes',
      occupancyHeatmapsDesc: 'Vizuāla augsta un zema lietojuma zonu attēlošana',
      meetingRoomAnalytics: 'Sanāksmju telpu analītika',
      meetingRoomAnalyticsDesc: 'Rezervācija pret faktisko lietojumu, neieradušos rādītāji',
      historicalTrends: 'Vēsturiskās tendences',
      historicalTrendsDesc: 'Dienas, nedēļas, mēneša lietojuma modeļi',
      spaceEfficiencyMetrics: 'Telpu efektivitātes rādītāji',
      spaceEfficiencyMetricsDesc: 'Izmantošanas rādītāji un optimizācijas iespējas',
      // Hybrid Workplace Section
      hybridBadge: 'Moderni darba modeļi',
      hybridTitle: 'Hibrīda darba vietas pārvaldība',
      hybridDesc: 'Biroja analītika kļūst vēl kritiskāka hibrīda darba vidēs, kur darbinieki sadala laiku starp biroju un attālināto darbu. Faktisko lietošanas modeļu izpratne palīdz organizācijām pareizi izvēlēties nekustamo īpašumu un projektēt telpas, kas atbalsta elastīgu darbu.',
      deskBookingSystems: 'Galdu rezervēšanas sistēmas',
      deskBookingSystemsDesc: 'Ļauj darbiniekiem iepriekš rezervēt galdus, ar reāllaika pieejamību, kas rāda, kuras vietas ir brīvas. Noslogojuma sensori validē rezervācijas un automātiski atbrīvo neieradušos.',
      meetingRoomAvailability: 'Sanāksmju telpu pieejamība',
      meetingRoomAvailabilityDesc: 'Rāda reāllaika pieejamību, pamatojoties uz faktisko noslogojumu, nevis tikai kalendāra rezervācijām. Identificē un atbrīvo tukšās sapulces, padarot vairāk telpu pieejamas produktīvai lietošanai.',
      spacePlanning: 'Telpu plānošana',
      spacePlanningDesc: 'Izmantojiet vēsturiskos datus, lai plānotu biroja izkārtojumus, kas atbilst faktiskajam pieprasījumam. Nosakiet optimālas galdu un darbinieku attiecības, sadarbības telpas un klusas zonas.',
      // Benefits Section
      benefitsBadge: 'Biznesa ietekme',
      benefitsTitle: 'Ieguvumi uzņēmumiem',
      benefitsDesc: 'Viedā biroja telpu pārvaldība nodrošina izmērāmus ieguvumus vairākās dimensijās—no tiešiem izmaksu ietaupījumiem līdz uzlabotai darbinieku pieredzei un ilgtspējības rezultātiem.',
      reducedCosts: 'Samazinātas nekustamā īpašuma izmaksas',
      reducedCostsDesc: 'Identificējiet nepietiekami izmantotās telpas un pareizi izvēlieties savu biroja platību. Daudzas organizācijas samazina īrētās telpas par 15-30% pēc analītikas ieviešanas.',
      betterExperience: 'Labāka darbinieku pieredze',
      betterExperienceDesc: 'Palīdziet darbiniekiem viegli atrast pieejamus galdus un sanāksmju telpas. Samaziniet berzi un vilšanos darba vietā.',
      higherProductivity: 'Augstāka produktivitāte',
      higherProductivityDesc: 'Optimizējiet biroja izkārtojumus, pamatojoties uz to, kā cilvēki faktiski strādā. Izveidojiet pareizo sadarbības un fokusa telpu sajaukumu.',
      dataDrivenPlanning: 'Uz datiem balstīta plānošana',
      dataDrivenPlanningDesc: 'Aizstājiet pieņēmumus ar faktiem. Pieņemiet nekustamā īpašuma un darba vietas lēmumus, pamatojoties uz faktiskajiem lietošanas datiem.',
      sustainabilityImprovements: 'Ilgtspējības uzlabojumi',
      sustainabilityImprovementsDesc: 'Samaziniet enerģijas izšķērdēšanu, kondicionējot tikai aizņemtās telpas. Atbalstiet ESG mērķus ar izmērāmiem efektivitātes uzlabojumiem.',
      operationalVisibility: 'Operacionālā redzamība',
      operationalVisibilityDesc: 'Saprotiet savu darba vietu kā nekad agrāk. Ēku vadītāji iegūst rīkus nepārtrauktai optimizācijai un uzlabošanai.',
      // CTA
      ctaTitle: 'Optimizējiet savu darba vietu',
      ctaDesc: 'Sazinieties ar 0g Baltics, lai projektētu viedos biroja risinājumus, kas uzlabo efektivitāti un samazina izmaksas. Neatkarīgi no tā, vai pārvaldāt korporatīvo galveno mītni, universitātes pilsētiņas vai komerciālās ēkas, mēs varam palīdzēt.',
      ctaButton: 'Runājiet ar mūsu komandu',
      // Use Cases Section
      useCasesBadge: 'Lietojumi',
      useCasesTitle: 'Lietošanas gadījumi',
      useCasesDesc: 'Viedo biroju risinājumi apkalpo organizācijas dažādās nozarēs un izmēros, sākot no korporatīvajām galvenajām mītnēm līdz izglītības iestādēm.',
      corporateHQ: 'Korporatīvā galvenā mītne',
      corporateHQDesc: 'Lielie uzņēmumi izmanto biroja analītiku, lai optimizētu daudzstāvu galvenās mītnes, pārvaldītu koplietošanas darba vietu programmas un atbalstītu hibrīda darba politikas tūkstošiem darbinieku.',
      techCompanies: 'Tehnoloģiju uzņēmumi',
      techCompaniesDesc: 'Tehnoloģiju uzņēmumi ar elastīgu darba kultūru paļaujas uz galdu rezervēšanu un noslogojuma datiem, lai izveidotu dinamiskas darba vietas, kas pielāgojas mainīgajām komandas vajadzībām.',
      governmentBuildings: 'Valdības ēkas',
      governmentBuildingsDesc: 'Publiskā sektora organizācijas izmanto analītiku, lai optimizētu nodokļu maksātāju finansētās iekārtas un demonstrētu efektīvu publisko resursu izmantošanu.',
      universities: 'Universitātes',
      universitiesDesc: 'Izglītības iestādes uzrauga lekciju zāles, bibliotēkas un mācību telpas, lai optimizētu grafikus un saprastu studentu lietošanas modeļus.',
      coworkingSpaces: 'Koplietošanas telpas',
      coworkingSpacesDesc: 'Elastīgu darba telpu operatori izmanto reāllaika noslogojumu, lai pārvaldītu galdu pieejamību, optimizētu izkārtojumus un sniegtu precīzus jaudas datus dalībniekiem.',
      commercialRealEstate: 'Komerciālais nekustamais īpašums',
      commercialRealEstateDesc: 'Īpašumu pārvaldnieki piedāvā ēku analītiku kā īrnieku pakalpojumu, sniedzot noslogojuma ieskatus, kas palīdz uzņēmumiem optimizēt viņu īrētās telpas.',
      // IoT Connectivity Section
      iotConnectivityBadge: 'Tīkla infrastruktūra',
      iotConnectivityTitle: 'IoT savienojamība viedajām ēkām',
      iotConnectivityDesc: '0g Baltics izmanto zema enerģijas patēriņa IoT tīklus, kas paredzēti ēku vidēm. Šie tīkli ļauj izvietot bezvadu sensorus bez sarežģītas vadojuma, ar baterijas kalpošanas laiku, ko mēra gados, nevis mēnešos.',
      connectivityOptions: 'Atbalstītās savienojamības iespējas:',
      lorawanName: 'LoRaWAN',
      lorawanDesc: 'Liels diapazons, zems enerģijas patēriņš, ideāls lielām ēkām',
      nbiotName: 'NB-IoT',
      nbiotDesc: 'Balstīts uz mobilo sakaru, dziļa iekštelpu iekļūšana',
      ltemName: 'LTE-M',
      ltemDesc: 'Mobilie aktīvi un augstākas joslas platības vajadzības',
      wifiName: 'Wi-Fi',
      wifiDesc: 'Integrācija ar esošo IT infrastruktūru',
      keyAdvantages: 'Galvenās priekšrocības:',
      longBatteryLife: 'Ilgs baterijas kalpošanas laiks',
      longBatteryLifeDesc: '5-10 gadi tipiskā sensora darbība',
      lowMaintenance: 'Zema apkope',
      lowMaintenanceDesc: 'Uzstādīt un aizmirst instalācija',
      secureCommunication: 'Droša komunikācija',
      secureCommunicationDesc: 'Šifrēta datu pārraide',
      scalableDeployment: 'Mērogojama izvietošana',
      scalableDeploymentDesc: 'No pilotprojekta līdz visas ēkas izvietojumam',
      learnMoreConnectivity: 'Uzziniet vairāk par IoT savienojamību',
      // Why 0g Baltics Section
      whyBadge: 'Jūsu partneris',
      whyTitle: 'Kāpēc 0g Baltics',
      whyDesc: '0g Baltics nodrošina pilnus viedā biroja risinājumus—no IoT sensoriem līdz mākoņa analītikas platformām. Mēs apvienojam aparatūras ekspertīzi, savienojamības infrastruktūru un analītikas iespējas integrētos risinājumos darba vietu optimizācijai.',
      service1: 'IoT sensori',
      service1Desc: 'Noslogojuma, galdu, sanāksmju telpu un vides sensori',
      service2: 'Savienojamības infrastruktūra',
      service2Desc: 'LoRaWAN, mobilo sakaru un hibrīda tīkla iespējas',
      service3: 'Mākoņa platformas',
      service3Desc: 'Analītikas paneļi un atskaišu rīki',
      service4: 'Sistēmu integrācija',
      service4Desc: 'Savienojums ar esošajām ēku un IT sistēmām',
      service5: 'Viedo ēku pieredze',
      service5Desc: 'Ekspertīze no viedās pilsētas un ēku projektiem',
      relatedSolutions: 'Saistītie risinājumi',
      relatedSmartCity: 'Viedās pilsētas infrastruktūra',
      relatedSmartCityDesc: 'Pilsētu un ēku IoT tīkli',
      relatedSmartLockers: 'Viedie skapīši',
      relatedSmartLockersDesc: 'Droši glabāšanas risinājumi',
      relatedAssetTracking: 'Aktīvu izsekošana',
      relatedAssetTrackingDesc: 'Aprīkojuma un resursu uzraudzība',
      relatedConnectivity: 'IoT savienojamība',
      relatedConnectivityDesc: 'LoRaWAN un mobilo sakaru tīkli',
    },

    assetTrackingPage: {
      metaTitle: 'Aktīvu izsekošanas risinājumi | GPS un IoT | 0g Baltics',
      metaDesc: 'Izsekojiet aktīvus iekštelpās un ārtelpās ar LoRaWAN, GPS, BLE un mobilo savienojamību.',
      heroBadge: 'Aktīvu izsekošana',
      heroTitle: 'Aktīvu izsekošanas risinājumi',
      heroSubtitle: 'Izsekojiet aprīkojumu, transportlīdzekļus un inventāru ar iekštelpu un ārtelpu pozicionēšanas risinājumiem.',
      indoorTracking: 'Iekštelpu izsekošana',
      indoorTrackingDesc: 'BLE un LoRaWAN balstīta pozicionēšana noliktavām un objektiem.',
      outdoorTracking: 'Ārtelpu izsekošana',
      outdoorTrackingDesc: 'GPS un mobilā izsekošana transportlīdzekļiem un mobilajiem aktīviem.',
      hybridTracking: 'Hibrīda izsekošana',
      hybridTrackingDesc: 'Netraucēta iekštelpu-ārtelpu izsekošana ar automātisku pārslēgšanos.',
      industriesBadge: 'Nozares',
      logistics: 'Loģistika',
      logisticsDesc: 'Izsekojiet sūtījumus, konteinerus un autoparku.',
      healthcare: 'Veselības aprūpe',
      healthcareDesc: 'Medicīniskā aprīkojuma izsekošana slimnīcās un klīnikās.',
      manufacturing: 'Ražošana',
      manufacturingDesc: 'Instrumentu un nepabeigto darbu izsekošana rūpnīcā.',
      ctaTitle: 'Sāciet izsekot savus aktīvus',
      ctaButton: 'Pieprasīt demonstrāciju',
      // What Is Section
      whatIsBadge: 'Tehnoloģijas izpratne',
      whatIsTitle: 'Kas ir IoT aktīvu izsekošana?',
      whatIsDesc: 'IoT aktīvu izsekošana ļauj uzņēmumiem uzraudzīt fizisko aktīvu atrašanās vietu, statusu un izmantošanu reāllaikā.',
      assetsTitle: 'Izsekojamie aktīvi ietver:',
      asset1: 'Celtniecības aprīkojums',
      asset2: 'Loģistikas konteineri',
      asset3: 'Transportlīdzekļi un piekabes',
      asset4: 'Rūpnieciskie instrumenti',
      asset5: 'Medicīniskais aprīkojums',
      asset6: 'Temperatūras jutīgas kravas',
      // Why Section
      whyBadge: 'Biznesa ietekme',
      whyTitle: 'Kāpēc uzņēmumi ievieš aktīvu izsekošanu',
      whyDesc: 'Organizācijas ievieš aktīvu izsekošanu, lai atrisinātu kritiskas darbības problēmas.',
      benefit1: 'Aktīvu zādzību novēršana',
      benefit1Desc: 'Samaziniet zādzības par 60%+',
      benefit2: 'Izmantošanas optimizācija',
      benefit2Desc: 'Palieliniet aktīvu izmantošanu par 25%+',
      benefit3: 'Apkopes plānošana',
      benefit3Desc: 'Novērsiet dārgus bojājumus',
      benefit4: 'Atrašanās vietas redzamība',
      benefit4Desc: 'Ziniet, kur atrodas aktīvi 24/7',
      // Multi-Connectivity Section
      networkBadge: 'Tīkla arhitektūra',
      networkTitle: 'Kāpēc daudzējāda savienojamība ir būtiska',
      networkDesc: 'Lielākā daļa IoT izvietojumu cieš neveiksmi, ja tie paļaujas uz vienu savienojamības tehnoloģiju. Aktīvi nepaliek vienā vietā—tie pārvietojas starp rūpnieciskajiem kampusiem, pilsētvidi, loģistikas maršrutiem un attālām vietām, kur tīkla pārklājums dramatiski mainās.',
      networkDesc2: '0g Baltics projektē daudzējādas savienojamības arhitektūras, kas apvieno vairākas tehnoloģijas. Ierīces automātiski izvēlas labāko pieejamo tīklu atkarībā no vides, nodrošinot nepārtrauktu aktīvu redzamību neatkarīgi no atrašanās vietas.',
      lorawanName: 'LoRaWAN',
      lorawanDesc: 'Liels attālums, zems enerģijas patēriņš kampusa un reģionālajam pārklājumam',
      nbiotName: 'NB-IoT / LTE-M',
      nbiotDesc: 'Mobilā savienojamība pilsētas un mobilajiem aktīviem',
      bleName: 'BLE bākas',
      bleDesc: 'Iekštelpu pozicionēšana un tuvuma izsekošana',
      satelliteName: 'Satelīts',
      satelliteDesc: 'Globālais pārklājums attālām un ārpus tīkla vietām',
      // Tracking Devices Section
      devicesBadge: 'Aparatūras risinājumi',
      devicesTitle: 'Profesionālas aktīvu izsekošanas ierīces',
      devicesDesc: '0g Baltics integrē profesionālas klases izsekošanas ierīces no vadošajiem ražotājiem, piemēram, Digital Matter. Šīs ierīces nodrošina GPS izsekošanu, satelīta savienojamību, mobilo savienojamību, BLE vārtejas iespējas un vairāku gadu akumulatora darbību—paredzētas rūpnieciskām vidēm un globāliem izvietojumiem.',
      gpsTracking: 'GPS izsekošana',
      gpsTrackingDesc: 'Augstas precizitātes ārtelpu pozicionēšana ar GNSS',
      satelliteConnectivity: 'Satelīta savienojamība',
      satelliteConnectivityDesc: 'Iridium satelīts attālām vietām',
      cellularNetworks: 'Mobilie tīkli',
      cellularNetworksDesc: '4G LTE, LTE-M, NB-IoT savienojamība',
      multiYearBattery: 'Vairāku gadu akumulators',
      multiYearBatteryDesc: 'Līdz 10+ gadiem ar viedo enerģijas pārvaldību',
      compactTracker: 'Kompakts aktīvu izsekotājs',
      compactTrackerDesc: 'Izturīgs ar baterijām darbināms GPS izsekotājs iekštelpu/ārtelpu aktīvu izsekošanai ar magnētisko aktivizāciju.',
      longLifeTracker: 'Ilgas dzīves izsekotājs',
      longLifeTrackerDesc: 'GPS izsekotājs ar garāko akumulatora darbības laiku tirgū—līdz 10+ gadiem uzstādi-un-aizmirsti izvietojumiem.',
      globalSatelliteTracker: 'Globālais satelīta izsekotājs',
      globalSatelliteTrackerDesc: 'Izturīgs vadu izsekotājs ar Iridium satelītu un 4G/2G savienojamību kritiskiem globāliem izvietojumiem.',
      // Campus Tracking Section
      campusBadge: 'Objekta redzamība',
      campusTitle: 'Kampusa aktīvu izsekošana',
      campusDesc: 'Lielas rūpnieciskās vietas darbojas kā mazas pilsētas. Rūpnīcas, lidostas, industriālie parki, universitātes un enerģētikas objekti satur tūkstošiem aktīvu, kas katru dienu pārvietojas. Bez izsekošanas organizācijas zaudē redzamību brīdī, kad aprīkojums atstāj centrālo noliktavu.',
      campusDesc2: 'Izmantojot LoRaWAN vārtejas un BLE bākas, organizācijas var izsekot aprīkojumu visā kampusā ar metru līmeņa precizitāti. Iekštelpu/ārtelpu pozicionēšana novērš aklās zonas, kamēr uz zonām balstīti brīdinājumi nodrošina, ka aktīvi paliek tur, kur tiem jābūt.',
      campusDeployments: 'Tipiskie kampusa izvietojumi',
      campusSite1: 'Ražošanas uzņēmumi',
      campusSite2: 'Lidostu objekti',
      campusSite3: 'Slimnīcu kampusi',
      campusSite4: 'Universitāšu teritorijas',
      campusSite5: 'Enerģētikas iekārtas',
      campusSite6: 'Loģistikas centri',
      multiSiteBadge: 'Vairāku objektu operācijas',
      multiSiteTitle: 'Kampusa-uz-kampusu redzamība',
      multiSiteDesc: 'Aktīvi bieži pārvietojas starp vairākiem objektiem. Celtniecības tehnika rotē starp projektiem. Mobilie ģeneratori ceļo uz pagaidu instalācijām. Apkopes aprīkojums apkalpo vairākus objektus. Bez izsekošanas starpobjektu pārvietošanās rada redzamības nepilnības.',
      multiSiteDesc2: 'IoT izsekošana ļauj organizācijām uzraudzīt aktīvu pārvietošanos starp objektiem, saprast tranzīta laikus un uzlabot izmantošanu visā organizācijā—ne tikai atsevišķos objektos.',
      realImpact: 'Reālā ietekme',
      realImpactDesc: 'Uzņēmumi parasti redz 25% uzlabojumu aktīvu izmantošanā, kad iegūst redzamību vairākos objektos, samazinot nepieciešamību pēc dublējoša aprīkojuma iegādēm.',
      // Global Tracking Section
      globalBadge: 'Visaptverošs pārklājums',
      globalTitle: 'Globālā aktīvu izsekošana',
      globalDesc: 'Loģistikas vidēs aktīvi ceļo lielus attālumus—starp valstīm, kontinentiem un okeāniem. Pārvadājumu konteineri pārvietojas no rūpnīcām Āzijā uz izplatīšanas centriem Eiropā. Augstas vērtības aprīkojums ceļo uz attālām projektu vietām. Temperatūras jutīgām zālēm nepieciešama nepārtraukta uzraudzība globālā tranzīta laikā.',
      globalDesc2: 'Globālās izsekošanas sistēmas apvieno mobilos tīklus (pieejami 190+ valstīs), satelīta savienojamību (okeānu šķērsošanai un attālām vietām) un mobilās vārtejas (reāllaika tranzīta uzraudzībai). Tas nodrošina uzticamu izsekošanu praktiski jebkurā vietā uz Zemes.',
      globalStat1: '190+',
      globalStat1Label: 'Valstis ar mobilo pārklājumu',
      globalStat2: '100%',
      globalStat2Label: 'Globālais satelīta pārklājums',
      globalStat3: '130',
      globalStat3Label: 'Valstis ar aktīviem izvietojumiem',
      globalStat4: '2.5M+',
      globalStat4Label: 'Ierīces izvietotas visā pasaulē',
      // Cold Chain Section
      coldChainBadge: 'Temperatūras jutīga loģistika',
      coldChainTitle: 'Aukstās ķēdes uzraudzība ar IoT',
      coldChainDesc: 'Temperatūras jutīgai loģistikai nepieciešama nepārtraukta uzraudzība visā piegādes ķēdē. Farmaceitiskie preparāti, vakcīnas, svaiga pārtika un bioloģiskie paraugi var tikt sabojāti pat īslaicīgu temperatūras noviržu dēļ. Tradicionālā uzraudzība ar manuālām pārbaudēm un papīra žurnāliem ir nepietiekama mūsdienu atbilstības prasībām.',
      coldChainDesc2: 'IoT sensori un BLE bākas nepārtraukti uzrauga temperatūru, mitrumu un kustību. Mobilās vārtejas transportlīdzekļos savāc sensoru datus un pārraida tos uz mākoņa platformām reāllaikā. Ja sliekšņi tiek pārsniegti, brīdinājumi tiek ģenerēti nekavējoties—ļaujot iejaukties pirms produkta zuduma.',
      tempLogging: 'Nepārtraukta temperatūras reģistrēšana',
      tempLoggingDesc: 'Sensoru nolasījumi ik 15 minūtes ar mākoņa glabātuvi',
      thresholdAlerts: 'Reāllaika sliekšņa brīdinājumi',
      thresholdAlertsDesc: 'Tūlītēji paziņojumi, kad robežas tiek pārsniegtas',
      chainCustody: 'Pilnīga aizbildnības ķēde',
      chainCustodyDesc: 'Revīzijas taka no izcelsmes līdz galamērķim',
      regulatoryCompliance: 'Regulatīvā atbilstība',
      regulatoryComplianceDesc: 'GDP, HACCP un farmācijas standarti atbalstīti',
      // Asset Recovery Section
      securityBadge: 'Drošība un aizsardzība',
      securityTitle: 'Aktīvu atgūšana un zādzību noteikšana',
      securityDesc: 'Augstas vērtības aktīvi pārstāv ievērojamus kapitāla ieguldījumus. Celtniecības aprīkojums, specializēti instrumenti un transportlīdzekļi ir galvenie zādzību mērķi. Kad notiek zādzība, ātra atgūšana ir kritiska—gan lai samazinātu finansiālos zaudējumus, gan lai uzturētu projektu grafikus.',
      securityDesc2: 'GPS izsekošanas ierīces ar slēptām uzstādīšanas iespējām ļauj nekavējoties noteikt nozagto aktīvu atrašanās vietu. Ģeorobežošana izveido virtuālās robežas—kad aktīvs atstāj atļauto zonu, brīdinājumi tiek aktivizēti nekavējoties. Pretaizdzīšanas režīms aktivizē augstfrekvences GPS atjauninājumus reāllaika izsekošanai atgūšanas operāciju laikā.',
      theftPrevention: 'Zādzību novēršanas funkcijas',
      theftFeature1: 'Ģeorobežošana ar tūlītējiem pārkāpumu brīdinājumiem',
      theftFeature2: 'Pēcstundu kustības noteikšana',
      theftFeature3: 'Slēpta ierīces uzstādīšana',
      theftFeature4: 'RF bākas pēdējās jūdzes atgūšanai',
      theftFeature5: 'Tiesībaizsardzības koordinācijas atbalsts',
      analyticsBadge: 'Operacionālā inteliģence',
      analyticsTitle: 'MI un analītika',
      analyticsDesc: 'Aktīvu izsekošana ģenerē milzīgus daudzumus operacionālo datu. Bez analītikas šie dati paliek neizmantoti. Progresīvas platformas pārveido neapstrādātus atrašanās vietas un sensoru datus par izmantojamu biznesa inteliģenci.',
      utilizationStats: 'Izmantošanas statistika',
      utilizationStatsDesc: 'Saprotiet aktīvu lietošanas modeļus',
      predictiveMaintenance: 'Prognozējošā apkope',
      predictiveMaintenanceDesc: 'Paredziet servisa vajadzības',
      fleetOptimization: 'Autoparka optimizācija',
      fleetOptimizationDesc: 'Uzlabojiet operacionālo efektivitāti',
      customReporting: 'Pielāgoti pārskati',
      customReportingDesc: 'Biznesa inteliģences eksporti',
      // Final CTA Section
      finalCtaTitle: 'Izvietojiet aktīvu izsekošanu ar 0g Baltics',
      finalCtaDesc: '0g Baltics projektē un izvieto progresīvus IoT aktīvu izsekošanas risinājumus visā Baltijas reģionā. No viena objekta pilotprojektiem līdz uzņēmuma mēroga izvietojumiem, mēs nodrošinām aparatūru, savienojamību un ekspertīzi, lai pārveidotu jūsu aktīvu pārvaldību.',
      finalCtaButton: 'Runāt ar ekspertu',
    },

    toolTrackingPage: {
      metaTitle: 'Instrumentu izsekošana | Rūpniecisko instrumentu pārvaldība | 0g Baltics',
      metaDesc: 'Izsekojiet rūpnieciskos instrumentus ar RFID, viedajiem skapīšiem un IoT sensoriem.',
      heroBadge: 'Instrumentu izsekošana',
      heroTitle: 'Rūpniecisko instrumentu izsekošanas risinājumi',
      heroSubtitle: 'Samaziniet instrumentu zudumus un uzlabojiet izmantošanu ar viedo izsekošanu un automatizētām izplatīšanas sistēmām.',
      rfidTracking: 'RFID izsekošana',
      rfidTrackingDesc: 'Marķējiet un izsekojiet katru instrumentu ar RFID tehnoloģiju.',
      smartLockers: 'Viedie skapīši',
      smartLockersDesc: 'Automatizēta instrumentu izplatīšana ar piekļuves kontroli.',
      inventory: 'Inventāra pārvaldība',
      inventoryDesc: 'Precīzi zināt, kādi instrumenti jums ir un kur tie atrodas.',
      analytics: 'Lietojuma analītika',
      analyticsDesc: 'Izprotiet instrumentu izmantošanu un optimizējiet savu inventāru.',
      ctaTitle: 'Pārtrauciet zaudēt instrumentus',
      ctaButton: 'Uzzināt vairāk',
      // BLE Tracking Section
      bleSectionBadge: 'BLE aktīvu izsekošana',
      bleSectionTitle: 'Instrumentu izsekošana: ziniet, kur atrodas katrs instruments',
      bleSectionDesc: 'Pievienojiet BLE sensorus saviem instrumentiem. Iegūstiet reāllaika redzamību par atrašanās vietu un statusu.',
      // ROI Stats
      stat1: 'Instrumentu zudumu samazinājums',
      stat2: 'Izmantošanas pieaugums',
      stat3: 'Mazāk laika meklēšanai',
      stat4: 'Zemākas nomaiņas izmaksas',
      // Why Track
      whyTrackTitle: 'Kāpēc izsekot instrumentus?',
      whyTrack1: 'Novērsiet zādzības un zudumus',
      whyTrack1Desc: 'BLE sensori brīdina, kad instrumenti pamet atļautās zonas.',
      whyTrack2: 'Atrodiet instrumentus uzreiz',
      whyTrack2Desc: 'Atveriet lietotni un sekundēs atrodiet jebkuru instrumentu kartē.',
      whyTrack3: 'Automatizējiet inventāru',
      whyTrack3Desc: 'BLE vārtejas automātiski nosaka instrumentus noliktavās.',
      whyTrack4: 'Nodrošiniet atbilstību',
      whyTrack4Desc: 'Izsekojiet pārbaudes datumus. Saņemiet brīdinājumus pirms derīguma termiņa.',
      // How It Works
      howItWorks: 'Kā tas darbojas',
      howStep1: 'Pievienojiet BLE sensoru (magnēts, līme vai paslēpts)',
      howStep2: 'Viedtālruņi un vārtejas automātiski nosaka instrumentus',
      howStep3: 'Izsekojiet atrašanās vietu, saņemiet brīdinājumus',
      // Buttons
      requestDemo: 'Pieprasīt demonstrāciju',
      viewSolutions: 'Skatīt risinājumus',
      scheduleDemo: 'Plānot demonstrāciju',
      // BLE Trackers Section
      bleTrackersTitle: 'Rūpnieciskas klases BLE izsekošanas sensori',
      coinBeaconName: 'Monētas bāka',
      coinBeaconDesc: 'Ļoti kompakts izsekotājs maziem instrumentiem. 3+ gadu akumulators.',
      proAssetTagName: 'Pro aktīvu birka',
      proAssetTagDesc: 'Profesionālas klases elektroinstrumentiem. BLE + LPWAN pozicionēšana.',
      industrialTrackerName: 'Rūpnieciskais izsekotājs',
      industrialTrackerDesc: 'Izturīgs ar GPS, BLE un LTE globālai augstas vērtības aprīkojuma izsekošanai.',
      learnConnectivity: 'Uzzināt vairāk par mūsu savienojamības tehnoloģijām',
      // Distribution Section
      officialPartner: 'Oficiālais partneris',
      distributionTitle: 'Instrumentu un aprīkojuma izplatīšana',
      distributionDesc: 'Automatizētas skapīšu sistēmas drošai instrumentu, IAL un patēriņa materiālu izplatīšanai. Žetonu identifikācija, pilnīga izsekojamība un reāllaika inventāra pārvaldība.',
      secureAccess: 'Droša piekļuve',
      secureAccessDesc: 'Žetonu autentifikācija katrai transakcijai',
      fullTraceability: 'Pilnīga izsekojamība',
      fullTraceabilityDesc: 'Ziniet, kas ko paņēma, kad un kāpēc',
      availability247: 'Pieejamība 24/7',
      availability247Desc: 'Pašapkalpošanās piekļuve jebkurā laikā',
      realtimeDashboard: 'Reāllaika panelis',
      realtimeDashboardDesc: 'Uzraugiet inventāru un lietošanas modeļus',
      // Products
      xtowerTitle: 'IAL izplatītājs X\'TOWER®',
      xtowerDesc: 'Nodrošiniet un optimizējiet savus patēriņa materiālus ar automatizētu karuseļa izplatīšanu. Samaziniet uzglabāšanas telpu par 90%.',
      xstoreTitle: 'Noliktavas un telpu pārvaldība: X\'STORE®',
      xstoreDesc: 'Nodrošiniet un izsekojiet savus materiālus īpašā noliktavas telpā. Pilnīga redzamība par inventāra līmeņiem.',
      xlockerTitle: 'Rūpniecisko piederumu izplatītājs X\'LOCKER®',
      xlockerDesc: 'Nodrošiniet un optimizējiet savus jutīgos materiālus un instrumentus.',
      xslingTitle: 'Sienas montējamais drošības jostu un siksnu statīvs X\'SLING®',
      xslingDesc: 'Nodrošiniet jutīgā aprīkojuma drošību un darbības statusu.',
      xweightTitle: 'Mazo patēriņa materiālu svēršanas izplatītājs: X\'WEIGHT®',
      xweightDesc: 'Nodrošiniet un optimizējiet skrūvju, bultskrūvju un mazo patēriņa materiālu izplatīšanu.',
      comingSoon: 'Drīzumā',
      // Why It Matters Section
      whyMattersTitle: 'Kāpēc instrumentu izsekošana un viedā izplatīšana ir svarīga',
      whyMattersDesc: 'Rūpnieciskās vidēs nepārvaldīti instrumenti uzņēmumiem katru gadu rada tūkstošu eiro zaudējumus, izšķiestu laiku un neefektivitāti.',
      trackEveryTool: 'Izsekojiet katru instrumentu',
      trackEveryToolDesc1: 'Bez redzamības instrumenti pazūd. Darbinieki uzkrāj aprīkojumu "katram gadījumam". Zādzības paliek nepamanītas, līdz inventāra uzskaite atklāj milzīgas nepilnības.',
      trackEveryToolDesc2: 'BLE izsekošana sniedz reāllaika atrašanās vietas datus par katru aktīvu. Ziniet, kam kas ir, kur tas atrodas un kad tas pēdējo reizi izmantots. Samaziniet zudumus par 40% un novērsiet ikdienas pazudušo aprīkojumu meklēšanu.',
      simplifyDistribution: 'Vienkāršojiet izplatīšanu',
      simplifyDistributionDesc1: 'Manuālas instrumentu noliktavas rada sastrēgumus. Darbinieki stāv rindā. Inventāra ieraksti ir neprecīzi. IAL derīguma termiņš beidzas nepamanīti. Atbilstība kļūst par murgu.',
      simplifyDistributionDesc2: 'Viedie skapīši nodrošina 24/7 pašapkalpošanās piekļuvi ar žetonu autentifikāciju. Katra transakcija tiek automātiski reģistrēta. Inventārs atjauninās reāllaikā. Atbilstība ir iebūvēta.',
      readyTransform: 'Gatavi pārveidot savu instrumentu pārvaldību?',
      readyTransformDesc: 'Sazinieties ar mums personalizētai demonstrācijai un uzziniet, kā mūsu risinājumi var samazināt zudumus un uzlabot darbības efektivitāti.',
    },

    connectivityPage: {
      metaTitle: 'IoT savienojamība | LoRaWAN, Sigfox, NB-IoT | 0g Baltics',
      metaDesc: 'Izvietojiet IoT savienojamības infrastruktūru ar LoRaWAN, Sigfox, NB-IoT un mobilajām tehnoloģijām.',
      // Hero Section
      heroBadge: 'Savienojamības infrastruktūra',
      heroTitle: 'IoT savienojamības infrastruktūra',
      heroSubtitle: 'Pareizas savienojamības izvēle ir būtiska veiksmīgiem IoT izvietojumiem. 0g Baltics projektē daudzteinklu infrastruktūras, apvienojot LoRaWAN, Sigfox, NB-IoT, BLE un satelītu savienojamību.',
      heroDesc: 'Katram IoT projektam ir atšķirīgas prasības attiecībā uz pārklājumu, enerģijas patēriņu, datu biežumu un infrastruktūras izmaksām. Mūsu uzdevums ir izvēlēties un apvienot visefektīvākās savienojamības tehnoloģijas, lai nodrošinātu uzticamību, mērogojamību un ilgtermiņa ilgtspēju.',
      // Multi-Connectivity Section
      multiBadge: 'Mūsu filozofija',
      multiTitle: 'Kāpēc daudzteinklu IoT ir svarīgs',
      multiDesc: '0g Baltics nekad neprojektē mono-savienojamības risinājumus. Mēs saprotam, ka dažādām ierīcēm, lietojuma gadījumiem un vidēm nepieciešamas dažādas tīkla tehnoloģijas, lai sasniegtu optimālu veiktspēju.',
      multiPoint1: 'Dažādām ierīcēm nepieciešami dažādi tīkli',
      multiPoint2: 'Infrastruktūrai jāpaliek elastīgai',
      multiPoint3: 'Izmaksu optimizācija ir būtiska',
      multiPoint4: 'Redundance uzlabo uzticamību',
      multiPoint5: 'Ierīču ilgmūžība jāsaglabā',
      commitmentTitle: 'Mūsu apņemšanās',
      commitmentDesc: '0g Baltics risinājumi nekad nav balstīti uz vienas savienojamības tehnoloģijas. Mēs projektējam hibrīda infrastruktūras, kas apvieno vairākus tīklus, lai samazinātu darbības izmaksas, vienlaikus maksimizējot drošību un aprīkojuma ilgmūžību.',
      // LoRaWAN Section
      lorawanBadge: 'Liela diapazona IoT',
      lorawanTitle: 'LoRaWAN infrastruktūra',
      lorawanDesc: 'LoRaWAN (Long Range Wide Area Network) ir daudzu liela mēroga IoT izvietojumu pamats. Šī tehnoloģija nodrošina liela attāluma sakarus ar minimālu enerģijas patēriņu, padarot to ideālu viedās pilsētas un komunālo pakalpojumu lietojumiem.',
      lorawanAdvantages: 'Galvenās priekšrocības',
      lorawanAdv1: 'Liela attāluma savienojamība (līdz 15km)',
      lorawanAdv2: 'Ārkārtīgi zems enerģijas patēriņš',
      lorawanAdv3: 'Privāta tīkla infrastruktūra',
      lorawanAdv4: 'Ideāli viedām pilsētām un komunālajiem pakalpojumiem',
      lorawanAdv5: 'Mērogojami izvietojumi',
      lorawanNote: 'Mēs izvietojam operatora līmeņa LoRaWAN infrastruktūru, izmantojot Tektelic vārtejas maksimālai uzticamībai.',
      lorawanUseCases: 'Tipiski lietojuma gadījumi',
      lorawanUse1Title: 'Viedā skaitīšana',
      lorawanUse1Desc: 'Attālināta ūdens, gāzes un elektrības skaitītāju nolasīšana',
      lorawanUse2Title: 'Vides monitorings',
      lorawanUse2Desc: 'Gaisa kvalitātes, temperatūras, mitruma sensori',
      lorawanUse3Title: 'Aktīvu izsekošana',
      lorawanUse3Desc: 'Transportlīdzekļu un aprīkojuma atrašanās vietas izsekošana',
      lorawanUse4Title: 'Viedās ēkas',
      lorawanUse4Desc: 'Noslogojuma, enerģijas un objektu pārvaldība',
      lorawanUse5Title: 'Pilsētas infrastruktūra',
      lorawanUse5Desc: 'Ielu apgaismojums, autostāvvietas, atkritumu apsaimniekošana',
      // Sigfox Section
      sigfoxBadge: 'Globālais IoT tīkls',
      sigfoxTitle: 'Sigfox savienojamība',
      sigfoxDesc: 'Sigfox ir globāla LPWAN (Low Power Wide Area Network) tehnoloģija, kas paredzēta vienkāršām, lēti IoT ierīcēm, kas pārraida nelielus datu apjomus. Tās īpaši zemais enerģijas patēriņš nodrošina baterijas darbības laiku, ko mēra gados, nevis mēnešos.',
      sigfoxAdvantages: 'Galvenās priekšrocības',
      sigfoxAdv1: 'Īpaši zems enerģijas patēriņš',
      sigfoxAdv2: 'Baterijas darbības laiks līdz 10+ gadiem',
      sigfoxAdv3: 'Vienkārša ierīču integrācija',
      sigfoxAdv4: 'Globāla IoT tīkla pārklājums',
      sigfoxWhen: 'Kad izmantot Sigfox',
      sigfoxWhenDesc: 'Sigfox ir visefektīvākais risinājums, kad ierīcēm jāsūta nelieli datu apjomi reti, un baterijas darbības laiks ir galvenais ierobežojums. Globālais tīkla pārklājums novērš nepieciešamību pēc privātas infrastruktūras investīcijām.',
      sigfoxUseCases: 'Tipiski lietojuma gadījumi',
      sigfoxUse1Title: 'Aktīvu izsekošana',
      sigfoxUse1Desc: 'Ilgtermiņa izsekošana ar vairāku gadu baterijas darbības laiku',
      sigfoxUse2Title: 'Vides sensori',
      sigfoxUse2Desc: 'Temperatūras, mitruma, spiediena monitorings',
      sigfoxUse3Title: 'Trauksmes sistēmas',
      sigfoxUse3Desc: 'Ielaušanās noteikšana un drošības brīdinājumi',
      sigfoxUse4Title: 'Komunālo pakalpojumu monitorings',
      sigfoxUse4Desc: 'Noplūdes noteikšana un patēriņa brīdinājumi',
      // NB-IoT Section
      nbiotBadge: 'Mobilais IoT',
      nbiotTitle: 'NB-IoT mobilā savienojamība',
      nbiotDesc: 'NB-IoT (Narrowband Internet of Things) izmanto esošo mobilo sakaru infrastruktūru, lai nodrošinātu uzticamu, drošu savienojamību IoT ierīcēm. Šī tehnoloģija izceļas vidēs, kur būtisks ir dziļš iekštelpu pārklājums un operatora līmeņa uzticamība.',
      nbiotAdvantages: 'Galvenās priekšrocības',
      nbiotAdv1: 'Telekomunikāciju operatora infrastruktūra',
      nbiotAdv2: 'Dziļš iekštelpu pārklājums',
      nbiotAdv3: 'Augsta uzticamība un drošība',
      nbiotAdv4: 'Plaša apgabala pārklājums',
      nbiotNote: 'NB-IoT ir īpaši izdevīgs lieliem infrastruktūras projektiem, kur ierīces tiek izvietotas sarežģītās vidēs, piemēram, pazemes komunālajos tīklos, pagrabos vai rūpnieciskās iekārtās.',
      nbiotUseCases: 'Tipiski lietojuma gadījumi',
      nbiotUse1Title: 'Viedā skaitīšana',
      nbiotUse1Desc: 'Komunālie skaitītāji sarežģītās vietās',
      nbiotUse2Title: 'Rūpnieciskie sensori',
      nbiotUse2Desc: 'Rūpnīcu un iekārtu monitorings',
      nbiotUse3Title: 'Viedā autostāvvieta',
      nbiotUse3Desc: 'Pazemes autostāvvietu noslogojums',
      nbiotUse4Title: 'Viedā infrastruktūra',
      nbiotUse4Desc: 'Tiltu un tuneļu monitorings',
      // BLE Section
      bleBadge: 'Iekštelpu pozicionēšana',
      bleTitle: 'Bluetooth Low Energy',
      bleDesc: 'Bluetooth Low Energy (BLE) tehnoloģija nodrošina precīzu iekštelpu pozicionēšanu un aktīvu izsekošanu ar minimālu enerģijas patēriņu. Tās savietojamība ar viedtālruņiem un plašā pieņemšana padara to ideālu lietotājiem paredzētām lietojumprogrammām.',
      bleAdvantages: 'Galvenās priekšrocības',
      bleAdv1: 'Ļoti zems enerģijas patēriņš',
      bleAdv2: 'Iekštelpu pozicionēšanas iespējas',
      bleAdv3: 'Augsta precizitāte (zem metra)',
      bleAdv4: 'Viedtālruņu savietojamība',
      bleUseCases: 'Tipiski lietojuma gadījumi',
      bleUse1Title: 'Iekštelpu aktīvu izsekošana',
      bleUse1Desc: 'Aprīkojuma un inventāra reāllaika atrašanās vieta',
      bleUse2Title: 'Slimnīcas aprīkojuma izsekošana',
      bleUse2Desc: 'Medicīnas ierīču atrašanās vieta un pārvaldība',
      bleUse3Title: 'Viedā biroja noslogojums',
      bleUse3Desc: 'Darba vietas izmantošanas monitorings',
      bleUse4Title: 'Iekštelpu atrašanās vietas pakalpojumi',
      bleUse4Desc: 'Navigācijas un ceļa meklēšanas lietojumprogrammas',
      bleLearnMore: 'Uzziniet vairāk par mūsu slimnīcas aktīvu izsekošanas risinājumiem, ko nodrošina BLE tehnoloģija.',
      // Satellite Section
      satelliteBadge: 'Globālais pārklājums',
      satelliteTitle: 'Satelīta IoT savienojamība',
      satelliteDesc: 'Satelīta savienojamība papildina zemes tīklus, nodrošinot pārklājumu attālos apgabalos, kur nav pieejama mobilo sakaru vai LPWAN infrastruktūra. Šī tehnoloģija nodrošina nepārtrauktu aktīvu un infrastruktūras monitoringu neatkarīgi no atrašanās vietas.',
      satelliteAdv1Title: 'Globālais pārklājums',
      satelliteAdv1Desc: 'Savienojamība jebkur uz Zemes, ieskaitot okeānus un attālus reģionus',
      satelliteAdv2Title: 'Attālo apgabalu savienojamība',
      satelliteAdv2Desc: 'Uzraugiet aktīvus vietās bez zemes infrastruktūras',
      satelliteAdv3Title: 'Tīkla redundance',
      satelliteAdv3Desc: 'Rezerves savienojamība, kad primārie tīkli nedarbojas',
      satelliteUse1Title: 'Attālās infrastruktūras monitorings',
      satelliteUse1Desc: 'Cauruļvadi, elektrolīnijas un objekti izolētās vietās',
      satelliteUse2Title: 'Loģistikas izsekošana',
      satelliteUse2Desc: 'Konteineru un autoparka izsekošana pāri robežām',
      satelliteUse3Title: 'Rūpnieciskie aktīvi',
      satelliteUse3Desc: 'Smaga tehnika un iekārtas attālos objektos',
      satelliteNote: '0g Baltics integrē satelīta savienojamības risinājumus, kas savietojami ar dažādām satelītu infrastruktūrām, ieskaitot Starlink, kad projekta prasības to pieprasa.',
      // Strategy Section
      strategyBadge: 'Mūsu metodika',
      strategyTitle: 'Kā 0g Baltics izvēlas pareizo tīklu',
      strategyDesc: 'Katrs IoT izvietojums sākas ar rūpīgu prasību analīzi. Mūsu sistemātiskā pieeja nodrošina, ka izvēlētās savienojamības tehnoloģijas atbilst projekta mērķiem un ierobežojumiem.',
      step1: '01',
      step1Title: 'Infrastruktūras novērtējums',
      step1Desc: 'Novērtējiet esošo infrastruktūru un pārklājuma prasības',
      step2: '02',
      step2Title: 'Ierīču prasības',
      step2Desc: 'Analizējiet datu biežumu, lietderīgās slodzes lielumu un sakaru vajadzības',
      step3: '03',
      step3Title: 'Baterijas optimizācija',
      step3Desc: 'Aprēķiniet enerģijas patēriņu un mērķa ierīces kalpošanas laiku',
      step4: '04',
      step4Title: 'Pārklājuma izpēte',
      step4Desc: 'Kartējiet pārklājuma zonas un identificējiet savienojamības robus',
      step5: '05',
      step5Title: 'Izvietošanas arhitektūra',
      step5Desc: 'Projektējiet hibrīda tīkla topoloģiju optimālai veiktspējai',
      strategyNote: 'Mūsu mērķis vienmēr ir samazināt darbības izmaksas, vienlaikus nodrošinot ilgtermiņa uzticamību un ierīču ilgmūžību.',
      // Industries Section
      industriesBadge: 'Lietojumi',
      industriesTitle: 'Savienojamība reāliem IoT izvietojumiem',
      industriesDesc: '0g Baltics nodrošina daudzteinklu IoT savienojamību dažādās nozarēs, katrai ar unikālām prasībām un izaicinājumiem.',
      industrySmartCities: 'Viedās pilsētas',
      industryUtilities: 'Komunālie pakalpojumi',
      industryLogistics: 'Loģistika',
      industryHealthcare: 'Veselības aprūpe',
      industryRealEstate: 'Nekustamais īpašums',
      industryIndustrial: 'Rūpniecība',
      industriesExplore: 'Izpētiet mūsu ierīču ekosistēmu un konsultāciju pakalpojumus, lai uzzinātu vairāk.',
      // CTA Section
      ctaTitle: 'Projektējiet savu IoT savienojamības infrastruktūru',
      ctaDesc: 'Gatavi izvietot daudzteinklu IoT risinājumu? Mūsu komanda palīdzēs jums izvēlēties pareizās savienojamības tehnoloģijas jūsu specifiskajām prasībām.',
      ctaButton: 'Apspriediet savu savienojamības stratēģiju',
    },

    tektelicPage: {
      metaTitle: 'TEKTELIC | LoRaWAN vārtejas un sensori | 0g Baltics',
      metaDesc: 'TEKTELIC profesionālas klases LoRaWAN vārtejas un sensori. Autorizēts partneris Baltijas reģionā.',
      // Hero Section
      heroBadge: 'Autorizēts partneris',
      heroTitle: 'Operatora līmeņa LoRaWAN infrastruktūra ar',
      heroTitleHighlight: 'Tektelic',
      heroSubtitle: 'Veidojiet mērogojamus IoT tīklus pilsētām, komunālajiem uzņēmumiem un rūpnieciskiem izvietojumiem, izmantojot Tektelic vārtejas un 0g Baltics infrastruktūras ekspertīzi.',
      heroDesc: '0g Baltics projektē un izvieto LoRaWAN infrastruktūru Baltijas valstīs un Eiropā, izmantojot Tektelic operatora līmeņa vārtejas. Mūsu izvietojumi atbalsta viedās pilsētas infrastruktūru, skaitītāju tīklus, vides monitoringu un rūpnieciskos IoT lietojumus.',
      ctaPrimary: 'Apspriest jūsu LoRaWAN projektu',
      ctaSecondary: 'Izpētīt starta komplektus',
      // Why Tektelic Section
      whyBadge: 'Tehnoloģiju partneris',
      whyTitle: 'Kāpēc Tektelic LoRaWAN vārtejas',
      whyDesc: 'Tektelic vārtejas ir izvietotas visā pasaulē liela mēroga LoRaWAN tīklos, kuriem uzticas telekomunikāciju operatori, pilsētas un uzņēmumi to uzticamības un veiktspējas dēļ.',
      feature1Title: 'Operatora līmeņa uzticamība',
      feature1Desc: 'Būvētas pēc telekomunikāciju standartiem 24/7 darbībai prasīgās vidēs',
      feature2Title: 'Rūpnieciskā līmeņa aparatūra',
      feature2Desc: 'Projektētas āra izvietošanai ar IP67 reitingiem un plašu temperatūras diapazonu',
      feature3Title: 'Mērogojama arhitektūra',
      feature3Desc: 'No vienas vārtejas pilotprojektiem līdz pilsētas mēroga tīkla izvietojumiem',
      feature4Title: 'Uzlabota tīkla pārvaldība',
      feature4Desc: 'Visaptveroši rīki uzraudzībai, konfigurēšanai un apkopei',
      feature5Title: 'Droša infrastruktūra',
      feature5Desc: 'Pilna šifrēšana un droša sāknēšana uzņēmumu izvietojumiem',
      feature6Title: 'Augstas veiktspējas RF dizains',
      feature6Desc: 'Optimizēts radiofrekvences dizains maksimālam pārklājumam un uzticamībai',
      // LoRaWAN for Cities Section
      citiesBadge: 'Viedās pilsētas infrastruktūra',
      citiesTitle: 'LoRaWAN infrastruktūra pilsētām un komunālajiem uzņēmumiem',
      citiesDesc: 'LoRaWAN tehnoloģija ļauj pilsētām un komunālajiem uzņēmumiem izvietot izmaksu ziņā efektīvu IoT infrastruktūru, kas vienlaikus atbalsta vairākus lietojumus. Viens LoRaWAN tīkls var apkalpot viedo skaitīšanu, vides monitoringu, aktīvu izsekošanu un citus lietojumus.',
      networkEfficiency: 'Tīkla efektivitāte',
      networkEfficiencyDesc: 'Viena LoRaWAN infrastruktūra var vienlaikus atbalstīt vairākus lietojumus, samazinot izvietošanas izmaksas un vienkāršojot tīkla pārvaldību dažādās pilsētas nodaļās.',
      useCasesTitle: 'Lietojuma gadījumi',
      useCase1Title: 'Viedā skaitīšana',
      useCase1Desc: 'Ūdens, gāze un elektrība',
      useCase2Title: 'Gaisa kvalitātes monitorings',
      useCase2Desc: 'CO2, daļiņas, piesārņotāji',
      useCase3Title: 'Autostāvvietu monitorings',
      useCase3Desc: 'Noslogojums un vadība',
      useCase4Title: 'Aktīvu izsekošana',
      useCase4Desc: 'Transportlīdzekļi un aprīkojums',
      useCase5Title: 'Atkritumu apsaimniekošana',
      useCase5Desc: 'Pildījuma līmeņa monitorings',
      useCase6Title: 'Vides monitorings',
      useCase6Desc: 'Temperatūra, mitrums',
      useCase7Title: 'Viedās ēkas',
      useCase7Desc: 'Enerģija un noslogojums',
      // Multi-Network Section
      multiNetworkBadge: 'Daudzteinklu stratēģija',
      multiNetworkTitle: 'Projektēta hibrīda IoT infrastruktūrai',
      multiNetworkDesc: '0g Baltics nekad neveido mono-savienojamības izvietojumus. LoRaWAN bieži ir IoT infrastruktūras mugurkauls, bet daudzi izvietojumi apvieno LoRaWAN ar Sigfox, NB-IoT, BLE vai satelītu savienojamību, lai optimizētu izmaksas, pārklājumu un uzticamību.',
      benefit1Title: 'Izmaksu optimizācija',
      benefit1Desc: 'Izvēlieties visefektīvāko savienojamību katram lietojuma gadījumam',
      benefit2Title: 'Tīkla redundance',
      benefit2Desc: 'Rezerves savienojamība kritiskiem lietojumiem',
      benefit3Title: 'Ierīču ilgmūžība',
      benefit3Desc: 'Optimizējiet baterijas darbības laiku ar pareizo tīkla izvēli',
      benefit4Title: 'Izvietošanas elastība',
      benefit4Desc: 'Pielāgojiet infrastruktūru mainīgajām prasībām',
      learnMultiNetwork: 'Uzziniet par mūsu daudzteinklu pieeju',
      connectivityTechnologies: 'Savienojamības tehnoloģijas',
      lorawanName: 'LoRaWAN',
      lorawanDesc: 'Liela diapazona mugurkaula infrastruktūra',
      sigfoxName: 'Sigfox',
      sigfoxDesc: 'Īpaši zema enerģijas patēriņa vienkārši ziņojumi',
      nbiotName: 'NB-IoT',
      nbiotDesc: 'Šūnu dziļā iekštelpu pārklājums',
      bleName: 'BLE',
      bleDesc: 'Iekštelpu pozicionēšana un izsekošana',
      satelliteName: 'Satelīts',
      satelliteDesc: 'Attālu teritoriju savienojamība',
      // Starter Kits Section
      kitsBadge: 'Ātrais starts',
      kitsTitle: 'LoRaWAN starta komplekti',
      kitsDesc: 'Sāciet savu LoRaWAN izvietojumu ātri ar pārbaudītiem infrastruktūras komplektiem, kas paredzēti dažādiem lietojuma gadījumiem un mērogiem.',
      pilotKitTitle: 'Pilotprojekta izvietošanas komplekts',
      pilotKitDesc: 'Ideāls organizācijām, kas uzsāk savu pirmo LoRaWAN pilotprojektu',
      pilotIncludes: 'Iekļauts:',
      pilotItem1: '1 Tektelic LoRaWAN vārteja',
      pilotItem2: 'Tīkla konfigurācija',
      pilotItem3: 'Ierīču integrācijas atbalsts',
      pilotItem4: 'Pamata paneļa piekļuve',
      pilotUseCases: 'Tipiski lietojumi:',
      pilotTag1: 'Koncepcijas pierādījums',
      pilotTag2: 'Mazs pilsētiņa',
      pilotTag3: 'Inovāciju projekti',
      smartCityKitTitle: 'Viedās pilsētas infrastruktūras komplekts',
      smartCityKitDesc: 'Paredzēts pašvaldībām un komunālajiem uzņēmumiem, kas uzsāk viedās pilsētas IoT infrastruktūru',
      smartCityIncludes: 'Iekļauts:',
      smartCityItem1: 'Vairākas Tektelic vārtejas',
      smartCityItem2: 'Tīkla arhitektūras dizains',
      smartCityItem3: 'Pārklājuma plānošana',
      smartCityItem4: 'Integrācija ar pilsētas platformām',
      smartCityUseCases: 'Tipiski lietojumi:',
      smartCityTag1: 'Viedā skaitīšana',
      smartCityTag2: 'Vides monitorings',
      smartCityTag3: 'Viedā autostāvvieta',
      smartCityTag4: 'Publiskā infrastruktūra',
      industrialKitTitle: 'Rūpnieciskais IoT tīkla komplekts',
      industrialKitDesc: 'Paredzēts loģistikai, komunālajiem uzņēmumiem un rūpnieciskām vidēm',
      industrialIncludes: 'Iekļauts:',
      industrialItem1: 'Rūpnieciskās vārtejas',
      industrialItem2: 'Aktīvu izsekošanas ierīces',
      industrialItem3: 'Tīkla optimizācija',
      industrialItem4: 'Integrācijas atbalsts',
      industrialUseCases: 'Tipiski lietojumi:',
      industrialTag1: 'Aktīvu izsekošana',
      industrialTag2: 'Noliktavas monitorings',
      industrialTag3: 'Enerģijas infrastruktūra',
      requestKitInfo: 'Pieprasīt komplekta informāciju',
      popular: 'POPULĀRS',
      // Device Ecosystem Section
      devicesBadge: 'Ierīču savietojamība',
      devicesTitle: 'Savietojamas IoT ierīces',
      devicesDesc: 'Mūsu LoRaWAN infrastruktūra darbojas ar plašu sertificētu ierīču ekosistēmu no vadošajiem ražotājiem, nodrošinot, ka varat izvēlēties labākos sensorus savām specifiskajām lietojuma prasībām.',
      viewDeviceEcosystem: 'Skatīt mūsu pilno ierīču ekosistēmu',
      device1Name: 'Adeunis',
      device1Desc: 'Rūpnieciskie sensori',
      device2Name: 'ATIM',
      device2Desc: 'Rūpnieciskās IoT ierīces',
      device3Name: 'Digital Matter',
      device3Desc: 'Aktīvu izsekotāji',
      device4Name: 'Vides sensori',
      device4Desc: 'Gaisa kvalitāte, temperatūra',
      device5Name: 'Skaitīšanas ierīces',
      device5Desc: 'Ūdens, gāze, elektrība',
      device6Name: 'Connected Inventions',
      device6Desc: 'Viedie sensori',
      // Services Section
      servicesBadge: 'Mūsu pakalpojumi',
      servicesTitle: 'Infrastruktūras dizains un izvietošana',
      servicesDesc: 'No sākotnējās plānošanas līdz liela mēroga izvēršanai, 0g Baltics nodrošina visaptverošus LoRaWAN infrastruktūras pakalpojumus.',
      service1: 'Tīkla dizains',
      service2: 'Vārteju izvietošana',
      service3: 'Pārklājuma optimizācija',
      service4: 'Ierīču izvēle',
      service5: 'ROI analīze',
      service6: 'Pilotprojektu izvietojumi',
      service7: 'Liela mēroga izvēršana',
      learnConsulting: 'Uzziniet vairāk par mūsu konsultāciju pakalpojumiem',
      // Who We Work With Section
      clientsBadge: 'Mūsu klienti',
      clientsTitle: 'Kas izvieto LoRaWAN infrastruktūru',
      client1Title: 'Pilsētas un pašvaldības',
      client1Desc: 'Viedās pilsētas infrastruktūra',
      client2Title: 'Komunālie uzņēmumi',
      client2Desc: 'Skaitīšana un monitorings',
      client3Title: 'Rūpnieciskie uzņēmumi',
      client3Desc: 'Aktīvu izsekošana un monitorings',
      client4Title: 'Loģistikas uzņēmumi',
      client4Desc: 'Autoparka un kravu izsekošana',
      client5Title: 'IoT integratori',
      client5Desc: 'Risinājumu izstrāde',
      // CTA Section
      ctaTitle: 'Sāciet savu LoRaWAN infrastruktūras projektu',
      ctaDesc: 'Apspriediet savu infrastruktūras projektu ar mūsu IoT speciālistiem. Mēs palīdzēsim jums projektēt un izvietot pareizo LoRaWAN risinājumu jūsu prasībām.',
      ctaButton: 'Sazināties ar mūsu LoRaWAN ekspertiem',
    },

    common: {
      learnMore: 'Uzzināt vairāk',
      contactUs: 'Sazināties ar mums',
      getStarted: 'Sākt darbu',
      requestDemo: 'Pieprasīt demonstrāciju',
      downloadBrochure: 'Lejupielādēt brošūru',
      viewCaseStudy: 'Skatīt gadījuma izpēti',
      keyBenefits: 'Galvenie ieguvumi',
      features: 'Funkcijas',
      useCases: 'Lietojumi',
      industries: 'Nozares',
      technologies: 'Tehnoloģijas',
      readMore: 'Lasīt vairāk',
      backToHome: 'Atpakaļ uz sākumu',
    },
    realEstatePage: {
      metaTitle: 'IoT risinājumi nekustamajam īpašumam un infrastruktūrai | Viedās ēkas Baltijā | 0g Baltics',
      metaDesc: 'Pārvērtiet nekustamā īpašuma aktīvus viedās, savienotās, uz datiem balstītās ēkās. IoT birojiem, viesnīcām, dzīvojamām ēkām, mazumtirdzniecībai, rūpniecības parkiem un infrastruktūrai Latvijā, Lietuvā un Igaunijā — enerģija, noplūžu atklāšana, gaisa kvalitāte, aizņemtība, viedie skapīši un daudz kas cits.',
      ogTitle: 'Viedās ēkas un infrastruktūra — IoT risinājumi no 0g Baltics',
      ogDesc: 'Samaziniet ekspluatācijas izmaksas, uzlabojiet īrnieku pieredzi un mazinot aktīvu riskus ar IoT risinājumiem nekustamajam īpašumam Baltijā.',
      heroBadge: 'Nekustamais īpašums un infrastruktūra',
      heroTitle: 'Viedās ēkas. Savienota infrastruktūra. Izmērāmi rezultāti.',
      heroSubtitle: 'Pārvērtiet katru ēku un objektu savienotā, uz datiem balstītā aktīvā — no enerģijas un noplūžu atklāšanas līdz gaisa kvalitātei, aizņemtībai un piekļuvei.',
      heroDesc: 'Viens daudzsavienojumu IoT komplekts nekustamā īpašuma attīstītājiem, īpašniekiem, operatoriem un aktīvu pārvaldniekiem Latvijā, Lietuvā un Igaunijā.',
      ctaDemo: 'Rezervēt 30 minūšu demonstrāciju',
      ctaExplore: 'Izpētīt risinājumus',
      ctaPilot: 'Uzsākt izmēģinājuma projektu',
      trustReady: 'Gatavs izvietošanai un izmēģinājumam',
      trustMulti: 'Daudzsavienojumi (LoRaWAN · NB-IoT · BLE · Satelīts)',
      trustLocal: 'Vietējais atbalsts Baltijā',
      trustScalable: 'No 1 ēkas līdz pilniem portfeļiem',
      trustIntegration: 'BMS, PropTech un ERP integrācijas',
      solutionsBadge: 'Risinājumu pārskats',
      solutionsTitle: 'Viena platforma, visi ēku lietošanas gadījumi',
      solutionsDesc: 'Izvietojiet tikai to, kas jums šodien nepieciešams — un paplašiniet rītdien. Visi risinājumi darbojas uz tā paša 0g Baltics IoT pamata, tāpēc katrs jauns lietojums izmanto jūsu esošo tīklu, ierīces un informācijas paneļus.',
      learnMore: 'Uzzināt vairāk',
      solEnergy: 'Enerģija un apakšmērīšana',
      solEnergyDesc: 'Apakšmērīta elektrība, siltums, gāze un ūdens pa stāviem, īrniekiem vai aktīviem — ar reāllaika patēriņu, salīdzināšanu un izmaksu sadali.',
      solLeak: 'Ūdens noplūžu atklāšana',
      solLeakDesc: 'Bezvadu noplūžu un plūsmas sensori stāvvados, iekārtu telpās un vienībās, lai atklātu problēmas minūtēs, nevis nedēļās.',
      solAir: 'Iekštelpu gaisa kvalitāte',
      solAirDesc: 'CO₂, temperatūras, mitruma, PM2.5 un TVOC uzraudzība veselīgām, atbilstošām un produktīvām iekštelpām.',
      solOccupancy: 'Aizņemtība un telpas izmantošana',
      solOccupancyDesc: 'Galdu, telpu un zonu aizņemtības analītika, lai optimizētu telpu, samazinātu neizmantoto jaudu un plānotu ar datiem.',
      solCleaning: 'Tīrīšanas un sanitāro telpu analītika',
      solCleaningDesc: 'Pēc pieprasījuma tīrīšana, balstoties uz reālu apmeklējumu un sanitāro telpu izmantošanu — mazāk stundu, labāki standarti.',
      solSafety: 'Drošība un vide',
      solSafetyDesc: 'Trauksmes pogas, temperatūras, trokšņu un vides brīdinājumi darbiniekiem, īrniekiem un kritiskām telpām.',
      solLockers: 'Pakomāti un viedie skapīši',
      solLockersDesc: 'Ēkā integrēti skapīši iedzīvotājiem, darbiniekiem un īrniekiem — sūtījumi, atslēgas, tīrīšanas rīki un daudz kas cits.',
      solParking: 'Stāvvietu aizņemtība',
      solParkingDesc: 'Reāllaika āra un iekštelpu stāvvietu aizņemtība, vadība un izmantošanas analītika garāžām un laukumiem.',
      solAsset: 'Aktīvu un iekārtu izsekošana',
      solAssetDesc: 'Izsekojiet HVAC iekārtas, rīkus, FM aprīkojumu un vērtīgus aktīvus vienā ēkā vai pilnā portfelī.',
      solESG: 'ESG un oglekļa atskaites',
      solESGDesc: 'Automatizēti, pārbaudāmi enerģijas un vides dati GRESB, CSRD, ES Taksonomijai un iekšējām ESG atskaitēm.',
      detailEnergyBadge: 'Enerģijas uzraudzība',
      detailEnergyTitle: 'Redziet, salīdziniet un samaziniet enerģiju katrā ēkā',
      detailEnergyDesc: 'Lielākā daļa portfeļu joprojām balstās uz vienu galveno skaitītāju objektā. Mēs pievienojam bezvadu apakšskaitītājus un vides sensorus, lai jūs varētu redzēt patēriņu pa stāviem un īrniekiem, un saistīt to ar āra un iekšējiem apstākļiem — tieši tur, kur rodas ietaupījumi.',
      detailEnergyPoint1: 'Bezvadu elektrības, siltuma, gāzes un ūdens apakšmērīšana pa zonām vai īrniekiem',
      detailEnergyPoint2: 'Tiešraides informācijas paneļi, salīdzinājumi un anomāliju brīdinājumi',
      detailEnergyPoint3: 'Īrnieku izmaksu sadale un enerģijas caurlaišana',
      detailEnergyPoint4: 'Gatavi BREEAM, LEED, GRESB un CSRD datu vajadzībām',
      statEnergySaving: 'Tipiski enerģijas ietaupījumi uzraudzītos objektos',
      statPayback: 'Tipiska atmaksāšanās gados',
      statMonitoring: 'Nepārtraukta uzraudzība',
      statAlerts: 'Reāllaika anomāliju brīdinājumi',
      detailLeakBadge: 'Noplūžu atklāšana',
      detailLeakTitle: 'Pasargājiet ēkas no #1 novēršamām bojājumu izmaksām',
      detailLeakDesc: 'Ūdens bojājumi ir vieni no dārgākajiem, visvairāk novēršamajiem zaudējumiem nekustamajā īpašumā. Mūsu bezvadu sensori uzrauga stāvvadus, iekārtu telpas, vannas istabas un neaizsargātās zonas 24/7 un izraisa brīdinājumus tajā brīdī, kad ūdens parādās tur, kur tam nevajadzētu būt.',
      detailLeakPoint1: 'Ar baterijām darbināmi bezvadu noplūžu, plūsmas un spiediena sensori',
      detailLeakPoint2: 'Tūlītēji brīdinājumi FM, apsardzei un īpašniekiem — SMS, e-pasts, BMS',
      detailLeakPoint3: 'Pieejama automātiska vārstu izslēgšanas integrācija',
      leakProblem: 'Neuzraudzītu ēku slēptās izmaksas',
      leakProblemDesc: 'Viena neatklāta noplūde var izmaksāt desmitiem tūkstošu eiro remontos, dīkstāvēs un apdrošināšanas prēmijās.',
      leakRisk1: 'Strukturāli un apdares bojājumi',
      leakRisk2: 'Īrnieku traucējumi un sūdzības',
      leakRisk3: 'Augošas apdrošināšanas prēmijas un atskaitījumi',
      detailAirBadge: 'Iekštelpu gaisa kvalitāte',
      detailAirTitle: 'Veselīgas, produktīvas iekštelpu vides — izmērītas',
      detailAirDesc: 'Iekštelpu gaisa kvalitāte tieši ietekmē produktivitāti, īrnieku apmierinātību, kavējumus un atbilstību. Mēs izvietojam diskrētus, ar baterijām darbināmus sensorus, kas nepārtraukti mēra veselīga iekštelpu gaisa galvenos parametrus.',
      airTemp: 'Temperatūra',
      airHumidity: 'Mitrums',
      airBenefits: 'Ko jūs iegūstat',
      airBenefit1: 'Veselīgāki, produktīvāki īrnieki un darbinieki',
      airBenefit2: 'Labāka HVAC vadība un enerģijas ietaupījumi',
      airBenefit3: 'Pierādījumi WELL, BREEAM un īrnieku atskaitēm',
      airBenefit4: 'Agrīna ventilācijas problēmu atklāšana',
      detailOccupancyBadge: 'Aizņemtība',
      detailOccupancyTitle: 'Projektējiet telpu ap reālu izmantošanu',
      detailOccupancyDesc: 'Izmēriet, kā patiesi tiek izmantoti galdi, telpas un veseli stāvi — un izmantojiet šos datus, lai optimizētu nomas līgumus, plānotu iekārtojumu, uzlabotu tīrīšanu un optimizētu HVAC grafikus.',
      occupancyUse1: 'Galdu, telpu un zonu aizņemtības analītika',
      occupancyUse2: 'Sapulču telpu izmantošana un neierašanās atklāšana',
      occupancyUse3: 'HVAC un apgaismojuma automatizācija, balstoties uz klātbūtni',
      occupancyUse4: 'Dati nomas, iekārtojuma un telpas stratēģijas lēmumiem',
      detailLockersBadge: 'Viedie skapīši',
      detailLockersTitle: 'Viedie skapīši, kas iebūvēti katrā ēkā',
      detailLockersDesc: 'Modulāri skapīšu risinājumi iedzīvotājiem, darbiniekiem, īrniekiem un apmeklētājiem — integrēti ar piekļuves kontroli un mūsu IoT platformu.',
      lockerUse1: 'Sūtījumu piegāde dzīvojamām un biroju ēkām',
      lockerUse2: 'Atslēgu un piekļuves ierīču pārvaldība',
      lockerUse3: 'Rīku un aprīkojuma izplatīšana FM komandām',
      lockerUse4: 'Click & collect mazumtirdzniecībai un jauktai izmantošanai',
      segmentsBadge: 'Lietošanas gadījumi pa segmentiem',
      segmentsTitle: 'Veidots katram nekustamā īpašuma un infrastruktūras profilam',
      segmentsDesc: 'No A klases birojiem līdz rūpniecības parkiem, katram segmentam ir savas specifiskās prioritātes. Mēs konfigurējam to pašu IoT kompleksu, lai atbilstu katram no tiem.',
      segOffice: 'Biroji',
      segOfficeDesc: 'Enerģija, aizņemtība, IAQ un viedā piekļuve iznomātājiem un korporatīvajiem lietotājiem.',
      segHotel: 'Viesnīcas un viesmīlība',
      segHotelDesc: 'Istabu komforts, enerģija, minibāra un aizmugures uzraudzība zīmolu un butika īpašumiem.',
      segResidential: 'Dzīvojamās un daudzdzīvokļu',
      segResidentialDesc: 'Apakšmērīšana, noplūžu aizsardzība, pakomāti un iedzīvotāju komforts BTR un dzīvokļiem.',
      segRetail: 'Mazumtirdzniecība un tirdzniecības centri',
      segRetailDesc: 'Apmeklētāju plūsma, aukstuma ķēde, HVAC un koplietojamo telpu enerģijas uzraudzība ar īrnieku izmaksu sadali.',
      segBanks: 'Bankas un publiskās ēkas',
      segBanksDesc: 'Droša vides uzraudzība, energoefektivitāte un atbilstības atskaites regulētajām telpām.',
      segParks: 'Rūpniecības un loģistikas parki',
      segParksDesc: 'Daudzīrnieku apakšmērīšana, aktīvu izsekošana, vārtu un piekļuves uzraudzība lielos objektos.',
      segDevelopers: 'Attīstītāji',
      segDevelopersDesc: 'Iepriekš aprīkots IoT komplekts, kas palielina aktīvu vērtību, ESG reitingu un īrnieku pievilcību no pirmās dienas.',
      segAssetManagers: 'Aktīvu un īpašumu pārvaldnieki',
      segAssetManagersDesc: 'Visa portfeļa informācijas paneļi, salīdzinājumi un automatizēti ESG dati katram pārvaldītajam aktīvam.',
      platformBadge: 'Savienojamība un platforma',
      platformTitle: 'Viens savienots komplekts — no sensora līdz informācijas panelim',
      platformDesc: 'Mēs projektējam, izvietojam un darbinām pilnu IoT ķēdi jūsu ēkām: ierīces, savienojamību, datu platformu, informācijas paneļus, brīdinājumus un integrācijas. Bez pārdevēju haosa, viens partneris visam dzīves ciklam.',
      platformDevices: 'Rūpnieciskas klases ierīces',
      platformConnectivity: 'Vairāku tīklu savienojamība',
      platformDashboard: 'Vienoti informācijas paneļi',
      platformAlerts: 'Viedie brīdinājumi',
      platformIntegration: 'BMS un PropTech integrācijas',
      platformSupport: 'Vietējais Baltijas atbalsts',
      techTitle: 'Pareizais tīkls katram lietojumam',
      techLoRaWAN: 'Tāls darbības rādiuss, zems patēriņš — ideāli piemērots apakšmērīšanai, noplūdēm, IAQ un aizņemtībai visās ēkās.',
      techNBIoT: 'Dziļš iekštelpu mobilais pārklājums — ideāli piemērots kritiskiem aktīviem un attāliem atsevišķiem objektiem.',
      techBLE: 'Iekštelpu mikro-atrašanās un aktīvu izsekošana ēku iekšpusē un lielos objektos.',
      learnMoreConnectivity: 'Skatīt visas savienojumu iespējas',
      bmsBadge: 'BMS un BACnet integrācija',
      bmsTitle: 'Pievienojas jūsu esošajai BMS — bez izraušanas un aizstāšanas',
      bmsDesc: 'Mūsu bezvadu IoT slānis papildina jūsu esošo ēku pārvaldības sistēmu ar reāllaika, detalizētiem datiem. Sensori tiek piegādāti iepriekš konfigurēti, vārtejas darbojas lokāli, un jūsu BMS saņem strukturētus datus pa protokoliem, kurus tā jau runā.',
      bmsProtocols: 'Atbalstītie protokoli',
      bmsBACnet: 'BACnet/IP',
      bmsBACnetDesc: 'Vietējais BACnet/IP savienotājs — punkti tieši tiek kartēti Niagara, Honeywell, Siemens un citās BMS platformās.',
      bmsModbus: 'Modbus TCP',
      bmsModbusDesc: 'Standarta Modbus TCP izvade mantojuma kontrolieriem, HVAC iekārtu telpām un rūpnieciskajai automatizācijai.',
      bmsRest: 'REST API un MQTT',
      bmsRestDesc: 'Atvērtie REST API, tīmekļa āķi un MQTT PropTech platformām, pielāgotiem informācijas paneļiem un datu ezeriem.',
      bmsLoRaWAN: 'LoRaWAN®',
      bmsLoRaWANDesc: 'Operatora klases LoRaWAN tīkls ar 5–10+ gadu baterijas kalpošanas laiku un pilna ēkas pārklājumu no vienas vārtejas.',
      bmsEcosystemTitle: 'Pilnīga sensoru ekosistēma',
      bmsEcosystemDesc: 'Izmantojiet pārbaudītu TEKTELIC ierīču portfolio — komforts, aizņemtība, noplūde, CO₂, viedais kondicionieris, aukstuma ķēde, darbinieku drošība — visi vienā tīklā.',
      bmsItWorks: 'Tas vienkārši darbojas',
      bmsItWorksDesc: 'Ierīces, kas darbojas uzreiz, minimāls inženiertehniskais darbs, zemas kopējās īpašumtiesību izmaksas un partneris, kas pārvalda dzīves ciklu — projektēšanu, izvietošanu, ekspluatāciju un apkopi.',
      bmsBenefit1: 'Nav jānoņem un jāaizstāj esošā BMS',
      bmsBenefit2: '5–10 gadu ierīces baterijas kalpošanas laiks',
      bmsBenefit3: 'Viena vārteja pārklāj visu ēku',
      bmsBenefit4: 'Niagara / OpenBlue / Honeywell gatavs',
      fmBadge: 'Facility Management IoT komplekts',
      fmTitle: 'LoRaWAN® risinājumi viedu ēku pārvaldībai',
      fmDesc: 'Mūsdienu objektu vadība ir sarežģītāka nekā jebkad — augošas enerģijas izmaksas, augstākas īrnieku prasības, stingrāka ESG atbilstība. Mūsu LoRaWAN® IoT komplekts sniedz reāllaika ieskatus, kas samazina ekspluatācijas izmaksas, uzlabo komfortu un automatizē atbilstību visā portfelī.',
      fmStat1: 'Tīrīšanas izmaksu samazinājums 60 dienās',
      fmStat2: 'Enerģijas ietaupījumi, optimizējot HVAC un aizņemtību',
      fmStat3: 'Automatizēta ESG atskaite — bez piepūles',
      fmStat4: 'Nepārtraukta gaisa, noplūžu un komforta uzraudzība',
      fmChallengesTitle: 'Risinām jūsu ēku pārvaldības izaicinājumus',
      fmChallengesDesc: 'Katrai ēkai ir savi darbības izaicinājumi. Mūsu risinājumi veidoti ap jūsu prioritātēm.',
      fmCh1Title: 'Komforta un gaisa kvalitātes sūdzības',
      fmCh1Desc: 'Slikta temperatūras, CO₂ un mitruma kontrole samazina īrnieku apmierinātību.',
      fmCh2Title: 'Slēpti riski un incidenti',
      fmCh2Desc: 'Noplūdes un iekārtu atteices strauji eskalējas bez agras atklāšanas.',
      fmCh3Title: 'Grūti pierādāma ESG un atbilstība',
      fmCh3Desc: 'Ilgtspējas un regulatīvās prasības ir grūti izpildīt bez nepārtrauktiem datiem.',
      fmCh4Title: 'Lēnas, manuālas darbplūsmas',
      fmCh4Desc: 'Apstaigāšana un reaktīvā apkope tērē laiku un palielina izmaksas.',
      fmCh5Title: 'Mērogošana aiz izmēģinājuma',
      fmCh5Desc: 'Daudzi IoT projekti apstājas pēc testēšanas. Paplašināšana šķiet sarežģīta un dārga.',
      fmCh6Title: 'Integrācija un datu silosi',
      fmCh6Desc: 'Dati ir izkaisīti dažādās sistēmās, apgrūtinot vienotu skatu un ātru rīcību.',
      fmAppsTitle: 'Gatavi lietošanas lietojumi',
      fmAppsDesc: 'Sāciet lietot IoT risinājumu dažās minūtēs. Skenējiet QR kodu un pārvaldiet visu no informācijas paneļa.',
      fmApp1Name: 'BREEZE',
      fmApp1Desc: 'Reāllaika iekštelpu gaisa kvalitātes informācijas paneļi un brīdinājumi.',
      fmApp2Name: 'LeapX',
      fmApp2Desc: 'Ātra un vienkārša IoT vārteju un ierīču aktivizēšana.',
      fmApp3Name: 'TEMPO',
      fmApp3Desc: 'Gudra sapulču telpu rezervēšana ar Outlook® / Google integrāciju.',
      fmApp4Name: 'LOCUS',
      fmApp4Desc: 'Vizualizācija un RTLS rīki facility management komandām.',
      fmBmsTitle: 'Bezvadu integrācija ar jūsu ēku pārvaldības sistēmu (BMS)',
      fmBmsDesc1: '0g Baltics nodrošina vienkāršu veidu, kā reāllaika datus par gaisa kvalitāti, aizņemtību, enerģiju, noplūdēm un aukstuma ķēdi pārsūtīt tieši jūsu ēku pārvaldības sistēmā (BMS).',
      fmBmsDesc2: 'Mūsu bezvadu risinājumi novērš sarežģītu kabeļu vilkšanu, samazina uzstādīšanas izmaksas un viegli mērogojas jebkurā objektā. Pateicoties atbalstam nozares standarta protokoliem kā BACnet, Modbus un Niagara, integrācija ir nevainojama — sniedzot vienu pilnu skatu uz jūsu ēkām, lai pieņemtu gudrākus lēmumus, samazinātu izmaksas un radītu veselīgākas vides.',
      fmBenefitsTitle: 'Efektivitāte, komforts un atbilstība — vienā risinājumā',
      fmBen1Title: 'Zemākas ekspluatācijas izmaksas',
      fmBen1Desc: 'Samaziniet tīrīšanas, apkopes un enerģijas izdevumus ar reāllaika ieskatiem.',
      fmBen2Title: 'Gudrāka enerģijas izmantošana',
      fmBen2Desc: 'Samaziniet enerģijas izšķērdēšanu, optimizējot HVAC, apgaismojumu un telpu izmantošanu.',
      fmBen3Title: 'Veselīgākas, ērtas telpas',
      fmBen3Desc: 'Uzturiet optimālu gaisa kvalitāti, temperatūru un mitrumu īrnieku labklājībai.',
      fmBen4Title: 'Vienkārša atbilstība',
      fmBen4Desc: 'Automātiski vāciet un atskaitiet ESG un regulatīvos datus — bez manuāla darba.',
      fmBen5Title: 'Uzticama drošības uzraudzība',
      fmBen5Desc: 'Tūlīt atklājiet noplūdes, vides riskus vai neparastu darbību.',
      fmBen6Title: 'Mērogojama vienkāršība',
      fmBen6Desc: 'Sāciet ar vienu ēku un paplašinieties visā portfelī bez sarežģītības.',
      relatedBadge: 'Saistītie risinājumi',
      relatedTitle: 'Saistītie risinājumi, ko varat apvienot',
      relatedPakuskapis: 'Mūsu pakomātu tīkls dzīvojamām un komerciālām ēkām Latvijā.',
      relatedSmartLockers: 'Viedie skapīši',
      relatedSmartLockersDesc: 'Pilns modulārs skapīšu klāsts sūtījumiem, atslēgām, rīkiem un click & collect.',
      relatedIAQ: 'Iekštelpu gaisa kvalitāte',
      relatedIAQDesc: 'Nepārtraukta IAQ uzraudzība veselīgākām, atbilstošām un produktīvām iekštelpām.',
      relatedEnergy: 'Ēku energoefektivitāte',
      relatedEnergyDesc: 'Enerģijas uzraudzība, HVAC optimizācija un ESG gatavas atskaites ēkām.',
      faqBadge: 'BUJ',
      faqTitle: 'Jautājumi, ko mums uzdod nekustamā īpašuma komandas',
      faq1Q: 'Vai mums jāaizstāj esošie skaitītāji vai BMS?',
      faq1A: 'Nē. Mūsu sensori ir neiejaucīgi un ar baterijām darbināmi, un tie integrējas ar jūsu esošajiem skaitītājiem, BMS un ERP. Jūs saglabājat to, kas darbojas, un paplašinat redzamību tur, kur jums ir aklās zonas.',
      faq2Q: 'Vai varam sākt ar vienu ēku pirms izvēršanas visā portfelī?',
      faq2A: 'Jā — lielākā daļa klientu sāk ar vienu izmēģinājuma ēku, apstiprina ROI 3–6 mēnešu laikā, pēc tam mērogo pārējo portfeli, izmantojot to pašu platformu un procesus.',
      faq3Q: 'Kā dati tiek aizsargāti un kur tie tiek mitināti?',
      faq3A: 'Dati ir šifrēti pārsūtīšanas laikā un miera stāvoklī, mitināti ES datu centros, un piekļuve ir balstīta uz lomām. Mēs pilnībā atbilstam GDPR un varam integrēties ar jūsu SSO.',
      faq4Q: 'Kādus tīklus jūs izmantojat Baltijā?',
      faq4A: 'Mēs apvienojam LoRaWAN, NB-IoT, LTE-M, BLE un satelītu atkarībā no ēkas, lietošanas gadījuma un pieejamā pārklājuma — tāpēc jūs nekad neesat ieslodzīts vienā tehnoloģijā.',
      faq5Q: 'Vai varam integrēt IoT datus ar mūsu BMS, PropTech vai ERP?',
      faq5A: 'Jā. Mēs nodrošinām REST API, webhooks un iepriekš izveidotus savienotājus kopīgām BMS, PropTech platformām, grāmatvedības un ERP sistēmām.',
      faq6Q: 'Kā tas atbalsta mūsu ESG un CSRD atskaites?',
      faq6A: 'Katrs izmērītais datu punkts (enerģija, ūdens, IAQ, aizņemtība) tiek saglabāts ar laika zīmogiem un metadatiem un var tikt eksportēts GRESB, BREEAM, LEED, CSRD un ES Taksonomijas atskaitēm.',
      faq7Q: 'Kā izskatās tipisks izmēģinājuma projekts?',
      faq7A: 'Tipisks izmēģinājums aptver 1 ēku ar 20–100 ierīcēm 3–6 mēnešu laikā, ar skaidri definētiem KPI (piem., enerģijas ietaupījumi, novērsti noplūžu notikumi, aizņemtības ieskati). Mēs rūpējamies par projektēšanu, izvietošanu un apmācību.',
      ctaTitle: 'Pārvērtīsim jūsu ēkas datiem pamatotos aktīvos',
      ctaDesc: 'Rezervējiet 30 minūšu zvanu ar mūsu nekustamā īpašuma IoT komandu. Mēs kartēsim jūsu prioritātes, parādīsim līdzīgus izvietojumus un piedāvāsim konkrētu izmēģinājumu.',
    },

  },

  // =====================
  // LITHUANIAN (LT)
  // =====================
  lt: {
    smartMeteringPage: {
      metaTitle: 'Išmaniojo matavimo sprendimai | IoT komunalinėms įmonėms | 0g Baltics',
      metaDesc: 'Įdiekite išmaniojo matavimo infrastruktūrą su LoRaWAN, Sigfox ir NB-IoT. Nuotolinis duomenų rinkimas vandens, dujų, elektros ir šilumos skaitikliams.',
      heroBadge: 'Išmanusis matavimas',
      heroTitle: 'Išmaniojo matavimo infrastruktūra komunalinėms įmonėms',
      heroSubtitle: 'Įdiekite nuotolinio skaitiklių nuskaitymo infrastruktūrą naudodami LoRaWAN, Sigfox, NB-IoT ir mobiliąsias technologijas.',
      ctaPrimary: 'Prašyti konsultacijos',
      ctaSecondary: 'Sužinoti daugiau',
      challengeBadge: 'Iššūkis',
      challengeTitle: 'Kodėl komunalinėms įmonėms reikia išmaniojo matavimo',
      challengeDesc: 'Tradicinis skaitiklių nuskaitymas yra brangus, linkęs į klaidas ir nesuteikia realaus laiko matomumo.',
      challenge1: 'Rankinio nuskaitymo išlaidos',
      challenge1Desc: 'Vizitai į vietą yra brangūs ir užima daug laiko',
      challenge2: 'Netikslus atsiskaitymas',
      challenge2Desc: 'Įvertinimo klaidos sukelia klientų ginčus',
      challenge3: 'Nuotėkio aptikimo vėlavimas',
      challenge3Desc: 'Problemos aptinkamos per vėlai',
      challenge4: 'Riboti duomenys',
      challenge4Desc: 'Mėnesiniai nuskaitymai neatskleidžia vartojimo modelių',
      solutionBadge: 'Mūsų sprendimas',
      solutionTitle: 'IoT pagrįstas išmanusis matavimas',
      solutionDesc: 'Mes diegiame išmaniojo matavimo infrastruktūrą, kuri prijungia esamus skaitiklius prie debesies.',
      solutionFeature1: 'Modernizuoti esamus skaitiklius',
      solutionFeature2: 'Kelios ryšio parinktys',
      solutionFeature3: 'Realaus laiko vartojimo duomenys',
      solutionFeature4: 'Nuotėkių ir anomalijų aptikimas',
      solutionFeature5: 'Atsiskaitymo integracija',
      solutionFeature6: 'Klientų portalas',
      techBadge: 'Technologijos',
      techTitle: 'Ryšio parinktys',
      techDesc: 'Mes pasirenkame tinkamą technologiją pagal jūsų infrastruktūrą ir biudžetą.',
      lorawanTitle: 'LoRaWAN',
      lorawanDesc: 'Didelis atstumas, mažas energijos suvartojimas. Idealiai tinka tankiems miesto diegimams.',
      sigfoxTitle: 'Sigfox',
      sigfoxDesc: 'Paprasta integracija, šalies masto aprėptis Baltijos šalyse.',
      nbiotTitle: 'NB-IoT',
      nbiotDesc: 'Pagrįsta mobiliuoju tinklu. Geras zonoms su mobiliuoju ryšiu.',
      cellularTitle: 'Mobilusis (4G/LTE)',
      cellularDesc: 'Didelio dažnio duomenų perdavimo reikalavimams.',
      useCasesBadge: 'Pritaikymai',
      useCasesTitle: 'Išmaniojo matavimo naudojimo atvejai',
      waterMetering: 'Vandens matavimas',
      waterMeteringDesc: 'Stebėkite suvartojimą, aptikite nuotėkius ir sumažinkite vandens nuostolius.',
      gasMetering: 'Dujų matavimas',
      gasMeteringDesc: 'Nuotolinis dujų skaitiklių nuskaitymas namų ūkiams ir įmonėms.',
      electricityMetering: 'Elektros matavimas',
      electricityMeteringDesc: 'Submatavimai komerciniams pastatams ir pramoninėms įrenginiams.',
      heatMetering: 'Šilumos matavimas',
      heatMeteringDesc: 'Centralizuoto šildymo suvartojimo stebėjimas ir atsiskaitymas.',
      ctaTitle: 'Pasiruošę modernizuoti savo matavimo infrastruktūrą?',
      ctaDesc: 'Susisiekite su mumis aptarti savo išmaniojo matavimo projektą.',
      ctaButton: 'Susisiekti',
      // Section: Network Quality
      networkBadge: 'Kritinė infrastruktūra',
      networkTitle: 'Tinklo patikimumas yra viskas',
      networkDesc: 'Daugelis išmaniojo matavimo projektų žlunga ne dėl skaitiklių, o dėl nepatikimo ryšio.',
      networkLocation1: 'Požeminės duobės',
      networkLocation2: 'Pastatų viduje',
      networkLocation3: 'Rūsiuose',
      networkLocation4: 'Atokiose vietose',
      networkWarning: 'Komunalinėms įmonėms reikia operatorinio lygio infrastruktūros, sukurtos dešimtmečių patikimam veikimui.',
      // Section: Carrier Grade
      carrierBadge: 'Infrastruktūros standartai',
      carrierTitle: 'Ką reiškia "Operatorinio lygio"?',
      carrierDesc: 'Operatorinio lygio infrastruktūra atitinka telekomunikacijų operatorių ir kritinės infrastruktūros reikalavimus.',
      carrierFeature1: 'Tinklo veikimo garantija',
      carrierFeature1Desc: '99.9%+ prieinamumas',
      carrierFeature2: 'Profesionali įranga',
      carrierFeature2Desc: 'Pramoninės klasės įrenginiai',
      carrierFeature3: 'Ilgalaikė parama',
      carrierFeature3Desc: '10+ metų gyvavimo ciklas',
      carrierFeature4: 'Saugumo atitiktis',
      carrierFeature4Desc: 'Įmonių lygio saugumas',
      // Section: LoRaWAN
      lorawanSectionBadge: 'Pagrindinė technologija',
      lorawanSectionTitle: 'LoRaWAN išmaniajam matavimui',
      lorawanSectionDesc: 'LoRaWAN yra pirmaujanti LPWAN technologija išmaniojo matavimo diegimams.',
      lorawanAdvantage1: 'Didelis atstumas (iki 15km)',
      lorawanAdvantage2: '10+ metų baterijos veikimas',
      lorawanAdvantage3: 'Gilus prasiskverbimas į patalpas',
      lorawanAdvantage4: 'Mažos infrastruktūros išlaidos',
      // Section: Cellular
      cellularSectionBadge: 'Alternatyvi technologija',
      cellularSectionTitle: 'Mobilusis IoT išmaniajam matavimui',
      cellularSectionDesc: 'NB-IoT ir LTE-M suteikia mobilaus ryšio pagrindu veikiantį ryšį zonose su geru mobiliuoju ryšiu.',
    },

    iaqPage: {
      metaTitle: 'Vidaus oro kokybės stebėjimas | CO2 jutikliai | 0g Baltics',
      metaDesc: 'Profesionalus vidaus oro kokybės stebėjimas su CO2, temperatūros, drėgmės ir PM jutikliais. Sprendimai mokykloms, biurams ir sveikatos priežiūros įstaigoms.',
      heroBadge: 'Vidaus oro kokybė',
      heroTitle: 'Vidaus oro kokybės stebėjimo sprendimai',
      heroSubtitle: 'Įdiekite profesionalias IAQ stebėjimo sistemas, kad užtikrintumėte sveiką vidaus aplinką mokyklose, biuruose, sveikatos priežiūros įstaigose ir viešuosiuose pastatuose.',
      // Technology badges
      techCO2: 'CO2',
      techTemperature: 'Temperatūra',
      techHumidity: 'Drėgmė',
      techPM25: 'PM2.5',
      techTVOC: 'TVOC',
      techOccupancy: 'Užimtumas',
      // Section: Why IAQ Matters
      whyBadge: 'Kodėl tai svarbu',
      whyTitle: 'Vidaus oro kokybės svarba',
      whyDesc: 'Žmonės praleidžia 90% laiko patalpose. Prasta oro kokybė veikia sveikatą, produktyvumą ir pažintines funkcijas.',
      whyPoint1: 'CO2 lygis virš 1000 ppm mažina pažintines funkcijas',
      whyPoint2: 'Prasta ventiliacija didina virusų plitimą',
      whyPoint3: 'Gerai vėdinamose klasėse mokiniai pasiekia geresnių rezultatų',
      whyPoint4: 'Pastato gyventojai praneša apie mažiau nedarbingumo dienų',
      // Why outcomes list
      outcome1: 'Sveikesnė vidaus aplinka gyventojams',
      outcome2: 'Geresni ventiliacijos sprendimai remiantis realiais duomenimis',
      outcome3: 'Parama ESG ir išmaniųjų pastatų strategijoms',
      outcome4: 'Pagerintas darbo vietos ir klasės komfortas',
      outcome5: 'Sumažintos operacinės aklosios zonos',
      // Key Decision Drivers
      keyDrivers: 'Pagrindiniai sprendimų veiksniai',
      driverHealth: 'Sveikata',
      driverComfort: 'Komfortas',
      driverVentilation: 'Ventiliacija',
      driverEfficiency: 'Efektyvumas',
      driverCompliance: 'Atitiktis',
      // Section: What We Monitor
      monitorBadge: 'Parametrai',
      monitorTitle: 'Ką stebime',
      typicalRange: 'Tipinis diapazonas',
      co2: 'CO2 (anglies dioksidas)',
      co2Desc: 'Pagrindinis ventiliacijos pakankamumo rodiklis',
      temperature: 'Temperatūra',
      temperatureDesc: 'Šiluminis komfortas gyventojams',
      humidity: 'Drėgmė',
      humidityDesc: 'Optimalus diapazonas 40-60% sveikatai',
      pm: 'Kietosios dalelės (PM)',
      pmDesc: 'PM2.5 ir PM10 oro kokybės vertinimui',
      tvoc: 'TVOC',
      tvocDesc: 'Lakiųjų organinių junginių aptikimas',
      // Section: Sensors - TEKTELIC
      sensorsBadge: 'Įranga',
      sensorsTitle: 'Profesionalios klasės jutikliai',
      sensorsDesc: 'Mes diegiame TEKTELIC ir Connected Inventions jutiklius patikimam, ilgalaikiam stebėjimui.',
      tektelicBreezeDesc: 'Išmanusis patalpos jutiklis CO2, temperatūros, drėgmės, barometrinio slėgio ir aplinkos šviesos stebėjimui. NDIR CO2 jutiklis su ±30 ppm + 3% tikslumu ir 5+ metų baterijos veikimo laiku.',
      tektelicBreezeVDesc: 'Išplečia BREEZE su PIR užimtumo aptikimu išmaniajai patalpos funkcionalumui. Idealus koreliuoti oro kokybę su erdvės naudojimu paklausos valdomai ventiliacijai.',
      tektelicBreezeDDesc: 'CO2 išmanusis patalpos jutiklis su E-Ink ekranu. Sujungia LoRaWAN, BLE ir E-Ink mažam energijos suvartojimui. Ekranas yra belaidis ir gali būti montuojamas ant sienos arba stalo.',
      whenTektelic: 'Kada rinktis TEKTELIC',
      tektelicRelevant: 'TEKTELIC jutikliai ypač aktualūs, kai jūsų projektui reikia:',
      tektelicBenefit1: 'LoRaWAN išmaniųjų pastatų infrastruktūros',
      tektelicBenefit2: 'Plečiamų įmonių diegimų',
      tektelicBenefit3: 'Operatoriaus klasės patikimumo',
      tektelicBenefit4: 'Integracijos su pastatų IoT sistemomis',
      tektelicBenefit5: 'Pasirinktinių BLE e-Ink ekranų',
      tektelicBenefit6: 'Profesionalios pagalbos ir dokumentacijos',
      bestFitTektelic: 'Geriausiai tinkamos aplinkos TEKTELIC',
      corpOffices: 'Įmonių biurai',
      schoolsUnis: 'Mokyklos ir universitetai',
      publicBuildings: 'Viešieji pastatai',
      healthcareFacilities: 'Sveikatos priežiūros įstaigos',
      // Section: Connected Inventions
      connectedBadge: 'Ilgaamžis stebėjimas',
      connectedTitle: 'Connected Inventions AirWits įrenginiai',
      connectedDesc: '0g Baltics taip pat naudoja Connected Inventions įrenginius specifiniams vidaus aplinkos stebėjimo poreikiams. AirWits produktų šeima siūlo specialiai sukurtus jutiklius, optimizuotus ilgam baterijos veikimo laikui, mažai priežiūrai ir ekonomiškai efektyviam diegimui pastatų portfeliuose.',
      airwitsBasicDesc: 'Temperatūros ir drėgmės stebėjimas su ±0.2°C tikslumu ir iki 10 metų baterijos veikimo laiku.',
      airwitsCO2Desc: 'CO2, temperatūros ir drėgmės stebėjimas ventiliacijos vertinimui ir komforto optimizavimui.',
      airwitsCO2PlusDesc: 'Pažangus CO2 stebėjimas su e-ink ekranu, rodančiu realaus laiko oro kokybės būseną ir spalvomis koduotus indikatorius.',
      airwitsPMDesc: 'PM1.0 / PM2.5 / PM10 kietųjų dalelių stebėjimas su temperatūra ir drėgme oro kokybės vertinimui.',
      flexSenseDesc: 'Modulinė kelių jutiklių platforma lanksčiam diegimui su pritaikomomis jutiklių kombinacijomis įvairiems naudojimo atvejams.',
      airwitsIAQDesc: 'TVOC stebėjimas su temperatūra ir drėgme teršalų įvykių ir prastos oro kokybės sąlygų aptikimui.',
      airwitsEcoSenseDesc: 'Saulės energija maitinama, be baterijų arba itin mažos priežiūros parinktis diegimo scenarijams, reikalaujantiems minimalios intervencijos.',
      whenConnected: 'Kada rinktis Connected Inventions',
      connectedUseful: 'Connected Inventions įrenginiai ypač naudingi, kai jūsų projektas vertina:',
      connectedBenefit1: 'Iki 10 metų baterijos veikimo laikas',
      connectedBenefit2: 'Maža bendra nuosavybės kaina',
      connectedBenefit3: 'Sigfox arba LoRaWAN parinktys',
      connectedBenefit4: 'Paprastas didelio masto diegimas',
      connectedBenefit5: 'Ekonomiškai efektyvus portfelio stebėjimas',
      connectedBenefit6: 'Veikimas be priežiūros',
      // Section: Device Selection
      deviceSelectionBadge: 'Sprendimo architektūra',
      deviceSelectionTitle: 'Tinkamo IAQ įrenginio pasirinkimas tinkamam pastatui',
      deviceSelectionDesc: '0g Baltics nesiūlo vieno įrenginio kiekvienam projektui. Vietoj to, mes parenkame tinkamą architektūrą priklausomai nuo biudžeto, tinklo prieinamumo, reikalingų parametrų, baterijos lūkesčių, ataskaitų poreikių ir integracijos reikalavimų.',
      tableBuilding: 'Pastato tipas',
      tableNeed: 'Projekto poreikis',
      tableSensor: 'Rekomenduojamas jutiklis',
      tableWhy: 'Kodėl tinka',
      // Table rows
      officePortfolio: 'Biurų portfelis',
      officeNeed: 'CO2, temperatūra, drėgmė per aukštus',
      officeWhy: 'LoRaWAN plečiamumas, užimtumo koreliacija, BLE ekrano parinktis',
      classroomSchool: 'Klasė / Mokykla',
      classroomNeed: 'Ventiliacijos stebėjimas, mokinių komfortas',
      classroomWhy: 'Realaus laiko CO2 matomumas, ilgas baterijos veikimo laikas, lengvas montavimas',
      uniCampus: 'Universiteto miestelis',
      uniNeed: 'Kelių pastatų IAQ strategija',
      uniSensor: 'Mišrus: TEKTELIC + AirWits',
      uniWhy: 'LoRaWAN infrastruktūra + ekonomiškai efektyvus plėtimas',
      healthcareClinics: 'Sveikatos priežiūra / Klinikos',
      healthcareNeed: 'Jautrių aplinkų stebėjimas',
      healthcareIaqSensor: 'AirWits PM + AirWits IAQ',
      healthcareWhy: 'PM ir TVOC kritiškai svarbūs pacientų aplinkoms',
      residentialAreas: 'Gyvenamosios bendros erdvės',
      residentialNeed: 'Pagrindinis komfortas ir ventiliacija',
      residentialSensor: 'AirWits / AirWits CO2',
      residentialWhy: 'Ekonomiškai efektyvus, maža priežiūra, ilga baterija',
      municipalBuildings: 'Savivaldybių pastatai',
      municipalNeed: 'Viešųjų erdvių stebėjimas, atitiktis',
      municipalWhy: 'Operatoriaus klasės patikimumas, audito įrašai',
      industrialIndoor: 'Pramoninės vidaus erdvės',
      industrialNeed: 'Proceso kontrolė, darbuotojų komfortas',
      industrialSensor: 'AirWits PM + CO2',
      industrialWhy: 'Kietųjų dalelių stebėjimas pramoninėms aplinkoms',
      // Section: IoT vs Manual
      iotVsManualBadge: 'Operacinis pranašumas',
      iotVsManualTitle: 'Kodėl IoT pagrįstas IAQ stebėjimas pranoksta rankinius tikrinimus',
      iotVsManualDesc: 'Tradicinis vidaus oro kokybės vertinimas remiasi periodiniais rankiniais tikrinimais su nešiojamais prietaisais. Technikas aplanko kiekvieną erdvę, atlieka matavimą, užregistruoja rezultatą ir eina toliau. Šis metodas turi fundamentalių apribojimų, kuriuos IoT pagrįstas stebėjimas pašalina.',
      iotVsManualDesc2: 'Sujungti jutikliai teikia nuolatinius duomenis vietoj atsitiktinių momentinių nuotraukų. Jie generuoja įspėjimus vietoj laukimo skundų. Jie kuria istorines tendencijas analizei ir lyginamajai analizei. Ir jie plečiasi per portfelius neproporcingai didindami darbo sąnaudas.',
      continuous247: 'Nuolatinis 24/7 stebėjimas',
      continuous247Desc: 'Duomenys kas 15-30 minučių, ne kartą per ketvirtį',
      realTimeAlerts: 'Realaus laiko įspėjimai',
      realTimeAlertsDesc: 'Momentinis pranešimas, kai viršijami slenksčiai',
      historicalTrends: 'Istorinės tendencijos',
      historicalTrendsDesc: 'Analizuokite modelius per savaites, mėnesius ir sezonus',
      portfolioBenchmark: 'Portfelio lyginamoji analizė',
      portfolioBenchmarkDesc: 'Palyginkite pastatus ir identifikuokite išskirtinius',
      betterDecisions: 'Geresni sprendimai',
      betterDecisionsDesc: 'Duomenimis pagrįsta priežiūra ir ventiliacijos optimizavimas',
      scalableOps: 'Plečiamos operacijos',
      scalableOpsDesc: 'Stebėkite šimtus erdvių be papildomo personalo',
      // Section: IAQ Energy
      iaqEnergyBadge: 'Pastato efektyvumas',
      iaqEnergyTitle: 'Subalansuokite sveikatą, komfortą ir energetinį efektyvumą',
      iaqEnergyDesc1: 'Vidaus oro kokybės stebėjimas yra ne tik apie sveikatą—tai taip pat apie pastato efektyvumą ir operacinį efektyvumą. Pastatai sudaro maždaug 40% pasaulinio energijos suvartojimo, o ŠVOK sistemos sudaro reikšmingą dalį. Intelektuali ventiliacija, pagrįsta faktinėmis vidaus sąlygomis, gali sumažinti energijos švaistymą išlaikant sveiką oro kokybę.',
      co2Ventilation: 'CO2 ir užimtumu pagrįsta ventiliacija',
      co2VentilationDesc: 'CO2 ir užimtumo stebėjimas gali palaikyti protingesnius ventiliacijos sprendimus. Kai erdvės nenaudojamos arba mažai naudojamos, ventiliacija gali būti sumažinta. Kai CO2 kyla, šviežio oro tiekimas gali būti padidintas. Šis paklausos valdomas metodas optimizuoja energijos naudojimą išlaikant oro kokybę.',
      tempHumidityInsights: 'Temperatūros ir drėgmės įžvalgos',
      tempHumidityInsightsDesc: 'Temperatūros ir drėgmės tendencijos padeda suprasti pastato komfortą ir ŠVOK veikimą. Zonų su nuolatinėmis problemomis identifikavimas įgalina tikslinius patobulinimus vietoj visos sistemos koregavimų. Geresni duomenys veda prie efektyvesnių operacijų.',
      iaqEnergyDesc2: 'IAQ duomenys palaiko geresnį patalpų valdymą teikdami objektyvius matavimus, kurie informuoja priežiūros prioritetus, renovacijos sprendimus ir sistemos atnaujinimus. Išmanieji pastatai reikalauja matavimo prieš optimizavimą—negalite pagerinti to, ko nematuojate.',
      iaqEnergyNote: 'Nors IAQ stebėjimas teikia duomenis, kurie gali informuoti energijos optimizavimo strategijas, konkretus energijos taupymas priklauso nuo daugelio veiksnių, įskaitant pastato sistemas, klimatą ir operacines praktikas. Mes sutelkiame dėmesį į tikslų matavimą ir veiksmingas įžvalgas, o ne per daug žadame konkrečius ROI skaičius.',
      // Section: Standards
      standardsBadge: 'Reguliacinė sistema',
      standardsTitle: 'Standartai, gairės ir IAQ geroji praktika',
      standardsDesc: 'Vidaus oro kokybės projektus dažnai skatina sveikatos lūkesčiai, ESG ir išmaniųjų pastatų politikos, mokyklų ir darbo vietų komforto tikslai bei ventiliacijos ir rizikos valdymo gairės. Atitinkamų sistemų supratimas padeda apibrėžti stebėjimo strategiją, ventiliacijos tikslus ir pastato efektyvumo lūkesčius.',
      whoGuidelines: 'PSO oro kokybės gairės',
      whoGuidelinesDesc: 'Pasaulio sveikatos organizacija teikia oro kokybės gaires, įskaitant kietųjų dalelių ribas. Šios rekomendacijos aktualios suprantant PM susijusį poveikį sveikatai ir nustatant stebėjimo tikslus pastatuose, susirūpinusiuose gyventojų sveikata.',
      ashraeStandard: 'ASHRAE standartas 241',
      ashraeStandardDesc: 'ASHRAE 241 nagrinėja infekcinių aerozolių kontrolę ir teikia gaires sveikesnėms vidaus aplinkoms. Šis standartas ypač aktualus sveikatos priežiūros, švietimo ir komercinėms pastatų, siekiantiems sumažinti oro keliu plintančių ligų perdavimo rizikas.',
      enStandard: 'EN 16798 šeima',
      enStandardDesc: 'Europoje EN 16798 standartai yra aktualūs vidaus aplinkos kokybei, ventiliacijos reikalavimams ir tikrinimo sistemoms. Jie teikia gaires pastatų projektavimui ir eksploatavimui, kad atitiktų vidaus aplinkos kokybės lūkesčius.',
      standardsImportant: 'IAQ stebėjimas palaiko įrodymais pagrįstą pastatų valdymą ir gali padėti organizacijoms dokumentuoti sąlygas, identifikuoti problemas ir nustatyti korekcinių veiksmų prioritetus. Nors jutikliai teikia duomenis, atitiktis konkretiems standartams priklauso nuo to, kaip tie duomenys naudojami platesnėse pastatų valdymo ir ventiliacijos strategijose.',
      iaqHelps: 'IAQ stebėjimas padeda:',
      iaqHelp1: 'Dokumentuoti vidaus sąlygas per laiką',
      iaqHelp2: 'Identifikuoti erdves, reikalaujančias intervencijos',
      iaqHelp3: 'Palaikyti ventiliacijos optimizavimo pastangas',
      iaqHelp4: 'Teikti audito įrašus atitikčiai',
      iaqHelp5: 'Lyginamajai analizei per pastatų portfelius',
      iaqHelp6: 'Informuoti renovacijos ir atnaujinimo sprendimus',
      // Section: Use Cases
      useCasesBadge: 'Pritaikymai',
      useCasesTitle: 'Kur vidaus oro kokybės stebėjimas teikia vertę',
      useCasesDesc: 'IAQ stebėjimas teikia operacinę naudą plačiam pastatų tipų spektrui. Kiekviena aplinka turi specifinius reikalavimus, kurie lemia jutiklio pasirinkimą ir diegimo strategiją.',
      ucOffices: 'Biurai',
      ucOfficesDesc: 'Pagerinkite darbo vietos komfortą ir aplinkos kokybę. Stebėkite CO2 lygius, kad užtikrintumėte tinkamą ventiliaciją, optimizuokite temperatūrą produktyvumui ir demonstruokite įsipareigojimą darbuotojų gerovei.',
      ucSchools: 'Mokyklos ir universitetai',
      ucSchoolsDesc: 'Palaikykite klasės ventiliaciją ir sukurkite geresnes mokymosi aplinkas. Tyrimai rodo, kad padidėję CO2 lygiai klasėse koreliuoja su sumažėjusiu pažintiniu veikimu ir padidėjusiu pravaikštų skaičiumi.',
      ucHealthcare: 'Sveikatos priežiūra ir klinikos',
      ucHealthcareDesc: 'Stebėkite jautrias vidaus aplinkas, kur oro kokybė tiesiogiai veikia pacientų rezultatus. PM ir TVOC stebėjimas ypač aktualus klinikiniuose nustatymuose, laukimo kambariuose ir pacientų priežiūros zonose.',
      ucMunicipal: 'Savivaldybių pastatai',
      ucMunicipalDesc: 'Suteikite matomumą viešosioms vidaus aplinkoms, įskaitant bibliotekas, bendruomenės centrus ir vyriausybės biurus. Palaikykite skaidrumą ir demonstruokite atsakingą viešųjų patalpų valdymą.',
      ucCommercial: 'Komercinis nekilnojamasis turtas',
      ucCommercialDesc: 'Lyginamajai analizei ir stebėkite kelis pastatus nuosekliai per portfelius. Teikite nuomininkams sveikų pastatų kredencialus ir palaikykite ESG ataskaitų reikalavimus.',
      ucIndustrial: 'Pramoninės vidaus erdvės',
      ucIndustrialDesc: 'Stebėkite vidaus sąlygas, kur komfortas, proceso kontrolė arba darbuotojų sauga yra svarbūs. Sekite kietąsias daleles gamybos aplinkose ir užtikrinkite atitiktį darbo vietos sveikatos standartams.',
      // CTA
      ctaTitle: 'Pagerinkite oro kokybę savo pastatuose',
      ctaButton: 'Prašyti demonstracijos',
      // Section: Why 0g Baltics
      whyUsBadge: 'Jūsų partneris',
      whyUsTitle: 'Kodėl 0g Baltics',
      whyUsDesc: 'Mes teikiame praktinę IoT diegimo patirtį vidaus oro kokybės stebėjimo projektuose visame Baltijos regione.',
      whyUsFeature1: 'Baltijos rinkos fokusas',
      whyUsFeature1Desc: 'Vietinis buvimas ir regioninių reikalavimų supratimas',
      whyUsFeature2: 'Praktinė IoT patirtis',
      whyUsFeature2Desc: 'Realaus pasaulio diegimo patirtis įvairių tipų pastatuose',
      whyUsFeature3: 'Daugelis ryšio būdų',
      whyUsFeature3Desc: 'LoRaWAN, Sigfox ir hibridinės architektūros',
      // Section: Sectors
      sectorsBadge: 'Pramonės šakos',
      sectorsTitle: 'IAQ stebėjimas pagal sektorius',
      sectorSchools: 'Mokyklos ir švietimas',
      sectorSchoolsDesc: 'Pagerinkite mokymosi aplinką su geresne oro kokybe',
      sectorOffices: 'Biurai ir darbo vietos',
      sectorOfficesDesc: 'Padidinkite produktyvumą ir sumažinkite nedarbingumo dienas',
      sectorHealthcare: 'Sveikatos priežiūros įstaigos',
      sectorHealthcareDesc: 'Kritinė oro kokybė pacientų saugumui',
      sectorPublic: 'Viešieji pastatai',
      sectorPublicDesc: 'Savivaldybių pastatai ir vyriausybės įstaigos',
      // FAQ Section
      faqBadge: 'DUK',
      faqTitle: 'Dažnai užduodami klausimai',
      faqDesc: 'Viskas, ką reikia žinoti apie vidaus oro kokybės stebėjimą',
      faq1Q: 'Kas yra vidaus oro kokybės stebėjimas?',
      faq1A: 'IAQ stebėjimas naudoja jutiklius nuolat matuoti CO2, temperatūrą, drėgmę, PM ir VOC patalpose.',
      faq2Q: 'Kokius parametrus turėčiau stebėti?',
      faq2A: 'Pradėkite nuo CO2 ir temperatūros. Pridėkite drėgmę, PM arba VOC pagal savo poreikius.',
      faq3Q: 'Kiek jutiklių man reikia?',
      faq3A: 'Paprastai vienas jutiklis kiekvienai patalpai arba zonai. Didelėms erdvėms gali prireikti kelių jutiklių.',
      faq4Q: 'Koks yra baterijos veikimo laikas?',
      faq4A: 'Profesionalūs jutikliai paprastai veikia 5-10 metų su baterijomis.',
    },

    smartCityPage: {
      metaTitle: 'Išmaniojo miesto IoT infrastruktūra | 0g Baltics',
      metaDesc: 'Kurkite išmaniojo miesto infrastruktūrą su LoRaWAN tinklais, aplinkos stebėjimu ir sujungtomis miesto paslaugomis.',
      heroBadge: 'Išmanieji miestai',
      heroTitle: 'Išmaniojo miesto IoT infrastruktūra',
      heroSubtitle: 'Mes padedame savivaldybėms diegti IoT infrastruktūrą išmanesniems, efektyvesniems miestams.',
      solutionsBadge: 'Sprendimai',
      solutionsTitle: 'Išmaniojo miesto pritaikymai',
      envMonitoring: 'Aplinkos stebėjimas',
      envMonitoringDesc: 'Oro kokybė, triukšmo lygis ir orų stebėjimas visame mieste.',
      smartLighting: 'Išmanusis apšvietimas',
      smartLightingDesc: 'Sujungtas gatvių apšvietimas su pritemdimu ir gedimų aptikimu.',
      wasteManagement: 'Atliekų tvarkymas',
      wasteManagementDesc: 'Išmaniosios šiukšliadėžės su užpildymo lygio jutikliais ir maršrutų optimizavimu.',
      parking: 'Parkavimo vietų prieinamumas',
      parkingDesc: 'Realaus laiko parkavimo vietų prieinamumas ir nukreipimo sistemos.',
      flooding: 'Potvynių stebėjimas',
      floodingDesc: 'Vandens lygio jutikliai ankstyvojo perspėjimo apie potvynius sistemoms.',
      traffic: 'Eismo modeliai',
      trafficDesc: 'Transporto priemonių skaičiavimas ir eismo srauto analizė.',
      // What Is section
      whatIsBadge: 'Pagrindo supratimas',
      whatIsTitle: 'Kas yra išmaniojo miesto IoT infrastruktūra?',
      whatIsDesc: 'Išmaniojo miesto infrastruktūra yra sujungtų miesto paslaugų skaitmeninis stuburą.',
      dataCollected: 'Surinkti miesto duomenys apima:',
      airQuality: 'Oro kokybė',
      water: 'Vandens suvartojimas',
      energy: 'Energijos naudojimas',
      assets: 'Viešieji turtai',
      // Why Projects Fail section
      failBadge: 'Dažni iššūkiai',
      failTitle: 'Kodėl daugelis išmaniojo miesto projektų nepavyksta',
      failDesc: 'Daugelis išmaniojo miesto iniciatyvų prasideda izoliuotais bandomaisiais projektais be ilgalaikės infrastruktūros strategijos.',
      commonProblems: 'Dažnos problemos:',
      problem1: 'Fragmentuotas ryšys su daugybe nesuderinamų sistemų',
      problem2: 'Nepatikimi tinklai, negalintys palaikyti augančio įrenginių skaičiaus',
      problem3: 'Bandomieji projektai, kurių negalima išplėsti iki viso miesto diegimo',
      problem4: 'Duomenų silosai, neleidžiantys vieningo miesto intelekto',
      solutionText: 'Sprendimas:',
      solutionDesc: 'Sėkmingos išmaniojo miesto strategijos prasideda nuo bendros ryšio infrastruktūros, galinčios palaikyti daug paslaugų—ne izoliuotų bandomųjų projektų, kurie negali augti.',
      // Connectivity Foundation section
      connectivityBadge: 'Tinklo technologijos',
      connectivityTitle: 'Išmaniųjų miestų pagrindas: Patikimas IoT ryšys',
      connectivityDesc: 'Išmaniesiems miestams reikia tinklų, galinčių palaikyti tūkstančius įrenginių, išdėstytų didelėse miesto teritorijose. Skirtingos technologijos tarnauja skirtingiems naudojimo atvejams, o sėkmingiausi diegimai derina kelias ryšio galimybes.',
      lorawanName: 'LoRaWAN',
      lorawanDesc: 'Didelis nuotolis, mažas energijos suvartojimas viso miesto aprėpčiai',
      nbiotName: 'NB-IoT',
      nbiotDesc: 'Korinio ryšio pagrindu giliam prasiskverbimui',
      ltemName: 'LTE-M',
      ltemDesc: 'Mobiliems objektams ir didesnei pralaidumui',
      bleName: 'BLE tinklai',
      bleDesc: 'Patalpų ir artumo taikymams',
      satelliteName: 'Palydovas',
      satelliteDesc: 'Nuotoliniam ir atsarginiam ryšiui',
      connectivityNote: '0g Baltics projektuoja daugiaryšio ryšio architektūras, užtikrinančias, kad kiekvienas įrenginys naudoja efektyviausią ryšio technologiją. Šis metodas maksimizuoja patikimumą, kartu minimizuodamas veiklos kaštus.',
      // Carrier Grade section
      carrierBadge: 'Infrastruktūros standartai',
      carrierTitle: 'Kodėl miestams reikia operatoriaus lygio infrastruktūros',
      carrierDesc1: 'Miesto infrastruktūra turi veikti nepertraukiamai daugelį metų—dažnai dešimtmečius. Todėl išmaniojo miesto ryšys turi būti suprojektuotas kaip operatoriaus lygio infrastruktūra, o ne vartotojų lygio sprendimai, kurie negali atitikti savivaldybių patikimumo reikalavimų.',
      carrierDesc2: 'Šie principai kilę iš telekomunikacijų pramonės ir užtikrina, kad miesto paslaugos išliktų veikiančios laikui bėgant, nepriklausomai nuo oro sąlygų, tinklo apkrovos ar sistemos pakeitimų.',
      carrierProvide: 'Operatoriaus lygio tinklai užtikrina:',
      highAvailability: 'Aukštas prieinamumas',
      highAvailabilityDesc: '99.9%+ veikimo tikslai',
      secureCommunications: 'Saugūs ryšiai',
      secureCommunicationsDesc: 'Šifruotas duomenų perdavimas',
      scalableArchitecture: 'Keičiamo dydžio architektūra',
      scalableArchitectureDesc: 'Auga kartu su miesto poreikiais',
      professionalMonitoring: 'Profesionalus stebėjimas',
      professionalMonitoringDesc: '24/7 tinklo operacijos',
      longTermReliability: 'Ilgalaikis patikimumas',
      longTermReliabilityDesc: '10+ metų infrastruktūra',
      redundancy: 'Perteklius',
      redundancyDesc: 'Persijungimo galimybės',
      infrastructureLayers: 'Infrastruktūros sluoksniai',
      layer5: '5 sluoksnis',
      layer5Name: 'Miesto paslaugos',
      layer5Desc: 'Parkavimas, apšvietimas, aplinka',
      layer4: '4 sluoksnis',
      layer4Name: 'Miesto platforma',
      layer4Desc: 'Duomenų valdymas ir analitika',
      layer3: '3 sluoksnis',
      layer3Name: 'Tinklo serveriai',
      layer3Desc: 'Įrenginių ir duomenų valdymas',
      layer2: '2 sluoksnis',
      layer2Name: 'Šliuzai',
      layer2Desc: 'Profesionalios bazinės stotys',
      layer1: '1 sluoksnis',
      layer1Name: 'Įrenginiai',
      layer1Desc: 'Jutikliai ir galiniai taškai',
      // LoRaWAN for Cities section
      lorawanBadge: 'LPWAN technologija',
      lorawanTitle: 'LoRaWAN tinklai miesto IoT',
      lorawanMainDesc: 'LoRaWAN yra viena plačiausiai taikomų technologijų išmaniojo miesto diegimams visame pasaulyje. Jos techninės charakteristikos daro ją idealiai tinkamą tūkstančių jutiklių sujungimui miesto aplinkose.',
      lorawanFeature1Title: 'Didelio nuotolio ryšys',
      lorawanFeature1Desc: 'Vienas šliuzas gali aprėpti kelis kilometrus miesto aplinkose, mažindamas infrastruktūros investicijas.',
      lorawanFeature2Title: 'Puikus prasiskverbimas',
      lorawanFeature2Desc: 'LoRa signalai pasiekia jutiklius pastatų viduje, po žeme ir sudėtingose vietose visame mieste.',
      lorawanFeature3Title: 'Mažas energijos suvartojimas',
      lorawanFeature3Desc: 'Baterijomis maitinami jutikliai gali veikti daugelį metų be priežiūros ar pakeitimo.',
      lorawanFeature4Title: 'Didelė tinklo talpa',
      lorawanFeature4Desc: 'Nedidelis skaičius šliuzų gali sujungti tūkstančius jutiklių visame mieste.',
      lorawanNote: '0g Baltics diegia operatoriaus lygio LoRaWAN infrastruktūrą naudodama profesionalius TEKTELIC šliuzus ir saugią tinklo architektūrą, skirtą savivaldybių lygio patikimumui.',
      stat1Value: '15+ km',
      stat1Label: 'Nuotolis kaimo vietovėse',
      stat2Value: '5+ km',
      stat2Label: 'Nuotolis miesto vietovėse',
      stat3Value: '10+ metų',
      stat3Label: 'Jutiklio baterijos tarnavimo laikas',
      stat4Value: '10 000+',
      stat4Label: 'Įrenginių vienam šliuzui',
      // Multi-Service Infrastructure section
      multiServiceBadge: 'Vieninga infrastruktūra',
      multiServiceTitle: 'Viena infrastruktūra, daug miesto paslaugų',
      multiServiceDesc: 'Kai miestas diegia bendrą IoT infrastruktūrą, daugelis paslaugų gali veikti tame pačiame tinkle. Šis metodas mažina kaštus, supaprastina operacijas ir leidžia miestams laikui bėgant plėsti inovacijas.',
      servicesShare: 'Paslaugos, kurios gali dalintis infrastruktūra:',
      smartParking: 'Išmanusis parkavimas',
      airQualityMonitoring: 'Oro kokybės stebėjimas',
      smartWaterMetering: 'Išmanusis vandens matavimas',
      wasteManagementService: 'Atliekų tvarkymas',
      municipalAssetTracking: 'Savivaldybės turto sekimas',
      smartStreetLighting: 'Išmanusis gatvių apšvietimas',
      smartBuildingMonitoring: 'Išmaniųjų pastatų stebėjimas',
      energyManagement: 'Energijos valdymas',
      // Innovation Ecosystem section
      innovationBadge: 'Ekonominė plėtra',
      innovationTitle: 'Naujos paslaugos ir ekonominė plėtra, įgalinta LPWAN tinklų',
      innovationDesc: 'Miesto masto LPWAN tinklas yra ne tik techninė infrastruktūra. Jis tampa inovacijų platforma, įgalinančia ekonominę veiklą ir naujas skaitmenines paslaugas. Miestai, diegiantys bendrą IoT infrastruktūrą, dažnai mato naujų verslų ir paslaugų atsiradimą.',
      organizationsTitle: 'Organizacijos, kurios gali pasinaudoti miesto IoT infrastruktūra:',
      localStartups: 'Vietiniai startuoliai',
      techCompanies: 'Technologijų įmonės',
      universities: 'Universitetai',
      researchLabs: 'Mokslinių tyrimų laboratorijos',
      publicServiceProviders: 'Viešųjų paslaugų teikėjai',
      privateOperators: 'Privatūs operatoriai',
      innovationBarrier: 'Užuot kūrusios savo ryšio infrastruktūrą, šios organizacijos gali pasikliauti miesto bendru IoT tinklu. Tai žymiai sumažina barjerą inovacijoms.',
      innovationAnalogy: 'Galvokite apie tai kaip apie šviesolaidinį plačiajuostį ryšį: IoT infrastruktūra tampa ilgalaikės ekonominės plėtros pagrindu, įgalinančiu paslaugas, kurios negali egzistuoti be patikimo ryšio.',
      newServicesTitle: 'Naujos paslaugos, kurios gali atsirasti miesto IoT infrastruktūroje:',
      smartLogistics: 'Išmaniosios logistikos paslaugos',
      smartLogisticsDesc: 'Paskutinės mylios pristatymo optimizavimas',
      envMonitoringStartups: 'Aplinkos stebėjimo startuoliai',
      envMonitoringStartupsDesc: 'Miesto oro ir triukšmo analitika',
      equipmentTracking: 'Įrangos sekimo paslaugos',
      equipmentTrackingDesc: 'Statybų ir pramoniniai objektai',
      sharedMobility: 'Dalijimosi judumo stebėjimas',
      sharedMobilityDesc: 'Dviračiai, paspirtukai, transporto priemonės',
      energyOptimization: 'Energijos optimizavimo paslaugos',
      energyOptimizationDesc: 'Pastatų ir tinklo valdymas',
      smartBuildingProviders: 'Išmaniųjų pastatų teikėjai',
      smartBuildingProvidersDesc: 'Komercinių pastatų paslaugos',
      // City Intelligence section
      intelligenceBadge: 'Miesto analitika',
      intelligenceTitle: 'Nuo jutiklių iki miesto intelekto',
      intelligenceDesc: 'Išmaniojo miesto platformos renka duomenis iš tūkstančių jutiklių, išdėstytų miesto teritorijose. Šie duomenys padeda miesto operatoriams suprasti miesto sistemas ir priimti geresnius sprendimus dėl infrastruktūros, paslaugų ir planavimo.',
      dataHelps: 'Duomenys padeda miestams suprasti:',
      trafficPatterns: 'Eismo modelius ir spūstis',
      envConditions: 'Aplinkos sąlygas ir oro kokybę',
      infrastructureUsage: 'Infrastruktūros naudojimą ir priežiūros poreikius',
      energyConsumption: 'Energijos suvartojimą įvairiuose objektuose',
      assetUtilization: 'Viešojo turto panaudojimą',
      urbanTrends: 'Tendencijas miesto planavimui',
      intelligenceConclusion: 'Laikui bėgant miestai gali optimizuoti operacijas ir priimti geresnius planavimo sprendimus, remdamiesi objektyviais duomenimis, o ne prielaidomis.',
      // Deployment Strategy section
      deploymentBadge: 'Diegimo metodas',
      deploymentTitle: 'Praktinė diegimo strategija miestams',
      deploymentDesc: 'Sėkmingi išmaniojo miesto diegimai seka palaipsniui metodui, kuris mažina riziką, kartu įgalindamas ilgalaikį mastelio keitimą.',
      step01: '01',
      step01Title: 'Įdiekite patikimą IoT ryšio infrastruktūrą',
      step01Desc: 'Sukurkite operatoriaus lygio tinklo aprėptį prioritetinėse zonose. Įrenkite profesionalius šliuzus ir sukonfigūruokite saugią tinklo architektūrą, galinčią palaikyti tūkstančius įrenginių.',
      step02: '02',
      step02Title: 'Prijunkite prioritetines paslaugas',
      step02Desc: 'Įdiekite pradinius naudojimo atvejus, tokius kaip išmanusis parkavimas, oro kokybės stebėjimas ar vandens matavimas. Sutelkite dėmesį į paslaugas, kurios teikia aiškią vertę ir demonstruoja infrastruktūros galimybes.',
      step03: '03',
      step03Title: 'Palaipsniui plėskite į naujas paslaugas',
      step03Desc: 'Laikui bėgant pridėkite naujas paslaugas ir skyrius. Bendra infrastruktūra palaiko papildomus naudojimo atvejus be naujų tinklo investicijų.',
      step04: '04',
      step04Title: 'Naudokite duomenų analitiką operacijų gerinimui',
      step04Desc: 'Išnaudokite surinktus duomenis miesto paslaugų optimizavimui, planavimo sprendimų informavimui ir vertės demonstravimui piliečiams bei suinteresuotosioms šalims.',
      // Why 0g Baltics section
      whyBadge: 'Jūsų partneris',
      whyTitle: 'Kodėl miestai dirba su 0g Baltics',
      whyDesc: '0g Baltics suteikia infrastruktūros ekspertizę, kurios miestams reikia sėkmingiems išmaniojo miesto diegimams kurti. Mūsų tikslas – padėti miestams kurti patikimą skaitmeninę infrastruktūrą, palaikančią ilgalaikes inovacijas.',
      service1Title: 'Operatoriaus lygio IoT ryšys',
      service1Desc: 'Tinklai, suprojektuoti savivaldybių patikimumo reikalavimams',
      service2Title: 'LoRaWAN infrastruktūros diegimas',
      service2Desc: 'Profesionalus tinklo projektavimas, montavimas ir valdymas',
      service3Title: 'Daugiaryšio ryšio architektūra',
      service3Desc: 'Hibridiniai tinklai, derinantys LoRaWAN, korinį ryšį ir daugiau',
      service4Title: 'Išmaniojo miesto konsultacijos',
      service4Desc: 'Strateginės gairės infrastruktūros planavimui',
      service5Title: 'Integracija su miesto platformomis',
      service5Desc: 'Sujunkite jutiklius su miesto valdymo sistemomis',
      relatedSolutions: 'Susiję 0g Baltics sprendimai',
      relatedConnectivity: 'Daugiateinklis ryšys',
      relatedConnectivityDesc: 'LoRaWAN, NB-IoT, Sigfox sprendimai',
      relatedTektelic: 'TEKTELIC infrastruktūra',
      relatedTektelicDesc: 'Operatoriaus lygio LoRaWAN šliuzai',
      relatedMetering: 'Išmanusis matavimas',
      relatedMeteringDesc: 'Vandens, energijos, dujų matavimas',
      relatedAssetTracking: 'Turto sekimas',
      relatedAssetTrackingDesc: 'Savivaldybės įrangos stebėjimas',
      relatedAirQuality: 'Oro kokybės stebėjimas',
      relatedAirQualityDesc: 'Aplinkos IoT jutikliai',
      // CTA section
      ctaTitle: 'Kurkite savo išmaniojo miesto infrastruktūrą',
      ctaDesc: 'Susisiekite su 0g Baltics, kad suprojektuotumėte keičiamo dydžio išmaniojo miesto ryšį ir infrastruktūrą. Mes padedame savivaldybėms ir miestams kurti skaitmeninį pagrindą ilgalaikėms miesto inovacijoms.',
      ctaButton: 'Kalbėkite su mūsų komanda',
    },

    smartLockersPage: {
      metaTitle: 'Išmaniosios spintelės | Automatizuotos siuntų ir įrankių spintelės | 0g Baltics',
      metaDesc: 'Išmaniųjų spintelių sprendimai siuntų pristatymui, įrankių valdymui ir viešosioms paslaugoms.',
      heroBadge: 'Išmaniosios spintelės',
      heroTitle: 'Išmaniųjų spintelių sprendimai',
      heroSubtitle: 'Automatizuota spintelių infrastruktūra logistikai, pramoninėms operacijoms ir viešosioms paslaugoms.',
      // Use case badges
      parcelDelivery: 'Siuntų pristatymas',
      toolManagement: 'Įrankių valdymas',
      beachLockers: 'Paplūdimio spintelės',
      retailPickup: 'Mažmeninė prekyba',
      keyManagement: 'Raktų valdymas',
      parcelLockers: 'Siuntų spintelės',
      parcelLockersDesc: 'Automatizuotas siuntų pristatymas ir atsiėmimas paskutinės mylios logistikai.',
      toolLockers: 'Įrankių valdymo spintelės',
      toolLockersDesc: 'Pramoninių įrankių paskirstymas ir sekimas su prieigos kontrole.',
      libraryLockers: 'Bibliotekos spintelės',
      libraryLockersDesc: 'Automatizuotas knygų atsiėmimas ir grąžinimas viešosioms bibliotekoms.',
      discoverToolTracking: 'Atraskite mūsų įrankių sekimo sprendimus',
      // What Are Smart Lockers
      whatAreBadge: 'Technologijos supratimas',
      whatAreTitle: 'Kas yra išmaniosios spintelės?',
      whatAreDesc1: 'Išmaniosios spintelės yra sujungtos saugojimo sistemos, leidžiančios saugiai saugoti ir paimti daiktus naudojant skaitmeninį autentifikavimą. Skirtingai nuo tradicinių spintelių, kurios remiasi fiziniais raktais ar kombinacijos spynomis, išmaniosios spintelės integruojasi su debesų platformomis ir teikia kelis prieigos metodus.',
      whatAreDesc2: 'Išmaniosios spintelės pakeičia rankines saugojimo sistemas automatizuota saugia prieiga, suteikdamos pilną matomumą į naudojimo modelius, inventoriaus lygius ir prieigos istoriją. Tai daro jas idealiomis didelės apimties operacijoms, kur svarbus sekimas ir atskaitomybė.',
      accessMethods: 'Vartotojai gali pasiekti spinteles naudodami:',
      accessMobile: 'Mobiliosios programėlės',
      accessPIN: 'PIN kodai',
      accessRFID: 'RFID kortelės',
      accessQR: 'QR kodai',
      accessBadge: 'Darbuotojų kortelės',
      accessPayment: 'Mokėjimo sistemos',
      cloudCapabilities: 'Debesų platformos galimybės:',
      capAccessControl: 'Prieigos kontrolė',
      capAccessControlDesc: 'Valdykite, kas gali pasiekti kurias spinteles ir kada',
      capNotifications: 'Pranešimai',
      capNotificationsDesc: 'SMS, el. pašto ir push pranešimai vartotojams',
      capUsageTracking: 'Naudojimo sekimas',
      capUsageTrackingDesc: 'Pilna visų spintelių transakcijų istorija',
      capAvailability: 'Spintelių prieinamumas',
      capAvailabilityDesc: 'Realaus laiko matomumas apie tuščius skyrius',
      capAnalytics: 'Analitika',
      capAnalyticsDesc: 'Naudojimo modeliai ir operaciniai įžvalgos',
      // Smart Locker Models
      modelsBadge: 'Aparatinės įrangos parinktys',
      modelsTitle: 'Išmaniųjų spintelių modeliai',
      modelsDesc: '0g Baltics siūlo kelias spintelių architektūras priklausomai nuo naudojimo atvejo. Kiekvienas modelis leidžia lanksčiai konfigūruoti skyrių dydžius pagal saugomus daiktus.',
      towerLockers: 'Bokšto spintelės',
      towerLockersDesc: 'Vertikalios spintelių sistemos, skirtos siuntų pristatymui ir logistikos centrams. Kompaktiškas pėdsakas su maksimalia talpa.',
      towerFeature1: 'Didelė talpa',
      towerFeature2: 'Mažas pėdsakas',
      towerFeature3: 'Paruošta lauko sąlygoms',
      storeLockers: 'Parduotuvės spintelės',
      storeLockersDesc: 'Didelės modulinės spintelės, tinkamos mažmeniniam atsiėmimui ir komercinėms aplinkoms. Sieninės arba laisvai stovinčios konfigūracijos.',
      storeFeature1: 'Modulinis dizainas',
      storeFeature2: 'Mažmeninė integracija',
      storeFeature3: 'Didelis matomumas',
      slingLockers: 'Sling spintelės',
      slingLockersDesc: 'Kompaktiškos spintelės, idealios mažoms erdvėms, biurams ir vidaus aplinkoms. Puikiai tinka įmonių ir kampuso diegimams.',
      slingFeature1: 'Erdvės taupymas',
      slingFeature2: 'Vidaus dizainas',
      slingFeature3: 'Lengvas montavimas',
      batteryPowered: 'Baterijomis maitinamos',
      batteryPoweredDesc: 'Spintelės, maitinamos baterijomis, leidžiančios diegti be elektros instaliacijos. Idealios atokioms ir laikinoms vietoms.',
      batteryFeature1: 'Be laidų',
      batteryFeature2: 'Lanksti vieta',
      batteryFeature3: 'Mažos montavimo išlaidos',
      // Retail Pickup Lockers
      retailTitle: 'Mažmeninės prekybos atsiėmimo spintelės',
      retailDesc: 'Mažmenininkai naudoja spinteles, kad leistų klientams greitai atsiimti internetinius užsakymus. Spustelėk-ir-atsiimk paslaugos vis populiarėja, spintelės leidžia 24/7 atsiėmimą be personalo pagalbos.',
      retailAdvantages: 'Privalumai:',
      retailAdv1: '24/7 atsiėmimo prieinamumas',
      retailAdv2: 'Sumažintos parduotuvės eilės',
      retailAdv3: 'Greitesnis klientų aptarnavimas',
      retailAdv4: 'Automatizuota atsiėmimo logistika',
      retailAdv5: 'Integracija su el. prekyba',
      retailNote: 'Mažmeninės spintelės vis dažniau naudojamos prekybos centruose, parduotuvėse ir el. prekybos išpildymo centruose.',
      // Beach Lockers
      beachTitle: 'Paplūdimio ir poilsio spintelės',
      beachDesc: 'Išmaniosios spintelės idealios poilsio aplinkoms, kur lankytojams reikia saugiai saugoti asmeninius daiktus. Baterijomis maitinamos parinktys leidžia diegti be infrastruktūros investicijų.',
      idealLocations: 'Idealios vietos:',
      beachLoc1: 'Paplūdimiai ir pakrantės zonos',
      beachLoc2: 'Parkai ir poilsio zonos',
      beachLoc3: 'Sporto įrenginiai',
      beachLoc4: 'Turizmo vietos',
      beachLoc5: 'Renginių vietos',
      beachNote: 'Vartotojai pasiekia spinteles per mobilias programėles arba mokėjimo sistemas, leidžiant operatoriams monetizuoti.',
      // Key Management
      keyTitle: 'Raktų valdymo spintelės',
      keyDesc: 'Sujungtos spintelės gali valdyti fizinius raktus transporto priemonių parkui, nekilnojamojo turto valdymui ir įrangos prieigai. Kiekviena transakcija sekama skaitmeniškai, sukuriant audito įrašą, kuris pagerina veiklos saugumą ir atskaitomybę.',
      keyFeature1: 'Transporto priemonių parko raktų valdymas',
      keyFeature2: 'Turto ir patalpų prieiga',
      keyFeature3: 'Įrangos raktų paskirstymas',
      keyFeature4: 'Rangovų ir lankytojų prieiga',
      keyFeature5: 'Pilnas skaitmeninis audito įrašas',
      // Battery Powered
      batteryTitle: 'Baterijomis maitinamos spintelės',
      batteryDesc: 'Baterijomis maitinamos spintelės leidžia montuoti vietose be elektros infrastruktūros. Tai dramatiškai sumažina montavimo išlaidas ir leidžia lanksčiai diegti vietose, kur laidai būtų nepraktiški.',
      batteryFeat1: 'Nereikia elektros instaliacijos',
      batteryFeat2: 'Greitas ir lengvas montavimas',
      batteryFeat3: 'Lanksti vietos pasirinkimas',
      batteryFeat4: 'Mažesnės bendros montavimo išlaidos',
      batteryFeat5: 'Idealu laikiniems diegimams',
      batteryUseCases: 'Dažni naudojimo atvejai:',
      batteryUseCasesDesc: 'Paplūdimiai, parkai, laikinos instaliacijos, atokios vietos ir vietos, kur elektros infrastruktūra brangi arba nepasiekiama.',
      // Software Platform
      platformBadge: 'Debesų valdymas',
      platformTitle: 'Spintelių valdymo platforma',
      platformDesc: 'Išmaniosios spintelės valdomos per debesų platformą, kuri suteikia pilną matomumą ir kontrolę spintelių tinklams. Operatoriai gali stebėti būseną, valdyti vartotojus ir analizuoti naudojimo modelius iš bet kurios vietos.',
      platformCapabilities: 'Platformos galimybės:',
      platAvailability: 'Prieinamumo stebėjimas',
      platAvailabilityDesc: 'Realaus laiko skyrių būsena',
      platUserMgmt: 'Vartotojų valdymas',
      platUserMgmtDesc: 'Prieigos kontrolė ir leidimai',
      platNotifications: 'Pranešimai',
      platNotificationsDesc: 'SMS, el. paštas, push įspėjimai',
      platAnalytics: 'Naudojimo analitika',
      platAnalyticsDesc: 'Tendencijos ir modeliai',
      platRemoteConfig: 'Nuotolinė konfigūracija',
      platRemoteConfigDesc: 'Nuotoliniu būdu atnaujinti nustatymus',
      platSecurityLogs: 'Saugumo žurnalai',
      platSecurityLogsDesc: 'Pilnas audito įrašas',
      // API and Integrations
      apiBadge: 'Atvira architektūra',
      apiTitle: 'Atviri API ir integracijos',
      apiDesc: 'Spintelių platformos gali integruotis su esamomis verslo sistemomis per API, leidžiant spintelėms tapti platesnės skaitmeninės infrastruktūros dalimi, o ne atskirais sprendimais.',
      intEcommerce: 'El. prekyba',
      intEcommerceDesc: 'Užsakymų valdymas',
      intIdentity: 'Tapatybė',
      intIdentityDesc: 'SSO ir LDAP',
      intPayment: 'Mokėjimai',
      intPaymentDesc: 'Kortelė ir mobilieji',
      intLogistics: 'Logistika',
      intLogisticsDesc: 'TMS integracija',
      intSmartCity: 'Išmanusis miestas',
      intSmartCityDesc: 'Miesto platformos',
      intERP: 'ERP',
      intERPDesc: 'Įmonės sistemos',
      // Smart Lockers for Cities
      citiesBadge: 'Miesto infrastruktūra',
      citiesTitle: 'Išmaniųjų spintelių infrastruktūra miestams',
      citiesDesc: 'Miestai gali diegti spintelių tinklus kaip išmaniojo miesto infrastruktūros dalį. Siuntų spintelės sumažina pristatymo eismą, viešosios saugojimo spintelės aptarnauja gyventojus ir turistus, o įrangos spintelės palaiko savivaldybės operacijas.',
      municipalApps: 'Savivaldybių spintelių taikymai:',
      cityApp1: 'Siuntų pristatymo tinklai, mažinantys eismą',
      cityApp2: 'Viešasis saugojimas gyventojams ir turistams',
      cityApp3: 'Paplūdimio ir poilsio zonų paslaugos',
      cityApp4: 'Savivaldybės įrangos valdymas',
      cityApp5: 'Miesto transporto priemonių ir patalpų raktų prieiga',
      learnSmartCity: 'Sužinokite daugiau apie išmaniojo miesto infrastruktūrą',
      networksInfra: 'Spintelių tinklai tampa infrastruktūra',
      networksInfraDesc: 'Kai miestai diegia spintelių tinklus, jie sukuria bendrą infrastruktūrą, kuri tarnauja keliems tikslams—sumažina paskutinės mylios pristatymo eismą, teikia gyventojų paslaugas ir palaiko savivaldybės operacijas.',
      cityBenefit1: 'Sumažina pristatymo transporto priemonių eismą',
      cityBenefit2: 'Įgalina 24/7 gyventojų paslaugas',
      cityBenefit3: 'Palaiko turizmą ir poilsį',
      cityBenefit4: 'Pagerina savivaldybės efektyvumą',
      // Why 0g Baltics
      whyBadge: 'Jūsų partneris',
      whyTitle: 'Kodėl diegti išmaniąsias spinteles su 0g Baltics',
      whyDesc: '0g Baltics derina tvirtą aparatinę įrangą, IoT ryšį, debesų platformas ir atviras integracijas, kad pateiktų spintelių sistemas, skirtas kelioms pramonės šakoms ir didelio masto diegimams.',
      whyFeature1: 'Tvirta aparatinė įranga',
      whyFeature1Desc: 'Pramoninės klasės spintelės, sukurtos didelės apimties operacijoms',
      whyFeature2: 'IoT ryšys',
      whyFeature2Desc: 'LoRaWAN, mobilieji ir WiFi sprendimai bet kuriai aplinkai',
      whyFeature3: 'Debesų platforma',
      whyFeature3Desc: 'Pilnas valdymas, analitika ir pranešimų sistema',
      whyFeature4: 'Atviros integracijos',
      whyFeature4Desc: 'API el. prekybai, logistikai, tapatybei ir mokėjimo sistemoms',
      whyFeature5: 'Daugiašakė patirtis',
      whyFeature5Desc: 'Logistikos, mažmeninės prekybos, pramonės ir viešojo sektoriaus diegimai',
      relatedSolutions: 'Susiję sprendimai',
      assetTrackingLabel: 'Turto sekimas',
      assetTrackingDesc: 'Sekite įrankius ir įrangą už spintelės ribų',
      connectivityLabel: 'IoT ryšys',
      connectivityDesc: 'LoRaWAN, NB-IoT ir mobilieji sprendimai',
      smartCityLabel: 'Išmaniojo miesto infrastruktūra',
      smartCityDesc: 'Miesto IoT diegimai',
      // FAQ
      faqBadge: 'DUK',
      faqTitle: 'Dažnai užduodami klausimai',
      faq1Q: 'Kas yra išmaniosios spintelės?',
      faq1A: 'Išmaniosios spintelės yra sujungtos saugojimo sistemos, leidžiančios saugiai saugoti ir paimti daiktus naudojant skaitmeninį autentifikavimą, tokį kaip PIN kodai, mobiliosios programėlės, RFID kortelės ar QR kodai. Skirtingai nuo tradicinių spintelių su fiziniais raktais, išmaniosios spintelės jungiasi prie debesų platformų, kurios valdo prieigos kontrolę, siunčia pranešimus ir teikia naudojimo analitiką.',
      faq2Q: 'Kaip veikia siuntų spintelės?',
      faq2A: 'Kai siunta pristatoma į spintelę, gavėjas gauna SMS arba el. pašto pranešimą su unikaliu PIN kodu. Jie aplanko spintelės vietą, įveda PIN į jutiklinį ekraną, ir skyrius su jų siunta automatiškai atsidaro. Siuntos paprastai laikomos 7 dienas prieš grąžinant siuntėjui. Bet kas su PIN gali atsiimti—tapatybės tikrinimas nereikalingas.',
      faq3Q: 'Kokios pramonės šakos naudoja išmaniąsias spinteles?',
      faq3A: 'Išmaniosios spintelės naudojamos daugelyje pramonės šakų: logistikoje ir el. prekyboje (siuntų pristatymas), mažmeninėje prekyboje (spustelėk-ir-atsiimk), gamyboje ir statyboje (įrankių valdymas), svetingume ir turizme (paplūdimio spintelės), įmonių biuruose (pašto ir paketų kambariai), sveikatos priežiūroje (mėginių ir vaistų saugojimas) ir savivaldybėse (viešasis saugojimas ir miesto paslaugos).',
      faq4Q: 'Ar spintelės gali veikti be elektros?',
      faq4A: 'Taip. Baterijomis maitinamos spintelės gali veikti ilgą laiką be elektros infrastruktūros. Tai leidžia diegti tokiose vietose kaip paplūdimiai, parkai, laikinos instaliacijos ir atokios vietos, kur laidai būtų brangūs arba nepraktiški. Baterijų spintelės bendrauja per mobiliuosius arba LoRaWAN tinklus.',
      faq5Q: 'Kiek saugios yra išmaniosios spintelės?',
      faq5A: 'Išmaniosios spintelės teikia kelis saugumo lygius: skaitmeninė prieigos kontrolė užtikrina, kad tik autorizuoti vartotojai gali atidaryti skyrius, kiekviena transakcija registruojama sukuriant pilną audito įrašą, skyriai naudoja tvirtus užrakinimo mechanizmus, o debesų platformos leidžia nuotolinį stebėjimą ir įspėjimus. Aukšto saugumo taikymams spintelės gali integruotis su biometriniu autentifikavimu.',
      faq6Q: 'Ar spintelių sistemos gali integruotis su esamomis platformomis?',
      faq6A: 'Taip. Modernios spintelių platformos teikia API, leidžiančias integruotis su el. prekybos platformomis (užsakymų valdymui), tapatybės sistemomis (SSO, LDAP), mokėjimo vartais, logistikos programine įranga (TMS), išmaniojo miesto platformomis ir įmonių ERP sistemomis. Tai leidžia spintelėms tapti platesnių verslo darbo eigų dalimi, o ne atskiromis sistemomis.',
      ctaTitle: 'Įdiekite išmaniąsias spinteles savo organizacijoje',
      ctaButton: 'Sužinoti daugiau',
    },

    buildingEnergyPage: {
      metaTitle: 'Pastatų energetinis efektyvumas | IoT sprendimai | 0g Baltics',
      metaDesc: 'Sumažinkite pastatų energijos suvartojimą su IoT stebėjimu ir išmaniaisiais valdikliais.',
      heroBadge: 'Energetinis efektyvumas',
      heroTitle: 'Pastatų energetinio efektyvumo sprendimai',
      heroSubtitle: 'Stebėkite ir optimizuokite energijos suvartojimą komerciniuose pastatuose su IoT jutikliais ir išmaniaisiais valdikliais.',
      // Hero badges
      heroBadge1: 'Energijos stebėjimas',
      heroBadge2: 'ŠVOK optimizavimas',
      heroBadge3: 'Užimtumo aptikimas',
      heroBadge4: 'Analitika',
      heroBadge5: 'Išlaidų mažinimas',
      // Problem
      problemBadge: 'Iššūkis',
      problemTitle: 'Pastatai švaisto energiją',
      problemDesc: 'Komerciniai pastatai sudaro 40% energijos suvartojimo. Dauguma veikia neefektyviai.',
      hvacBadge: 'Didžiausia galimybė',
      hvacTitle: 'ŠVOK optimizavimas',
      hvacDesc: 'ŠVOK sistemos sudaro 40-60% pastatų energijos suvartojimo.',
      hvacParadoxTitle: 'Dabartinių reguliavimo sistemų paradoksas',
      hvacParadoxDesc: 'Tradicinės ŠVOK reguliavimo sistemos paradoksaliai dažnai lemia didesnį energijos suvartojimą.',
      hvacProblem1: 'Įjungimo/išjungimo ciklai sukuria temperatūros svyravimus',
      hvacProblem2: 'Fiksuoti grafikai ignoruoja faktinius naudojimo modelius',
      hvacProblem3: 'Nėra koreliacijos tarp CO2 ir ventiliacijos',
      hvacProblem4: 'Sistemos kompensuoja per didelę įrangą',
      harmonySolution: 'Išmanusis sprendimas',
      harmonyTitle: 'TEKTELIC Harmony',
      harmonyDesc: 'Išmanusis termostatas, sujungiantis CO2 stebėjimą, temperatūros/drėgmės jutiklius ir ŠVOK valdymą.',
      harmonyBenefit: 'Koreliuodamas CO2 lygius su ventiliacijos poreikiais ir faktiniu užimtumu, Harmony optimizuoja ŠVOK veikimą, sumažindamas energijos suvartojimą 20-40%.',
      // Invisibility section
      invisibilityBadge: 'Pagrindinis iššūkis',
      invisibilityTitle: 'Energijos suvartojimas dažnai nematomas',
      invisibilityDesc: 'Daugelis organizacijų neturi aiškaus matymo, kaip jų pastatai iš tikrųjų vartoja energiją.',
      invisibility1: 'Kuri įranga suvartoja daugiausiai energijos',
      invisibility2: 'Kada vyksta suvartojimo pikai',
      invisibility3: 'Kur yra neefektyvumas',
      invisibility4: 'Ar suvartojimas didėja',
      withoutMonitoring: 'Be stebėjimo',
      withMonitoring: 'Su IoT stebėjimu',
      // Stats
      stat1: 'pasaulinės energijos suvartoja pastatai',
      stat2: 'tipinis energijos švaistymas komerciniuose pastatuose',
      stat3: 'sutaupymai pasiekiami su išmaniuoju stebėjimu',
      stat4: 'tipinis atsipirkimo laikotarpis',
      // IoT Monitoring Section
      iotMonitoringBadge: 'Technologijų pagrindas',
      iotMonitoringTitle: 'IoT energijos stebėjimo sistemos',
      iotMonitoringDesc: 'IoT jutikliai leidžia nuolat stebėti pastatų energijos suvartojimą detaliu lygiu. Skirtingai nuo tradicinės komunalinių paslaugų apskaitos, teikiančios tik mėnesio bendrus duomenis, išmanieji jutikliai fiksuoja išsamią informaciją apie tai, kaip, kada ir kur energija vartojama.',
      iotDevicesMeasure: 'IoT įrenginiai gali matuoti:',
      measureElectricity: 'Elektra',
      measureElectricityDesc: 'Bendras ir grandinės lygio suvartojimas',
      measureWater: 'Vanduo',
      measureWaterDesc: 'Naudojimo modeliai ir nuotėkių aptikimas',
      measureTemperature: 'Temperatūra',
      measureTemperatureDesc: 'Šiluminiai duomenys pagal zonas',
      measureHumidity: 'Drėgmė',
      measureHumidityDesc: 'Komfortas ir sistemos veikimas',
      measureOccupancy: 'Užimtumas',
      measureOccupancyDesc: 'Erdvės naudojimo modeliai',
      measureEquipment: 'Įranga',
      measureEquipmentDesc: 'Veikimo laikas ir operacinė būsena',
      iotDataTransmission: 'Šie duomenys perduodami belaidžiu būdu per LoRaWAN ar mobiliuosius tinklus į skaitmenines platformas, kur operatoriai gali analizuoti suvartojimo modelius ir nustatyti optimizavimo galimybes.',
      // Energy Analytics Section
      analyticsBadge: 'Duomenimis pagrįsti sprendimai',
      analyticsTitle: 'Energijos analitika ir optimizavimas',
      analyticsDesc: 'Surinkus energijos duomenis, analitikos įrankiai padeda nustatyti neefektyvumus ir optimizavimo galimybes. Modernios energijos valdymo platformos taiko algoritmus, kad aptiktų modelius, anomalijas ir koreliacijas, kurias būtų neįmanoma nustatyti rankiniu būdu.',
      typicalInsights: 'Tipinės įžvalgos apima:',
      insight1: 'Nenormalus energijos suvartojimas, rodantis įrangos problemas',
      insight2: 'Įranga veikianti ne darbo valandomis',
      insight3: 'Neefektyvus šildymo ar vėsinimo sistemos veikimas',
      insight4: 'Prastas pastato planavimas, švaistantis energiją',
      insight5: 'Suvartojimo tendencijos, reikalaujančios dėmesio',
      analyticsConclusion: 'Organizacijos gali naudoti šias įžvalgas, kad nustatytų patobulinimų prioritetus, patikrintų pokyčių poveikį ir nuolat optimizuotų pastatų veikimą.',
      // Occupancy Section
      occupancyBadge: 'Paklausos valdoma kontrolė',
      occupancyTitle: 'Užimtumu pagrįstas energijos valdymas',
      occupancyDesc: 'Pastatai dažnai šildomi, vėsinami ir apšviečiami net tada, kai erdvės visiškai tuščios. Konferencijų salės paruoštos susitikimams, kurie nevyksta, biurai kondicionuojami savaitgaliais, ištisi aukštai apšviesti naktį—tai reiškia didelį energijos švaistymą.',
      occupancyDesc2: 'Užimtumo jutikliai leidžia pastatams pritaikyti energijos suvartojimą faktiniams naudojimo modeliams. Kai erdvės neužimtos, apšvietimas gali pritemdyti arba išsijungti, ŠVOK gali sumažinti galią, o įranga gali pereiti į budėjimo režimą. Kai žmonės atvyksta, sistemos automatiškai reaguoja.',
      highImpactApps: 'Didelio poveikio taikymai:',
      appMeetingRooms: 'Posėdžių salės',
      appClassrooms: 'Klasės',
      appIndividualOffices: 'Individualūs biurai',
      appPublicBuildings: 'Viešieji pastatai',
      appCommonAreas: 'Bendros erdvės',
      appParkingFacilities: 'Automobilių stovėjimo aikštelės',
      // Sensors Section
      sensorsBadge: 'Jutiklių infrastruktūra',
      sensorsTitle: 'Jutikliai energiškai efektyviems pastatams',
      sensorsDesc: 'Energijos optimizavimas remiasi kelių tipų jutikliais, veikiančiais kartu, kad sukurtų pilną pastato sąlygų vaizdą. Kiekvienas jutiklis teikia specifinius duomenis, prisidedančius prie bendro energijos intelekto.',
      sensorTemp: 'Temperatūros jutikliai',
      sensorTempDesc: 'Stebėkite šilumines sąlygas zonose, kad optimizuotumėte šildymą ir vėsinimą. Nustatykite karštus ir šaltus taškus, rodančius ŠVOK disbalansą.',
      sensorHumidity: 'Drėgmės jutikliai',
      sensorHumidityDesc: 'Sekite drėgmės lygius, veikiančius komfortą ir sistemos veikimą. Optimizuokite drėkinimą ir išvenkite pelėsio sąlygų.',
      sensorCO2: 'CO2 jutikliai',
      sensorCO2Desc: 'Matuokite oro kokybę ir ventiliacijos pakankamumą. Įgalinkite paklausos valdomą ventiliaciją, balansuojančią oro kokybę ir energiją.',
      sensorOccupancy: 'Užimtumo jutikliai',
      sensorOccupancyDesc: 'Aptikite erdvės naudojimą realiu laiku. Įgalinkite automatinį apšvietimo, ŠVOK ir įrangos reguliavimą pagal buvimą.',
      sensorEnergy: 'Energijos skaitikliai',
      sensorEnergyDesc: 'Matuokite elektros suvartojimą pastato, aukšto ar grandinės lygiu. Nustatykite didelio suvartojimo įrangą ir naudojimo modelius.',
      sensorWater: 'Vandens skaitikliai',
      sensorWaterDesc: 'Sekite vandens suvartojimą ir aptikite nuotėkius. Nustatykite nenormalų naudojimą, rodantį vandentiekio problemas ar švaistymą.',
      learnMoreIAQ: 'Sužinokite daugiau apie vidaus oro kokybės stebėjimą',
      // Standards Section
      standardsBadge: 'Reguliavimo kontekstas',
      standardsTitle: 'Energetinio efektyvumo standartai ir reglamentai',
      standardsDesc: 'Daugelis šalių dabar reikalauja geresnio pastatų energetinio efektyvumo. Reguliavimo sistemos griežtėja, ir organizacijoms vis dažniau reikia stebėjimo sistemų, kad palaikytų atitiktį ir tvarumo ataskaitas.',
      epbdTitle: 'ES Pastatų energetinio naudingumo direktyva (EPBD)',
      epbdDesc: 'Reikalauja valstybių narių nustatyti minimalius pastatų energetinio naudingumo reikalavimus ir įgyvendinti energetinio sertifikavimo sistemas. Direktyva skatina beveik nulinės energijos pastatus.',
      epcTitle: 'Energetinio naudingumo sertifikatai (EPC)',
      epcDesc: 'Pastatai turi būti vertinami pagal energetinį efektyvumą, sertifikatai reikalingi pardavimui ar nuomai daugelyje jurisdikcijų. Geresni reitingai gali padidinti turto vertę ir nuomos patrauklumą.',
      esgTitle: 'ESG ir tvarumo ataskaitos',
      esgDesc: 'Įmonių tvarumo ataskaitos vis dažniau reikalauja išsamių energijos suvartojimo duomenų. Stebėjimo sistemos teikia patikrinamus duomenis, reikalingus ESG atskleidimams ir tvarumo sertifikatams.',
      monitoringSupports: 'Energijos stebėjimas palaiko:',
      support1: 'Reguliavimo atitikties dokumentaciją',
      support2: 'Energetinio naudingumo sertifikatų tobulinimą',
      support3: 'ESG ir tvarumo ataskaitas',
      support4: 'Anglies pėdsako skaičiavimą',
      support5: 'Žaliųjų pastatų sertifikavimą (LEED, BREEAM)',
      support6: 'Audito ir tikrinimo procesus',
      // ROI Section
      roiBadge: 'Verslo poveikis',
      roiTitle: 'Energijos stebėjimo verslo pagrindimas',
      roiDesc: 'Energijos stebėjimas teikia išmatuojamą, patikrinamą naudą. Organizacijos, įdiegusios išsamias stebėjimo sistemas, paprastai mato reikšmingą grąžą per sumažintas energijos išlaidas, pagerintas operacijas ir išvengtus įrangos gedimus.',
      typicalBenefits: 'Tipinė nauda',
      benefit1: '10-25% energijos išlaidų sumažinimas',
      benefit2: 'Prailgintas įrangos tarnavimo laikas',
      benefit3: 'Sumažintos priežiūros išlaidos',
      benefit4: 'Pagerintas gyventojų komfortas',
      benefit5: 'Geresnis operacinis matomumas',
      benefit6: 'Duomenys strateginiams sprendimams',
      investmentTitle: 'Investicijų apsvarstymai',
      investment1: 'Mažos kainos belaidžiai jutikliai',
      investment2: 'Minimalūs montavimo trikdžiai',
      investment3: 'Plečiamas diegimo būdas',
      investment4: 'Debesų platforma—be IT infrastruktūros',
      investment5: 'Išmatuojama ROI per 2-3 metus',
      investment6: 'Nuolatiniai operaciniai sutaupymai',
      roiNote: 'Net maži patobulinimai svarbūs: 10% energijos suvartojimo sumažinimas pastatui su €100 000 metinių energijos išlaidų generuoja €10 000 metinių sutaupymų—kaupiamų metai po metų.',
      // Why 0g Baltics Section
      whyBadge: 'Jūsų partneris',
      whyTitle: 'Kodėl dirbti su 0g Baltics',
      whyDesc: '0g Baltics teikia išsamius energijos stebėjimo sprendimus, sujungiančius profesionalius IoT jutiklius, patikimą ryšio infrastruktūrą ir galingas analitikos platformas. Mūsų tikslas—padėti organizacijoms sumažinti energijos suvartojimą ir pagerinti pastatų veikimą.',
      whyFeature1: 'IoT jutikliai pastatams',
      whyFeature1Desc: 'Temperatūros, drėgmės, užimtumo, energijos ir aplinkos stebėjimas',
      whyFeature2: 'Energijos stebėjimo sistemos',
      whyFeature2Desc: 'Realaus laiko suvartojimo sekimas ir analitika',
      whyFeature3: 'LoRaWAN ryšys',
      whyFeature3Desc: 'Patikima, mažos galios belaidė infrastruktūra',
      whyFeature4: 'Duomenų platformos ir analitika',
      whyFeature4Desc: 'Įžvalgos ir optimizavimo rekomendacijos',
      whyFeature5: 'Išmaniųjų pastatų konsultacijos',
      whyFeature5Desc: 'Strategijos ir diegimo gairės',
      relatedSolutions: 'Susiję sprendimai',
      relatedIAQ: 'Vidaus oro kokybė',
      relatedIAQDesc: 'CO2, temperatūros, drėgmės stebėjimas',
      relatedSmartCity: 'Išmaniojo miesto infrastruktūra',
      relatedSmartCityDesc: 'Miesto pastatų diegimai',
      relatedConnectivity: 'IoT ryšys',
      relatedConnectivityDesc: 'LoRaWAN ir mobilieji variantai',
      relatedMetering: 'Išmanioji apskaita',
      relatedMeteringDesc: 'Vandens ir energijos apskaita',
      // CTA
      ctaTitle: 'Pagerinkite savo pastato energetinį efektyvumą',
      ctaDesc: 'Susisiekite su 0g Baltics, kad įdiegtumėte išmanias energijos stebėjimo sistemas savo pastatuose. Nesvarbu, ar valdote biurus, universitetus ar pramonės objektus, galime padėti sumažinti suvartojimą ir optimizuoti operacijas.',
      ctaButton: 'Kalbėkite su mūsų komanda',
    },

    officeSpacePage: {
      metaTitle: 'Biuro erdvės valdymas | Užimtumo analitika | 0g Baltics',
      metaDesc: 'Optimizuokite biuro erdvės panaudojimą su IoT užimtumo jutikliais.',
      heroBadge: 'Biuro valdymas',
      heroTitle: 'Išmanusis biuro erdvės valdymas',
      heroSubtitle: 'Optimizuokite darbo vietos panaudojimą su užimtumo jutikliais, stalų rezervavimo sistemomis ir analitika.',
      // Hero badges
      heroBadge1: 'Užimtumo aptikimas',
      heroBadge2: 'Stalų stebėjimas',
      heroBadge3: 'Posėdžių salių analitika',
      heroBadge4: 'Hibridinis darbas',
      // What Is Section
      whatIsBadge: 'Iššūkio supratimas',
      whatIsTitle: 'Kas yra biuro erdvės valdymas?',
      whatIsDesc: 'Biuro erdvės valdymas yra praktika suprasti, stebėti ir optimizuoti, kaip naudojamos fizinės darbo vietos.',
      driversTitle: 'Pagrindiniai išmaniojo biuro valdymo veiksniai:',
      driver1: 'Hibridinio darbo modeliai',
      driver2: 'Lankstūs biuro išdėstymai',
      driver3: 'Bendro naudojimo darbo vietų politikos',
      driver4: 'Posėdžių salių optimizavimas',
      visibilityTitle: 'IoT jutikliai suteikia matomumą:',
      visibility1: 'Realaus laiko užimtumas',
      visibility1Desc: 'Kurios erdvės šiuo metu naudojamos',
      visibility2: 'Naudojimo modeliai',
      visibility2Desc: 'Piko valandos, ramūs periodai, dienos tendencijos',
      visibility3: 'Erdvės efektyvumas',
      visibility3Desc: 'Panaudojimo rodikliai pagal zonas',
      visibility4: 'Aplinkos sąlygos',
      visibility4Desc: 'Temperatūra, oro kokybė, komfortas',
      // Hidden Costs Section
      costBadge: 'Verslo problema',
      costTitle: 'Paslėptos tuščios biuro erdvės išlaidos',
      costDesc: 'Tyrimai nuolat rodo, kad daugelis įmonių bet kuriuo metu turi 30-60% nepanaudotų biuro pajėgumų.',
      costsTitle: 'Veiklos išlaidos už tuščią erdvę:',
      cost1: 'Nuomos ir nuomos išlaidos',
      cost2: 'Šildymo išlaidos',
      cost3: 'Vėsinimas ir elektra',
      cost4: 'Apšvietimo išlaidos',
      stat1: 'tipinis nepanaudotas biuro pajėgumas',
      stat2: 'išlaidos už m² per metus (ES vidurkis)',
      stat3: 'potencialūs sutaupymai su optimizavimu',
      stat4: 'tipinė ROI išmanioms biuro sistemoms',
      // Smart Sensors Section
      sensorsBadge: 'Jutiklių technologija',
      sensorsTitle: 'Išmanieji biuro jutikliai',
      sensorsDesc: 'Skirtingų tipų IoT jutikliai atlieka skirtingas funkcijas biuro erdvės valdyme. Visi jutikliai renka anoniminius naudojimo duomenis—be asmeninės identifikacijos ar stebėjimo—teikdami apibendrintas įžvalgas, kurios gerbia darbuotojų privatumą.',
      occupancySensors: 'Užimtumo jutikliai',
      occupancySensorsDesc: 'Ant lubų montuojami jutikliai aptinka buvimą ir skaičiuoja žmones tokiose zonose kaip atviri aukštai, bendros erdvės ir didelės posėdžių salės. Teikia zonos lygio užimtumo duomenis.',
      deskSensors: 'Stalų jutikliai',
      deskSensorsDesc: 'Maži jutikliai, montuojami po stalais, aptinka, ar atskiros darbo vietos yra užimtos. Įgalina tikslų stalo po stalo panaudojimo sekimą bendro naudojimo aplinkose.',
      meetingRoomSensors: 'Posėdžių salių jutikliai',
      meetingRoomSensorsDesc: 'Specializuoti jutikliai posėdžių salėms seka faktinį naudojimą prieš kalendoriaus rezervacijas, aptinka neatvykusius ir identifikuoja vaiduokliškus susitikimus, kurie švaisto vertingą erdvę.',
      bestFor: 'Geriausiai tinka:',
      openPlanOffices: 'Atviro plano biurai',
      commonAreas: 'Bendros erdvės',
      cafeterias: 'Kavinės',
      largeMeetingRooms: 'Didelės posėdžių salės',
      hotDeskingAreas: 'Bendro naudojimo darbo vietų zonos',
      assignedDesks: 'Priskirti stalai',
      focusBooths: 'Susitelkimo kabinos',
      phoneRooms: 'Telefono kabinos',
      conferenceRooms: 'Konferencijų salės',
      huddleSpaces: 'Mažos susitikimų erdvės',
      videoCallRooms: 'Video skambučių kambariai',
      executiveSuites: 'Vadovų kabinetai',
      // IAQ Section
      iaqBadge: 'Aplinkos intelektas',
      iaqTitle: 'Vidaus oro kokybės stebėjimas',
      iaqDesc: 'Išmanieji biurai turėtų stebėti daugiau nei tik užimtumą. Aplinkos sąlygos tiesiogiai veikia darbuotojų komfortą, produktyvumą ir gerovę. Prasta oro kokybė, nekomfortiškos temperatūros ar didelė drėgmė sukuria neoptimalias darbo sąlygas.',
      iaqDesc2: 'Aplinkos jutiklių integravimas su užimtumo duomenimis suteikia pilną vaizdą apie darbo vietos sąlygas, leidžiant pastatų valdytojams optimizuoti tiek erdvės panaudojimą, tiek komfortą.',
      co2Levels: 'CO2 lygiai',
      co2LevelsDesc: 'Rodo ventiliacijos pakankamumą ir oro šviežumą',
      temperature: 'Temperatūra',
      temperatureDesc: 'Šiluminio komforto stebėjimas pagal zonas',
      humidity: 'Drėgmė',
      humidityDesc: 'Veikia komfortą ir pastato sveikatą',
      ventilation: 'Ventiliacija',
      ventilationDesc: 'Oro kokybės ir cirkuliacijos vertinimas',
      impactTitle: 'Poveikis darbo vietos veikimui',
      impactStat1: 'produktyvumo padidėjimas su pagerinta oro kokybe',
      impactStat2: 'nedarbingumo dienų sumažėjimas su geresne ventiliacija',
      impactStat3: 'pažintinių funkcijų pagerėjimas su žemesniu CO2 lygiu',
      learnMoreIAQ: 'Sužinokite daugiau apie IAQ stebėjimą',
      // Analytics Platform Section
      analyticsBadge: 'Duomenimis pagrįstos įžvalgos',
      analyticsTitle: 'Biuro analitikos platforma',
      analyticsDesc: 'Visi jutikliai jungiasi prie debesų analitikos platformos, kuri paverčia neapdorotus duomenis į veiksmingus įžvalgas. Pastatų valdytojai įgyja pilną matomumą apie tai, kaip jų biuro erdvės iš tikrųjų naudojamos, leidžiant priimti įrodymais pagrįstus sprendimus.',
      platformCapabilities: 'Platformos galimybės:',
      realTimeDashboards: 'Realaus laiko prietaisų skydeliai',
      realTimeDashboardsDesc: 'Tiesioginis vaizdas į dabartinį užimtumą visose zonose',
      occupancyHeatmaps: 'Užimtumo šilumos žemėlapiai',
      occupancyHeatmapsDesc: 'Vizualus aukšto ir žemo naudojimo zonų vaizdavimas',
      meetingRoomAnalytics: 'Posėdžių salių analitika',
      meetingRoomAnalyticsDesc: 'Rezervacija prieš faktinį naudojimą, neatvykusių rodikliai',
      historicalTrends: 'Istorinės tendencijos',
      historicalTrendsDesc: 'Dienos, savaitės, mėnesio naudojimo modeliai',
      spaceEfficiencyMetrics: 'Erdvės efektyvumo rodikliai',
      spaceEfficiencyMetricsDesc: 'Panaudojimo rodikliai ir optimizavimo galimybės',
      // Hybrid Workplace Section
      hybridBadge: 'Modernūs darbo modeliai',
      hybridTitle: 'Hibridinio darbo vietos valdymas',
      hybridDesc: 'Biuro analitika tampa dar svarbesnė hibridinio darbo aplinkose, kur darbuotojai dalina laiką tarp biuro ir nuotolinio darbo. Faktinių naudojimo modelių supratimas padeda organizacijoms teisingai pasirinkti savo nekilnojamąjį turtą ir projektuoti erdves, kurios palaiko lankstų darbą.',
      deskBookingSystems: 'Stalų rezervavimo sistemos',
      deskBookingSystemsDesc: 'Leidžia darbuotojams iš anksto rezervuoti stalus, su realaus laiko prieinamumu, rodančiu, kurios vietos yra laisvos. Užimtumo jutikliai patvirtina rezervacijas ir automatiškai atleidžia neatvykusius.',
      meetingRoomAvailability: 'Posėdžių salių prieinamumas',
      meetingRoomAvailabilityDesc: 'Rodo realaus laiko prieinamumą pagal faktinį užimtumą, o ne tik kalendoriaus rezervacijas. Identifikuoja ir atleidžia vaiduokliškus susitikimus, padarydami daugiau kambarių prieinamų produktyviam naudojimui.',
      spacePlanning: 'Erdvės planavimas',
      spacePlanningDesc: 'Naudokite istorinius duomenis planuoti biuro išdėstymus, atitinkančius faktinę paklausą. Nustatykite optimalius stalų ir darbuotojų santykius, bendradarbiavimo erdves ir ramias zonas.',
      // Benefits Section
      benefitsBadge: 'Verslo poveikis',
      benefitsTitle: 'Nauda įmonėms',
      benefitsDesc: 'Išmanusis biuro erdvės valdymas teikia išmatuojamą naudą keliose dimensijose—nuo tiesioginių išlaidų sutaupymų iki pagerintos darbuotojų patirties ir tvarumo rezultatų.',
      reducedCosts: 'Sumažintos nekilnojamojo turto išlaidos',
      reducedCostsDesc: 'Identifikuokite nepilnai panaudotą erdvę ir teisingai pasirinkite savo biuro plotą. Daugelis organizacijų sumažina nuomojamą erdvę 15-30% įdiegus analitiką.',
      betterExperience: 'Geresnė darbuotojų patirtis',
      betterExperienceDesc: 'Padėkite darbuotojams lengvai rasti laisvus stalus ir posėdžių sales. Sumažinkite trintį ir nusivylimą darbo vietoje.',
      higherProductivity: 'Didesnis produktyvumas',
      higherProductivityDesc: 'Optimizuokite biuro išdėstymus pagal tai, kaip žmonės iš tikrųjų dirba. Sukurkite tinkamą bendradarbiavimo ir susitelkimo erdvių derinį.',
      dataDrivenPlanning: 'Duomenimis pagrįstas planavimas',
      dataDrivenPlanningDesc: 'Pakeiskite prielaidas faktais. Priimkite nekilnojamojo turto ir darbo vietos sprendimus pagal faktinius naudojimo duomenis.',
      sustainabilityImprovements: 'Tvarumo patobulinimai',
      sustainabilityImprovementsDesc: 'Sumažinkite energijos švaistymą kondicionuodami tik užimtas erdves. Palaikykite ESG tikslus su išmatuojamais efektyvumo pagerėjimais.',
      operationalVisibility: 'Veiklos matomumas',
      operationalVisibilityDesc: 'Supraskite savo darbo vietą kaip niekada anksčiau. Pastatų valdytojai įgyja įrankius nuolatiniam optimizavimui ir tobulinimui.',
      // CTA
      ctaTitle: 'Optimizuokite savo darbo vietą',
      ctaDesc: 'Susisiekite su 0g Baltics, kad suprojektuotumėte išmaniuosius biuro sprendimus, kurie pagerina efektyvumą ir sumažina išlaidas. Nesvarbu, ar valdote įmonių būstines, universitetų miestelius ar komercinius pastatus, galime padėti.',
      ctaButton: 'Kalbėkite su mūsų komanda',
      // Use Cases Section
      useCasesBadge: 'Pritaikymai',
      useCasesTitle: 'Naudojimo atvejai',
      useCasesDesc: 'Išmanieji biuro sprendimai aptarnauja organizacijas įvairiose pramonėse ir dydžiuose, nuo įmonių būstinių iki švietimo įstaigų.',
      corporateHQ: 'Įmonių būstinė',
      corporateHQDesc: 'Dideli verslai naudoja biuro analitiką daugiaukštėms būstinėms optimizuoti, bendro naudojimo darbo vietų programoms valdyti ir hibridinio darbo politikoms tūkstančiams darbuotojų palaikyti.',
      techCompanies: 'Technologijų įmonės',
      techCompaniesDesc: 'Technologijų įmonės su lanksčia darbo kultūra remiasi stalų rezervavimu ir užimtumo duomenimis, kad sukurtų dinamiškas darbo vietas, prisitaikančias prie besikeičiančių komandos poreikių.',
      governmentBuildings: 'Vyriausybės pastatai',
      governmentBuildingsDesc: 'Viešojo sektoriaus organizacijos naudoja analitiką mokesčių mokėtojų finansuojamoms patalpoms optimizuoti ir efektyviam viešųjų išteklių naudojimui parodyti.',
      universities: 'Universitetai',
      universitiesDesc: 'Švietimo įstaigos stebi paskaitas sales, bibliotekas ir mokymosi erdves, kad optimizuotų tvarkaraščius ir suprastų studentų naudojimo modelius.',
      coworkingSpaces: 'Bendro naudojimo erdvės',
      coworkingSpacesDesc: 'Lanksčių darbo erdvių operatoriai naudoja realaus laiko užimtumą stalų prieinamumui valdyti, išdėstymams optimizuoti ir tiksliems pajėgumo duomenims nariams teikti.',
      commercialRealEstate: 'Komercinis nekilnojamasis turtas',
      commercialRealEstateDesc: 'Turto valdytojai siūlo pastatų analitiką kaip nuomininko patogumą, teikdami užimtumo įžvalgas, kurios padeda verslams optimizuoti jų nuomojamas erdves.',
      // IoT Connectivity Section
      iotConnectivityBadge: 'Tinklo infrastruktūra',
      iotConnectivityTitle: 'IoT ryšys išmaniesiems pastatams',
      iotConnectivityDesc: '0g Baltics naudoja mažos galios IoT tinklus, sukurtus pastatų aplinkoms. Šie tinklai įgalina belaidį jutiklių diegimą be sudėtingo laidų vedimo, su akumuliatoriaus tarnavimo laiku, matuojamu metais, o ne mėnesiais.',
      connectivityOptions: 'Palaikomi ryšio variantai:',
      lorawanName: 'LoRaWAN',
      lorawanDesc: 'Didelis nuotolis, mažas energijos suvartojimas, idealus dideliems pastatams',
      nbiotName: 'NB-IoT',
      nbiotDesc: 'Korinio ryšio pagrindu, gilus patalpų prasiskverbimas',
      ltemName: 'LTE-M',
      ltemDesc: 'Mobilūs turtai ir didesnio pralaidumo poreikiai',
      wifiName: 'Wi-Fi',
      wifiDesc: 'Integracija su esama IT infrastruktūra',
      keyAdvantages: 'Pagrindiniai privalumai:',
      longBatteryLife: 'Ilgas akumuliatoriaus tarnavimo laikas',
      longBatteryLifeDesc: '5-10 metų tipinė jutiklio veikla',
      lowMaintenance: 'Maža priežiūra',
      lowMaintenanceDesc: 'Įdiekite ir pamiršite instaliacija',
      secureCommunication: 'Saugus ryšys',
      secureCommunicationDesc: 'Šifruotas duomenų perdavimas',
      scalableDeployment: 'Keičiamo dydžio diegimas',
      scalableDeploymentDesc: 'Nuo bandomojo projekto iki viso pastato diegimo',
      learnMoreConnectivity: 'Sužinokite daugiau apie IoT ryšį',
      // Why 0g Baltics Section
      whyBadge: 'Jūsų partneris',
      whyTitle: 'Kodėl 0g Baltics',
      whyDesc: '0g Baltics teikia pilnus išmaniojo biuro sprendimus—nuo IoT jutiklių iki debesų analitikos platformų. Jungiame aparatinės įrangos ekspertizę, ryšio infrastruktūrą ir analitikos galimybes į integruotus sprendimus darbo vietos optimizavimui.',
      service1: 'IoT jutikliai',
      service1Desc: 'Užimtumo, stalų, posėdžių salių ir aplinkos jutikliai',
      service2: 'Ryšio infrastruktūra',
      service2Desc: 'LoRaWAN, korinio ryšio ir hibridinio tinklo variantai',
      service3: 'Debesų platformos',
      service3Desc: 'Analitikos prietaisų skydeliai ir ataskaitų įrankiai',
      service4: 'Sistemų integracija',
      service4Desc: 'Sujungimas su esamomis pastato ir IT sistemomis',
      service5: 'Išmaniųjų pastatų patirtis',
      service5Desc: 'Ekspertizė iš išmaniojo miesto ir pastatų projektų',
      relatedSolutions: 'Susiję sprendimai',
      relatedSmartCity: 'Išmaniojo miesto infrastruktūra',
      relatedSmartCityDesc: 'Miesto ir pastatų IoT tinklai',
      relatedSmartLockers: 'Išmanieji spinteliai',
      relatedSmartLockersDesc: 'Saugūs saugojimo sprendimai',
      relatedAssetTracking: 'Turto sekimas',
      relatedAssetTrackingDesc: 'Įrangos ir išteklių stebėjimas',
      relatedConnectivity: 'IoT ryšys',
      relatedConnectivityDesc: 'LoRaWAN ir korinio ryšio tinklai',
    },

    assetTrackingPage: {
      metaTitle: 'Turto sekimo sprendimai | GPS ir IoT | 0g Baltics',
      metaDesc: 'Sekite turtą patalpose ir lauke su LoRaWAN, GPS, BLE ir mobiliuoju ryšiu.',
      heroBadge: 'Turto sekimas',
      heroTitle: 'Turto sekimo sprendimai',
      heroSubtitle: 'Sekite įrangą, transporto priemones ir inventorių su vidaus ir lauko pozicionavimo sprendimais.',
      indoorTracking: 'Vidaus sekimas',
      indoorTrackingDesc: 'BLE ir LoRaWAN pagrįstas pozicionavimas sandėliams ir objektams.',
      outdoorTracking: 'Lauko sekimas',
      outdoorTrackingDesc: 'GPS ir mobilusis sekimas transporto priemonėms ir mobiliajam turtui.',
      hybridTracking: 'Hibridinis sekimas',
      hybridTrackingDesc: 'Sklandus vidaus-lauko sekimas su automatiniu perjungimu.',
      industriesBadge: 'Pramonės šakos',
      logistics: 'Logistika',
      logisticsDesc: 'Sekite siuntas, konteinerius ir transporto priemonių parką.',
      healthcare: 'Sveikatos priežiūra',
      healthcareDesc: 'Medicinos įrangos sekimas ligoninėse ir klinikose.',
      manufacturing: 'Gamyba',
      manufacturingDesc: 'Įrankių ir nebaigtos gamybos sekimas gamykloje.',
      ctaTitle: 'Pradėkite sekti savo turtą',
      ctaButton: 'Prašyti demonstracijos',
      // What Is Section
      whatIsBadge: 'Technologijos supratimas',
      whatIsTitle: 'Kas yra IoT turto sekimas?',
      whatIsDesc: 'IoT turto sekimas leidžia įmonėms stebėti fizinio turto vietą, būseną ir naudojimą realiu laiku.',
      assetsTitle: 'Sekamas turtas apima:',
      asset1: 'Statybos įranga',
      asset2: 'Logistikos konteineriai',
      asset3: 'Transporto priemonės ir priekabos',
      asset4: 'Pramoniniai įrankiai',
      asset5: 'Medicinos įranga',
      asset6: 'Temperatūrai jautrūs kroviniai',
      // Why Section
      whyBadge: 'Verslo poveikis',
      whyTitle: 'Kodėl įmonės diegia turto sekimą',
      whyDesc: 'Organizacijos diegia turto sekimą, kad išspręstų kritines veiklos problemas.',
      benefit1: 'Turto vagysčių prevencija',
      benefit1Desc: 'Sumažinkite vagystes 60%+',
      benefit2: 'Panaudojimo optimizavimas',
      benefit2Desc: 'Padidinkite turto naudojimą 25%+',
      benefit3: 'Priežiūros planavimas',
      benefit3Desc: 'Užkirskite kelią brangiems gedimams',
      benefit4: 'Vietos matomumas',
      benefit4Desc: 'Žinokite, kur yra turtas 24/7',
      // Multi-Connectivity Section
      networkBadge: 'Tinklo architektūra',
      networkTitle: 'Kodėl daugelio ryšių palaikymas yra būtinas',
      networkDesc: 'Daugelis IoT diegimų žlunga, kai remiasi viena ryšio technologija. Turtas nelieka vienoje vietoje—jis juda tarp pramoninių teritorijų, miesto aplinkos, logistikos maršrutų ir atokių vietų, kur tinklo aprėptis dramatiškai kinta.',
      networkDesc2: '0g Baltics projektuoja daugelio ryšių architektūras, jungiančias kelias technologijas. Įrenginiai automatiškai pasirenka geriausią prieinamą tinklą priklausomai nuo aplinkos, užtikrindami nepertraukiamą turto matomumą nepriklausomai nuo vietos.',
      lorawanName: 'LoRaWAN',
      lorawanDesc: 'Ilgo nuotolio, mažos energijos teritorijos ir regioninei aprėpčiai',
      nbiotName: 'NB-IoT / LTE-M',
      nbiotDesc: 'Mobilusis ryšys miesto ir mobiliajam turtui',
      bleName: 'BLE švyturiai',
      bleDesc: 'Vidaus pozicionavimas ir artumo sekimas',
      satelliteName: 'Palydovas',
      satelliteDesc: 'Globalus aprėptis atokioms ir už tinklo ribų esančioms vietoms',
      // Tracking Devices Section
      devicesBadge: 'Aparatinės įrangos sprendimai',
      devicesTitle: 'Profesionalūs turto sekimo įrenginiai',
      devicesDesc: '0g Baltics integruoja profesionalios klasės sekimo įrenginius iš pirmaujančių gamintojų, tokių kaip Digital Matter. Šie įrenginiai suteikia GPS sekimą, palydovinį ryšį, mobilųjį ryšį, BLE vartų galimybes ir kelių metų baterijos veikimo laiką—sukurti pramoninėms aplinkoms ir globaliam diegimui.',
      gpsTracking: 'GPS sekimas',
      gpsTrackingDesc: 'Aukšto tikslumo lauko pozicionavimas su GNSS',
      satelliteConnectivity: 'Palydovinis ryšys',
      satelliteConnectivityDesc: 'Iridium palydovas atokioms vietoms',
      cellularNetworks: 'Mobilieji tinklai',
      cellularNetworksDesc: '4G LTE, LTE-M, NB-IoT ryšys',
      multiYearBattery: 'Kelių metų baterija',
      multiYearBatteryDesc: 'Iki 10+ metų su išmaniuoju energijos valdymu',
      compactTracker: 'Kompaktiškas turto sekiklis',
      compactTrackerDesc: 'Atsparus, baterija maitinamas GPS sekiklis vidaus/lauko turto sekimui su magnetine aktyvacija.',
      longLifeTracker: 'Ilgaamžis sekiklis',
      longLifeTrackerDesc: 'GPS sekiklis su ilgiausiu baterijos veikimo laiku rinkoje—iki 10+ metų pastatyk-ir-pamiršk diegimams.',
      globalSatelliteTracker: 'Globalus palydovinis sekiklis',
      globalSatelliteTrackerDesc: 'Atsparus laidinis sekiklis su Iridium palydovu ir 4G/2G ryšiu kritiškiems globaliems diegimams.',
      // Campus Tracking Section
      campusBadge: 'Objekto matomumas',
      campusTitle: 'Teritorijos turto sekimas',
      campusDesc: 'Didelės pramoninės vietos veikia kaip maži miestai. Gamyklos, oro uostai, pramoniniai parkai, universitetai ir energetikos objektai turi tūkstančius turto vienetų, kurie kasdien juda. Be sekimo, organizacijos praranda matomumą tą akimirką, kai įranga palieka centrinį sandėlį.',
      campusDesc2: 'Naudojant LoRaWAN vartus ir BLE švyturius, organizacijos gali sekti įrangą visoje teritorijoje metrų tikslumo lygiu. Vidaus/lauko pozicionavimas pašalina akląsias zonas, o zonomis pagrįsti įspėjimai užtikrina, kad turtas liktų ten, kur jam priklauso.',
      campusDeployments: 'Tipiniai teritorijos diegimai',
      campusSite1: 'Gamybos įmonės',
      campusSite2: 'Oro uostų objektai',
      campusSite3: 'Ligoninių teritorijos',
      campusSite4: 'Universitetų teritorijos',
      campusSite5: 'Energetikos įrenginiai',
      campusSite6: 'Logistikos centrai',
      multiSiteBadge: 'Kelių objektų operacijos',
      multiSiteTitle: 'Teritorijos-į-teritoriją matomumas',
      multiSiteDesc: 'Turtas dažnai juda tarp kelių objektų. Statybos technika rotacijos tarp projektų. Mobilūs generatoriai keliauja į laikinas instaliacijas. Priežiūros įranga aptarnauja kelis objektus. Be sekimo, tarp-objektų judėjimai sukuria matomumo spragas.',
      multiSiteDesc2: 'IoT sekimas leidžia organizacijoms stebėti turto judėjimą tarp objektų, suprasti tranzito laikus ir gerinti panaudojimą visoje organizacijoje—ne tik atskiruose objektuose.',
      realImpact: 'Realus poveikis',
      realImpactDesc: 'Įmonės paprastai mato 25% turto panaudojimo pagerėjimą, kai įgyja matomumą keliuose objektuose, sumažindamos poreikį dubliuotų įrangos pirkimų.',
      // Global Tracking Section
      globalBadge: 'Pasaulinis aprėptis',
      globalTitle: 'Globalus turto sekimas',
      globalDesc: 'Logistikos aplinkose turtas keliauja dideliais atstumais—tarp šalių, žemynų ir vandenynų. Konteineriai juda iš gamyklų Azijoje į platinimo centrus Europoje. Didelės vertės įranga keliauja į atokius projektų objektus. Temperatūrai jautriems vaistams reikalingas nuolatinis stebėjimas globalaus tranzito metu.',
      globalDesc2: 'Globalaus sekimo sistemos jungia mobiliuosius tinklus (prieinami 190+ šalių), palydovinį ryšį (vandenynų kirtimui ir atokioms vietoms) ir mobiliuosius vartus (realaus laiko tranzito stebėjimui). Tai užtikrina patikimą sekimą praktiškai bet kur Žemėje.',
      globalStat1: '190+',
      globalStat1Label: 'Šalių su mobiliuoju aprėptiu',
      globalStat2: '100%',
      globalStat2Label: 'Globalus palydovinis aprėptis',
      globalStat3: '130',
      globalStat3Label: 'Šalių su aktyviais diegimais',
      globalStat4: '2.5M+',
      globalStat4Label: 'Įrenginių įdiegta visame pasaulyje',
      // Cold Chain Section
      coldChainBadge: 'Temperatūrai jautri logistika',
      coldChainTitle: 'Šaltos grandinės stebėjimas su IoT',
      coldChainDesc: 'Temperatūrai jautriai logistikai reikalingas nuolatinis stebėjimas visoje tiekimo grandinėje. Vaistai, vakcinos, šviežias maistas ir biologiniai pavyzdžiai gali būti sugadinti net trumpalaikių temperatūros nuokrypių. Tradicinis stebėjimas su rankinėmis patikromis ir popieriniais žurnalais yra nepakankamas šiuolaikiniams atitikties reikalavimams.',
      coldChainDesc2: 'IoT jutikliai ir BLE švyturiai nuolat stebi temperatūrą, drėgmę ir judėjimą. Mobilieji vartai transporto priemonėse renka jutiklių duomenis ir perduoda juos į debesų platformas realiu laiku. Jei ribos viršijamos, įspėjimai generuojami iš karto—leidžiant įsikišti prieš produkto praradimą.',
      tempLogging: 'Nuolatinis temperatūros registravimas',
      tempLoggingDesc: 'Jutiklių rodmenys kas 15 minučių su debesų saugykla',
      thresholdAlerts: 'Realaus laiko ribų įspėjimai',
      thresholdAlertsDesc: 'Momentiniai pranešimai, kai ribos viršijamos',
      chainCustody: 'Pilna saugojimo grandinė',
      chainCustodyDesc: 'Audito pėdsakas nuo kilmės iki paskirties',
      regulatoryCompliance: 'Reguliacinis atitikimas',
      regulatoryComplianceDesc: 'GDP, HACCP ir farmacijos standartai palaikomi',
      // Asset Recovery Section
      securityBadge: 'Saugumas ir apsauga',
      securityTitle: 'Turto atgavimas ir vagysčių aptikimas',
      securityDesc: 'Didelės vertės turtas reiškia reikšmingas kapitalo investicijas. Statybos įranga, specializuoti įrankiai ir transporto priemonės yra pagrindiniai vagysčių taikiniai. Kai įvyksta vagystė, greitas atgavimas yra kritiškas—tiek siekiant sumažinti finansinius nuostolius, tiek išlaikyti projektų grafikus.',
      securityDesc2: 'GPS sekimo įrenginiai su slaptomis montavimo parinktimis leidžia akimirksniu nustatyti pavogto turto vietą. Geofencingas sukuria virtualias ribas—kai turtas palieka įgaliotą zoną, įspėjimai suaktyvinami iš karto. Anti-vagysčių režimas suaktyvina aukšto dažnio GPS atnaujinimus realaus laiko sekimui atgavimo operacijų metu.',
      theftPrevention: 'Vagysčių prevencijos funkcijos',
      theftFeature1: 'Geofencingas su momentiniais pažeidimų įspėjimais',
      theftFeature2: 'Po darbo valandų judėjimo aptikimas',
      theftFeature3: 'Slaptas įrenginio montavimas',
      theftFeature4: 'RF švyturiai paskutinės mylios atgavimui',
      theftFeature5: 'Teisėsaugos koordinacijos palaikymas',
      analyticsBadge: 'Operacinė žvalgyba',
      analyticsTitle: 'DI ir analitika',
      analyticsDesc: 'Turto sekimas generuoja didžiulius kiekius operacinių duomenų. Be analitikos, šie duomenys lieka nepanaudoti. Pažangios platformos paverčia neapdorotus vietos ir jutiklių duomenis į veiksmingą verslo žvalgybą.',
      utilizationStats: 'Panaudojimo statistika',
      utilizationStatsDesc: 'Supraskite turto naudojimo modelius',
      predictiveMaintenance: 'Nuspėjamoji priežiūra',
      predictiveMaintenanceDesc: 'Numatykite aptarnavimo poreikius',
      fleetOptimization: 'Parko optimizavimas',
      fleetOptimizationDesc: 'Pagerinkite veiklos efektyvumą',
      customReporting: 'Pasirinktinės ataskaitos',
      customReportingDesc: 'Verslo žvalgybos eksportai',
      // Final CTA Section
      finalCtaTitle: 'Įdiekite turto sekimą su 0g Baltics',
      finalCtaDesc: '0g Baltics projektuoja ir diegia pažangius IoT turto sekimo sprendimus visame Baltijos regione. Nuo vieno objekto bandomųjų projektų iki visos įmonės diegimų, mes teikiame aparatinę įrangą, ryšį ir ekspertizę, kad transformuotume jūsų turto valdymą.',
      finalCtaButton: 'Kalbėti su ekspertu',
    },

    toolTrackingPage: {
      metaTitle: 'Įrankių sekimas | Pramoninių įrankių valdymas | 0g Baltics',
      metaDesc: 'Sekite pramoninius įrankius su RFID, išmaniosiomis spintelėmis ir IoT jutikliais.',
      heroBadge: 'Įrankių sekimas',
      heroTitle: 'Pramoninių įrankių sekimo sprendimai',
      heroSubtitle: 'Sumažinkite įrankių praradimą ir pagerinkite panaudojimą su išmaniuoju sekimu ir automatizuotomis paskirstymo sistemomis.',
      rfidTracking: 'RFID sekimas',
      rfidTrackingDesc: 'Pažymėkite ir sekite kiekvieną įrankį su RFID technologija.',
      smartLockers: 'Išmaniosios spintelės',
      smartLockersDesc: 'Automatizuotas įrankių paskirstymas su prieigos kontrole.',
      inventory: 'Inventoriaus valdymas',
      inventoryDesc: 'Tiksliai žinokite, kokius įrankius turite ir kur jie yra.',
      analytics: 'Naudojimo analitika',
      analyticsDesc: 'Supraskite įrankių panaudojimą ir optimizuokite savo inventorių.',
      ctaTitle: 'Nustokite prarasti įrankius',
      ctaButton: 'Sužinoti daugiau',
      // BLE Tracking Section
      bleSectionBadge: 'BLE turto sekimas',
      bleSectionTitle: 'Įrankių sekimas: žinokite, kur yra kiekvienas įrankis',
      bleSectionDesc: 'Pritvirtinkite BLE jutiklius prie savo įrankių. Gaukite realaus laiko matomumą apie vietą ir būseną.',
      // ROI Stats
      stat1: 'Įrankių praradimo sumažinimas',
      stat2: 'Panaudojimo padidėjimas',
      stat3: 'Mažiau laiko ieškant',
      stat4: 'Mažesnės pakeitimo išlaidos',
      // Why Track
      whyTrackTitle: 'Kodėl sekti įrankius?',
      whyTrack1: 'Pašalinkite vagystes ir praradimus',
      whyTrack1Desc: 'BLE jutikliai įspėja, kai įrankiai palieka leidžiamas zonas.',
      whyTrack2: 'Raskite įrankius akimirksniu',
      whyTrack2Desc: 'Atidarykite programėlę ir per sekundes raskite bet kurį įrankį žemėlapyje.',
      whyTrack3: 'Automatizuokite inventorių',
      whyTrack3Desc: 'BLE vartai automatiškai aptinka įrankius sandėliuose.',
      whyTrack4: 'Užtikrinkite atitiktį',
      whyTrack4Desc: 'Sekite patikrinimo datas. Gaukite įspėjimus prieš pasibaigiant terminui.',
      // How It Works
      howItWorks: 'Kaip tai veikia',
      howStep1: 'Pritvirtinkite BLE jutiklį (magnetas, klijai arba paslėptas)',
      howStep2: 'Išmanieji telefonai ir vartai automatiškai aptinka įrankius',
      howStep3: 'Sekite vietą, gaukite įspėjimus',
      // Buttons
      requestDemo: 'Prašyti demonstracijos',
      viewSolutions: 'Peržiūrėti sprendimus',
      scheduleDemo: 'Planuoti demonstraciją',
      // BLE Trackers Section
      bleTrackersTitle: 'Pramoninės klasės BLE sekimo jutikliai',
      coinBeaconName: 'Monetos švyturys',
      coinBeaconDesc: 'Itin kompaktiškas sekiklis mažiems įrankiams. 3+ metų baterija.',
      proAssetTagName: 'Pro turto žyma',
      proAssetTagDesc: 'Profesionalios klasės elektriniams įrankiams. BLE + LPWAN pozicionavimas.',
      industrialTrackerName: 'Pramoninis sekiklis',
      industrialTrackerDesc: 'Atsparus su GPS, BLE ir LTE globaliam didelės vertės įrangos sekimui.',
      learnConnectivity: 'Sužinoti daugiau apie mūsų ryšio technologijas',
      // Distribution Section
      officialPartner: 'Oficialus partneris',
      distributionTitle: 'Įrankių ir įrangos paskirstymas',
      distributionDesc: 'Automatizuotos spintelių sistemos saugiam įrankių, AAP ir vartojamųjų medžiagų paskirstymui. Kortelės identifikacija, pilnas atsekamumas ir realaus laiko inventoriaus valdymas.',
      secureAccess: 'Saugi prieiga',
      secureAccessDesc: 'Kortelės autentifikacija kiekvienai transakcijai',
      fullTraceability: 'Pilnas atsekamumas',
      fullTraceabilityDesc: 'Žinokite, kas ką paėmė, kada ir kodėl',
      availability247: 'Prieinamumas 24/7',
      availability247Desc: 'Savitarnos prieiga bet kuriuo metu',
      realtimeDashboard: 'Realaus laiko prietaisų skydelis',
      realtimeDashboardDesc: 'Stebėkite inventorių ir naudojimo modelius',
      // Products
      xtowerTitle: 'AAP dalytuvas X\'TOWER®',
      xtowerDesc: 'Apsaugokite ir optimizuokite savo vartojamąsias medžiagas su automatizuotu karuselės paskirstymu. Sumažinkite saugojimo vietą 90%.',
      xstoreTitle: 'Sandėlio ir patalpų valdymas: X\'STORE®',
      xstoreDesc: 'Apsaugokite ir sekite savo medžiagas specialioje saugojimo patalpoje. Pilnas matomumas apie inventoriaus lygius.',
      xlockerTitle: 'Pramoninių reikmenų dalytuvas X\'LOCKER®',
      xlockerDesc: 'Apsaugokite ir optimizuokite savo jautrias medžiagas ir įrankius.',
      xslingTitle: 'Sieninis diržų ir petnešų stovas X\'SLING®',
      xslingDesc: 'Užtikrinkite jautrios įrangos saugumą ir veikimo būseną.',
      xweightTitle: 'Mažų vartojamųjų medžiagų svėrimo dalytuvas: X\'WEIGHT®',
      xweightDesc: 'Apsaugokite ir optimizuokite varžtų, sraigtų ir mažų vartojamųjų medžiagų paskirstymą.',
      comingSoon: 'Netrukus',
      // Why It Matters Section
      whyMattersTitle: 'Kodėl įrankių sekimas ir išmanusis paskirstymas svarbus',
      whyMattersDesc: 'Pramoninėse aplinkose nevaldomi įrankiai įmonėms kasmet kainuoja tūkstančius eurų nuostoliais, sugaištu laiku ir neefektyvumu.',
      trackEveryTool: 'Sekite kiekvieną įrankį',
      trackEveryToolDesc1: 'Be matomumo įrankiai dingsta. Darbuotojai kaupia įrangą "visam atvejui". Vagystės lieka nepastebėtos, kol inventorizacija atskleidžia didžiulius trūkumus.',
      trackEveryToolDesc2: 'BLE sekimas suteikia realaus laiko vietos duomenis apie kiekvieną turtą. Žinokite, kas ką turi, kur tai yra ir kada paskutinį kartą naudota. Sumažinkite nuostolius 40% ir pašalinkite kasdienę dingusios įrangos paiešką.',
      simplifyDistribution: 'Supaprastinkite paskirstymą',
      simplifyDistributionDesc1: 'Rankiniai įrankių sandėliai sukuria kliūtis. Darbuotojai laukia eilėje. Inventoriaus įrašai netikslūs. AAP galiojimas baigiasi nepastebėtas. Atitiktis tampa košmaru.',
      simplifyDistributionDesc2: 'Išmaniosios spintelės užtikrina 24/7 savitarnos prieigą su kortelės autentifikacija. Kiekviena transakcija automatiškai registruojama. Inventorius atnaujinamas realiu laiku. Atitiktis įdiegta.',
      readyTransform: 'Pasiruošę transformuoti savo įrankių valdymą?',
      readyTransformDesc: 'Susisiekite su mumis dėl asmeninės demonstracijos ir sužinokite, kaip mūsų sprendimai gali sumažinti nuostolius ir pagerinti veiklos efektyvumą.',
    },

    connectivityPage: {
      metaTitle: 'IoT ryšys | LoRaWAN, Sigfox, NB-IoT | 0g Baltics',
      metaDesc: 'Įdiekite IoT ryšio infrastruktūrą su LoRaWAN, Sigfox, NB-IoT ir mobiliosiomis technologijomis. Tinklo planavimo ir diegimo paslaugos.',
      // Hero Section
      heroBadge: 'Ryšio infrastruktūra',
      heroTitle: 'IoT ryšio infrastruktūra',
      heroSubtitle: 'Tinkamo ryšio pasirinkimas yra būtinas sėkmingiems IoT diegimams. 0g Baltics projektuoja daugiateinkles infrastruktūras, apjungiančias LoRaWAN, Sigfox, NB-IoT, BLE ir palydovinį ryšį.',
      heroDesc: 'Kiekvienas IoT projektas turi skirtingus reikalavimus aprėpčiai, energijos suvartojimui, duomenų dažniui ir infrastruktūros kaštams. Mūsų užduotis – parinkti ir apjungti efektyviausias ryšio technologijas, užtikrinančias patikimumą, mastelį ir ilgalaikį tvarumą.',
      // Multi-Connectivity Section
      multiBadge: 'Mūsų filosofija',
      multiTitle: 'Kodėl daugiateinklis IoT yra svarbus',
      multiDesc: '0g Baltics niekada nekuria mono-ryšio sprendimų. Mes suprantame, kad skirtingiems įrenginiams, naudojimo atvejams ir aplinkoms reikalingos skirtingos tinklo technologijos optimaliam veikimui pasiekti.',
      multiPoint1: 'Skirtingiems įrenginiams reikalingi skirtingi tinklai',
      multiPoint2: 'Infrastruktūra turi išlikti lanksti',
      multiPoint3: 'Kaštų optimizavimas yra būtinas',
      multiPoint4: 'Perteklius pagerina patikimumą',
      multiPoint5: 'Įrenginių ilgaamžiškumas turi būti išsaugotas',
      commitmentTitle: 'Mūsų įsipareigojimas',
      commitmentDesc: '0g Baltics sprendimai niekada nėra pagrįsti viena ryšio technologija. Mes projektuojame hibridines infrastruktūras, kurios apjungia kelis tinklus, siekiant sumažinti veiklos kaštus ir maksimizuoti saugumą bei įrangos ilgaamžiškumą.',
      // LoRaWAN Section
      lorawanBadge: 'Ilgo nuotolio IoT',
      lorawanTitle: 'LoRaWAN infrastruktūra',
      lorawanDesc: 'LoRaWAN (Long Range Wide Area Network) yra daugelio didelio masto IoT diegimų pagrindas. Ši technologija leidžia ilgo nuotolio komunikaciją su minimaliu energijos suvartojimu, todėl idealiai tinka išmaniojo miesto ir komunalinių paslaugų programoms.',
      lorawanAdvantages: 'Pagrindiniai privalumai',
      lorawanAdv1: 'Ilgo nuotolio ryšys (iki 15 km)',
      lorawanAdv2: 'Ypač mažas energijos suvartojimas',
      lorawanAdv3: 'Privati tinklo infrastruktūra',
      lorawanAdv4: 'Idealiai tinka išmaniesiems miestams ir komunalinėms paslaugoms',
      lorawanAdv5: 'Keičiamo dydžio diegimai',
      lorawanNote: 'Mes diegiame operatoriaus lygio LoRaWAN infrastruktūrą naudodami Tektelic šliuzus maksimaliam patikimumui.',
      lorawanUseCases: 'Tipiniai naudojimo atvejai',
      lorawanUse1Title: 'Išmanioji apskaita',
      lorawanUse1Desc: 'Nuotolinis vandens, dujų ir elektros skaitiklių nuskaitymas',
      lorawanUse2Title: 'Aplinkos stebėjimas',
      lorawanUse2Desc: 'Oro kokybės, temperatūros, drėgmės jutikliai',
      lorawanUse3Title: 'Turto sekimas',
      lorawanUse3Desc: 'Transporto priemonių ir įrangos vietos sekimas',
      lorawanUse4Title: 'Išmanieji pastatai',
      lorawanUse4Desc: 'Užimtumo, energijos ir įrenginių valdymas',
      lorawanUse5Title: 'Miesto infrastruktūra',
      lorawanUse5Desc: 'Gatvių apšvietimas, parkavimas, atliekų tvarkymas',
      // Sigfox Section
      sigfoxBadge: 'Globalus IoT tinklas',
      sigfoxTitle: 'Sigfox ryšys',
      sigfoxDesc: 'Sigfox yra globali LPWAN (Low Power Wide Area Network) technologija, skirta paprastiems, pigiems IoT įrenginiams, perduodantiems nedidelius duomenų kiekius. Jos itin mažas energijos suvartojimas užtikrina baterijos veikimo laiką, matuojamą metais, o ne mėnesiais.',
      sigfoxAdvantages: 'Pagrindiniai privalumai',
      sigfoxAdv1: 'Itin mažas energijos suvartojimas',
      sigfoxAdv2: 'Baterijos veikimo laikas iki 10+ metų',
      sigfoxAdv3: 'Paprasta įrenginių integracija',
      sigfoxAdv4: 'Globalus IoT tinklo aprėptis',
      sigfoxWhen: 'Kada naudoti Sigfox',
      sigfoxWhenDesc: 'Sigfox yra efektyviausias sprendimas, kai įrenginiams reikia retai siųsti nedidelius duomenų kiekius, o baterijos veikimo laikas yra pagrindinis apribojimas. Globali tinklo aprėptis pašalina privačios infrastruktūros investicijų poreikį.',
      sigfoxUseCases: 'Tipiniai naudojimo atvejai',
      sigfoxUse1Title: 'Turto sekimas',
      sigfoxUse1Desc: 'Ilgalaikis sekimas su daugiamečiu baterijos veikimo laiku',
      sigfoxUse2Title: 'Aplinkos jutikliai',
      sigfoxUse2Desc: 'Temperatūros, drėgmės, slėgio stebėjimas',
      sigfoxUse3Title: 'Signalizacijos sistemos',
      sigfoxUse3Desc: 'Įsilaužimo aptikimas ir saugumo įspėjimai',
      sigfoxUse4Title: 'Komunalinių paslaugų stebėjimas',
      sigfoxUse4Desc: 'Nutekėjimų aptikimas ir suvartojimo įspėjimai',
      // NB-IoT Section
      nbiotBadge: 'Mobilus IoT',
      nbiotTitle: 'NB-IoT mobilusis ryšys',
      nbiotDesc: 'NB-IoT (Narrowband Internet of Things) naudoja esamą mobilųjį tinklą patikimam, saugiam IoT įrenginių ryšiui užtikrinti. Ši technologija puikiai tinka aplinkoms, kuriose būtinas gilus vidaus patalpų aprėptis ir operatoriaus lygio patikimumas.',
      nbiotAdvantages: 'Pagrindiniai privalumai',
      nbiotAdv1: 'Telekomunikacijų operatoriaus infrastruktūra',
      nbiotAdv2: 'Gili vidaus patalpų aprėptis',
      nbiotAdv3: 'Aukštas patikimumas ir saugumas',
      nbiotAdv4: 'Plati teritorijos aprėptis',
      nbiotNote: 'NB-IoT yra ypač naudingas dideliems infrastruktūros projektams, kur įrenginiai diegiami sudėtingose aplinkose, tokiose kaip požeminiai komunaliniai tinklai, rūsiai ar pramoniniai objektai.',
      nbiotUseCases: 'Tipiniai naudojimo atvejai',
      nbiotUse1Title: 'Išmanioji apskaita',
      nbiotUse1Desc: 'Komunaliniai skaitikliai sudėtingose vietose',
      nbiotUse2Title: 'Pramoniniai jutikliai',
      nbiotUse2Desc: 'Gamyklų ir įrenginių stebėjimas',
      nbiotUse3Title: 'Išmanusis parkavimas',
      nbiotUse3Desc: 'Požeminių aikštelių užimtumas',
      nbiotUse4Title: 'Išmanioji infrastruktūra',
      nbiotUse4Desc: 'Tiltų ir tunelių stebėjimas',
      // BLE Section
      bleBadge: 'Vidaus pozicionavimas',
      bleTitle: 'Bluetooth Low Energy',
      bleDesc: 'Bluetooth Low Energy (BLE) technologija leidžia tiksliai pozicionuoti vidaus patalpose ir sekti turtą su minimaliu energijos suvartojimu. Jos suderinamumas su išmaniaisiais telefonais ir platus priėmimas daro ją idealią vartotojams skirtoms programoms.',
      bleAdvantages: 'Pagrindiniai privalumai',
      bleAdv1: 'Labai mažas energijos suvartojimas',
      bleAdv2: 'Vidaus pozicionavimo galimybės',
      bleAdv3: 'Aukštas tikslumas (mažiau nei metras)',
      bleAdv4: 'Išmaniųjų telefonų suderinamumas',
      bleUseCases: 'Tipiniai naudojimo atvejai',
      bleUse1Title: 'Vidaus turto sekimas',
      bleUse1Desc: 'Įrangos ir inventoriaus vieta realiuoju laiku',
      bleUse2Title: 'Ligoninės įrangos sekimas',
      bleUse2Desc: 'Medicinos prietaisų vieta ir valdymas',
      bleUse3Title: 'Išmanusis biuro užimtumas',
      bleUse3Desc: 'Darbo vietos naudojimo stebėjimas',
      bleUse4Title: 'Vidaus vietos paslaugos',
      bleUse4Desc: 'Navigacijos ir maršruto nustatymo programos',
      bleLearnMore: 'Sužinokite daugiau apie mūsų ligoninės turto sekimo sprendimus, kuriuos maitina BLE technologija.',
      // Satellite Section
      satelliteBadge: 'Globalus aprėptis',
      satelliteTitle: 'Palydovinis IoT ryšys',
      satelliteDesc: 'Palydovinis ryšys papildo antžeminius tinklus, užtikrindamas aprėptį atokiose vietovėse, kur nėra mobiliojo ar LPWAN infrastruktūros. Ši technologija užtikrina nuolatinį turto ir infrastruktūros stebėjimą nepriklausomai nuo vietos.',
      satelliteAdv1Title: 'Globali aprėptis',
      satelliteAdv1Desc: 'Ryšys bet kur Žemėje, įskaitant vandenynus ir atokias vietoves',
      satelliteAdv2Title: 'Atokių vietovių ryšys',
      satelliteAdv2Desc: 'Stebėkite turtą vietose be antžeminės infrastruktūros',
      satelliteAdv3Title: 'Tinklo perteklius',
      satelliteAdv3Desc: 'Atsarginis ryšys, kai pagrindiniai tinklai neveikia',
      satelliteUse1Title: 'Atokios infrastruktūros stebėjimas',
      satelliteUse1Desc: 'Vamzdynai, elektros linijos ir objektai izoliuotose vietose',
      satelliteUse2Title: 'Logistikos sekimas',
      satelliteUse2Desc: 'Konteinerių ir transporto sekimas per sienas',
      satelliteUse3Title: 'Pramoniniai aktyvai',
      satelliteUse3Desc: 'Sunki technika ir mašinos atokiose vietose',
      satelliteNote: '0g Baltics integruoja palydovinio ryšio sprendimus, suderinamus su įvairiomis palydovinėmis infrastruktūromis, įskaitant Starlink, kai to reikalauja projekto poreikiai.',
      // Strategy Section
      strategyBadge: 'Mūsų metodika',
      strategyTitle: 'Kaip 0g Baltics parenka tinkamą tinklą',
      strategyDesc: 'Kiekvienas IoT diegimas prasideda nuodugnią reikalavimų analize. Mūsų sisteminis požiūris užtikrina, kad pasirinktos ryšio technologijos atitinka projekto tikslus ir apribojimus.',
      step1: '01',
      step1Title: 'Infrastruktūros vertinimas',
      step1Desc: 'Įvertinkite esamą infrastruktūrą ir aprėpties reikalavimus',
      step2: '02',
      step2Title: 'Įrenginių reikalavimai',
      step2Desc: 'Analizuokite duomenų dažnį, naudingosios apkrovos dydį ir komunikacijos poreikius',
      step3: '03',
      step3Title: 'Baterijos optimizavimas',
      step3Desc: 'Apskaičiuokite energijos suvartojimą ir tikslinę įrenginio eksploatavimo trukmę',
      step4: '04',
      step4Title: 'Aprėpties tyrimas',
      step4Desc: 'Nubraižykite aprėpties zonas ir identifikuokite ryšio spragas',
      step5: '05',
      step5Title: 'Diegimo architektūra',
      step5Desc: 'Suprojektuokite hibridinę tinklo topologiją optimaliam veikimui',
      strategyNote: 'Mūsų tikslas visada yra sumažinti veiklos kaštus, kartu užtikrinant ilgalaikį patikimumą ir įrenginių ilgaamžiškumą.',
      // Industries Section
      industriesBadge: 'Taikymai',
      industriesTitle: 'Ryšys realiems IoT diegimams',
      industriesDesc: '0g Baltics teikia daugiateinklį IoT ryšį įvairioms pramonės šakoms, kiekvienai su unikaliais reikalavimais ir iššūkiais.',
      industrySmartCities: 'Išmanieji miestai',
      industryUtilities: 'Komunalinės paslaugos',
      industryLogistics: 'Logistika',
      industryHealthcare: 'Sveikatos priežiūra',
      industryRealEstate: 'Nekilnojamasis turtas',
      industryIndustrial: 'Pramonė',
      industriesExplore: 'Tyrinėkite mūsų įrenginių ekosistemą ir konsultavimo paslaugas, kad sužinotumėte daugiau.',
      // CTA Section
      ctaTitle: 'Suprojektuokite savo IoT ryšio infrastruktūrą',
      ctaDesc: 'Pasiruošę diegti daugiateinklį IoT sprendimą? Mūsų komanda padės jums pasirinkti tinkamas ryšio technologijas jūsų specifiniams reikalavimams.',
      ctaButton: 'Aptarkite savo ryšio strategiją',
    },

    tektelicPage: {
      metaTitle: 'TEKTELIC | LoRaWAN vartai ir jutikliai | 0g Baltics',
      metaDesc: 'TEKTELIC profesionalios klasės LoRaWAN vartai ir jutikliai. Įgaliotas partneris Baltijos regione.',
      // Hero Section
      heroBadge: 'Įgaliotas partneris',
      heroTitle: 'Operatoriaus lygio LoRaWAN infrastruktūra su',
      heroTitleHighlight: 'Tektelic',
      heroSubtitle: 'Kurkite keičiamo dydžio IoT tinklus miestams, komunaliniams ūkiams ir pramoniniams diegimams naudodami Tektelic šliuzus ir 0g Baltics infrastruktūros ekspertizę.',
      heroDesc: '0g Baltics projektuoja ir diegia LoRaWAN infrastruktūrą Baltijos šalyse ir Europoje naudodami Tektelic operatoriaus lygio šliuzus. Mūsų diegimai palaiko išmaniojo miesto infrastruktūrą, skaitiklių tinklus, aplinkos stebėjimą ir pramoninius IoT pritaikymus.',
      ctaPrimary: 'Aptarti jūsų LoRaWAN projektą',
      ctaSecondary: 'Naršyti pradžios rinkinius',
      // Why Tektelic Section
      whyBadge: 'Technologijų partneris',
      whyTitle: 'Kodėl Tektelic LoRaWAN šliuzai',
      whyDesc: 'Tektelic šliuzai yra diegiami visame pasaulyje didelio masto LoRaWAN tinkluose, kuriais pasitiki telekomunikacijų operatoriai, miestai ir įmonės dėl jų patikimumo ir našumo.',
      feature1Title: 'Operatoriaus lygio patikimumas',
      feature1Desc: 'Sukurti pagal telekomunikacijų standartus 24/7 veikimui reikliose aplinkose',
      feature2Title: 'Pramoninio lygio aparatinė įranga',
      feature2Desc: 'Suprojektuoti lauko diegimui su IP67 apsauga ir plačiu temperatūros diapazonu',
      feature3Title: 'Keičiamo dydžio architektūra',
      feature3Desc: 'Nuo vieno šliuzo bandomųjų projektų iki miesto masto tinklo diegimų',
      feature4Title: 'Išplėstinis tinklo valdymas',
      feature4Desc: 'Išsamūs įrankiai stebėjimui, konfigūravimui ir priežiūrai',
      feature5Title: 'Saugi infrastruktūra',
      feature5Desc: 'Pilnas šifravimas ir saugus įkrovimas įmonių diegimams',
      feature6Title: 'Aukšto našumo RF dizainas',
      feature6Desc: 'Optimizuotas radijo dažnių dizainas maksimaliai aprėpčiai ir patikimumui',
      // LoRaWAN for Cities Section
      citiesBadge: 'Išmaniojo miesto infrastruktūra',
      citiesTitle: 'LoRaWAN infrastruktūra miestams ir komunaliniams ūkiams',
      citiesDesc: 'LoRaWAN technologija leidžia miestams ir komunaliniams ūkiams diegti ekonomiškai efektyvią IoT infrastruktūrą, kuri vienu metu palaiko kelias programas. Vienas LoRaWAN tinklas gali aptarnauti išmaniąją apskaita, aplinkos stebėjimą, turto sekimą ir daugiau.',
      networkEfficiency: 'Tinklo efektyvumas',
      networkEfficiencyDesc: 'Viena LoRaWAN infrastruktūra gali vienu metu palaikyti kelias programas, mažindama diegimo kaštus ir supaprastindama tinklo valdymą skirtinguose miesto padaliniuose.',
      useCasesTitle: 'Naudojimo atvejai',
      useCase1Title: 'Išmanioji apskaita',
      useCase1Desc: 'Vanduo, dujos ir elektra',
      useCase2Title: 'Oro kokybės stebėjimas',
      useCase2Desc: 'CO2, dalelės, teršalai',
      useCase3Title: 'Parkavimo stebėjimas',
      useCase3Desc: 'Užimtumas ir nukreipimas',
      useCase4Title: 'Turto sekimas',
      useCase4Desc: 'Transporto priemonės ir įranga',
      useCase5Title: 'Atliekų tvarkymas',
      useCase5Desc: 'Užpildymo lygio stebėjimas',
      useCase6Title: 'Aplinkos stebėjimas',
      useCase6Desc: 'Temperatūra, drėgmė',
      useCase7Title: 'Išmanieji pastatai',
      useCase7Desc: 'Energija ir užimtumas',
      // Multi-Network Section
      multiNetworkBadge: 'Daugiateinklio strategija',
      multiNetworkTitle: 'Sukurta hibridinei IoT infrastruktūrai',
      multiNetworkDesc: '0g Baltics niekada nekuria mono-ryšio diegimų. LoRaWAN dažnai yra IoT infrastruktūros stuburas, bet daugelis diegimų derina LoRaWAN su Sigfox, NB-IoT, BLE ar palydoviniu ryšiu, kad optimizuotų kaštus, aprėptį ir patikimumą.',
      benefit1Title: 'Kaštų optimizavimas',
      benefit1Desc: 'Pasirinkite efektyviausią ryšį kiekvienam naudojimo atvejui',
      benefit2Title: 'Tinklo perteklius',
      benefit2Desc: 'Atsarginis ryšys kritinėms programoms',
      benefit3Title: 'Įrenginių ilgaamžiškumas',
      benefit3Desc: 'Optimizuokite baterijos veikimo laiką pasirinkdami tinkamą tinklą',
      benefit4Title: 'Diegimo lankstumas',
      benefit4Desc: 'Pritaikykite infrastruktūrą besikeičiantiems reikalavimams',
      learnMultiNetwork: 'Sužinokite apie mūsų daugiateinklio metodą',
      connectivityTechnologies: 'Ryšio technologijos',
      lorawanName: 'LoRaWAN',
      lorawanDesc: 'Ilgo nuotolio stuburinė infrastruktūra',
      sigfoxName: 'Sigfox',
      sigfoxDesc: 'Itin mažos galios paprasti pranešimai',
      nbiotName: 'NB-IoT',
      nbiotDesc: 'Korinio ryšio gilus vidaus aprėptis',
      bleName: 'BLE',
      bleDesc: 'Vidaus pozicionavimas ir sekimas',
      satelliteName: 'Palydovas',
      satelliteDesc: 'Nuotolinių vietovių ryšys',
      // Starter Kits Section
      kitsBadge: 'Greitas startas',
      kitsTitle: 'LoRaWAN pradžios rinkiniai',
      kitsDesc: 'Greitai pradėkite savo LoRaWAN diegimą su išbandytais infrastruktūros paketais, sukurtais skirtingiems naudojimo atvejams ir mastams.',
      pilotKitTitle: 'Bandomojo diegimo rinkinys',
      pilotKitDesc: 'Idealus organizacijoms, pradedančioms pirmąjį LoRaWAN bandomąjį projektą',
      pilotIncludes: 'Įtraukta:',
      pilotItem1: '1 Tektelic LoRaWAN šliuzas',
      pilotItem2: 'Tinklo konfigūracija',
      pilotItem3: 'Įrenginių integracijos palaikymas',
      pilotItem4: 'Pagrindinis prietaisų skydelio priėjimas',
      pilotUseCases: 'Tipiniai naudojimo atvejai:',
      pilotTag1: 'Koncepcijos įrodymas',
      pilotTag2: 'Mažas miestelis',
      pilotTag3: 'Inovacijų projektai',
      smartCityKitTitle: 'Išmaniojo miesto infrastruktūros rinkinys',
      smartCityKitDesc: 'Skirtas savivaldybėms ir komunaliniams ūkiams, pradedantiems išmaniojo miesto IoT infrastruktūrą',
      smartCityIncludes: 'Įtraukta:',
      smartCityItem1: 'Keli Tektelic šliuzai',
      smartCityItem2: 'Tinklo architektūros dizainas',
      smartCityItem3: 'Aprėpties planavimas',
      smartCityItem4: 'Integracija su miesto platformomis',
      smartCityUseCases: 'Tipinės programos:',
      smartCityTag1: 'Išmanioji apskaita',
      smartCityTag2: 'Aplinkos stebėjimas',
      smartCityTag3: 'Išmanusis parkavimas',
      smartCityTag4: 'Viešoji infrastruktūra',
      industrialKitTitle: 'Pramoninis IoT tinklo rinkinys',
      industrialKitDesc: 'Skirtas logistikai, komunaliniams ūkiams ir pramoninėms aplinkoms',
      industrialIncludes: 'Įtraukta:',
      industrialItem1: 'Pramoniniai šliuzai',
      industrialItem2: 'Turto sekimo įrenginiai',
      industrialItem3: 'Tinklo optimizavimas',
      industrialItem4: 'Integracijos palaikymas',
      industrialUseCases: 'Tipinės programos:',
      industrialTag1: 'Turto sekimas',
      industrialTag2: 'Sandėlio stebėjimas',
      industrialTag3: 'Energijos infrastruktūra',
      requestKitInfo: 'Prašyti rinkinio informacijos',
      popular: 'POPULIARUS',
      // Device Ecosystem Section
      devicesBadge: 'Įrenginių suderinamumas',
      devicesTitle: 'Suderinami IoT įrenginiai',
      devicesDesc: 'Mūsu LoRaWAN infrastruktūra veikia su plačia sertifikuotų įrenginių ekosistema iš pirmaujančių gamintojų, užtikrinant, kad galėtumėte pasirinkti geriausius jutiklius jūsų specifiniams programų reikalavimams.',
      viewDeviceEcosystem: 'Peržiūrėti mūsu pilną įrenginių ekosistemą',
      device1Name: 'Adeunis',
      device1Desc: 'Pramoniniai jutikliai',
      device2Name: 'ATIM',
      device2Desc: 'Pramoniniai IoT įrenginiai',
      device3Name: 'Digital Matter',
      device3Desc: 'Turto sekliai',
      device4Name: 'Aplinkos jutikliai',
      device4Desc: 'Oro kokybė, temperatūra',
      device5Name: 'Apskaitos įrenginiai',
      device5Desc: 'Vanduo, dujos, elektra',
      device6Name: 'Connected Inventions',
      device6Desc: 'Išmanieji jutikliai',
      // Services Section
      servicesBadge: 'Mūsu paslaugos',
      servicesTitle: 'Infrastruktūros dizainas ir diegimas',
      servicesDesc: 'Nuo pradinio planavimo iki didelio masto diegimų, 0g Baltics teikia išsamias LoRaWAN infrastruktūros paslaugas.',
      service1: 'Tinklo dizainas',
      service2: 'Šliuzų diegimas',
      service3: 'Aprėpties optimizavimas',
      service4: 'Įrenginių pasirinkimas',
      service5: 'ROI analizė',
      service6: 'Bandomieji diegimai',
      service7: 'Didelio masto diegimai',
      learnConsulting: 'Sužinokite daugiau apie mūsu konsultavimo paslaugas',
      // Who We Work With Section
      clientsBadge: 'Mūsų klientai',
      clientsTitle: 'Kas diegia LoRaWAN infrastruktūrą',
      client1Title: 'Miestai ir savivaldybės',
      client1Desc: 'Išmaniojo miesto infrastruktūra',
      client2Title: 'Komunaliniai ūkiai',
      client2Desc: 'Apskaita ir stebėjimas',
      client3Title: 'Pramoninės įmonės',
      client3Desc: 'Turto sekimas ir stebėjimas',
      client4Title: 'Logistikos įmonės',
      client4Desc: 'Parko ir krovinių sekimas',
      client5Title: 'IoT integratoriai',
      client5Desc: 'Sprendimų kūrimas',
      // CTA Section
      ctaTitle: 'Pradėkite savo LoRaWAN infrastruktūros projektą',
      ctaDesc: 'Aptarkite savo infrastruktūros projektą su mūsu IoT specialistais. Padėsime suprojektuoti ir įdiegti tinkamą LoRaWAN sprendimą jūsu reikalavimams.',
      ctaButton: 'Susisiekite su mūsų LoRaWAN ekspertais',
    },

    common: {
      learnMore: 'Sužinoti daugiau',
      contactUs: 'Susisiekite su mumis',
      getStarted: 'Pradėti',
      requestDemo: 'Prašyti demonstracijos',
      downloadBrochure: 'Atsisiųsti brošiūrą',
      viewCaseStudy: 'Peržiūrėti atvejo tyrimą',
      keyBenefits: 'Pagrindiniai privalumai',
      features: 'Funkcijos',
      useCases: 'Naudojimo atvejai',
      industries: 'Pramonės šakos',
      technologies: 'Technologijos',
      readMore: 'Skaityti daugiau',
      backToHome: 'Grįžti į pradžią',
    },
    realEstatePage: {
      metaTitle: 'IoT sprendimai nekilnojamajam turtui ir infrastruktūrai | Išmaniieji pastatai Baltijoje | 0g Baltics',
      metaDesc: 'Paverskite nekilnojamojo turto objektus išmaniaisiais, susietais, duomenimis grįstais pastatais. IoT biurams, viešbučiams, gyvenamiesiems, mažmeninės prekybos, pramonės parkams ir infrastruktūrai Latvijoje, Lietuvoje ir Estijoje — energija, nuotėkių aptikimas, oro kokybė, užimtumas, išmaniosios spintelės ir daugiau.',
      ogTitle: 'Išmaniieji pastatai ir infrastruktūra — IoT sprendimai iš 0g Baltics',
      ogDesc: 'Sumažinkite eksploatacines išlaidas, pagerinkite nuomininkų patirtį ir sumažinkite turto riziką su raktinio pristatymo IoT nekilnojamajam turtui Baltijoje.',
      heroBadge: 'Nekilnojamasis turtas ir infrastruktūra',
      heroTitle: 'Išmaniieji pastatai. Susieta infrastruktūra. Išmatuojami rezultatai.',
      heroSubtitle: 'Paverskite kiekvieną pastatą ir objektą į susietą, duomenimis grįstą turtą — nuo energijos ir nuotėkių aptikimo iki oro kokybės, užimtumo ir prieigos.',
      heroDesc: 'Vienas daugiafunkcis IoT rinkinys nekilnojamojo turto plėtotojams, savininkams, operatoriams ir turto valdytojams Latvijoje, Lietuvoje ir Estijoje.',
      ctaDemo: 'Užsisakyti 30 min demonstraciją',
      ctaExplore: 'Išnagrinėti sprendimus',
      ctaPilot: 'Pradėti bandomąjį projektą',
      trustReady: 'Paruoštas diegimui ir bandymams',
      trustMulti: 'Daugiafunkcis ryšys (LoRaWAN · NB-IoT · BLE · Palydovas)',
      trustLocal: 'Vietinė pagalba Baltijoje',
      trustScalable: 'Nuo 1 pastato iki pilnų portfelių',
      trustIntegration: 'BMS, PropTech ir ERP integracijos',
      solutionsBadge: 'Sprendimų apžvalga',
      solutionsTitle: 'Viena platforma, kiekvienas pastato naudojimo atvejis',
      solutionsDesc: 'Įdiekite tik tai, ko jums reikia šiandien — ir praplėskite rytoj. Visi sprendimai veikia ant tos pačios 0g Baltics IoT bazės, todėl kiekvienas naujas naudojimo atvejis pakartotinai naudoja jūsų esamą tinklą, įrenginius ir prietaisų skydus.',
      learnMore: 'Sužinoti daugiau',
      solEnergy: 'Energija ir submatavimas',
      solEnergyDesc: 'Submatuojama elektra, šiluma, dujos ir vanduo pagal aukštus, nuomininkus ar turtą — su realaus laiko vartojimu, palyginimu ir sąnaudų paskirstymu.',
      solLeak: 'Vandens nuotėkių aptikimas',
      solLeakDesc: 'Belaidžiai nuotėkių ir srauto jutikliai stove, įrangos patalpose ir vienetuose, kad būtų aptiktos problemos per minutes, o ne savaites.',
      solAir: 'Patalpų oro kokybė',
      solAirDesc: 'CO₂, temperatūros, drėgmės, PM2.5 ir TVOC stebėjimas sveikoms, atitinkančioms standartus ir produktyvioms vidaus erdvėms.',
      solOccupancy: 'Užimtumas ir erdvės naudojimas',
      solOccupancyDesc: 'Stalų, kambarių ir zonų užimtumo analizė, siekiant optimizuoti erdvę, sumažinti nepanaudotą pajėgumą ir planuoti pagal duomenis.',
      solCleaning: 'Valymo ir sanitarinių patalpų analizė',
      solCleaningDesc: 'Valymas pagal poreikį, remiantis tikru lankomumu ir sanitarinių patalpų naudojimu — mažiau valandų, geresni standartai.',
      solSafety: 'Sauga ir aplinka',
      solSafetyDesc: 'Panikos mygtukai, temperatūros, triukšmo ir aplinkos įspėjimai darbuotojams, nuomininkams ir kritinėms patalpoms.',
      solLockers: 'Siuntų ir išmaniosios spintelės',
      solLockersDesc: 'Pastatuose integruotos spintelės gyventojams, darbuotojams ir nuomininkams — siuntos, raktai, valymo įrankiai ir daugiau.',
      solParking: 'Automobilių stovėjimo užimtumas',
      solParkingDesc: 'Realaus laiko lauko ir vidaus automobilių stovėjimo užimtumas, orientavimas ir naudojimo analizė garažams ir aikštelėms.',
      solAsset: 'Turto ir įrangos sekimas',
      solAssetDesc: 'Sekite HVAC įrenginius, įrankius, FM įrangą ir vertingą turtą viename pastate ar pilname portfelyje.',
      solESG: 'ESG ir anglies atskaitomybė',
      solESGDesc: 'Automatizuoti, patikrinami energijos ir aplinkos duomenys GRESB, CSRD, ES Taksonomijai ir vidinei ESG atskaitomybei.',
      detailEnergyBadge: 'Energijos stebėjimas',
      detailEnergyTitle: 'Matykite, palyginkite ir sumažinkite energiją kiekviename pastate',
      detailEnergyDesc: 'Dauguma portfelių vis dar remiasi vienu pagrindiniu skaitikliu objekte. Mes pridedame belaidžius submatiklius ir aplinkos jutiklius, kad galėtumėte matyti vartojimą aukštu po aukšto ir nuomininko, susieti jį su lauko ir vidaus sąlygomis — būtent ten, iš kur ateina taupymas.',
      detailEnergyPoint1: 'Belaidis elektros, šilumos, dujų ir vandens submatavimas pagal zoną ar nuomininką',
      detailEnergyPoint2: 'Tiesioginiai prietaisų skydai, palyginimai ir anomalijų įspėjimai',
      detailEnergyPoint3: 'Nuomininkų sąnaudų paskirstymas ir energijos perdavimas',
      detailEnergyPoint4: 'Parengta BREEAM, LEED, GRESB ir CSRD duomenų poreikiams',
      statEnergySaving: 'Tipiškas energijos taupymas stebimuose objektuose',
      statPayback: 'Metai tipinis IG',
      statMonitoring: 'Nepertraukiamas stebėjimas',
      statAlerts: 'Realaus laiko anomalijų įspėjimai',
      detailLeakBadge: 'Nuotėkių aptikimas',
      detailLeakTitle: 'Apsaugokite savo pastatus nuo #1 išvengiamų žalos išlaidų',
      detailLeakDesc: 'Vandens žala yra viena brangiausių, labiausiai išvengiamų nuostolių nekilnojamajame turte. Mūsų belaidžiai jutikliai stebi stove, įrangos patalpas, vonios kambarius ir pažeidžiamas zonas 24/7 ir sukelia įspėjimus tą akimirką, kai vanduo atsiranda ten, kur neturėtų.',
      detailLeakPoint1: 'Baterijomis maitinami belaidžiai nuotėkių, srauto ir slėgio jutikliai',
      detailLeakPoint2: 'Nedelsiant įspėjimai FM, apsaugai ir savininkams — SMS, el. paštas, BMS',
      detailLeakPoint3: 'Galima automatinė vožtuvo uždarymo integracija',
      leakProblem: 'Paslėptos nestebimų pastatų sąnaudos',
      leakProblemDesc: 'Vienas neaptiktas nuotėkis gali kainuoti dešimtis tūkstančių eurų remontams, prastovoms ir draudimo įmokoms.',
      leakRisk1: 'Struktūriniai ir apdailos pažeidimai',
      leakRisk2: 'Nuomininkų trikdymas ir skundai',
      leakRisk3: 'Augančios draudimo įmokos ir franšizės',
      detailAirBadge: 'Patalpų oro kokybė',
      detailAirTitle: 'Sveikos, produktyvios vidaus aplinkos — išmatuotos',
      detailAirDesc: 'Patalpų oro kokybė tiesiogiai veikia produktyvumą, nuomininkų pasitenkinimą, nedarbingumą ir atitikimą. Mes diegiame diskretinius, baterijomis maitinamus jutiklius, kurie nuolat matuoja pagrindinius sveiko vidaus oro parametrus.',
      airTemp: 'Temperatūra',
      airHumidity: 'Drėgmė',
      airBenefits: 'Ką jūs gaunate',
      airBenefit1: 'Sveikesni, produktyvesni nuomininkai ir darbuotojai',
      airBenefit2: 'Geresnė HVAC kontrolė ir energijos taupymas',
      airBenefit3: 'Įrodymai WELL, BREEAM ir nuomininkų ataskaitoms',
      airBenefit4: 'Ankstyvas ventiliacijos problemų aptikimas',
      detailOccupancyBadge: 'Užimtumas',
      detailOccupancyTitle: 'Kurkite erdvę pagal realų naudojimą',
      detailOccupancyDesc: 'Išmatuokite, kaip iš tikrųjų naudojami stalai, kambariai ir ištisi aukštai — ir naudokite šiuos duomenis nuomoms optimizuoti, įrengimams planuoti, valymui gerinti ir HVAC tvarkaraščiams optimizuoti.',
      occupancyUse1: 'Stalų, kambarių ir zonų užimtumo analizė',
      occupancyUse2: 'Susirinkimų kambarių naudojimas ir neatvykimo aptikimas',
      occupancyUse3: 'HVAC ir apšvietimo automatizavimas pagal buvimą',
      occupancyUse4: 'Duomenys nuomos, įrengimo ir erdvės strategijos sprendimams',
      detailLockersBadge: 'Išmaniosios spintelės',
      detailLockersTitle: 'Išmaniosios spintelės, integruotos į kiekvieną pastatą',
      detailLockersDesc: 'Modulinė spintelių sprendimai gyventojams, darbuotojams, nuomininkams ir lankytojams — integruoti su prieigos kontrole ir mūsų IoT platforma.',
      lockerUse1: 'Siuntų pristatymas gyvenamiesiems ir biurų pastatams',
      lockerUse2: 'Raktų ir prieigos įrenginių valdymas',
      lockerUse3: 'Įrankių ir įrangos platinimas FM komandoms',
      lockerUse4: 'Click & collect mažmeninei prekybai ir mišriam naudojimui',
      segmentsBadge: 'Naudojimo atvejai pagal segmentą',
      segmentsTitle: 'Sukurta kiekvienam nekilnojamojo turto ir infrastruktūros profiliui',
      segmentsDesc: 'Nuo A klasės biurų iki pramonės parkų, kiekvienas segmentas turi specifinių prioritetų. Mes konfigūruojame tą patį IoT rinkinį, kad jis atitiktų kiekvieną iš jų.',
      segOffice: 'Biurai',
      segOfficeDesc: 'Energija, užimtumas, IAQ ir išmanioji prieiga nuomotojams ir korporatyviniams naudotojams.',
      segHotel: 'Viešbučiai ir svetingumas',
      segHotelDesc: 'Kambarių komfortas, energija, minibaras ir užkulisių stebėjimas prekės ženklų ir butikų objektams.',
      segResidential: 'Gyvenamoji ir daugiabučiai',
      segResidentialDesc: 'Submatavimas, nuotėkių apsauga, siuntų spintelės ir gyventojų komfortas BTR ir butams.',
      segRetail: 'Mažmeninė prekyba ir prekybos centrai',
      segRetailDesc: 'Lankomumas, šaltoji grandinė, HVAC ir bendrųjų patalpų energijos stebėjimas su nuomininkų sąnaudų paskirstymu.',
      segBanks: 'Bankai ir viešieji pastatai',
      segBanksDesc: 'Saugus aplinkos stebėjimas, energijos efektyvumas ir atitikimo atskaitomybė reguliuojamoms erdvėms.',
      segParks: 'Pramonės ir logistikos parkai',
      segParksDesc: 'Daugianuomininkų submatavimas, turto sekimas, vartų ir prieigos stebėjimas dideliuose objektuose.',
      segDevelopers: 'Plėtotojai',
      segDevelopersDesc: 'Iš anksto įrengtas IoT rinkinys, kuris didina turto vertę, ESG reitingą ir nuomininkų patrauklumą nuo pirmos dienos.',
      segAssetManagers: 'Turto ir nekilnojamojo turto valdytojai',
      segAssetManagersDesc: 'Viso portfelio prietaisų skydai, palyginimai ir automatizuoti ESG duomenys kiekvienam valdomam turtui.',
      platformBadge: 'Ryšys ir platforma',
      platformTitle: 'Vienas susietas rinkinys — nuo jutiklio iki prietaisų skydo',
      platformDesc: 'Mes projektuojame, diegiame ir eksploatuojame pilną IoT grandinę jūsų pastatams: įrenginius, ryšį, duomenų platformą, prietaisų skydus, įspėjimus ir integracijas. Jokios tiekėjų mozaikos, vienas partneris visam gyvavimo ciklui.',
      platformDevices: 'Pramoninės klasės įrenginiai',
      platformConnectivity: 'Daugiatinklinis ryšys',
      platformDashboard: 'Suvienyti prietaisų skydai',
      platformAlerts: 'Išmanieji įspėjimai',
      platformIntegration: 'BMS ir PropTech integracijos',
      platformSupport: 'Vietinė Baltijos pagalba',
      techTitle: 'Tinkamas tinklas kiekvienam naudojimo atvejui',
      techLoRaWAN: 'Didelis atstumas, mažas galios suvartojimas — idealus submatavimui, nuotėkiams, IAQ ir užimtumui visuose pastatuose.',
      techNBIoT: 'Gilus vidaus mobilusis padengimas — puikiai tinka kritiniam turtui ir atokiems pavieniams objektams.',
      techBLE: 'Vidaus mikro-lokacija ir turto sekimas pastatuose ir dideliuose objektuose.',
      learnMoreConnectivity: 'Peržiūrėti visas ryšio parinktis',
      bmsBadge: 'BMS ir BACnet integracija',
      bmsTitle: 'Prisijungia prie jūsų esamos BMS — be išplėšimo ir pakeitimo',
      bmsDesc: 'Mūsų belaidis IoT sluoksnis papildo jūsų esamą pastato valdymo sistemą realaus laiko, detaliais duomenimis. Jutikliai tiekiami iš anksto sukonfigūruoti, šliuzai veikia vietoje, o jūsų BMS gauna struktūrizuotus duomenis per protokolus, kuriuos ji jau kalba.',
      bmsProtocols: 'Palaikomi protokolai',
      bmsBACnet: 'BACnet/IP',
      bmsBACnetDesc: 'Gimtasis BACnet/IP jungtis — taškai tiesiogiai atvaizduojami į Niagara, Honeywell, Siemens ir kitas BMS platformas.',
      bmsModbus: 'Modbus TCP',
      bmsModbusDesc: 'Standartinė Modbus TCP išvestis senosioms valdymo sistemoms, HVAC įrangos patalpoms ir pramoninei automatikai.',
      bmsRest: 'REST API ir MQTT',
      bmsRestDesc: 'Atviri REST API, webhooks ir MQTT PropTech platformoms, pritaikytiems prietaisų skydams ir duomenų ežerams.',
      bmsLoRaWAN: 'LoRaWAN®',
      bmsLoRaWANDesc: 'Operatoriaus klasės LoRaWAN tinklas su 5–10+ metų baterijos tarnavimo laiku ir viso pastato aprėptimi iš vieno šliuzo.',
      bmsEcosystemTitle: 'Visiška jutiklių ekosistema',
      bmsEcosystemDesc: 'Pasinaudokite išbandytu TEKTELIC įrenginių portfeliu — komfortas, užimtumas, nuotėkis, CO₂, išmanusis kondicionierius, šaltoji grandinė, darbuotojų sauga — visi viename tinkle.',
      bmsItWorks: 'Tiesiog veikia',
      bmsItWorksDesc: 'Iš karto veikiantys įrenginiai, minimalios inžinerinės pastangos, mažos bendros nuosavybės išlaidos ir partneris, kuris valdo gyvavimo ciklą — projektavimą, diegimą, eksploataciją ir priežiūrą.',
      bmsBenefit1: 'Nereikia išplėšti ir pakeisti esamos BMS',
      bmsBenefit2: '5–10 metų įrenginio baterijos tarnavimo laikas',
      bmsBenefit3: 'Vienas šliuzas padengia visą pastatą',
      bmsBenefit4: 'Niagara / OpenBlue / Honeywell paruošta',
      fmBadge: 'Facility Management IoT rinkinys',
      fmTitle: 'LoRaWAN® sprendimai išmaniam pastatų valdymui',
      fmDesc: 'Šiuolaikinių objektų valdymas yra sudėtingesnis nei bet kada — augančios energijos kainos, didesni nuomininkų lūkesčiai, griežtesnė ESG atitiktis. Mūsų LoRaWAN® IoT rinkinys teikia realaus laiko įžvalgas, kurios sumažina eksploatacines išlaidas ir automatizuoja atitiktį visame portfelyje.',
      fmStat1: 'Valymo išlaidų sumažinimas per 60 dienų',
      fmStat2: 'Energijos taupymas optimizuojant HVAC ir užimtumą',
      fmStat3: 'Automatizuota ESG atskaitomybė — be vargo',
      fmStat4: 'Nepertraukiama oro, nuotėkių ir komforto stebėjimas',
      fmChallengesTitle: 'Sprendžiame jūsų pastatų valdymo iššūkius',
      fmChallengesDesc: 'Kiekvienas pastatas turi savo veiklos iššūkių. Mūsų sprendimai sukurti pagal jūsų prioritetus.',
      fmCh1Title: 'Komforto ir oro kokybės skundai',
      fmCh1Desc: 'Prasta temperatūros, CO₂ ir drėgmės kontrolė mažina nuomininkų pasitenkinimą.',
      fmCh2Title: 'Paslėptos rizikos ir incidentai',
      fmCh2Desc: 'Nuotėkiai ir įrangos gedimai greitai eskaluojasi be ankstyvo aptikimo.',
      fmCh3Title: 'Sunkiai įrodoma ESG ir atitiktis',
      fmCh3Desc: 'Tvarumo ir reguliavimo reikalavimus sunku įvykdyti be nuolatinių duomenų.',
      fmCh4Title: 'Lėti, rankiniai darbo procesai',
      fmCh4Desc: 'Apvažiavimai ir reaktyvi priežiūra švaisto laiką ir didina išlaidas.',
      fmCh5Title: 'Plėtra už bandomojo projekto ribų',
      fmCh5Desc: 'Daug IoT projektų sustoja po bandymų. Plėtra atrodo sudėtinga ir brangi.',
      fmCh6Title: 'Integracija ir duomenų siloai',
      fmCh6Desc: 'Duomenys pasiskirstę tarp atskirų sistemų, sunku gauti vieną vaizdą.',
      fmAppsTitle: 'Paruoštos naudoti programos',
      fmAppsDesc: 'Pradėkite naudoti IoT sprendimą per kelias minutes. Nuskaitykite QR kodą ir valdykite viską iš prietaisų skydo.',
      fmApp1Name: 'BREEZE',
      fmApp1Desc: 'Realaus laiko vidaus oro kokybės prietaisų skydai ir įspėjimai.',
      fmApp2Name: 'LeapX',
      fmApp2Desc: 'Greitas ir sklandus IoT šliuzų ir įrenginių aktyvavimas.',
      fmApp3Name: 'TEMPO',
      fmApp3Desc: 'Išmanus susirinkimų salių rezervavimas su Outlook® / Google integracija.',
      fmApp4Name: 'LOCUS',
      fmApp4Desc: 'Vizualizacija ir RTLS įrankiai objektų valdymo komandoms.',
      fmBmsTitle: 'Belaidė integracija su jūsų pastato valdymo sistema (BMS)',
      fmBmsDesc1: '0g Baltics leidžia lengvai perduoti realaus laiko duomenis apie oro kokybę, užimtumą, energiją, nuotėkius ir šaltąją grandinę tiesiai į jūsų pastato valdymo sistemą (BMS).',
      fmBmsDesc2: 'Mūsų belaidžiai sprendimai pašalina sudėtingą laidų tiesimą, sumažina įrengimo išlaidas ir lengvai plečiasi bet kuriame objekte. Palaikant pramonės standartinius protokolus, tokius kaip BACnet, Modbus ir Niagara, integracija yra sklandi — suteikiant vieną pilną vaizdą į jūsų pastatus, kad priimtumėte protingesnius sprendimus, mažintumėte išlaidas ir kurtumėte sveikesnę aplinką.',
      fmBenefitsTitle: 'Efektyvumas, komfortas ir atitiktis — viename sprendime',
      fmBen1Title: 'Mažesnės eksploatacinės išlaidos',
      fmBen1Desc: 'Sumažinkite valymo, priežiūros ir energijos išlaidas su realaus laiko įžvalgomis.',
      fmBen2Title: 'Protingesnis energijos naudojimas',
      fmBen2Desc: 'Sumažinkite energijos švaistymą optimizuodami HVAC, apšvietimą ir erdvę.',
      fmBen3Title: 'Sveikesnės, patogesnės erdvės',
      fmBen3Desc: 'Palaikykite optimalią oro kokybę, temperatūrą ir drėgmę nuomininkų gerovei.',
      fmBen4Title: 'Sklandi atitiktis',
      fmBen4Desc: 'Automatiškai rinkite ir teikite ESG ir reguliavimo duomenis — jokių rankinio darbo.',
      fmBen5Title: 'Patikimas saugos stebėjimas',
      fmBen5Desc: 'Iš karto aptikite nuotėkius, aplinkos rizikas ar neįprastą veiklą.',
      fmBen6Title: 'Mastelio paprastumas',
      fmBen6Desc: 'Pradėkite nuo vieno pastato ir plėskitės visame portfelyje be sudėtingumo.',
      relatedBadge: 'Susiję sprendimai',
      relatedTitle: 'Susiję sprendimai, kuriuos galite derinti',
      relatedPakuskapis: 'Mūsų siuntų spintelių tinklas gyvenamiesiems ir komerciniams pastatams Latvijoje.',
      relatedSmartLockers: 'Išmaniosios spintelės',
      relatedSmartLockersDesc: 'Pilnas modulinis spintelių asortimentas siuntoms, raktams, įrankiams ir click & collect.',
      relatedIAQ: 'Patalpų oro kokybė',
      relatedIAQDesc: 'Nepertraukiamas IAQ stebėjimas sveikesnėms, atitinkančioms standartus ir produktyvioms vidaus erdvėms.',
      relatedEnergy: 'Pastatų energijos efektyvumas',
      relatedEnergyDesc: 'Energijos stebėjimas, HVAC optimizavimas ir ESG parengta atskaitomybė pastatams.',
      faqBadge: 'DUK',
      faqTitle: 'Klausimai, kuriuos mums užduoda nekilnojamojo turto komandos',
      faq1Q: 'Ar reikia pakeisti esamus skaitiklius ar BMS?',
      faq1A: 'Ne. Mūsų jutikliai yra neinvaziniai ir baterijomis maitinami, ir integruojasi su jūsų esamais skaitikliais, BMS ir ERP. Jūs išlaikote tai, kas veikia, ir praplečiate matomumą ten, kur yra aklos zonos.',
      faq2Q: 'Ar galime pradėti nuo vieno pastato prieš išplėsdami visame portfelyje?',
      faq2A: 'Taip — dauguma klientų pradeda nuo vieno bandomojo pastato, patikrina IG per 3–6 mėnesius, tada mastelio keitimas likusiame portfelyje, pakartotinai naudojant tą pačią platformą ir procesus.',
      faq3Q: 'Kaip duomenys apsaugoti ir kur jie saugomi?',
      faq3A: 'Duomenys šifruojami perdavimo ir ramybės būsenoje, saugomi ES duomenų centruose, o prieiga yra pagal vaidmenis. Mes visiškai atitinkame BDAR ir galime integruotis su jūsų SSO.',
      faq4Q: 'Kokius tinklus naudojate Baltijoje?',
      faq4A: 'Mes deriname LoRaWAN, NB-IoT, LTE-M, BLE ir palydovinį ryšį, priklausomai nuo pastato, naudojimo atvejo ir prieinamo padengimo — todėl jūs niekada nesate užrakinti vienoje technologijoje.',
      faq5Q: 'Ar galime integruoti IoT duomenis su mūsų BMS, PropTech ar ERP?',
      faq5A: 'Taip. Mes teikiame REST API, webhooks ir iš anksto sukurtus jungtis bendroms BMS, PropTech platformoms, apskaitos ir ERP sistemoms.',
      faq6Q: 'Kaip tai palaiko mūsų ESG ir CSRD atskaitomybę?',
      faq6A: 'Kiekvienas matuojamas duomenų taškas (energija, vanduo, IAQ, užimtumas) saugomas su laiko žymekliais ir metaduomenimis ir gali būti eksportuojamas GRESB, BREEAM, LEED, CSRD ir ES Taksonomijos atskaitomybei.',
      faq7Q: 'Kaip atrodo tipiškas bandomasis projektas?',
      faq7A: 'Tipiškas bandymas apima 1 pastatą su 20–100 įrenginių per 3–6 mėnesius, su aiškiai apibrėžtais KPI (pvz., energijos taupymas, išvengti nuotėkių įvykiai, užimtumo įžvalgos). Mes rūpinamės projektavimu, diegimu ir mokymu.',
      ctaTitle: 'Paverskime jūsų pastatus duomenimis grįstu turtu',
      ctaDesc: 'Užsisakykite 30 minučių skambutį su mūsų nekilnojamojo turto IoT komanda. Mes nustatysime jūsų prioritetus, parodysime panašius diegimus ir pasiūlysime konkretų bandymą.',
    },

  },

  // =====================
  // ESTONIAN (EE)
  // =====================
  ee: {
    smartMeteringPage: {
      metaTitle: 'Nutika mõõtmise lahendused | IoT kommunaalettevõtetele | 0g Baltics',
      metaDesc: 'Juurutage nutika mõõtmise infrastruktuur LoRaWAN, Sigfox ja NB-IoT abil. Kaugandmete kogumine vee, gaasi, elektri ja sooja arvestitele.',
      heroBadge: 'Nutikas mõõtmine',
      heroTitle: 'Nutika mõõtmise infrastruktuur kommunaalettevõtetele',
      heroSubtitle: 'Juurutage kaugarvestite lugemise infrastruktuur kasutades LoRaWAN, Sigfox, NB-IoT ja mobiilitehnoloogiaid.',
      ctaPrimary: 'Taotle konsultatsiooni',
      ctaSecondary: 'Loe lähemalt',
      challengeBadge: 'Väljakutse',
      challengeTitle: 'Miks kommunaalettevõtted vajavad nutikat mõõtmist',
      challengeDesc: 'Traditsiooniline arvestite lugemine on kallis, veaohtlik ja ei paku reaalajas nähtavust tarbimismudelites.',
      challenge1: 'Käsitsi lugemise kulud',
      challenge1Desc: 'Kohalevisiidid on kallid ja aeganõudvad',
      challenge2: 'Ebatäpne arveldamine',
      challenge2Desc: 'Hindamisvead põhjustavad klientide vaidlusi',
      challenge3: 'Lekke tuvastamise viivitus',
      challenge3Desc: 'Probleemid avastatakse liiga hilja',
      challenge4: 'Piiratud andmed',
      challenge4Desc: 'Igakuised näidud ei paljasta tarbimismustreid',
      solutionBadge: 'Meie lahendus',
      solutionTitle: 'IoT-põhine nutikas mõõtmine',
      solutionDesc: 'Me juurutame nutika mõõtmise infrastruktuuri, mis ühendab olemasolevad arvestid pilvega.',
      solutionFeature1: 'Olemasolevate arvestite moderniseerimine',
      solutionFeature2: 'Mitu ühenduvusvalikut',
      solutionFeature3: 'Reaalajas tarbimisandmed',
      solutionFeature4: 'Lekete ja anomaaliate tuvastamine',
      solutionFeature5: 'Arvelduse integreerimine',
      solutionFeature6: 'Klientide portaal',
      techBadge: 'Tehnoloogiad',
      techTitle: 'Ühenduvusvalikud',
      techDesc: 'Valime õige tehnoloogia teie infrastruktuuri ja eelarve põhjal.',
      lorawanTitle: 'LoRaWAN',
      lorawanDesc: 'Pikk ulatus, madal energiatarve. Ideaalne tihedateks linnapiirkonna juurutusteks.',
      sigfoxTitle: 'Sigfox',
      sigfoxDesc: 'Lihtne integreerimine, üleriigiline leviala Baltikumis.',
      nbiotTitle: 'NB-IoT',
      nbiotDesc: 'Mobiilsidevõrgul põhinev. Hea mobiilse levialaga piirkondadele.',
      cellularTitle: 'Mobiilne (4G/LTE)',
      cellularDesc: 'Kõrgsageduslike andmeedastusnõuete jaoks.',
      useCasesBadge: 'Rakendused',
      useCasesTitle: 'Nutika mõõtmise kasutusjuhud',
      waterMetering: 'Vee mõõtmine',
      waterMeteringDesc: 'Jälgige tarbimist, tuvastage lekkeid ja vähendage veekadusid.',
      gasMetering: 'Gaasi mõõtmine',
      gasMeteringDesc: 'Gaasiarvestite kauglugemine kodumajapidamistele ja ettevõtetele.',
      electricityMetering: 'Elektri mõõtmine',
      electricityMeteringDesc: 'Alamõõtmine ärihoonetest ja tööstusrajatistest.',
      heatMetering: 'Soojuse mõõtmine',
      heatMeteringDesc: 'Kaugkütte tarbimise jälgimine ja arveldamine.',
      ctaTitle: 'Valmis oma mõõtmise infrastruktuuri moderniseerima?',
      ctaDesc: 'Võtke meiega ühendust, et arutada oma nutika mõõtmise projekti.',
      ctaButton: 'Võta ühendust',
      // Section: Network Quality
      networkBadge: 'Kriitiline infrastruktuur',
      networkTitle: 'Võrgu usaldusväärsus on kõik',
      networkDesc: 'Paljud nutika mõõtmise projektid ebaõnnestuvad mitte arvestite, vaid ebausaldusväärse ühenduvuse tõttu.',
      networkLocation1: 'Maa-alused kaevud',
      networkLocation2: 'Hoonete sees',
      networkLocation3: 'Keldrites',
      networkLocation4: 'Kaugetes kohtades',
      networkWarning: 'Kommunaalettevõtted vajavad operaatoriklassi infrastruktuuri, mis on loodud aastakümnete pikkuseks usaldusväärseks tööks.',
      // Section: Carrier Grade
      carrierBadge: 'Infrastruktuuri standardid',
      carrierTitle: 'Mida tähendab "operaatoriklass"?',
      carrierDesc: 'Operaatoriklassi infrastruktuur vastab telekommunikatsioonioperaatorite ja kriitilise infrastruktuuri nõuetele.',
      carrierFeature1: 'Võrgu tööaja garantii',
      carrierFeature1Desc: '99.9%+ kättesaadavus',
      carrierFeature2: 'Professionaalne riistvara',
      carrierFeature2Desc: 'Tööstusklassi seadmed',
      carrierFeature3: 'Pikaajaline tugi',
      carrierFeature3Desc: '10+ aasta elutsükkel',
      carrierFeature4: 'Turvanõuetele vastavus',
      carrierFeature4Desc: 'Ettevõtteklassi turvalisus',
      // Section: LoRaWAN
      lorawanSectionBadge: 'Peamine tehnoloogia',
      lorawanSectionTitle: 'LoRaWAN nutikaks mõõtmiseks',
      lorawanSectionDesc: 'LoRaWAN on juhtiv LPWAN tehnoloogia nutika mõõtmise juurutusteks.',
      lorawanAdvantage1: 'Pikk ulatus (kuni 15km)',
      lorawanAdvantage2: '10+ aasta akukestvus',
      lorawanAdvantage3: 'Sügav sissetung hoonetesse',
      lorawanAdvantage4: 'Madalad infrastruktuuri kulud',
      // Section: Cellular
      cellularSectionBadge: 'Alternatiivne tehnoloogia',
      cellularSectionTitle: 'Mobiilne IoT nutikaks mõõtmiseks',
      cellularSectionDesc: 'NB-IoT ja LTE-M pakuvad mobiilsidevõrgul põhinevat ühenduvust hea mobiilse levialaga piirkondades.',
    },

    iaqPage: {
      metaTitle: 'Siseõhu kvaliteedi seire | CO2 andurid | 0g Baltics',
      metaDesc: 'Professionaalne siseõhu kvaliteedi seire CO2, temperatuuri, niiskuse ja PM anduritega. Lahendused koolidele, kontoritele ja tervishoiuasutustele.',
      heroBadge: 'Siseõhu kvaliteet',
      heroTitle: 'Siseõhu kvaliteedi seirelahendused',
      heroSubtitle: 'Juurutage professionaalseid IAQ seiresüsteeme, et tagada tervislik sisekeskkond koolides, kontorites, tervishoiuasutustes ja avalikes hoonetes.',
      // Technology badges
      techCO2: 'CO2',
      techTemperature: 'Temperatuur',
      techHumidity: 'Niiskus',
      techPM25: 'PM2.5',
      techTVOC: 'TVOC',
      techOccupancy: 'Täituvus',
      // Section: Why IAQ Matters
      whyBadge: 'Miks see oluline on',
      whyTitle: 'Siseõhu kvaliteedi tähtsus',
      whyDesc: 'Inimesed veedavad 90% ajast siseruumides. Halb õhukvaliteet mõjutab tervist, tootlikkust ja kognitiivseid võimeid.',
      whyPoint1: 'CO2 tase üle 1000 ppm vähendab kognitiivseid funktsioone',
      whyPoint2: 'Halb ventilatsioon suurendab viiruste levikut',
      whyPoint3: 'Hästi ventileeritud klassides õppivad õpilased paremini',
      whyPoint4: 'Hoone elanikud teatavad vähem haiguspäevi',
      // Why outcomes list
      outcome1: 'Tervislikumad sisekeskkonnad elanikele',
      outcome2: 'Paremad ventilatsiooni otsused reaalsete andmete põhjal',
      outcome3: 'Tugi ESG ja nutikate hoonete strateegiatele',
      outcome4: 'Paranenud töökoha ja klassiruumi mugavus',
      outcome5: 'Vähendatud operatiivsed pimealad',
      // Key Decision Drivers
      keyDrivers: 'Peamised otsustegurid',
      driverHealth: 'Tervis',
      driverComfort: 'Mugavus',
      driverVentilation: 'Ventilatsioon',
      driverEfficiency: 'Efektiivsus',
      driverCompliance: 'Vastavus',
      // Section: What We Monitor
      monitorBadge: 'Parameetrid',
      monitorTitle: 'Mida me jälgime',
      typicalRange: 'Tüüpiline vahemik',
      co2: 'CO2 (süsinikdioksiid)',
      co2Desc: 'Peamine ventilatsiooni piisavuse näitaja',
      temperature: 'Temperatuur',
      temperatureDesc: 'Soojuslik mugavus elanikele',
      humidity: 'Niiskus',
      humidityDesc: 'Optimaalne vahemik 40-60% tervise jaoks',
      pm: 'Tahked osakesed (PM)',
      pmDesc: 'PM2.5 ja PM10 õhukvaliteedi hindamiseks',
      tvoc: 'TVOC',
      tvocDesc: 'Lenduvate orgaaniliste ühendite tuvastamine',
      // Section: Sensors - TEKTELIC
      sensorsBadge: 'Riistvara',
      sensorsTitle: 'Professionaalse klassi andurid',
      sensorsDesc: 'Me juurutame TEKTELIC ja Connected Inventions andureid usaldusväärseks, pikaajaliseks seireks.',
      tektelicBreezeDesc: 'Nutikas ruumiandur CO2, temperatuuri, niiskuse, õhurõhu ja ümbritseva valguse seireks. NDIR CO2 andur ±30 ppm + 3% täpsusega ja 5+ aastat aku tööaeg.',
      tektelicBreezeVDesc: 'Laiendab BREEZE PIR täituvuse tuvastamisega nutika ruumi funktsionaalsuseks. Ideaalne õhukvaliteedi korreleerimiseks ruumi kasutamisega nõudluspõhiseks ventilatsiooniks.',
      tektelicBreezeDDesc: 'CO2 nutikas ruumiandur E-Ink ekraaniga. Kombineerib LoRaWAN, BLE ja E-Ink madala energiatarbimise jaoks. Ekraan on juhtmevaba ja seda saab paigaldada seinale või lauale.',
      whenTektelic: 'Millal valida TEKTELIC',
      tektelicRelevant: 'TEKTELIC andurid on eriti asjakohased, kui teie projekt vajab:',
      tektelicBenefit1: 'LoRaWAN nutikate hoonete infrastruktuuri',
      tektelicBenefit2: 'Skaleeritavaid ettevõtte juurutusi',
      tektelicBenefit3: 'Operaatori klassi usaldusväärsust',
      tektelicBenefit4: 'Integratsiooni hoone IoT süsteemidega',
      tektelicBenefit5: 'Valikulisi BLE e-Ink ekraane',
      tektelicBenefit6: 'Professionaalset tuge ja dokumentatsiooni',
      bestFitTektelic: 'Parimad sobivad keskkonnad TEKTELIC jaoks',
      corpOffices: 'Ettevõtte kontorid',
      schoolsUnis: 'Koolid ja ülikoolid',
      publicBuildings: 'Avalikud hooned',
      healthcareFacilities: 'Tervishoiuasutused',
      // Section: Connected Inventions
      connectedBadge: 'Pika elueaga seire',
      connectedTitle: 'Connected Inventions AirWits seadmed',
      connectedDesc: '0g Baltics kasutab ka Connected Inventions seadmeid spetsiifiliste sisekeskkonna seire vajaduste jaoks. AirWits tooteperekond pakub eesmärgipõhiseid andureid, mis on optimeeritud pikaks aku tööajaks, väheseks hoolduseks ja kulutõhusaks juurutamiseks hooneportfellides.',
      airwitsBasicDesc: 'Temperatuuri ja niiskuse seire ±0.2°C täpsusega ja kuni 10 aasta aku tööaeg.',
      airwitsCO2Desc: 'CO2, temperatuuri ja niiskuse seire ventilatsiooni hindamiseks ja mugavuse optimeerimiseks.',
      airwitsCO2PlusDesc: 'Täiustatud CO2 seire e-ink ekraaniga, mis näitab reaalajas õhukvaliteedi olekut ja värvikooditud näitajaid.',
      airwitsPMDesc: 'PM1.0 / PM2.5 / PM10 tahkete osakeste seire temperatuuri ja niiskusega õhukvaliteedi hindamiseks.',
      flexSenseDesc: 'Modulaarne mitme anduri platvorm paindlikuks juurutamiseks kohandatavate andurite kombinatsioonidega erinevateks kasutusjuhtumiteks.',
      airwitsIAQDesc: 'TVOC seire temperatuuri ja niiskusega saasteainete sündmuste ja halva õhukvaliteedi tingimuste tuvastamiseks.',
      airwitsEcoSenseDesc: 'Päikeseenergial töötav, akuvaba või ülimadala hooldusega valik juurutamisstsenaariumiteks, mis nõuavad minimaalset sekkumist.',
      whenConnected: 'Millal valida Connected Inventions',
      connectedUseful: 'Connected Inventions seadmed on eriti kasulikud, kui teie projekt väärtustab:',
      connectedBenefit1: 'Kuni 10 aasta aku tööaeg',
      connectedBenefit2: 'Madal kogu omandikulu',
      connectedBenefit3: 'Sigfox või LoRaWAN valikud',
      connectedBenefit4: 'Lihtne suure ulatusega juurutamine',
      connectedBenefit5: 'Kulutõhus portfelli seire',
      connectedBenefit6: 'Hooldusvaba töö',
      // Section: Device Selection
      deviceSelectionBadge: 'Lahenduse arhitektuur',
      deviceSelectionTitle: 'Õige IAQ seadme valimine õigele hoonele',
      deviceSelectionDesc: '0g Baltics ei suru igale projektile ühte seadet. Selle asemel valime õige arhitektuuri sõltuvalt eelarvest, võrgu saadavusest, vajalikest parameetritest, aku ootustest, aruandlusvajadusest ja integratsiooni nõuetest.',
      tableBuilding: 'Hoone tüüp',
      tableNeed: 'Projekti vajadus',
      tableSensor: 'Soovitatav andur',
      tableWhy: 'Miks see sobib',
      // Table rows
      officePortfolio: 'Kontoriportfell',
      officeNeed: 'CO2, temperatuur, niiskus korruste lõikes',
      officeWhy: 'LoRaWAN skaleeritavus, täituvuse korrelatsioon, BLE ekraani valik',
      classroomSchool: 'Klassiruum / Kool',
      classroomNeed: 'Ventilatsiooni seire, õpilaste mugavus',
      classroomWhy: 'Reaalajas CO2 nähtavus, pikk aku tööaeg, lihtne paigaldus',
      uniCampus: 'Ülikooli linnak',
      uniNeed: 'Mitme hoone IAQ strateegia',
      uniSensor: 'Segatüüpi: TEKTELIC + AirWits',
      uniWhy: 'LoRaWAN infrastruktuur + kulutõhus laienemine',
      healthcareClinics: 'Tervishoid / Kliinikud',
      healthcareNeed: 'Tundlike keskkondade seire',
      healthcareIaqSensor: 'AirWits PM + AirWits IAQ',
      healthcareWhy: 'PM ja TVOC kriitilised patsientide keskkondadele',
      residentialAreas: 'Elamu ühisalad',
      residentialNeed: 'Põhiline mugavus ja ventilatsioon',
      residentialSensor: 'AirWits / AirWits CO2',
      residentialWhy: 'Kulutõhus, vähe hooldust, pikk aku',
      municipalBuildings: 'Omavalitsuste hooned',
      municipalNeed: 'Avalike ruumide seire, vastavus',
      municipalWhy: 'Operaatori klassi usaldusväärsus, auditi jäljed',
      industrialIndoor: 'Tööstuslikud siseruumid',
      industrialNeed: 'Protsessi kontroll, töötajate mugavus',
      industrialSensor: 'AirWits PM + CO2',
      industrialWhy: 'Tahkete osakeste seire tööstuskeskkondadele',
      // Section: IoT vs Manual
      iotVsManualBadge: 'Operatiivne eelis',
      iotVsManualTitle: 'Miks IoT-põhine IAQ seire ületab käsitsi kohapealseid kontrolle',
      iotVsManualDesc: 'Traditsiooniline siseõhu kvaliteedi hindamine tugineb perioodilisele käsitsi kontrollile kaasaskantavate seadmetega. Tehnik külastab iga ruumi, teeb mõõtmise, registreerib tulemuse ja liigub edasi. Sellel lähenemisel on põhilised piirangud, mille IoT-põhine seire kõrvaldab.',
      iotVsManualDesc2: 'Ühendatud andurid pakuvad pidevaid andmeid juhuslike hetkepiltide asemel. Nad genereerivad hoiatusi kaebuste ootamise asemel. Nad loovad ajaloolisi trende analüüsiks ja võrdluseks. Ja nad skaleeruvad portfellides ilma tööjõukulude proportsionaalse suurenemiseta.',
      continuous247: 'Pidev 24/7 seire',
      continuous247Desc: 'Andmed iga 15-30 minuti järel, mitte kord kvartalis',
      realTimeAlerts: 'Reaalajas hoiatused',
      realTimeAlertsDesc: 'Kohene teade künniste ületamisel',
      historicalTrends: 'Ajaloolised trendid',
      historicalTrendsDesc: 'Analüüsige mustreid nädalate, kuude ja hooaegade lõikes',
      portfolioBenchmark: 'Portfelli võrdlusanalüüs',
      portfolioBenchmarkDesc: 'Võrrelge hooneid ja tuvastage kõrvalekalded',
      betterDecisions: 'Paremad otsused',
      betterDecisionsDesc: 'Andmepõhine hooldus ja ventilatsiooni optimeerimine',
      scalableOps: 'Skaleeritavad operatsioonid',
      scalableOpsDesc: 'Jälgige sadu ruume ilma lisapersonalita',
      // Section: IAQ Energy
      iaqEnergyBadge: 'Hoone jõudlus',
      iaqEnergyTitle: 'Tasakaalustage tervis, mugavus ja energiatõhusus',
      iaqEnergyDesc1: 'Siseõhu kvaliteedi seire ei puuduta ainult tervist—see puudutab ka hoone jõudlust ja tegevuse efektiivsust. Hooned moodustavad ligikaudu 40% globaalsest energiatarbimisest, kusjuures HVAC süsteemid esindavad märkimisväärset osa. Intelligentne ventilatsioon, mis põhineb tegelikel sisetingimustel, võib vähendada energiaraiskamist säilitades samal ajal tervisliku õhukvaliteedi.',
      co2Ventilation: 'CO2 ja täituvuspõhine ventilatsioon',
      co2VentilationDesc: 'CO2 ja täituvuse jälgimine võib toetada nutikamaid ventilatsiooni otsuseid. Kui ruumid on tühjad või vähesel kasutamisel, saab ventilatsiooni vähendada. Kui CO2 tõuseb, saab värske õhu varustust suurendada. See nõudluspõhine lähenemine optimeerib energiakasutust säilitades õhukvaliteeti.',
      tempHumidityInsights: 'Temperatuuri ja niiskuse ülevaated',
      tempHumidityInsightsDesc: 'Temperatuuri ja niiskuse trendid aitavad mõista hoone mugavust ja HVAC jõudlust. Püsivate probleemidega tsoonide tuvastamine võimaldab sihitud parandusi süsteemiüleste kohanduste asemel. Paremad andmed viivad tõhusamate operatsioonideni.',
      iaqEnergyDesc2: 'IAQ andmed toetavad paremat rajatiste haldamist, pakkudes objektiivseid mõõtmisi, mis informeerivad hooldusprioriteetide, renoveerimise otsuste ja süsteemi uuenduste kohta. Nutikad hooned vajavad mõõtmist enne optimeerimist—te ei saa parandada seda, mida te ei mõõda.',
      iaqEnergyNote: 'Kuigi IAQ seire pakub andmeid, mis võivad informeerida energia optimeerimise strateegiaid, sõltuvad konkreetsed energiasäästud paljudest teguritest, sealhulgas hoone süsteemidest, kliimast ja tegevuspraktikatest. Me keskendume täpsete mõõtmiste ja rakendatavate ülevaadete pakkumisele, mitte konkreetsete ROI numbrite ülelubamisele.',
      // Section: Standards
      standardsBadge: 'Regulatiivne raamistik',
      standardsTitle: 'Standardid, juhised ja IAQ parimad praktikad',
      standardsDesc: 'Siseõhu kvaliteedi projekte juhivad sageli terviseootused, ESG ja nutikate hoonete poliitikad, koolide ja töökohtade mugavuse eesmärgid ning ventilatsiooni ja riskijuhtimise juhised. Asjakohaste raamistike mõistmine aitab määratleda seirestrateegiat, ventilatsiooni eesmärke ja hoone jõudluse ootusi.',
      whoGuidelines: 'WHO õhukvaliteedi juhised',
      whoGuidelinesDesc: 'Maailma Terviseorganisatsioon pakub õhukvaliteedi juhiseid, sealhulgas tahkete osakeste künniseid. Need soovitused on asjakohased PM-iga seotud tervisemõjude mõistmiseks ja seire eesmärkide seadmiseks hoonetes, mis on mures elanike tervise pärast.',
      ashraeStandard: 'ASHRAE standard 241',
      ashraeStandardDesc: 'ASHRAE 241 käsitleb nakkuslike aerosoolide kontrolli ja pakub juhiseid tervislikumate sisekeskkondade jaoks. See standard on eriti asjakohane tervishoiu-, haridus- ja ärihoonete jaoks, mis soovivad vähendada õhu kaudu leviva ülekande riske.',
      enStandard: 'EN 16798 perekond',
      enStandardDesc: 'Euroopas on EN 16798 standardid asjakohased sisekeskkonna kvaliteedi, ventilatsiooni nõuete ja kontrolliraamistike jaoks. Need pakuvad juhiseid hoonete projekteerimiseks ja käitamiseks, et vastata sisekeskkonna kvaliteedi ootustele.',
      standardsImportant: 'IAQ seire toetab tõenduspõhist hoonete haldamist ja võib aidata organisatsioonidel dokumenteerida tingimusi, tuvastada probleeme ja seada prioriteete parandusmeetmetele. Kuigi andurid pakuvad andmeid, sõltub vastavus konkreetsetele standarditele sellest, kuidas neid andmeid kasutatakse laiemates hoonete haldus- ja ventilatsiooni strateegiates.',
      iaqHelps: 'IAQ seire aitab:',
      iaqHelp1: 'Dokumenteerida sisetingimusi aja jooksul',
      iaqHelp2: 'Tuvastada sekkumist vajavaid ruume',
      iaqHelp3: 'Toetada ventilatsiooni optimeerimise jõupingutusi',
      iaqHelp4: 'Pakkuda auditi jälgi vastavuse jaoks',
      iaqHelp5: 'Võrdlusanalüüs hooneportfellide lõikes',
      iaqHelp6: 'Informeerida renoveerimis- ja uuendamisotsuseid',
      // Section: Use Cases
      useCasesBadge: 'Rakendused',
      useCasesTitle: 'Kus siseõhu kvaliteedi seire pakub väärtust',
      useCasesDesc: 'IAQ seire pakub operatiivseid eeliseid laias hoonetüüpide valikus. Igal keskkonnal on spetsiifilised nõuded, mis määravad anduri valiku ja juurutamisstrateegia.',
      ucOffices: 'Kontorid',
      ucOfficesDesc: 'Parandage töökoha mugavust ja keskkonna kvaliteeti. Jälgige CO2 tasemeid piisava ventilatsiooni tagamiseks, optimeerige temperatuuri tootlikkuse jaoks ja demonstreerige pühendumust töötajate heaolule.',
      ucSchools: 'Koolid ja ülikoolid',
      ucSchoolsDesc: 'Toetage klassiruumi ventilatsiooni ja looge paremad õpikeskkonnad. Uuringud näitavad, et kõrgenenud CO2 tasemed klassides korreleeruvad vähenenud kognitiivse jõudluse ja suurenenud puudumistega.',
      ucHealthcare: 'Tervishoid ja kliinikud',
      ucHealthcareDesc: 'Jälgige tundlikke sisekeskkondi, kus õhukvaliteet mõjutab otseselt patsientide tulemusi. PM ja TVOC seire on eriti asjakohane kliinilistes seadetes, ootesaalides ja patsientide hooldusalades.',
      ucMunicipal: 'Omavalitsuste hooned',
      ucMunicipalDesc: 'Looge nähtavust avalike sisekeskkondade jaoks, sealhulgas raamatukogud, kogukonnakeskused ja valitsuse kontorid. Toetage läbipaistvust ja demonstreerige vastutustundlikku avalike rajatiste haldamist.',
      ucCommercial: 'Ärikinnisvara',
      ucCommercialDesc: 'Võrdlus- ja jälgige mitut hoonet järjepidevalt portfellides. Pakkuge üürnikele tervisliku hoone mandaate ja toetage ESG aruandluse nõudeid.',
      ucIndustrial: 'Tööstuslikud siseruumid',
      ucIndustrialDesc: 'Jälgige sisetingimusi, kus mugavus, protsessi kontroll või töötajate ohutus on oluline. Jälgige tahkeid osakesi tootmiskeskkondades ja tagage vastavus töökoha tervisestandarditele.',
      // CTA
      ctaTitle: 'Parandage oma hoonete õhukvaliteeti',
      ctaButton: 'Taotle demo',
      // Section: Why 0g Baltics
      whyUsBadge: 'Teie partner',
      whyUsTitle: 'Miks 0g Baltics',
      whyUsDesc: 'Pakume praktilist IoT juurutamise kogemust siseõhu kvaliteedi seire projektides kogu Balti piirkonnas.',
      whyUsFeature1: 'Balti turu fookus',
      whyUsFeature1Desc: 'Kohalik kohalolek ja piirkondlike nõuete mõistmine',
      whyUsFeature2: 'Praktiline IoT kogemus',
      whyUsFeature2Desc: 'Reaalne juurutamise kogemus erinevat tüüpi hoonetes',
      whyUsFeature3: 'Mitmekesine ühenduvus',
      whyUsFeature3Desc: 'LoRaWAN, Sigfox ja hübriidarhitektuurid',
      // Section: Sectors
      sectorsBadge: 'Tööstusharud',
      sectorsTitle: 'IAQ seire sektorite kaupa',
      sectorSchools: 'Koolid ja haridus',
      sectorSchoolsDesc: 'Parandage õpikeskkonda parema õhukvaliteediga',
      sectorOffices: 'Kontorid ja töökohad',
      sectorOfficesDesc: 'Suurendage tootlikkust ja vähendage haiguspäevi',
      sectorHealthcare: 'Tervishoiuasutused',
      sectorHealthcareDesc: 'Kriitiline õhukvaliteet patsientide ohutuse tagamiseks',
      sectorPublic: 'Avalikud hooned',
      sectorPublicDesc: 'Omavalitsuste hooned ja riigiasutused',
      // FAQ Section
      faqBadge: 'KKK',
      faqTitle: 'Korduma kippuvad küsimused',
      faqDesc: 'Kõik, mida peate teadma siseõhu kvaliteedi seire kohta',
      faq1Q: 'Mis on siseõhu kvaliteedi seire?',
      faq1A: 'IAQ seire kasutab andureid CO2, temperatuuri, niiskuse, PM ja VOC pidevaks mõõtmiseks siseruumides.',
      faq2Q: 'Milliseid parameetreid peaksin jälgima?',
      faq2A: 'Alustage CO2 ja temperatuuriga. Lisage niiskus, PM või VOC vastavalt oma vajadustele.',
      faq3Q: 'Kui palju andureid mul vaja on?',
      faq3A: 'Tavaliselt üks andur ruumi või tsooni kohta. Suured ruumid võivad vajada mitut andurit.',
      faq4Q: 'Milline on aku tööaeg?',
      faq4A: 'Professionaalsed andurid töötavad tavaliselt 5-10 aastat akutoitel.',
    },

    smartCityPage: {
      metaTitle: 'Nutika linna IoT infrastruktuur | 0g Baltics',
      metaDesc: 'Ehitage nutika linna infrastruktuur LoRaWAN võrkude, keskkonnaseire ja ühendatud linnateenustega.',
      heroBadge: 'Nutikad linnad',
      heroTitle: 'Nutika linna IoT infrastruktuur',
      heroSubtitle: 'Me aitame omavalitsustel juurutada IoT infrastruktuuri nutikamate, tõhusamate linnade jaoks.',
      solutionsBadge: 'Lahendused',
      solutionsTitle: 'Nutika linna rakendused',
      envMonitoring: 'Keskkonnaseire',
      envMonitoringDesc: 'Õhukvaliteet, müratase ja ilmastiku jälgimine kogu linnas.',
      smartLighting: 'Nutikas valgustus',
      smartLightingDesc: 'Ühendatud tänavavalgustus hämarolemise ja rikete tuvastamisega.',
      wasteManagement: 'Jäätmekäitlus',
      wasteManagementDesc: 'Nutikad prügikastid täitumistaseme andurite ja marsruudi optimeerimisega.',
      parking: 'Parkimiskohtade saadavus',
      parkingDesc: 'Reaalajas parkimiskohtade saadavus ja suunamissüsteemid.',
      flooding: 'Üleujutuste seire',
      floodingDesc: 'Veetaseme andurid varajase hoiatuse süsteemidele.',
      traffic: 'Liiklusmustrid',
      trafficDesc: 'Sõidukite loendamine ja liiklusvoo analüüs.',
      // What Is section
      whatIsBadge: 'Aluse mõistmine',
      whatIsTitle: 'Mis on nutika linna IoT infrastruktuur?',
      whatIsDesc: 'Nutika linna infrastruktuur on ühendatud linnateenuste digitaalne selgroog.',
      dataCollected: 'Kogutud linnaandmed hõlmavad:',
      airQuality: 'Õhukvaliteet',
      water: 'Veetarbimine',
      energy: 'Energiakasutus',
      assets: 'Avalikud varad',
      // Why Projects Fail section
      failBadge: 'Levinud väljakutsed',
      failTitle: 'Miks paljud nutika linna projektid ebaõnnestuvad',
      failDesc: 'Paljud nutika linna algatused algavad isoleeritud pilootprojektidega ilma pikaajalise infrastruktuuri strateegiata.',
      commonProblems: 'Levinud probleemid:',
      problem1: 'Killustunud ühenduvus mitme kokkusobimatu süsteemiga',
      problem2: 'Ebausaldusväärsed võrgud, mis ei suuda toetada kasvavat seadmete arvu',
      problem3: 'Pilootprojektid, mida ei saa laiendada kogu linna juurutamiseks',
      problem4: 'Andmesiilod, mis takistavad ühtset linnaintellekti',
      solutionText: 'Lahendus:',
      solutionDesc: 'Edukad nutika linna strateegiad algavad jagatud ühenduvuse infrastruktuuriga, mis suudab toetada paljusid teenuseid—mitte isoleeritud pilootprojektidega, mis ei saa kasvada.',
      // Connectivity Foundation section
      connectivityBadge: 'Võrgutehnoloogiad',
      connectivityTitle: 'Nutikate linnade alus: Usaldusväärne IoT ühenduvus',
      connectivityDesc: 'Nutikad linnad vajavad võrke, mis suudavad toetada tuhandeid seadmeid, mis on hajutatud suurtes linnapiirkondades. Erinevad tehnoloogiad teenivad erinevaid kasutusjuhtumeid ja kõige edukamad juurutused ühendavad mitut ühenduvusvõimalust.',
      lorawanName: 'LoRaWAN',
      lorawanDesc: 'Pikk ulatus, madal energiatarve kogu linna katvuseks',
      nbiotName: 'NB-IoT',
      nbiotDesc: 'Mobiilsidevõrgul põhinev sügavaks läbitungimiseks',
      ltemName: 'LTE-M',
      ltemDesc: 'Mobiilsetele varadele ja suuremale ribalaiusele',
      bleName: 'BLE võrgud',
      bleDesc: 'Siseruumide ja lähedusrakenduste jaoks',
      satelliteName: 'Satelliit',
      satelliteDesc: 'Kaugühenduvuseks ja varuühenduvuseks',
      connectivityNote: '0g Baltics projekteerib mitmeühenduvuse arhitektuure, tagades, et iga seade kasutab kõige tõhusamat sidetehnoloogiat. See lähenemine maksimeerib usaldusväärsust, minimeerides samal ajal tegevuskulusid.',
      // Carrier Grade section
      carrierBadge: 'Infrastruktuuri standardid',
      carrierTitle: 'Miks linnad vajavad operaatoritaseme infrastruktuuri',
      carrierDesc1: 'Linnainfrastruktuur peab pidevalt töötama palju aastaid—sageli aastakümneid. Seega peab nutika linna ühenduvus olema projekteeritud operaatoritaseme infrastruktuurina, mitte tarbijatase lahendustena, mis ei suuda vastata omavalitsuste usaldusväärsuse nõuetele.',
      carrierDesc2: 'Need põhimõtted pärinevad telekommunikatsioonitööstusest ja tagavad, et linnateenused jäävad aja jooksul töökorras, olenemata ilmastikutingimustest, võrgu koormusest või süsteemimuudatustest.',
      carrierProvide: 'Operaatoritaseme võrgud pakuvad:',
      highAvailability: 'Kõrge saadavus',
      highAvailabilityDesc: '99.9%+ tööaja eesmärgid',
      secureCommunications: 'Turvaline side',
      secureCommunicationsDesc: 'Krüpteeritud andmeedastus',
      scalableArchitecture: 'Skaleeritav arhitektuur',
      scalableArchitectureDesc: 'Kasvab koos linna vajadustega',
      professionalMonitoring: 'Professionaalne seire',
      professionalMonitoringDesc: '24/7 võrguoperatsioonid',
      longTermReliability: 'Pikaajaline usaldusväärsus',
      longTermReliabilityDesc: '10+ aasta infrastruktuur',
      redundancy: 'Redundantsus',
      redundancyDesc: 'Ümberlülitumisvõimalused',
      infrastructureLayers: 'Infrastruktuuri kihid',
      layer5: 'Kiht 5',
      layer5Name: 'Linnateenused',
      layer5Desc: 'Parkimine, valgustus, keskkond',
      layer4: 'Kiht 4',
      layer4Name: 'Linnaplatvorm',
      layer4Desc: 'Andmehaldus ja analüütika',
      layer3: 'Kiht 3',
      layer3Name: 'Võrguserverid',
      layer3Desc: 'Seadmete ja andmete haldamine',
      layer2: 'Kiht 2',
      layer2Name: 'Lüüsid',
      layer2Desc: 'Professionaalsed tugijaamad',
      layer1: 'Kiht 1',
      layer1Name: 'Seadmed',
      layer1Desc: 'Andurid ja lõpp-punktid',
      // LoRaWAN for Cities section
      lorawanBadge: 'LPWAN tehnoloogia',
      lorawanTitle: 'LoRaWAN võrgud linna IoT jaoks',
      lorawanMainDesc: 'LoRaWAN on üks laialdasemalt kasutatavaid tehnoloogiaid nutika linna juurutustes üle maailma. Selle tehnilised omadused muudavad selle ideaalselt sobivaks tuhandete andurite ühendamiseks linnakeskkondades.',
      lorawanFeature1Title: 'Pika ulatusega ühenduvus',
      lorawanFeature1Desc: 'Üksikud lüüsid võivad katta mitu kilomeetrit linnakeskkondades, vähendades infrastruktuuri investeeringuid.',
      lorawanFeature2Title: 'Suurepärane läbitungimine',
      lorawanFeature2Desc: 'LoRa signaalid jõuavad anduriteni hoonete sees, maa all ja keerulistes asukohtades kogu linnas.',
      lorawanFeature3Title: 'Madal energiatarve',
      lorawanFeature3Desc: 'Akutoitega andurid võivad töötada aastaid ilma hoolduse või asendamiseta.',
      lorawanFeature4Title: 'Suur võrgu mahutavus',
      lorawanFeature4Desc: 'Väike arv lüüse võib ühendada tuhandeid andureid kogu linnas.',
      lorawanNote: '0g Baltics juurutab operaatoritaseme LoRaWAN infrastruktuuri, kasutades professionaalseid TEKTELIC lüüse ja turvalist võrguarhitektuuri, mis on loodud omavalitsuse taseme usaldusväärsuse jaoks.',
      stat1Value: '15+ km',
      stat1Label: 'Ulatus maapiirkondades',
      stat2Value: '5+ km',
      stat2Label: 'Ulatus linnapiirkondades',
      stat3Value: '10+ aastat',
      stat3Label: 'Anduri aku eluiga',
      stat4Value: '10 000+',
      stat4Label: 'Seadmeid lüüsi kohta',
      // Multi-Service Infrastructure section
      multiServiceBadge: 'Ühtne infrastruktuur',
      multiServiceTitle: 'Üks infrastruktuur, palju linnateenuseid',
      multiServiceDesc: 'Kui linn juurutab jagatud IoT infrastruktuuri, võivad paljud teenused töötada samas võrgus. See lähenemine vähendab kulusid, lihtsustab operatsioone ja võimaldab linnadel aja jooksul innovatsiooni laiendada.',
      servicesShare: 'Teenused, mis võivad jagada infrastruktuuri:',
      smartParking: 'Nutikas parkimine',
      airQualityMonitoring: 'Õhukvaliteedi seire',
      smartWaterMetering: 'Nutikas veearvesti',
      wasteManagementService: 'Jäätmekäitlus',
      municipalAssetTracking: 'Omavalitsuse varade jälgimine',
      smartStreetLighting: 'Nutikas tänavavalgustus',
      smartBuildingMonitoring: 'Nutikate hoonete seire',
      energyManagement: 'Energiahaldus',
      // Innovation Ecosystem section
      innovationBadge: 'Majandusareng',
      innovationTitle: 'LPWAN võrkude võimaldatud uued teenused ja majandusareng',
      innovationDesc: 'Linna ulatuses LPWAN võrk ei ole ainult tehniline infrastruktuur. See muutub innovatsiooniplatvormiks, mis võimaldab majandustegevust ja uusi digitaalseid teenuseid. Linnad, mis juurutavad jagatud IoT infrastruktuuri, näevad sageli uute ettevõtete ja teenuste tekkimist.',
      organizationsTitle: 'Organisatsioonid, mis saavad kasutada linna IoT infrastruktuuri:',
      localStartups: 'Kohalikud idufirmad',
      techCompanies: 'Tehnoloogiaettevõtted',
      universities: 'Ülikoolid',
      researchLabs: 'Uurimislaboratooriumid',
      publicServiceProviders: 'Avalike teenuste osutajad',
      privateOperators: 'Eraoperaatorid',
      innovationBarrier: 'Selle asemel, et ehitada oma ühenduvuse infrastruktuuri, saavad need organisatsioonid tugineda linna jagatud IoT võrgule. See vähendab oluliselt innovatsiooni barjääri.',
      innovationAnalogy: 'Mõelge sellele nagu kiudoptilisele lairibaühendusele: IoT infrastruktuur muutub pikaajalise majandusarengu aluseks, võimaldades teenuseid, mis ei saa eksisteerida ilma usaldusväärse ühenduvuseta.',
      newServicesTitle: 'Uued teenused, mis võivad tekkida linna IoT infrastruktuuril:',
      smartLogistics: 'Nutikad logistikateenused',
      smartLogisticsDesc: 'Viimase miili kohaletoimetamise optimeerimine',
      envMonitoringStartups: 'Keskkonnaseire idufirmad',
      envMonitoringStartupsDesc: 'Linna õhu ja müra analüütika',
      equipmentTracking: 'Seadmete jälgimisteenused',
      equipmentTrackingDesc: 'Ehitus- ja tööstusvarad',
      sharedMobility: 'Jagatud liikuvuse seire',
      sharedMobilityDesc: 'Jalgrattad, tõukerattad, sõidukid',
      energyOptimization: 'Energia optimeerimise teenused',
      energyOptimizationDesc: 'Hoonete ja võrgu haldamine',
      smartBuildingProviders: 'Nutikate hoonete teenusepakkujad',
      smartBuildingProvidersDesc: 'Ärikinnisvara teenused',
      // City Intelligence section
      intelligenceBadge: 'Linnaanalüütika',
      intelligenceTitle: 'Anduritest linnaintellektini',
      intelligenceDesc: 'Nutika linna platvormid koguvad andmeid tuhandetelt anduritelt, mis on paigutatud linnapiirkondadesse. Need andmed aitavad linnaoperaatoritel mõista linnasüsteeme ja teha paremaid otsuseid infrastruktuuri, teenuste ja planeerimise kohta.',
      dataHelps: 'Andmed aitavad linnadel mõista:',
      trafficPatterns: 'Liiklusmustreid ja ummikuid',
      envConditions: 'Keskkonnatingimusi ja õhukvaliteeti',
      infrastructureUsage: 'Infrastruktuuri kasutust ja hooldusvajadusi',
      energyConsumption: 'Energiatarbimist erinevates rajatistes',
      assetUtilization: 'Avalike varade kasutamist',
      urbanTrends: 'Suundumusi linnaplaneerimiseks',
      intelligenceConclusion: 'Aja jooksul saavad linnad optimeerida tegevusi ja teha paremaid planeerimisotsuseid, tuginedes objektiivsetele andmetele, mitte oletustele.',
      // Deployment Strategy section
      deploymentBadge: 'Juurutamise lähenemine',
      deploymentTitle: 'Praktiline juurutamisstrateegia linnadele',
      deploymentDesc: 'Edukad nutika linna juurutused järgivad järkjärgulist lähenemist, mis vähendab riski, võimaldades samal ajal pikaajalist skaleeritavust.',
      step01: '01',
      step01Title: 'Juurutage usaldusväärne IoT ühenduvuse infrastruktuur',
      step01Desc: 'Looge operaatoritaseme võrgukatvus prioriteetsetes piirkondades. Paigaldage professionaalsed lüüsid ja konfigureerige turvaline võrguarhitektuur, mis suudab toetada tuhandeid seadmeid.',
      step02: '02',
      step02Title: 'Ühendage prioriteetsed teenused',
      step02Desc: 'Juurutage esialgsed kasutusjuhtumid, nagu nutikas parkimine, õhukvaliteedi seire või veearvesti. Keskenduge teenustele, mis pakuvad selget väärtust ja demonstreerivad infrastruktuuri võimalusi.',
      step03: '03',
      step03Title: 'Laiendage järk-järgult uutele teenustele',
      step03Desc: 'Lisage aja jooksul uusi teenuseid ja osakondi. Jagatud infrastruktuur toetab täiendavaid kasutusjuhtumeid ilma uute võrguinvesteeringuteta.',
      step04: '04',
      step04Title: 'Kasutage andmeanalüütikat operatsioonide parandamiseks',
      step04Desc: 'Kasutage kogutud andmeid linnateenuste optimeerimiseks, planeerimisotsuste teavitamiseks ja väärtuse demonstreerimiseks kodanikele ja sidusrühmadele.',
      // Why 0g Baltics section
      whyBadge: 'Teie partner',
      whyTitle: 'Miks linnad töötavad 0g Balticsiga',
      whyDesc: '0g Baltics pakub infrastruktuuri ekspertiisi, mida linnad vajavad edukate nutika linna juurutuste ehitamiseks. Meie eesmärk on aidata linnadel ehitada usaldusväärset digitaalset infrastruktuuri, mis toetab pikaajalist innovatsiooni.',
      service1Title: 'Operaatoritaseme IoT ühenduvus',
      service1Desc: 'Võrgud, mis on projekteeritud omavalitsuste usaldusväärsuse nõuetele',
      service2Title: 'LoRaWAN infrastruktuuri juurutamine',
      service2Desc: 'Professionaalne võrgu projekteerimine, paigaldamine ja haldamine',
      service3Title: 'Mitmeühenduvuse arhitektuur',
      service3Desc: 'Hübriidvõrgud, mis ühendavad LoRaWAN, mobiilsidet ja muud',
      service4Title: 'Nutika linna nõustamine',
      service4Desc: 'Strateegilised juhised infrastruktuuri planeerimiseks',
      service5Title: 'Integratsioon linnaplatvormidega',
      service5Desc: 'Ühendage andurid linna haldussüsteemidega',
      relatedSolutions: 'Seotud 0g Baltics lahendused',
      relatedConnectivity: 'Mitmevõrgu ühenduvus',
      relatedConnectivityDesc: 'LoRaWAN, NB-IoT, Sigfox lahendused',
      relatedTektelic: 'TEKTELIC infrastruktuur',
      relatedTektelicDesc: 'Operaatoritaseme LoRaWAN lüüsid',
      relatedMetering: 'Nutikas arvestus',
      relatedMeteringDesc: 'Vee, energia, gaasi arvestus',
      relatedAssetTracking: 'Varade jälgimine',
      relatedAssetTrackingDesc: 'Omavalitsuse seadmete seire',
      relatedAirQuality: 'Õhukvaliteedi seire',
      relatedAirQualityDesc: 'Keskkonna IoT andurid',
      // CTA section
      ctaTitle: 'Ehitage oma nutika linna infrastruktuur',
      ctaDesc: 'Võtke ühendust 0g Balticsiga, et projekteerida skaleeritav nutika linna ühenduvus ja infrastruktuur. Me aitame omavalitsustel ja linnadel ehitada digitaalset alust pikaajaliste linnainnovatsioonide jaoks.',
      ctaButton: 'Rääkige meie meeskonnaga',
    },

    smartLockersPage: {
      metaTitle: 'Nutikad kapid | Automatiseeritud paki- ja tööriistade kapid | 0g Baltics',
      metaDesc: 'Nutikate kappide lahendused pakiveole, tööriistade haldamisele ja avalikele teenustele. Automatiseeritud, ühendatud ja skaleeritav.',
      heroBadge: 'Nutikad kapid',
      heroTitle: 'Nutikate kappide lahendused',
      heroSubtitle: 'Automatiseeritud kappide infrastruktuur logistikale, tööstusoperatsioonidele ja avalikele teenustele.',
      // Use case badges
      parcelDelivery: 'Pakivedu',
      toolManagement: 'Tööriistade haldamine',
      beachLockers: 'Rannakapid',
      retailPickup: 'Jaemüügi vastuvõtt',
      keyManagement: 'Võtmete haldamine',
      // Types
      parcelLockers: 'Pakikapid',
      parcelLockersDesc: 'Automatiseeritud pakkide kohaletoimetamine ja vastuvõtmine viimase miili logistikale.',
      toolLockers: 'Tööriistade haldamise kapid',
      toolLockersDesc: 'Tööstusliku tööriistade jaotamine ja jälgimine ligipääsukontrolliga.',
      libraryLockers: 'Raamatukogu kapid',
      libraryLockersDesc: 'Automatiseeritud raamatute vastuvõtmine ja tagastamine avalikele raamatukogudele.',
      discoverToolTracking: 'Avastage meie tööriistade jälgimise lahendused',
      // What Are Smart Lockers
      whatAreBadge: 'Tehnoloogia mõistmine',
      whatAreTitle: 'Mis on nutikad kapid?',
      whatAreDesc1: 'Nutikad kapid on ühendatud hoiusüsteemid, mis võimaldavad esemeid turvaliselt hoida ja kätte saada digitaalse autentimise abil. Erinevalt traditsioonilistest kappidest, mis tuginevad füüsilistele võtmetele või kombinatsioonlukkudele, integreeruvad nutikad kapid pilveplatvormidega ja pakuvad mitmeid ligipääsumeetodeid.',
      whatAreDesc2: 'Nutikad kapid asendavad käsitsi hoiusüsteemid automatiseeritud turvalise ligipääsuga, pakkudes täielikku nähtavust kasutusmudelitesse, inventaritasemetesse ja ligipääsuajalukku. See teeb need ideaalseks suure mahuga operatsioonidele, kus jälgimine ja vastutus on olulised.',
      accessMethods: 'Kasutajad saavad kappidele ligi:',
      accessMobile: 'Mobiilirakendused',
      accessPIN: 'PIN-koodid',
      accessRFID: 'RFID-kaardid',
      accessQR: 'QR-koodid',
      accessBadge: 'Töötaja pääsmed',
      accessPayment: 'Maksesüsteemid',
      cloudCapabilities: 'Pilveplatvormi võimalused:',
      capAccessControl: 'Ligipääsukontroll',
      capAccessControlDesc: 'Hallake, kes saab milliseid kappe ja millal kasutada',
      capNotifications: 'Teavitused',
      capNotificationsDesc: 'SMS, e-posti ja push-teavitused kasutajatele',
      capUsageTracking: 'Kasutuse jälgimine',
      capUsageTrackingDesc: 'Kõigi kappide tehingute täielik ajalugu',
      capAvailability: 'Kappide saadavus',
      capAvailabilityDesc: 'Reaalajas nähtavus tühjadesse sektsioonidesse',
      capAnalytics: 'Analüütika',
      capAnalyticsDesc: 'Kasutusmudelid ja operatiivsed ülevaated',
      // Smart Locker Models
      modelsBadge: 'Riistvara valikud',
      modelsTitle: 'Nutikate kappide mudelid',
      modelsDesc: '0g Baltics pakub mitmeid kappide arhitektuure sõltuvalt kasutusjuhtumist. Iga mudel võimaldab paindlikku sektsioonide suuruste konfiguratsiooni vastavalt hoiustavatele esemetele.',
      towerLockers: 'Tornikkapid',
      towerLockersDesc: 'Vertikaalsed kappide süsteemid, mis on loodud pakiveole ja logistikakeskustele. Kompaktne jalajälg maksimaalse hoiuvõimsusega.',
      towerFeature1: 'Suur mahutavus',
      towerFeature2: 'Väike jalajälg',
      towerFeature3: 'Väliskasutuseks valmis',
      storeLockers: 'Poekapid',
      storeLockersDesc: 'Suured modulaarsed kapid, mis sobivad jaemüügi vastuvõtuks ja ärikeskkondadele. Seinale kinnitatavad või vabalt seisvad konfiguratsioonid.',
      storeFeature1: 'Modulaarne disain',
      storeFeature2: 'Jaemüügi integratsioon',
      storeFeature3: 'Kõrge nähtavus',
      slingLockers: 'Sling-kapid',
      slingLockersDesc: 'Kompaktsed kapid, ideaalsed väikestele ruumidele, kontoritele ja siseruumidele. Suurepärane ettevõtte ja ülikoolilinnaku juurutustele.',
      slingFeature1: 'Ruumisäästlik',
      slingFeature2: 'Sisedisain',
      slingFeature3: 'Lihtne paigaldus',
      batteryPowered: 'Akutoitega',
      batteryPoweredDesc: 'Akudega toitavad kapid, mis võimaldavad juurutamist ilma elektrijuhtmeteta. Ideaalne kaugetele ja ajutistele asukohtadele.',
      batteryFeature1: 'Ilma juhtmeteta',
      batteryFeature2: 'Paindlik asukoht',
      batteryFeature3: 'Madal paigalduskulu',
      // Retail Pickup Lockers
      retailTitle: 'Jaemüügi vastuvõtukapid',
      retailDesc: 'Jaemüüjad kasutavad kappe, et võimaldada klientidel veebipõhiseid tellimusi kiiresti kätte saada. Kliki-ja-korja teenused on üha populaarsemad, kapid võimaldavad 24/7 vastuvõttu ilma personali abita.',
      retailAdvantages: 'Eelised:',
      retailAdv1: '24/7 vastuvõtu saadavus',
      retailAdv2: 'Vähendatud poe järjekorrad',
      retailAdv3: 'Kiirem klienditeenindus',
      retailAdv4: 'Automatiseeritud vastuvõtu logistika',
      retailAdv5: 'E-kaubanduse integratsioon',
      retailNote: 'Jaemüügi kapid on üha enam kasutusel supermarketites, kaubanduskeskustes ja e-kaubanduse täitmiskeskustes.',
      // Beach Lockers
      beachTitle: 'Ranna- ja vaba aja kapid',
      beachDesc: 'Nutikad kapid on ideaalsed vaba aja keskkondadele, kus külastajad vajavad turvalist hoiukohta isiklikele asjadele. Akutoitega valikud võimaldavad juurutamist ilma infrastruktuuri investeeringuteta.',
      idealLocations: 'Ideaalsed asukohad:',
      beachLoc1: 'Rannad ja rannikualad',
      beachLoc2: 'Pargid ja puhkealad',
      beachLoc3: 'Spordirajatised',
      beachLoc4: 'Turismikohad',
      beachLoc5: 'Ürituste kohad',
      beachNote: 'Kasutajad pääsevad kappidele ligi mobiilirakenduste või maksesüsteemide kaudu, võimaldades operaatoritele monetiseerimist.',
      // Key Management
      keyTitle: 'Võtmete haldamise kapid',
      keyDesc: 'Ühendatud kapid saavad hallata füüsilisi võtmeid autopargi, kinnisvarahalduse ja seadmete ligipääsu jaoks. Iga tehing jälgitakse digitaalselt, luues auditi jälje, mis parandab operatiivset turvalisust ja vastutust.',
      keyFeature1: 'Autopargi võtmete haldamine',
      keyFeature2: 'Kinnisvara ja rajatiste ligipääs',
      keyFeature3: 'Seadmete võtmete jaotamine',
      keyFeature4: 'Töövõtjate ja külastajate ligipääs',
      keyFeature5: 'Täielik digitaalne auditi jälg',
      // Battery Powered
      batteryTitle: 'Akutoitega kapid',
      batteryDesc: 'Akutoitega kapid võimaldavad paigaldust asukohtades ilma elektri infrastruktuurita. See vähendab dramaatiliselt paigalduskulusid ja võimaldab paindlikku juurutamist asukohtades, kus juhtmed oleksid ebapraktilised.',
      batteryFeat1: 'Elektrijuhtmeid pole vaja',
      batteryFeat2: 'Kiire ja lihtne paigaldus',
      batteryFeat3: 'Paindlik asukoha valik',
      batteryFeat4: 'Madalam kogupagalduskulu',
      batteryFeat5: 'Ideaalne ajutistele juurutustele',
      batteryUseCases: 'Levinud kasutusjuhtumid:',
      batteryUseCasesDesc: 'Rannad, pargid, ajutised paigaldused, kauged alad ja asukohad, kus elektri infrastruktuur on kallis või kättesaamatu.',
      // Software Platform
      platformBadge: 'Pilvehaldus',
      platformTitle: 'Kappide haldusplatvorm',
      platformDesc: 'Nutikaid kappe hallatakse pilveplatvormi kaudu, mis pakub täielikku nähtavust ja kontrolli kappide võrkude üle. Operaatorid saavad jälgida staatust, hallata kasutajaid ja analüüsida kasutusmudeleid kõikjalt.',
      platformCapabilities: 'Platvormi võimalused:',
      platAvailability: 'Saadavuse jälgimine',
      platAvailabilityDesc: 'Reaalajas sektsioonide staatus',
      platUserMgmt: 'Kasutajate haldamine',
      platUserMgmtDesc: 'Ligipääsukontroll ja õigused',
      platNotifications: 'Teavitused',
      platNotificationsDesc: 'SMS, e-post, push-hoiatused',
      platAnalytics: 'Kasutuse analüütika',
      platAnalyticsDesc: 'Trendid ja mustrid',
      platRemoteConfig: 'Kaugkonfiguratsioon',
      platRemoteConfigDesc: 'Seadete kauguuendamine',
      platSecurityLogs: 'Turvalisuse logid',
      platSecurityLogsDesc: 'Täielik auditi jälg',
      // API and Integrations
      apiBadge: 'Avatud arhitektuur',
      apiTitle: 'Avatud API-d ja integratsioonid',
      apiDesc: 'Kappide platvormid saavad integreeruda olemasolevate ärisüsteemidega API-de kaudu, võimaldades kappidel saada osaks laiemast digitaalsest infrastruktuurist, mitte eraldiseisvateks lahendusteks.',
      intEcommerce: 'E-kaubandus',
      intEcommerceDesc: 'Tellimuste haldamine',
      intIdentity: 'Identiteet',
      intIdentityDesc: 'SSO ja LDAP',
      intPayment: 'Makse',
      intPaymentDesc: 'Kaart ja mobiil',
      intLogistics: 'Logistika',
      intLogisticsDesc: 'TMS integratsioon',
      intSmartCity: 'Nutikas linn',
      intSmartCityDesc: 'Linnaplatvormid',
      intERP: 'ERP',
      intERPDesc: 'Ettevõtte süsteemid',
      // Smart Lockers for Cities
      citiesBadge: 'Linnainfrastruktuur',
      citiesTitle: 'Nutikate kappide infrastruktuur linnadele',
      citiesDesc: 'Linnad saavad juurutada kappide võrke osana nutika linna infrastruktuurist. Pakikapid vähendavad tarneliiklust, avalikud hoiukapid teenindavad elanikke ja turiste ning seadmete kapid toetavad munitsipaaloperatsioone.',
      municipalApps: 'Munitsipaal kappide rakendused:',
      cityApp1: 'Pakiveo võrgud, mis vähendavad liiklust',
      cityApp2: 'Avalik hoiustamine elanikele ja turistidele',
      cityApp3: 'Ranna- ja vaba aja piirkonna teenused',
      cityApp4: 'Munitsipaalseadmete haldamine',
      cityApp5: 'Linna sõidukite ja rajatiste võtmete ligipääs',
      learnSmartCity: 'Loe lähemalt nutika linna infrastruktuuri kohta',
      networksInfra: 'Kappide võrgud saavad infrastruktuuriks',
      networksInfraDesc: 'Kui linnad juurutavad kappide võrke, loovad nad jagatud infrastruktuuri, mis teenib mitmeid eesmärke—vähendades viimase miili tarneliiklust, pakkudes elanike teenuseid ja toetades munitsipaaloperatsioone.',
      cityBenefit1: 'Vähendab tarnesõidukite liiklust',
      cityBenefit2: 'Võimaldab 24/7 elanike teenuseid',
      cityBenefit3: 'Toetab turismi ja vaba aega',
      cityBenefit4: 'Parandab munitsipaalefektiivsust',
      // Why 0g Baltics
      whyBadge: 'Teie partner',
      whyTitle: 'Miks juurutada nutikaid kappe 0g Balticsiga',
      whyDesc: '0g Baltics kombineerib töökindla riistvara, IoT ühenduvuse, pilveplatvormid ja avatud integratsioonid, et pakkuda kappide süsteeme, mis on loodud mitmele tööstusharule ja suuremahulistele juurutustele.',
      whyFeature1: 'Töökindel riistvara',
      whyFeature1Desc: 'Tööstusliku taseme kapid, mis on ehitatud suure mahuga operatsioonidele',
      whyFeature2: 'IoT ühenduvus',
      whyFeature2Desc: 'LoRaWAN, mobiilne ja WiFi valikud igale keskkonnale',
      whyFeature3: 'Pilveplatvorm',
      whyFeature3Desc: 'Täielik haldus, analüütika ja teavitussüsteem',
      whyFeature4: 'Avatud integratsioonid',
      whyFeature4Desc: 'API-d e-kaubandusele, logistikale, identiteedile ja maksesüsteemidele',
      whyFeature5: 'Mitme tööstusharu kogemus',
      whyFeature5Desc: 'Logistika, jaemüügi, tööstus- ja avaliku sektori juurutused',
      relatedSolutions: 'Seotud lahendused',
      assetTrackingLabel: 'Varade jälgimine',
      assetTrackingDesc: 'Jälgige tööriistu ja seadmeid väljaspool kappi',
      connectivityLabel: 'IoT ühenduvus',
      connectivityDesc: 'LoRaWAN, NB-IoT ja mobiilsed valikud',
      smartCityLabel: 'Nutika linna infrastruktuur',
      smartCityDesc: 'Linna IoT juurutused',
      // FAQ
      faqBadge: 'KKK',
      faqTitle: 'Korduma kippuvad küsimused',
      faq1Q: 'Mis on nutikad kapid?',
      faq1A: 'Nutikad kapid on ühendatud hoiusüsteemid, mis võimaldavad esemeid turvaliselt hoida ja kätte saada digitaalse autentimise abil, nagu PIN-koodid, mobiilirakendused, RFID-kaardid või QR-koodid. Erinevalt traditsioonilistest füüsiliste võtmetega kappidest ühenduvad nutikad kapid pilveplatvormidega, mis haldavad ligipääsukontrolli, saadavad teavitusi ja pakuvad kasutusanalüütikat.',
      faq2Q: 'Kuidas pakikapid töötavad?',
      faq2A: 'Kui pakk toimetatakse kappi, saab saaja SMS-i või e-posti teavituse unikaalse PIN-koodiga. Nad külastavad kapi asukohta, sisestavad PIN-koodi puuteekraanile ja nende pakki sisaldav sektsioon avaneb automaatselt. Pakke hoitakse tavaliselt 7 päeva enne saatjale tagastamist. Igaüks PIN-koodiga saab kätte tulla—isikutuvastust pole vaja.',
      faq3Q: 'Millised tööstusharud kasutavad nutikaid kappe?',
      faq3A: 'Nutikaid kappe kasutatakse paljudes tööstusharudes: logistika ja e-kaubandus (pakivedu), jaemüük (kliki-ja-korja), tootmine ja ehitus (tööriistade haldamine), külalislahkus ja turism (ranna- ja vaba aja kapid), ettevõtte kontorid (posti- ja pakiruumid), tervishoid (proovide ja ravimite hoiustamine) ning munitsipaalteenused (avalik hoiustamine ja linnateenused).',
      faq4Q: 'Kas kapid saavad töötada ilma elektrita?',
      faq4A: 'Jah. Akutoitega kapid saavad töötada pikemaid perioode ilma elektri infrastruktuurita. See võimaldab juurutamist kohtades nagu rannad, pargid, ajutised paigaldused ja kauged alad, kus juhtmed oleksid kallid või ebapraktilised. Akukapid suhtlevad mobiilside või LoRaWAN võrkude kaudu.',
      faq5Q: 'Kui turvalised on nutikad kapid?',
      faq5A: 'Nutikad kapid pakuvad mitut turvalisuse tasandit: digitaalne ligipääsukontroll tagab, et ainult volitatud kasutajad saavad sektsioone avada, iga tehing logitakse luues täieliku auditi jälje, sektsioonid kasutavad töökindlaid lukustusmehhanisme ning pilveplatvormid võimaldavad kaughaldust ja hoiatusi. Kõrge turvalisuse rakenduste jaoks saavad kapid integreeruda biomeetrilise autentimisega.',
      faq6Q: 'Kas kappide süsteemid saavad integreeruda olemasolevate platvormidega?',
      faq6A: 'Jah. Kaasaegsed kappide platvormid pakuvad API-sid, mis võimaldavad integratsiooni e-kaubanduse platvormidega (tellimuste haldamiseks), identiteedisüsteemidega (SSO, LDAP), makselüüsidega, logistikatarkvaraga (TMS), nutika linna platvormidega ja ettevõtte ERP süsteemidega. See võimaldab kappidel saada osaks laiematest äri töövoogudest, mitte eraldiseisvateks süsteemideks.',
      ctaTitle: 'Juurutage nutikad kapid oma organisatsioonis',
      ctaButton: 'Loe lähemalt',
    },

    buildingEnergyPage: {
      metaTitle: 'Hoonete energiatõhusus | IoT lahendused | 0g Baltics',
      metaDesc: 'Vähendage hoonete energiatarbimist IoT seire ja nutikate juhtseadmetega.',
      heroBadge: 'Energiatõhusus',
      heroTitle: 'Hoonete energiatõhususe lahendused',
      heroSubtitle: 'Jälgige ja optimeerige energiatarbimist ärihoonetes IoT andurite ja nutikate juhtseadmetega.',
      // Hero badges
      heroBadge1: 'Energia seire',
      heroBadge2: 'HVAC optimeerimine',
      heroBadge3: 'Täituvuse tuvastamine',
      heroBadge4: 'Analüütika',
      heroBadge5: 'Kulude vähendamine',
      // Problem
      problemBadge: 'Väljakutse',
      problemTitle: 'Hooned raiskavad energiat',
      problemDesc: 'Ärihooned moodustavad 40% energiatarbimisest. Enamik töötab ebaefektiivselt.',
      hvacBadge: 'Suurim võimalus',
      hvacTitle: 'HVAC optimeerimine',
      hvacDesc: 'HVAC süsteemid moodustavad 40-60% hoonete energiatarbimisest.',
      hvacParadoxTitle: 'Praeguste regulatsioonisüsteemide paradoks',
      hvacParadoxDesc: 'Traditsioonilised HVAC regulatsioonisüsteemid paradoksaalselt põhjustavad sageli suuremat energiatarbimist.',
      hvacProblem1: 'Sisse/välja lülitamise tsüklid tekitavad temperatuuri kõikumisi',
      hvacProblem2: 'Fikseeritud ajakavad ignoreerivad tegelikke kasutusmustreid',
      hvacProblem3: 'Puudub korrelatsioon CO2 ja ventilatsiooni vahel',
      hvacProblem4: 'Süsteemid kompenseerivad üledimensioneeritud seadmetega',
      harmonySolution: 'Nutikas lahendus',
      harmonyTitle: 'TEKTELIC Harmony',
      harmonyDesc: 'Intelligentne termostaat, mis ühendab CO2 seire, temperatuuri/niiskuse andurid ja HVAC juhtimise.',
      harmonyBenefit: 'Korreleerides CO2 tasemeid ventilatsioonivajaduste ja tegeliku täituvusega, optimeerib Harmony HVAC tööd, vähendades energiatarbimist 20-40%.',
      // Invisibility section
      invisibilityBadge: 'Peamine väljakutse',
      invisibilityTitle: 'Energiatarbimine on sageli nähtamatu',
      invisibilityDesc: 'Paljudel organisatsioonidel puudub selge ülevaade sellest, kuidas nende hooned tegelikult energiat tarbivad.',
      invisibility1: 'Milline seade tarbib kõige rohkem energiat',
      invisibility2: 'Millal toimuvad tarbimise tipud',
      invisibility3: 'Kus on ebaefektiivsus',
      invisibility4: 'Kas tarbimine kasvab',
      withoutMonitoring: 'Ilma seireta',
      withMonitoring: 'IoT seirega',
      // Stats
      stat1: 'globaalsest energiast tarbivad hooned',
      stat2: 'tüüpiline energiaraiskamine ärihoonetes',
      stat3: 'säästud saavutatavad nutika seirega',
      stat4: 'tüüpiline tasuvusaeg',
      // IoT Monitoring Section
      iotMonitoringBadge: 'Tehnoloogiline alus',
      iotMonitoringTitle: 'IoT energia seiresüsteemid',
      iotMonitoringDesc: 'IoT andurid võimaldavad pidevat hoonete energiatarbimise jälgimist detailsel tasemel. Erinevalt traditsioonilisest kommunaalmõõtmisest, mis annab ainult kuu koondandmeid, püüavad nutikad andurid üksikasjalikku teavet selle kohta, kuidas, millal ja kus energiat tarbitakse.',
      iotDevicesMeasure: 'IoT seadmed saavad mõõta:',
      measureElectricity: 'Elekter',
      measureElectricityDesc: 'Kogutarbimine ja ahelataseme tarbimine',
      measureWater: 'Vesi',
      measureWaterDesc: 'Kasutusmustrid ja lekete tuvastamine',
      measureTemperature: 'Temperatuur',
      measureTemperatureDesc: 'Tsoonipõhised soojusandmed',
      measureHumidity: 'Niiskus',
      measureHumidityDesc: 'Mugavus ja süsteemi jõudlus',
      measureOccupancy: 'Täituvus',
      measureOccupancyDesc: 'Ruumi kasutamise mustrid',
      measureEquipment: 'Seadmed',
      measureEquipmentDesc: 'Tööaeg ja operatiivne staatus',
      iotDataTransmission: 'Need andmed edastatakse juhtmevabalt LoRaWAN või mobiilside võrkude kaudu digitaalsetele platvormidele, kus operaatorid saavad analüüsida tarbimismustreid ja tuvastada optimeerimise võimalusi.',
      // Energy Analytics Section
      analyticsBadge: 'Andmepõhised otsused',
      analyticsTitle: 'Energia analüütika ja optimeerimine',
      analyticsDesc: 'Kui energiaandmed on kogutud, aitavad analüütikatööriistad tuvastada ebaefektiivsusi ja optimeerimise võimalusi. Kaasaegsed energiahalduse platvormid rakendavad algoritme mustrite, anomaaliate ja korrelatsioonide tuvastamiseks, mida oleks käsitsi võimatu tuvastada.',
      typicalInsights: 'Tüüpilised ülevaated hõlmavad:',
      insight1: 'Ebanormaalne energiatarbimine, mis viitab seadmete probleemidele',
      insight2: 'Seadmed, mis töötavad väljaspool planeeritud töötunde',
      insight3: 'Ebaefektiivne kütte- või jahutussüsteemi töö',
      insight4: 'Halb hoone ajastamine, mis raiskab energiat',
      insight5: 'Tarbimistrendid, mis vajavad tähelepanu',
      analyticsConclusion: 'Organisatsioonid saavad neid ülevaateid kasutada paranduste prioriteetide seadmiseks, muudatuste mõju kontrollimiseks ja hoonete töö pidevaks optimeerimiseks.',
      // Occupancy Section
      occupancyBadge: 'Nõudluspõhine juhtimine',
      occupancyTitle: 'Täituvusel põhinev energiahaldus',
      occupancyDesc: 'Hooneid köetakse, jahutatakse ja valgustatakse sageli isegi siis, kui ruumid on täiesti tühjad. Koosolekuruumid, mis on valmistatud kohtumisteks, mis ei toimu, kontorid, mida konditsioneeritakse nädalavahetustel, terved korrused, mida valgustatakse öösiti—see kõik tähendab märkimisväärset energiaraiskamist.',
      occupancyDesc2: 'Täituvusandurid võimaldavad hoonetel kohandada energiatarbimist tegelike kasutusmudelitega. Kui ruumid on hõivamata, saab valgustust hämardada või välja lülitada, HVAC saab vähendada võimsust ja seadmed saavad minna ooterežiimi. Kui inimesed saabuvad, reageerivad süsteemid automaatselt.',
      highImpactApps: 'Suure mõjuga rakendused:',
      appMeetingRooms: 'Koosolekuruumid',
      appClassrooms: 'Klassiruumid',
      appIndividualOffices: 'Individuaalsed kontorid',
      appPublicBuildings: 'Avalikud hooned',
      appCommonAreas: 'Ühisalad',
      appParkingFacilities: 'Parkimisrajatised',
      // Sensors Section
      sensorsBadge: 'Andurite infrastruktuur',
      sensorsTitle: 'Andurid energiatõhusatele hoonetele',
      sensorsDesc: 'Energia optimeerimine tugineb mitut tüüpi anduritele, mis töötavad koos, et luua täielik pilt hoone tingimustest. Iga andur pakub spetsiifilisi andmeid, mis aitavad kaasa üldisele energia intelligentsusele.',
      sensorTemp: 'Temperatuuriandurid',
      sensorTempDesc: 'Jälgige soojustingimusi tsoonide lõikes, et optimeerida kütet ja jahutust. Tuvastage kuumad ja külmad kohad, mis viitavad HVAC tasakaalustamatusele.',
      sensorHumidity: 'Niiskusandurid',
      sensorHumidityDesc: 'Jälgige niiskustaset, mis mõjutab mugavust ja süsteemi jõudlust. Optimeerige õhukuivatamist ja vältige hallituse tingimusi.',
      sensorCO2: 'CO2 andurid',
      sensorCO2Desc: 'Mõõtke õhukvaliteeti ja ventilatsiooni piisavust. Võimaldage nõudluspõhist ventilatsiooni, mis tasakaalustab õhukvaliteeti ja energiat.',
      sensorOccupancy: 'Täituvusandurid',
      sensorOccupancyDesc: 'Tuvastage ruumikasutus reaalajas. Võimaldage automaatne valgustuse, HVAC ja seadmete kohandamine kohalolu põhjal.',
      sensorEnergy: 'Energiamõõturid',
      sensorEnergyDesc: 'Mõõtke elektritarbimist hoone, korruse või ahela tasemel. Tuvastage suure tarbimisega seadmed ja kasutusmustrid.',
      sensorWater: 'Veemõõturid',
      sensorWaterDesc: 'Jälgige veetarbimist ja tuvastage lekkeid. Tuvastage ebanormaalne kasutamine, mis viitab torustiku probleemidele või raiskamisele.',
      learnMoreIAQ: 'Lisateavet siseõhu kvaliteedi seire kohta',
      // Standards Section
      standardsBadge: 'Regulatiivne kontekst',
      standardsTitle: 'Energiatõhususe standardid ja regulatsioonid',
      standardsDesc: 'Paljud riigid nõuavad nüüd paremat hoonete energiatõhusust. Regulatiivsed raamistikud muutuvad rangemaks ja organisatsioonid vajavad üha enam seiresüsteeme, et toetada vastavust ja jätkusuutlikkuse aruandlust.',
      epbdTitle: 'EL hoonete energiatõhususe direktiiv (EPBD)',
      epbdDesc: 'Nõuab liikmesriikidelt minimaalsete hoonete energiatõhususe nõuete kehtestamist ja energiasertifitseerimissüsteemide rakendamist. Direktiiv suunab peaaegu nullenergiahoonetele.',
      epcTitle: 'Energiatõhususe sertifikaadid (EPC)',
      epcDesc: 'Hooneid tuleb hinnata energiatõhususe järgi, sertifikaadid on nõutavad müügiks või üürimiseks paljudes jurisdiktsioonides. Paremad reitingud võivad suurendada kinnisvara väärtust ja üürimisvõimet.',
      esgTitle: 'ESG ja jätkusuutlikkuse aruandlus',
      esgDesc: 'Ettevõtete jätkusuutlikkuse aruandlus nõuab üha enam üksikasjalikke energiatarbimise andmeid. Seiresüsteemid pakuvad kontrollitavaid andmeid, mida on vaja ESG avalikustamiseks ja jätkusuutlikkuse sertifikaatideks.',
      monitoringSupports: 'Energia seire toetab:',
      support1: 'Regulatiivse vastavuse dokumentatsiooni',
      support2: 'Energiatõhususe sertifikaatide parandamist',
      support3: 'ESG ja jätkusuutlikkuse aruandlust',
      support4: 'Süsiniku jalajälje arvutamist',
      support5: 'Roheliste hoonete sertifitseerimist (LEED, BREEAM)',
      support6: 'Auditi ja kontrollimise protsesse',
      // ROI Section
      roiBadge: 'Ärimõju',
      roiTitle: 'Energia seire äripõhjendus',
      roiDesc: 'Energia seire pakub mõõdetavaid, kontrollitavaid eeliseid. Organisatsioonid, kes rakendavad kõikehõlmavaid seiresüsteeme, näevad tavaliselt märkimisväärset tulu vähenenud energiakulude, paranenud tegevuse ja välditavate seadmete rikete kaudu.',
      typicalBenefits: 'Tüüpilised eelised',
      benefit1: '10-25% energiakulude vähenemine',
      benefit2: 'Pikendatud seadmete eluiga',
      benefit3: 'Vähendatud hoolduskulud',
      benefit4: 'Paranenud elanike mugavus',
      benefit5: 'Parem operatiivne nähtavus',
      benefit6: 'Andmed strateegiliste otsuste jaoks',
      investmentTitle: 'Investeerimiskaalutlused',
      investment1: 'Madala hinnaga juhtmevabad andurid',
      investment2: 'Minimaalne paigalduse häiring',
      investment3: 'Skaleeritav juurutusmeetod',
      investment4: 'Pilveplatvorm—IT infrastruktuuri pole vaja',
      investment5: 'Mõõdetav ROI 2-3 aasta jooksul',
      investment6: 'Jätkuvad operatiivsed säästud',
      roiNote: 'Isegi väikesed parandused on olulised: 10% energiatarbimise vähenemine hoonel, mille aastased energiakulud on €100 000, toob €10 000 aastast säästu—mis kumuleerub aasta-aastalt.',
      // Why 0g Baltics Section
      whyBadge: 'Teie partner',
      whyTitle: 'Miks töötada 0g Balticsiga',
      whyDesc: '0g Baltics pakub kõikehõlmavaid energia seire lahendusi, mis ühendavad professionaalsed IoT andurid, usaldusväärse ühenduvuse infrastruktuuri ja võimsad analüütikaplatvormid. Meie eesmärk on aidata organisatsioonidel vähendada energiatarbimist ja parandada hoonete jõudlust.',
      whyFeature1: 'IoT andurid hoonetele',
      whyFeature1Desc: 'Temperatuuri, niiskuse, täituvuse, energia ja keskkonna seire',
      whyFeature2: 'Energia seiresüsteemid',
      whyFeature2Desc: 'Reaalajas tarbimise jälgimine ja analüütika',
      whyFeature3: 'LoRaWAN ühenduvus',
      whyFeature3Desc: 'Usaldusväärne, madala energiatarbega juhtmevaba infrastruktuur',
      whyFeature4: 'Andmeplatvormid ja analüütika',
      whyFeature4Desc: 'Ülevaated ja optimeerimise soovitused',
      whyFeature5: 'Nutikate hoonete konsultatsioon',
      whyFeature5Desc: 'Strateegia ja juurutamise juhised',
      relatedSolutions: 'Seotud lahendused',
      relatedIAQ: 'Siseõhu kvaliteet',
      relatedIAQDesc: 'CO2, temperatuuri, niiskuse seire',
      relatedSmartCity: 'Nutika linna infrastruktuur',
      relatedSmartCityDesc: 'Linna hoonete juurutused',
      relatedConnectivity: 'IoT ühenduvus',
      relatedConnectivityDesc: 'LoRaWAN ja mobiilsed valikud',
      relatedMetering: 'Nutikas arvestus',
      relatedMeteringDesc: 'Vee ja energia arvestus',
      // CTA
      ctaTitle: 'Parandage oma hoone energiatõhusust',
      ctaDesc: 'Võtke ühendust 0g Balticsiga, et juurutada nutikaid energia seiresüsteeme oma hoonetele. Olenemata sellest, kas haldate kontoreid, ülikoole või tööstusrajatisi, saame aidata teil vähendada tarbimist ja optimeerida tegevust.',
      ctaButton: 'Rääkige meie meeskonnaga',
    },

    officeSpacePage: {
      metaTitle: 'Kontoriruumide haldamine | Täituvuse analüütika | 0g Baltics',
      metaDesc: 'Optimeerige kontoriruumide kasutamist IoT täituvusanduritega.',
      heroBadge: 'Kontori haldamine',
      heroTitle: 'Nutikas kontoriruumide haldamine',
      heroSubtitle: 'Optimeerige tööruumi kasutamist täituvusandurite, laudade broneerimissüsteemide ja analüütikaga.',
      // Hero badges
      heroBadge1: 'Täituvuse tuvastamine',
      heroBadge2: 'Laudade jälgimine',
      heroBadge3: 'Koosolekuruumide analüütika',
      heroBadge4: 'Hübriidtöö',
      // What Is Section
      whatIsBadge: 'Väljakutse mõistmine',
      whatIsTitle: 'Mis on kontoriruumide haldamine?',
      whatIsDesc: 'Kontoriruumide haldamine on praktika mõista, jälgida ja optimeerida, kuidas füüsilisi tööruume kasutatakse.',
      driversTitle: 'Nutika kontori haldamise peamised tegurid:',
      driver1: 'Hübriidsed töömudelid',
      driver2: 'Paindlikud kontori paigutused',
      driver3: 'Jagatud töökohtade poliitikad',
      driver4: 'Koosolekuruumide optimeerimine',
      visibilityTitle: 'IoT andurid pakuvad nähtavust:',
      visibility1: 'Reaalajas täituvus',
      visibility1Desc: 'Milliseid ruume praegu kasutatakse',
      visibility2: 'Kasutusmudelid',
      visibility2Desc: 'Tipptunnid, vaiksed perioodid, igapäevased trendid',
      visibility3: 'Ruumi efektiivsus',
      visibility3Desc: 'Kasutuse määrad tsoonide kaupa',
      visibility4: 'Keskkonnatingimused',
      visibility4Desc: 'Temperatuur, õhukvaliteet, mugavus',
      // Hidden Costs Section
      costBadge: 'Äriprobleem',
      costTitle: 'Tühja kontoriruumi peidetud kulud',
      costDesc: 'Uuringud näitavad järjekindlalt, et paljudel ettevõtetel on igal hetkel 30-60% kasutamata kontorivõimsust.',
      costsTitle: 'Tegevuskulud tühja ruumi eest:',
      cost1: 'Üüri- ja rendikulud',
      cost2: 'Küttekulud',
      cost3: 'Jahutus ja elekter',
      cost4: 'Valgustuskulud',
      stat1: 'tüüpiline kasutamata kontorivõimsus',
      stat2: 'kulu m² kohta aastas (EL keskmine)',
      stat3: 'potentsiaalne sääst optimeerimisega',
      stat4: 'tüüpiline ROI nutikatele kontorisüsteemidele',
      // Smart Sensors Section
      sensorsBadge: 'Andurite tehnoloogia',
      sensorsTitle: 'Nutikad kontoriandurid',
      sensorsDesc: 'Erinevat tüüpi IoT andurid täidavad kontoriruumide haldamisel erinevaid eesmärke. Kõik andurid koguvad anonüümseid kasutusandmeid—ilma isikutuvastuse või jälgimiseta—pakkudes koondatud ülevaateid, mis austavad töötajate privaatsust.',
      occupancySensors: 'Täituvusandurid',
      occupancySensorsDesc: 'Lakke paigaldatud andurid tuvastavad kohalolu ja loevad inimesi sellistes piirkondades nagu avatud korrused, ühisalad ja suured koosolekuruumid. Pakuvad tsooni taseme täituvusandmeid.',
      deskSensors: 'Laudade andurid',
      deskSensorsDesc: 'Väikesed andurid, mis on paigaldatud laudade alla, tuvastavad, kas üksikud töökohad on hõivatud. Võimaldavad täpset laua-laua kaupa kasutuse jälgimist jagatud töökohtade keskkondades.',
      meetingRoomSensors: 'Koosolekuruumide andurid',
      meetingRoomSensorsDesc: 'Spetsiaalsed andurid koosolekuruumide jaoks jälgivad tegelikku kasutust vs kalendribroneeringuid, tuvastavad mittekohalejäänuid ja identifitseerivad tühje koosolekuid, mis raiskavad väärtuslikku ruumi.',
      bestFor: 'Sobib paremini:',
      openPlanOffices: 'Avatud plaaniga kontorid',
      commonAreas: 'Ühisalad',
      cafeterias: 'Kohvikud',
      largeMeetingRooms: 'Suured koosolekuruumid',
      hotDeskingAreas: 'Jagatud töökohtade alad',
      assignedDesks: 'Määratud lauad',
      focusBooths: 'Keskendumiskabiinid',
      phoneRooms: 'Telefonikambalid',
      conferenceRooms: 'Konverentsiruumid',
      huddleSpaces: 'Väikesed kogunemisruumid',
      videoCallRooms: 'Videokõnede ruumid',
      executiveSuites: 'Juhtide kabinetid',
      // IAQ Section
      iaqBadge: 'Keskkonna intelligentsus',
      iaqTitle: 'Siseõhu kvaliteedi seire',
      iaqDesc: 'Nutikad kontorid peaksid jälgima enamat kui ainult täituvust. Keskkonnatingimused mõjutavad otseselt töötajate mugavust, tootlikkust ja heaolu. Halb õhukvaliteet, ebamugavad temperatuurid või kõrge niiskus loovad ebaoptimaalseid töötingimusi.',
      iaqDesc2: 'Keskkonnasensorite integreerimine täituvusandmetega pakub täielikku pilti töökoha tingimustest, võimaldades rajatiste haldajatel optimeerida nii ruumikasutust kui ka mugavust.',
      co2Levels: 'CO2 tasemed',
      co2LevelsDesc: 'Näitab ventilatsiooni piisavust ja õhu värskust',
      temperature: 'Temperatuur',
      temperatureDesc: 'Soojuslik mugavuse jälgimine tsoonide kaupa',
      humidity: 'Niiskus',
      humidityDesc: 'Mõjutab mugavust ja hoone tervist',
      ventilation: 'Ventilatsioon',
      ventilationDesc: 'Õhukvaliteedi ja ringluse hindamine',
      impactTitle: 'Mõju töökoha jõudlusele',
      impactStat1: 'tootlikkuse kasv parema õhukvaliteediga',
      impactStat2: 'haiguspäevade vähenemine parema ventilatsiooniga',
      impactStat3: 'kognitiivne paranemine madalama CO2 tasemega',
      learnMoreIAQ: 'Lisateavet IAQ seire kohta',
      // Analytics Platform Section
      analyticsBadge: 'Andmepõhised ülevaated',
      analyticsTitle: 'Kontori analüütika platvorm',
      analyticsDesc: 'Kõik andurid ühenduvad pilvepõhise analüütikaplatvormiga, mis muudab toorandmed rakendatavateks ülevaadeteks. Rajatiste haldajad saavad täieliku nähtavuse sellesse, kuidas nende kontoriruume tegelikult kasutatakse, võimaldades tõenduspõhiseid otsuseid.',
      platformCapabilities: 'Platvormi võimalused:',
      realTimeDashboards: 'Reaalajas töölauad',
      realTimeDashboardsDesc: 'Reaalajas vaade praegusele täituvusele kõigis tsoonides',
      occupancyHeatmaps: 'Täituvuse soojuskaardid',
      occupancyHeatmapsDesc: 'Kõrge ja madala kasutusega alade visuaalne esitus',
      meetingRoomAnalytics: 'Koosolekuruumide analüütika',
      meetingRoomAnalyticsDesc: 'Broneering vs tegelik kasutus, mittekohalejäämise määrad',
      historicalTrends: 'Ajaloolised trendid',
      historicalTrendsDesc: 'Päeva, nädala, kuu kasutusmudelid',
      spaceEfficiencyMetrics: 'Ruumi efektiivsuse mõõdikud',
      spaceEfficiencyMetricsDesc: 'Kasutuse määrad ja optimeerimise võimalused',
      // Hybrid Workplace Section
      hybridBadge: 'Kaasaegsed töömudelid',
      hybridTitle: 'Hübriidse töökoha haldamine',
      hybridDesc: 'Kontori analüütika muutub veelgi kriitilisemaks hübriidtöö keskkondades, kus töötajad jagavad aega kontori ja kaugtöö vahel. Tegelike kasutusmudelite mõistmine aitab organisatsioonidel õigesti dimensioneerida oma kinnisvara ja kujundada ruume, mis toetavad paindlikku tööd.',
      deskBookingSystems: 'Laudade broneerimissüsteemid',
      deskBookingSystemsDesc: 'Võimaldavad töötajatel laudu ette broneerida, reaalajas saadavusega, mis näitab, millised kohad on vabad. Täituvusandurid kinnitavad broneeringuid ja vabastavad mittekohalejäänud automaatselt.',
      meetingRoomAvailability: 'Koosolekuruumide saadavus',
      meetingRoomAvailabilityDesc: 'Näitab reaalajas saadavust tegeliku täituvuse põhjal, mitte ainult kalendribroneeringute põhjal. Tuvastab ja vabastab tühjad koosolekud, muutes rohkem ruume produktiivseks kasutamiseks kättesaadavaks.',
      spacePlanning: 'Ruumi planeerimine',
      spacePlanningDesc: 'Kasutage ajaloolisi andmeid kontori paigutuste planeerimiseks, mis vastavad tegelikule nõudlusele. Määrake optimaalsed laudade ja töötajate suhted, koostööruumid ja vaiksed tsoonid.',
      // Benefits Section
      benefitsBadge: 'Ärimõju',
      benefitsTitle: 'Kasud ettevõtetele',
      benefitsDesc: 'Nutikas kontoriruumide haldamine pakub mõõdetavaid kasusid mitmes dimensioonis—otsestest kulude kokkuhoiust parema töötajakogemuse ja jätkusuutlikkuse tulemusteni.',
      reducedCosts: 'Vähendatud kinnisvarakulud',
      reducedCostsDesc: 'Tuvastage alakasutatud ruumi ja dimensioneerige oma kontoripind õigesti. Paljud organisatsioonid vähendavad üüritud ruumi 15-30% pärast analüütika rakendamist.',
      betterExperience: 'Parem töötajakogemus',
      betterExperienceDesc: 'Aidake töötajatel hõlpsalt leida saadaolevaid laudu ja koosolekuruume. Vähendage hõõrdumist ja frustratsiooni töökohal.',
      higherProductivity: 'Kõrgem tootlikkus',
      higherProductivityDesc: 'Optimeerige kontori paigutusi selle põhjal, kuidas inimesed tegelikult töötavad. Looge õige segu koostöö- ja keskendumisruumidest.',
      dataDrivenPlanning: 'Andmepõhine planeerimine',
      dataDrivenPlanningDesc: 'Asendage eeldused faktidega. Tehke kinnisvara ja töökoha otsuseid tegelike kasutusandmete põhjal.',
      sustainabilityImprovements: 'Jätkusuutlikkuse parandused',
      sustainabilityImprovementsDesc: 'Vähendage energiaraiskamist konditsioneerides ainult hõivatud ruume. Toetage ESG eesmärke mõõdetavate efektiivsuse kasudega.',
      operationalVisibility: 'Operatiivne nähtavus',
      operationalVisibilityDesc: 'Mõistke oma töökohta nagu kunagi varem. Rajatiste haldajad saavad tööriistad pidevaks optimeerimiseks ja parandamiseks.',
      // CTA
      ctaTitle: 'Optimeerige oma töökohta',
      ctaDesc: 'Võtke ühendust 0g Balticsiga, et projekteerida nutikaid kontorilahendusi, mis parandavad efektiivsust ja vähendavad kulusid. Olenemata sellest, kas haldate ettevõtte peakorterit, ülikoolilinnakuid või ärikinnisvarą, saame aidata.',
      ctaButton: 'Rääkige meie meeskonnaga',
      // Use Cases Section
      useCasesBadge: 'Rakendused',
      useCasesTitle: 'Kasutusjuhud',
      useCasesDesc: 'Nutikad kontorilahendused teenindavad organisatsioone erinevates tööstusharudes ja suurustes, alates ettevõtete peakontoritest kuni haridusasutusteni.',
      corporateHQ: 'Ettevõtte peakontor',
      corporateHQDesc: 'Suured ettevõtted kasutavad kontori analüütikat mitmekorruseliste peakontorite optimeerimiseks, jagatud töökohtade programmide haldamiseks ja hübriidtöö poliitikate toetamiseks tuhandetele töötajatele.',
      techCompanies: 'Tehnoloogiaettevõtted',
      techCompaniesDesc: 'Paindliku töökultuuriga tehnoloogiaettevõtted toetuvad laua broneerimisele ja täituvusandmetele, et luua dünaamilisi tööruume, mis kohanduvad muutuvate meeskonna vajadustega.',
      governmentBuildings: 'Riigihooned',
      governmentBuildingsDesc: 'Avaliku sektori organisatsioonid kasutavad analüütikat maksumaksjate rahastatud rajatiste optimeerimiseks ja avalike ressursside tõhusa kasutamise demonstreerimiseks.',
      universities: 'Ülikoolid',
      universitiesDesc: 'Haridusasutused jälgivad loengusaale, raamatukogusid ja õpperuume ajakavade optimeerimiseks ja üliõpilaste kasutusmudelite mõistmiseks.',
      coworkingSpaces: 'Ühiskontorid',
      coworkingSpacesDesc: 'Paindlike tööruumide operaatorid kasutavad reaalajas täituvust laudade saadavuse haldamiseks, paigutuste optimeerimiseks ja täpsete mahtuvusandmete pakkumiseks liikmetele.',
      commercialRealEstate: 'Ärikinnisvara',
      commercialRealEstateDesc: 'Kinnisvarahaldurid pakuvad hoone analüütikat üürniku mugavusena, pakkudes täituvuse ülevaateid, mis aitavad ettevõtetel optimeerida nende üüritud ruume.',
      // IoT Connectivity Section
      iotConnectivityBadge: 'Võrguinfrastruktuur',
      iotConnectivityTitle: 'IoT ühenduvus nutikatele hoonetele',
      iotConnectivityDesc: '0g Baltics kasutab madalat energiatarbimist IoT võrke, mis on loodud hoonetele. Need võrgud võimaldavad juhtmevaba andurite paigaldust ilma keeruka juhtmestuseta, akukestvusega, mida mõõdetakse aastates, mitte kuudes.',
      connectivityOptions: 'Toetatud ühenduvusvõimalused:',
      lorawanName: 'LoRaWAN',
      lorawanDesc: 'Pikk ulatus, madal energiatarbimine, ideaalne suurtele hoonetele',
      nbiotName: 'NB-IoT',
      nbiotDesc: 'Mobiilsidel põhinev, sügav siseruumide läbitungimine',
      ltemName: 'LTE-M',
      ltemDesc: 'Mobiilsed varad ja suurema ribalaiuse vajadused',
      wifiName: 'Wi-Fi',
      wifiDesc: 'Integratsioon olemasoleva IT infrastruktuuriga',
      keyAdvantages: 'Peamised eelised:',
      longBatteryLife: 'Pikk akukestvus',
      longBatteryLifeDesc: '5-10 aastat tüüpiline anduri töö',
      lowMaintenance: 'Madal hooldus',
      lowMaintenanceDesc: 'Paigalda ja unusta installatsioon',
      secureCommunication: 'Turvaline side',
      secureCommunicationDesc: 'Krüpteeritud andmeedastus',
      scalableDeployment: 'Skaleeritav paigaldus',
      scalableDeploymentDesc: 'Pilootist kogu hoone katvuseni',
      learnMoreConnectivity: 'Lisateavet IoT ühenduvuse kohta',
      // Why 0g Baltics Section
      whyBadge: 'Teie partner',
      whyTitle: 'Miks 0g Baltics',
      whyDesc: '0g Baltics pakub terviklikke nutikaid kontorilahendusi—IoT anduritest pilve analüütikaplatvormideni. Ühendame riistvara ekspertiisi, ühenduvuse infrastruktuuri ja analüütika võimalused integreeritud lahendusteks töökoha optimeerimiseks.',
      service1: 'IoT andurid',
      service1Desc: 'Täituvus-, laua-, koosolekuruumi- ja keskkonnaandurid',
      service2: 'Ühenduvuse infrastruktuur',
      service2Desc: 'LoRaWAN, mobiilside ja hübriidvõrgu võimalused',
      service3: 'Pilveplatvormid',
      service3Desc: 'Analüütika töölauad ja aruandlustööriistad',
      service4: 'Süsteemide integratsioon',
      service4Desc: 'Ühenduge olemasolevate hoone ja IT süsteemidega',
      service5: 'Nutika hoone kogemus',
      service5Desc: 'Ekspertiis nutika linna ja hoone projektidest',
      relatedSolutions: 'Seotud lahendused',
      relatedSmartCity: 'Nutika linna infrastruktuur',
      relatedSmartCityDesc: 'Linna ja hoone IoT võrgud',
      relatedSmartLockers: 'Nutikad kapid',
      relatedSmartLockersDesc: 'Turvalised hoidmislahendused',
      relatedAssetTracking: 'Varade jälgimine',
      relatedAssetTrackingDesc: 'Seadmete ja ressursside seire',
      relatedConnectivity: 'IoT ühenduvus',
      relatedConnectivityDesc: 'LoRaWAN ja mobiilsidevõrgud',
    },

    assetTrackingPage: {
      metaTitle: 'Varade jälgimise lahendused | GPS ja IoT | 0g Baltics',
      metaDesc: 'Jälgige varasid siseruumides ja väljas LoRaWAN, GPS, BLE ja mobiilsidega.',
      heroBadge: 'Varade jälgimine',
      heroTitle: 'Varade jälgimise lahendused',
      heroSubtitle: 'Jälgige seadmeid, sõidukeid ja inventari sise- ja välisruumides positsioneerimislahendustega.',
      indoorTracking: 'Siseruumide jälgimine',
      indoorTrackingDesc: 'BLE ja LoRaWAN põhine positsioneerimine ladudele ja rajatistele.',
      outdoorTracking: 'Välisruumide jälgimine',
      outdoorTrackingDesc: 'GPS ja mobiilne jälgimine sõidukitele ja mobiilsetele varadele.',
      hybridTracking: 'Hübriidne jälgimine',
      hybridTrackingDesc: 'Sujuv sise-välis jälgimine automaatse lülitumisega.',
      industriesBadge: 'Tööstusharud',
      logistics: 'Logistika',
      logisticsDesc: 'Jälgige saadetisi, konteinereid ja sõidukiparki.',
      healthcare: 'Tervishoid',
      healthcareDesc: 'Meditsiiniseadmete jälgimine haiglates ja kliinikutes.',
      manufacturing: 'Tootmine',
      manufacturingDesc: 'Tööriistade ja poolelioleva töö jälgimine tehases.',
      ctaTitle: 'Alustage oma varade jälgimist',
      ctaButton: 'Taotle demo',
      // What Is Section
      whatIsBadge: 'Tehnoloogia mõistmine',
      whatIsTitle: 'Mis on IoT varade jälgimine?',
      whatIsDesc: 'IoT varade jälgimine võimaldab ettevõtetel jälgida füüsiliste varade asukohta, olekut ja kasutamist reaalajas.',
      assetsTitle: 'Jälgitavad varad hõlmavad:',
      asset1: 'Ehitusseadmed',
      asset2: 'Logistikakonteinerid',
      asset3: 'Sõidukid ja haagised',
      asset4: 'Tööstuslikud tööriistad',
      asset5: 'Meditsiiniseadmed',
      asset6: 'Temperatuuritundlikud kaup',
      // Why Section
      whyBadge: 'Äritegevuse mõju',
      whyTitle: 'Miks ettevõtted juurutavad varade jälgimist',
      whyDesc: 'Organisatsioonid juurutavad varade jälgimist kriitiliste tegevusprobleemide lahendamiseks.',
      benefit1: 'Varade varguse ennetamine',
      benefit1Desc: 'Vähendage vargusi 60%+',
      benefit2: 'Kasutuse optimeerimine',
      benefit2Desc: 'Suurendage varade kasutust 25%+',
      benefit3: 'Hoolduse planeerimine',
      benefit3Desc: 'Vältige kulukaid rikkeid',
      benefit4: 'Asukoha nähtavus',
      benefit4Desc: 'Teadke, kus varad asuvad 24/7',
      // Multi-Connectivity Section
      networkBadge: 'Võrguarhitektuur',
      networkTitle: 'Miks mitme ühenduvuse tugi on oluline',
      networkDesc: 'Enamik IoT juurutusi ebaõnnestub, kui need tuginevad ühele ühendustehnoloogiale. Varad ei jää ühte kohta—need liiguvad tööstuslinnakute, linnaruumi, logistikamaršrutide ja kaugete asukohtade vahel, kus võrgu leviala dramaatiliselt varieerub.',
      networkDesc2: '0g Baltics projekteerib mitme ühenduvuse arhitektuure, mis kombineerivad mitmeid tehnoloogiaid. Seadmed valivad automaatselt parima saadaoleva võrgu sõltuvalt keskkonnast, tagades pideva varade nähtavuse olenemata asukohast.',
      lorawanName: 'LoRaWAN',
      lorawanDesc: 'Pika ulatusega, madala energiatarbega linnaku ja piirkondliku leviala jaoks',
      nbiotName: 'NB-IoT / LTE-M',
      nbiotDesc: 'Mobiilne ühenduvus linnas ja mobiilsete varade jaoks',
      bleName: 'BLE majakad',
      bleDesc: 'Siseruumide positsioneerimine ja läheduse jälgimine',
      satelliteName: 'Satelliit',
      satelliteDesc: 'Globaalne leviala kaugetele ja võrguvälise asukohtadele',
      // Tracking Devices Section
      devicesBadge: 'Riistvaralahendused',
      devicesTitle: 'Professionaalsed varade jälgimisseadmed',
      devicesDesc: '0g Baltics integreerib professionaalseid jälgimisseadmeid juhtivatelt tootjatelt nagu Digital Matter. Need seadmed pakuvad GPS jälgimist, satelliitühenduvust, mobiilset ühenduvust, BLE lüüsi võimalusi ja mitmeaastast aku tööaega—loodud tööstuskeskkondade ja globaalsete juurutuste jaoks.',
      gpsTracking: 'GPS jälgimine',
      gpsTrackingDesc: 'Kõrge täpsusega väliruumide positsioneerimine GNSS-iga',
      satelliteConnectivity: 'Satelliitühenduvus',
      satelliteConnectivityDesc: 'Iridium satelliit kaugetele asukohtadele',
      cellularNetworks: 'Mobiilsed võrgud',
      cellularNetworksDesc: '4G LTE, LTE-M, NB-IoT ühenduvus',
      multiYearBattery: 'Mitmeaastane aku',
      multiYearBatteryDesc: 'Kuni 10+ aastat nutika energiahaldusega',
      compactTracker: 'Kompaktne varade jälgija',
      compactTrackerDesc: 'Vastupidav akutoitega GPS jälgija sise-/välisruumide varade jälgimiseks magnetilise aktiveerimisega.',
      longLifeTracker: 'Pika elueaga jälgija',
      longLifeTrackerDesc: 'GPS jälgija pikima aku tööajaga turul—kuni 10+ aastat paigalda-ja-unusta juurutustele.',
      globalSatelliteTracker: 'Globaalne satelliitjälgija',
      globalSatelliteTrackerDesc: 'Vastupidav juhtmega jälgija Iridium satelliidi ja 4G/2G ühenduvusega kriitiliste globaalsete juurutuste jaoks.',
      // Campus Tracking Section
      campusBadge: 'Objekti nähtavus',
      campusTitle: 'Linnaku varade jälgimine',
      campusDesc: 'Suured tööstuslikud objektid toimivad nagu väikesed linnad. Tehased, lennujaamad, tööstuspargid, ülikoolid ja energiarajatised sisaldavad tuhandeid varasid, mis igapäevaselt liiguvad. Ilma jälgimiseta kaotavad organisatsioonid nähtavuse hetkel, mil seadmed lahkuvad keskladust.',
      campusDesc2: 'Kasutades LoRaWAN lüüse ja BLE majakaid, saavad organisatsioonid jälgida seadmeid kogu linnakus meetri täpsusega. Sise-/välisruumide positsioneerimine kõrvaldab pimealad, samas kui tsoonipõhised hoiatused tagavad, et varad jäävad sinna, kuhu nad kuuluvad.',
      campusDeployments: 'Tüüpilised linnaku juurutused',
      campusSite1: 'Tootmisettevõtted',
      campusSite2: 'Lennujaama rajatised',
      campusSite3: 'Haigla linnakud',
      campusSite4: 'Ülikooli territooriumid',
      campusSite5: 'Energiarajatised',
      campusSite6: 'Logistikakeskused',
      multiSiteBadge: 'Mitme objekti operatsioonid',
      multiSiteTitle: 'Linnakust-linnakusse nähtavus',
      multiSiteDesc: 'Varad liiguvad sageli mitme objekti vahel. Ehitustehnika roteerub projektide vahel. Mobiilsed generaatorid reisivad ajutistele paigaldustele. Hooldusseadmed teenindavad mitut rajatist. Ilma jälgimiseta tekitavad objektidevahelised liikumised nähtavuslünki.',
      multiSiteDesc2: 'IoT jälgimine võimaldab organisatsioonidel jälgida varade liikumist rajatiste vahel, mõista transiidiaegu ja parandada kasutust kogu organisatsioonis—mitte ainult üksikutes objektides.',
      realImpact: 'Reaalne mõju',
      realImpactDesc: 'Ettevõtted näevad tavaliselt 25% paranemist varade kasutuses, kui nad saavad nähtavuse mitmes objektis, vähendades vajadust dubleerivate seadmete ostude järele.',
      // Global Tracking Section
      globalBadge: 'Ülemaailmne leviala',
      globalTitle: 'Globaalne varade jälgimine',
      globalDesc: 'Logistikakeskkondades reisivad varad suurte vahemaade taha—riikide, mandrite ja ookeanide vahel. Konteinerid liiguvad Aasia tehastest Euroopa jaotuskeskustesse. Kõrge väärtusega seadmed reisivad kaugetele projektiobjektidele. Temperatuuritundlikud ravimid vajavad pidevat jälgimist globaalse transiidi ajal.',
      globalDesc2: 'Globaalsed jälgimissüsteemid kombineerivad mobiilseid võrke (saadaval 190+ riigis), satelliitühenduvust (ookeanide ületamiseks ja kaugetele piirkondadele) ja mobiilseid lüüse (reaalajas transiidi jälgimiseks). See tagab usaldusväärse jälgimise praktiliselt kõikjal Maal.',
      globalStat1: '190+',
      globalStat1Label: 'Riiki mobiilse levialaga',
      globalStat2: '100%',
      globalStat2Label: 'Globaalne satelliidi leviala',
      globalStat3: '130',
      globalStat3Label: 'Riiki aktiivsete juurutustega',
      globalStat4: '2.5M+',
      globalStat4Label: 'Seadet juurutatud üle maailma',
      // Cold Chain Section
      coldChainBadge: 'Temperatuuritundlik logistika',
      coldChainTitle: 'Külmaketi seire IoT-ga',
      coldChainDesc: 'Temperatuuritundlik logistika nõuab pidevat seiret kogu tarneahela vältel. Ravimid, vaktsiinid, värske toit ja bioloogilised proovid võivad kahjustuda isegi lühiajaliste temperatuurikõrvalekallete tõttu. Traditsiooniline seire käsitsi kontrollide ja paberlogidega on ebapiisav kaasaegsete vastavusnõuete jaoks.',
      coldChainDesc2: 'IoT andurid ja BLE majakad jälgivad pidevalt temperatuuri, niiskust ja liikumist. Mobiilsed lüüsid sõidukites koguvad andurite andmeid ja edastavad need pilveplatvormidele reaalajas. Kui künnised ületatakse, genereeritakse hoiatused kohe—võimaldades sekkumist enne toote kadumist.',
      tempLogging: 'Pidev temperatuuri logimine',
      tempLoggingDesc: 'Andurite näidud iga 15 minuti järel pilvesalvestusega',
      thresholdAlerts: 'Reaalajas künnise hoiatused',
      thresholdAlertsDesc: 'Kohesed teated, kui piirid ületatakse',
      chainCustody: 'Täielik hooldusahel',
      chainCustodyDesc: 'Auditi jälg päritolust sihtkohani',
      regulatoryCompliance: 'Regulatiivne vastavus',
      regulatoryComplianceDesc: 'GDP, HACCP ja farmaatsia standardid toetatud',
      // Asset Recovery Section
      securityBadge: 'Turvalisus ja kaitse',
      securityTitle: 'Varade taastamine ja varguste avastamine',
      securityDesc: 'Kõrge väärtusega varad esindavad märkimisväärseid kapitaliinvesteeringuid. Ehitusseadmed, spetsialiseeritud tööriistad ja sõidukid on peamised varguse sihtmärgid. Kui vargus toimub, on kiire taastamine kriitiline—nii rahaliste kaotuste minimeerimiseks kui ka projektigraafikute säilitamiseks.',
      securityDesc2: 'GPS jälgimisseadmed varjatud paigaldusvalikutega võimaldavad varastatud varade kohest leidmist. Geofencing loob virtuaalsed piirid—kui vara lahkub lubatud tsoonist, käivitatakse hoiatused kohe. Vargusevastane režiim aktiveerib kõrgsagedusliku GPS uuendused reaalajas jälgimiseks taastamisoperatsioonide ajal.',
      theftPrevention: 'Varguse ennetamise funktsioonid',
      theftFeature1: 'Geofencing koheste rikkumishoiatustega',
      theftFeature2: 'Töövälisel ajal liikumise tuvastamine',
      theftFeature3: 'Varjatud seadme paigaldus',
      theftFeature4: 'RF majakad viimase miili taastamiseks',
      theftFeature5: 'Õiguskaitse koordinatsiooni tugi',
      analyticsBadge: 'Operatiivne luure',
      analyticsTitle: 'AI ja analüütika',
      analyticsDesc: 'Varade jälgimine genereerib tohutuid koguseid operatiivandmeid. Ilma analüütikaka jäävad need andmed kasutamata. Täiustatud platvormid muudavad töötlemata asukoha- ja anduri andmed kasutatavaks äriteabeks.',
      utilizationStats: 'Kasutusstatistika',
      utilizationStatsDesc: 'Mõistke varade kasutusmustreid',
      predictiveMaintenance: 'Ennustav hooldus',
      predictiveMaintenanceDesc: 'Oodake teenusvajadusi',
      fleetOptimization: 'Pargi optimeerimine',
      fleetOptimizationDesc: 'Parandage tegevuse efektiivsust',
      customReporting: 'Kohandatud aruandlus',
      customReportingDesc: 'Äriteabe ekspordid',
      // Final CTA Section
      finalCtaTitle: 'Juurutage varade jälgimine koos 0g Balticsiga',
      finalCtaDesc: '0g Baltics projekteerib ja juurutab täiustatud IoT varade jälgimise lahendusi üle Baltikumi. Alates ühe objekti pilootprojektidest kuni ettevõtteüleste juurutusteni pakume riistvara, ühenduvust ja ekspertiisi teie varade haldamise transformeerimiseks.',
      finalCtaButton: 'Rääkige eksperdiga',
    },

    toolTrackingPage: {
      metaTitle: 'Tööriistade jälgimine | Tööstuslike tööriistade haldamine | 0g Baltics',
      metaDesc: 'Jälgige tööstuslikke tööriistu RFID, nutikate kappide ja IoT anduritega.',
      heroBadge: 'Tööriistade jälgimine',
      heroTitle: 'Tööstuslike tööriistade jälgimise lahendused',
      heroSubtitle: 'Vähendage tööriistade kadumist ja parandage kasutamist nutika jälgimise ja automatiseeritud jaotussüsteemidega.',
      rfidTracking: 'RFID jälgimine',
      rfidTrackingDesc: 'Märgistage ja jälgige iga tööriista RFID tehnoloogiaga.',
      smartLockers: 'Nutikad kapid',
      smartLockersDesc: 'Automatiseeritud tööriistade jaotamine ligipääsukontrolliga.',
      inventory: 'Inventari haldamine',
      inventoryDesc: 'Teadke täpselt, millised tööriistad teil on ja kus need asuvad.',
      analytics: 'Kasutusanalüütika',
      analyticsDesc: 'Mõistke tööriistade kasutamist ja optimeerige oma inventari.',
      ctaTitle: 'Lõpetage tööriistade kaotamine',
      ctaButton: 'Loe lähemalt',
      // BLE Tracking Section
      bleSectionBadge: 'BLE varade jälgimine',
      bleSectionTitle: 'Tööriistade jälgimine: teadke, kus iga tööriist asub',
      bleSectionDesc: 'Kinnitage BLE andurid oma tööriistadele. Saage reaalajas nähtavus asukoha ja oleku kohta.',
      // ROI Stats
      stat1: 'Tööriistade kadumise vähenemine',
      stat2: 'Kasutuse suurenemine',
      stat3: 'Vähem aega otsimisele',
      stat4: 'Madalamad asenduskulud',
      // Why Track
      whyTrackTitle: 'Miks tööriistu jälgida?',
      whyTrack1: 'Kõrvaldage vargused ja kadumised',
      whyTrack1Desc: 'BLE andurid hoiatavad, kui tööriistad lahkuvad lubatud tsoonidest.',
      whyTrack2: 'Leidke tööriistad kohe',
      whyTrack2Desc: 'Avage rakendus ja leidke sekunditega mis tahes tööriist kaardil.',
      whyTrack3: 'Automatiseerige inventuur',
      whyTrack3Desc: 'BLE lüüsid tuvastavad tööriistad laos automaatselt.',
      whyTrack4: 'Tagage vastavus',
      whyTrack4Desc: 'Jälgige kontrollkuupäevi. Saage hoiatusi enne aegumist.',
      // How It Works
      howItWorks: 'Kuidas see toimib',
      howStep1: 'Kinnitage BLE andur (magnet, liim või peidetud)',
      howStep2: 'Nutitelefonid ja lüüsid tuvastavad tööriistad automaatselt',
      howStep3: 'Jälgige asukohta, saage hoiatusi',
      // Buttons
      requestDemo: 'Taotlege demo',
      viewSolutions: 'Vaadake lahendusi',
      scheduleDemo: 'Planeerige demo',
      // BLE Trackers Section
      bleTrackersTitle: 'Tööstusklassi BLE jälgimisandurid',
      coinBeaconName: 'Mündi majakas',
      coinBeaconDesc: 'Ültkompaktne jälgija väikestele tööriistadele. 3+ aasta aku.',
      proAssetTagName: 'Pro vara silt',
      proAssetTagDesc: 'Professionaalne klass elektritööriistadele. BLE + LPWAN positsioneerimine.',
      industrialTrackerName: 'Tööstuslik jälgija',
      industrialTrackerDesc: 'Vastupidav GPS, BLE ja LTE-ga kõrge väärtusega seadmete ülemaailmseks jälgimiseks.',
      learnConnectivity: 'Loe lähemalt meie ühendustehnoloogiatest',
      // Distribution Section
      officialPartner: 'Ametlik partner',
      distributionTitle: 'Tööriistade ja seadmete jaotamine',
      distributionDesc: 'Automatiseeritud kapisüsteemid tööriistade, isikukaitsevahendite ja tarbematerjalide turvaliseks jaotamiseks. Märgi tuvastamine, täielik jälgitavus ja reaalajas inventarihaldus.',
      secureAccess: 'Turvaline ligipääs',
      secureAccessDesc: 'Märgi autentimine iga tehingu jaoks',
      fullTraceability: 'Täielik jälgitavus',
      fullTraceabilityDesc: 'Teadke, kes mida võttis, millal ja miks',
      availability247: 'Kättesaadavus 24/7',
      availability247Desc: 'Iseteeninduslik ligipääs igal ajal',
      realtimeDashboard: 'Reaalajas armatuurlaud',
      realtimeDashboardDesc: 'Jälgige inventari ja kasutusmustreid',
      // Products
      xtowerTitle: 'Isikukaitsevahendite väljastaja X\'TOWER®',
      xtowerDesc: 'Kaitske ja optimeerige oma tarbematerjale automatiseeritud karusselljagamisega. Vähendage hoiuruumi 90%.',
      xstoreTitle: 'Lao ja ruumide haldamine: X\'STORE®',
      xstoreDesc: 'Kaitske ja jälgige oma materjale eraldi hoiuruumis. Täielik nähtavus inventari tasemete kohta.',
      xlockerTitle: 'Tööstustarvete väljastaja X\'LOCKER®',
      xlockerDesc: 'Kaitske ja optimeerige oma tundlikke materjale ja tööriistu.',
      xslingTitle: 'Seinale kinnitatav rihmade ja rakmete alus X\'SLING®',
      xslingDesc: 'Tagage tundlike seadmete ohutus ja töökorras olek.',
      xweightTitle: 'Väikeste tarbematerjalide kaalumisväljastaja: X\'WEIGHT®',
      xweightDesc: 'Kaitske ja optimeerige kruvide, poltide ja väikeste tarbematerjalide jaotamist.',
      comingSoon: 'Tulekul',
      // Why It Matters Section
      whyMattersTitle: 'Miks tööriistade jälgimine ja nutikas jaotamine on oluline',
      whyMattersDesc: 'Tööstuskeskkondades lähevad haldamata tööriistad ettevõtetele aastas maksma tuhandeid eurosid kadude, raisatud aja ja ebaefektiivsusena.',
      trackEveryTool: 'Jälgige iga tööriista',
      trackEveryToolDesc1: 'Ilma nähtavuseta tööriistad kaovad. Töötajad koguvad varustust "igaks juhuks". Vargused jäävad märkamatuks kuni inventuur paljastab suured puudujäägid.',
      trackEveryToolDesc2: 'BLE jälgimine annab teile reaalajas asukohaandmed iga vara kohta. Teadke, kellel mis on, kus see asub ja millal viimati kasutati. Vähendage kadusid 40% ja kõrvaldage igapäevane puuduvate seadmete otsimine.',
      simplifyDistribution: 'Lihtsustage jaotamist',
      simplifyDistributionDesc1: 'Käsitsi tööriistade laod tekitavad kitsaskohti. Töötajad ootavad järjekorras. Inventarikirjed on ebatäpsed. Isikukaitsevahendid aeguvad märkamatult. Vastavus muutub õudusunenäoks.',
      simplifyDistributionDesc2: 'Nutikad kapid pakuvad 24/7 iseteeninduslikku ligipääsu märgi autentimisega. Iga tehing logitakse automaatselt. Inventar uueneb reaalajas. Vastavus on sisse ehitatud.',
      readyTransform: 'Valmis oma tööriistade haldamist muutma?',
      readyTransformDesc: 'Võtke meiega ühendust personaalse demo jaoks ja vaadake, kuidas meie lahendused võivad vähendada kadusid ja parandada tegevuse efektiivsust.',
    },

    connectivityPage: {
      metaTitle: 'IoT ühenduvus | LoRaWAN, Sigfox, NB-IoT | 0g Baltics',
      metaDesc: 'Juurutage IoT ühenduvuse infrastruktuur LoRaWAN, Sigfox, NB-IoT ja mobiilitehnoloogiatega. Võrgu planeerimise ja juurutamise teenused.',
      // Hero Section
      heroBadge: 'Ühenduvuse infrastruktuur',
      heroTitle: 'IoT ühenduvuse infrastruktuur',
      heroSubtitle: 'Õige ühenduvuse valimine on edukate IoT juurutuste jaoks hädavajalik. 0g Baltics projekteerib mitmevõrgulist infrastruktuuri, ühendades LoRaWAN, Sigfox, NB-IoT, BLE ja satelliitühenduvust.',
      heroDesc: 'Igal IoT projektil on erinevad nõuded leviala, energiatarbimise, andmete sageduse ja infrastruktuuri kulude osas. Meie ülesanne on valida ja kombineerida kõige tõhusamaid ühenduvustehnoloogiaid, et tagada usaldusväärsus, skaleeritavus ja pikaajaline jätkusuutlikkus.',
      // Multi-Connectivity Section
      multiBadge: 'Meie filosoofia',
      multiTitle: 'Miks mitmevõrguline IoT on oluline',
      multiDesc: '0g Baltics ei projekteeri kunagi mono-ühenduvuse lahendusi. Me mõistame, et erinevad seadmed, kasutusjuhud ja keskkonnad nõuavad erinevaid võrgutehnoloogiaid optimaalse jõudluse saavutamiseks.',
      multiPoint1: 'Erinevad seadmed vajavad erinevaid võrke',
      multiPoint2: 'Infrastruktuur peab jääma paindlikuks',
      multiPoint3: 'Kulude optimeerimine on hädavajalik',
      multiPoint4: 'Redundantsus parandab usaldusväärsust',
      multiPoint5: 'Seadmete pikaealisus tuleb säilitada',
      commitmentTitle: 'Meie kohustus',
      commitmentDesc: '0g Baltics lahendused ei põhine kunagi ühel ühenduvustehnoloogial. Me projekteerime hübriidinfrastruktuure, mis ühendavad mitut võrku, et vähendada tegevuskulusid ja maksimeerida turvalisust ning seadmete pikaealisust.',
      // LoRaWAN Section
      lorawanBadge: 'Pika ulatusega IoT',
      lorawanTitle: 'LoRaWAN infrastruktuur',
      lorawanDesc: 'LoRaWAN (Long Range Wide Area Network) on paljude suuremahuliste IoT juurutuste alus. See tehnoloogia võimaldab pika ulatusega side minimaalse energiatarbimisega, muutes selle ideaalseks nutika linna ja kommunaalteenuste rakendusteks.',
      lorawanAdvantages: 'Peamised eelised',
      lorawanAdv1: 'Pika ulatusega ühenduvus (kuni 15 km)',
      lorawanAdv2: 'Äärmiselt madal energiatarbimine',
      lorawanAdv3: 'Privaatne võrguinfrastruktuur',
      lorawanAdv4: 'Ideaalne nutikatele linnadele ja kommunaalteenustele',
      lorawanAdv5: 'Skaleeritavad juurutused',
      lorawanNote: 'Me juurutame operaatoritaseme LoRaWAN infrastruktuuri, kasutades Tektelic lüüse maksimaalse usaldusväärsuse tagamiseks.',
      lorawanUseCases: 'Tüüpilised kasutusjuhud',
      lorawanUse1Title: 'Nutikas arvestus',
      lorawanUse1Desc: 'Vee, gaasi ja elektriarvesti kaugnäidud',
      lorawanUse2Title: 'Keskkonnaseire',
      lorawanUse2Desc: 'Õhukvaliteedi, temperatuuri, niiskuse andurid',
      lorawanUse3Title: 'Varade jälgimine',
      lorawanUse3Desc: 'Sõidukite ja seadmete asukoha jälgimine',
      lorawanUse4Title: 'Nutikad hooned',
      lorawanUse4Desc: 'Täituvuse, energia ja rajatiste haldamine',
      lorawanUse5Title: 'Linnainfrastruktuur',
      lorawanUse5Desc: 'Tänavavalgustus, parkimine, jäätmekäitlus',
      // Sigfox Section
      sigfoxBadge: 'Globaalne IoT võrk',
      sigfoxTitle: 'Sigfox ühenduvus',
      sigfoxDesc: 'Sigfox on globaalne LPWAN (Low Power Wide Area Network) tehnoloogia, mis on loodud lihtsatele, odavatele IoT seadmetele, mis edastavad väikesi andmekoguseid. Selle ülimadal energiatarbimine võimaldab aku eluiga, mida mõõdetakse aastates, mitte kuudes.',
      sigfoxAdvantages: 'Peamised eelised',
      sigfoxAdv1: 'Ülimadal energiatarbimine',
      sigfoxAdv2: 'Aku eluiga kuni 10+ aastat',
      sigfoxAdv3: 'Lihtne seadmete integratsioon',
      sigfoxAdv4: 'Globaalne IoT võrgu leviala',
      sigfoxWhen: 'Millal kasutada Sigfox',
      sigfoxWhenDesc: 'Sigfox on kõige tõhusam lahendus, kui seadmed peavad harva saatma väikesi andmekoguseid ja aku eluiga on peamine piirang. Globaalne võrguleviala välistab privaatse infrastruktuuri investeeringute vajaduse.',
      sigfoxUseCases: 'Tüüpilised kasutusjuhud',
      sigfoxUse1Title: 'Varade jälgimine',
      sigfoxUse1Desc: 'Pikaajaline jälgimine mitmeaastase aku elueaga',
      sigfoxUse2Title: 'Keskkonnaandurid',
      sigfoxUse2Desc: 'Temperatuuri, niiskuse, rõhu seire',
      sigfoxUse3Title: 'Häiresüsteemid',
      sigfoxUse3Desc: 'Sissemurdmise tuvastamine ja turvahoiatused',
      sigfoxUse4Title: 'Kommunaalseire',
      sigfoxUse4Desc: 'Lekke tuvastamine ja tarbimishoiatused',
      // NB-IoT Section
      nbiotBadge: 'Mobiilne IoT',
      nbiotTitle: 'NB-IoT mobiilne ühenduvus',
      nbiotDesc: 'NB-IoT (Narrowband Internet of Things) kasutab olemasolevat mobiilsidevõrgu infrastruktuuri, et pakkuda usaldusväärset, turvalist ühenduvust IoT seadmetele. See tehnoloogia on suurepärane keskkondades, kus on vajalik sügav siseruumide leviala ja operaatoritaseme usaldusväärsus.',
      nbiotAdvantages: 'Peamised eelised',
      nbiotAdv1: 'Telekommunikatsioonioperaatori infrastruktuur',
      nbiotAdv2: 'Sügav siseruumide leviala',
      nbiotAdv3: 'Kõrge usaldusväärsus ja turvalisus',
      nbiotAdv4: 'Lai piirkonna leviala',
      nbiotNote: 'NB-IoT on eriti kasulik suurte infrastruktuuriprojektide jaoks, kus seadmed paigaldatakse keerulistesse keskkondadesse nagu maa-alused kommunaalteenused, keldrid või tööstusrajatised.',
      nbiotUseCases: 'Tüüpilised kasutusjuhud',
      nbiotUse1Title: 'Nutikas arvestus',
      nbiotUse1Desc: 'Kommunaalarvestid keerulistes asukohtades',
      nbiotUse2Title: 'Tööstusandurid',
      nbiotUse2Desc: 'Tehaste ja rajatiste seire',
      nbiotUse3Title: 'Nutikas parkimine',
      nbiotUse3Desc: 'Maa-aluste parklate täituvus',
      nbiotUse4Title: 'Nutikas infrastruktuur',
      nbiotUse4Desc: 'Sildade ja tunnelite seire',
      // BLE Section
      bleBadge: 'Siseruumide positsioneerimine',
      bleTitle: 'Bluetooth Low Energy',
      bleDesc: 'Bluetooth Low Energy (BLE) tehnoloogia võimaldab täpset siseruumide positsioneerimist ja varade jälgimist minimaalse energiatarbimisega. Selle ühilduvus nutitelefonidega ja laialdane levik teeb selle ideaalseks kasutajatele suunatud rakendusteks.',
      bleAdvantages: 'Peamised eelised',
      bleAdv1: 'Väga madal energiatarbimine',
      bleAdv2: 'Siseruumide positsioneerimise võimalused',
      bleAdv3: 'Kõrge täpsus (alla meetri)',
      bleAdv4: 'Nutitelefoni ühilduvus',
      bleUseCases: 'Tüüpilised kasutusjuhud',
      bleUse1Title: 'Siseruumide varade jälgimine',
      bleUse1Desc: 'Seadmete ja varude reaalajas asukoht',
      bleUse2Title: 'Haigla seadmete jälgimine',
      bleUse2Desc: 'Meditsiiniseadmete asukoht ja haldamine',
      bleUse3Title: 'Nutika kontori täituvus',
      bleUse3Desc: 'Tööruumi kasutamise seire',
      bleUse4Title: 'Siseruumide asukohateenused',
      bleUse4Desc: 'Navigatsiooni- ja teejuhatamisrakendused',
      bleLearnMore: 'Lugege lähemalt meie haigla varade jälgimislahendustest, mida toetab BLE tehnoloogia.',
      // Satellite Section
      satelliteBadge: 'Globaalne leviala',
      satelliteTitle: 'Satelliit IoT ühenduvus',
      satelliteDesc: 'Satelliitühenduvus täiendab maapealseid võrke, pakkudes leviala kaugetes piirkondades, kus mobiilside- või LPWAN-infrastruktuur puudub. See tehnoloogia tagab varade ja infrastruktuuri pideva seire olenemata asukohast.',
      satelliteAdv1Title: 'Globaalne leviala',
      satelliteAdv1Desc: 'Ühenduvus kõikjal Maal, sealhulgas ookeanidel ja kaugetes piirkondades',
      satelliteAdv2Title: 'Kaugpiirkondade ühenduvus',
      satelliteAdv2Desc: 'Jälgige varasid kohtades, kus puudub maapealne infrastruktuur',
      satelliteAdv3Title: 'Võrgu redundantsus',
      satelliteAdv3Desc: 'Varuühenduvus, kui põhivõrgud ebaõnnestuvad',
      satelliteUse1Title: 'Kauge infrastruktuuri seire',
      satelliteUse1Desc: 'Torujuhtmed, elektriliinid ja rajatised isoleeritud piirkondades',
      satelliteUse2Title: 'Logistika jälgimine',
      satelliteUse2Desc: 'Konteinerite ja autopargi jälgimine üle piiride',
      satelliteUse3Title: 'Tööstusvarad',
      satelliteUse3Desc: 'Rasketehnika ja masinad kaugetes asukohtades',
      satelliteNote: '0g Baltics integreerib satelliitühenduvuse lahendusi, mis ühilduvad erinevate satelliitinfrastruktuuridega, sealhulgas Starlink, kui projekti nõuded seda nõuavad.',
      // Strategy Section
      strategyBadge: 'Meie metoodika',
      strategyTitle: 'Kuidas 0g Baltics valib õige võrgu',
      strategyDesc: 'Iga IoT juurutus algab põhjaliku nõuete analüüsiga. Meie süstemaatiline lähenemine tagab, et valitud ühendustehnoloogiad vastavad projekti eesmärkidele ja piirangutele.',
      step1: '01',
      step1Title: 'Infrastruktuuri hindamine',
      step1Desc: 'Hinnake olemasolevat infrastruktuuri ja leviala nõudeid',
      step2: '02',
      step2Title: 'Seadmete nõuded',
      step2Desc: 'Analüüsige andmete sagedust, kasuliku koormuse suurust ja sidevajadusi',
      step3: '03',
      step3Title: 'Aku optimeerimine',
      step3Desc: 'Arvutage energiatarbimine ja seadme sihteline eluiga',
      step4: '04',
      step4Title: 'Leviala uuring',
      step4Desc: 'Kaardistage levialad ja tuvastage ühenduvuse lüngad',
      step5: '05',
      step5Title: 'Juurutuse arhitektuur',
      step5Desc: 'Projekteerige hübriidne võrgutopoloogia optimaalseks jõudluseks',
      strategyNote: 'Meie eesmärk on alati vähendada tegevuskulusid, tagades samal ajal pikaajalise usaldusväärsuse ja seadmete pikaealisuse.',
      // Industries Section
      industriesBadge: 'Rakendused',
      industriesTitle: 'Ühenduvus reaalsete IoT juurutuste jaoks',
      industriesDesc: '0g Baltics pakub mitmevõrgulist IoT ühenduvust erinevates tööstusharudes, igaühel unikaalsete nõuete ja väljakutsetega.',
      industrySmartCities: 'Nutikad linnad',
      industryUtilities: 'Kommunaalteenused',
      industryLogistics: 'Logistika',
      industryHealthcare: 'Tervishoid',
      industryRealEstate: 'Kinnisvara',
      industryIndustrial: 'Tööstus',
      industriesExplore: 'Tutvuge meie seadmete ökosüsteemi ja nõustamisteenustega, et saada rohkem teavet.',
      // CTA Section
      ctaTitle: 'Projekteerige oma IoT ühenduvuse infrastruktuur',
      ctaDesc: 'Kas olete valmis juurutama mitmevõrgulist IoT lahendust? Meie meeskond aitab teil valida õiged ühendustehnoloogiad teie konkreetsetele nõuetele.',
      ctaButton: 'Arutage oma ühenduvuse strateegiat',
    },

    tektelicPage: {
      metaTitle: 'TEKTELIC | LoRaWAN lüüsid ja andurid | 0g Baltics',
      metaDesc: 'TEKTELIC professionaalse klassi LoRaWAN lüüsid ja andurid. Volitatud partner Balti regioonis.',
      // Hero Section
      heroBadge: 'Volitatud partner',
      heroTitle: 'Operaatoritaseme LoRaWAN infrastruktuur',
      heroTitleHighlight: 'Tektelic',
      heroSubtitle: 'Ehitage skaleeritavaid IoT võrke linnadele, kommunaalettevõtetele ja tööstuslikele juurutustele kasutades Tektelic lüüse ja 0g Baltics infrastruktuuri ekspertiisi.',
      heroDesc: '0g Baltics projekteerib ja juurutab LoRaWAN infrastruktuuri Baltikumis ja Euroopas kasutades Tektelic operaatoritaseme lüüse. Meie juurutused toetavad nutika linna infrastruktuuri, arvestusvõrke, keskkonnaseiret ja tööstuslikke IoT rakendusi.',
      ctaPrimary: 'Arutage oma LoRaWAN projekti',
      ctaSecondary: 'Uurige starterikomplekte',
      // Why Tektelic Section
      whyBadge: 'Tehnoloogiapartner',
      whyTitle: 'Miks Tektelic LoRaWAN lüüsid',
      whyDesc: 'Tektelic lüüsid on juurutatud üle maailma suuremahulistes LoRaWAN võrkudes, mida usaldavad telekommunikatsioonioperaatorid, linnad ja ettevõtted nende usaldusväärsuse ja jõudluse tõttu.',
      feature1Title: 'Operaatoritaseme usaldusväärsus',
      feature1Desc: 'Ehitatud telekommunikatsiooni standardite järgi 24/7 tööks nõudlikes keskkondades',
      feature2Title: 'Tööstustaseme riistvara',
      feature2Desc: 'Projekteeritud välitingimuste juurutamiseks IP67 kaitsega ja laia temperatuurivahemikuga',
      feature3Title: 'Skaleeritav arhitektuur',
      feature3Desc: 'Üksiku lüüsi pilootprojektidest kuni linnaulatuse võrgujuurutusteni',
      feature4Title: 'Täiustatud võrguhaldus',
      feature4Desc: 'Põhjalikud tööriistad seireks, konfigureerimiseks ja hoolduseks',
      feature5Title: 'Turvaline infrastruktuur',
      feature5Desc: 'Täielik krüpteerimine ja turvaline alglaadur ettevõtete juurutustele',
      feature6Title: 'Kõrge jõudlusega RF disain',
      feature6Desc: 'Optimeeritud raadiosageduse disain maksimaalse leviala ja usaldusväärsuse jaoks',
      // LoRaWAN for Cities Section
      citiesBadge: 'Nutika linna infrastruktuur',
      citiesTitle: 'LoRaWAN infrastruktuur linnadele ja kommunaalettevõtetele',
      citiesDesc: 'LoRaWAN tehnoloogia võimaldab linnadel ja kommunaalettevõtetel juurutada kuluefektiivset IoT infrastruktuuri, mis toetab samaaegselt mitut rakendust. Üks LoRaWAN võrk võib teenindada nutikat arvestust, keskkonnaseiret, varade jälgimist ja muud.',
      networkEfficiency: 'Võrgu efektiivsus',
      networkEfficiencyDesc: 'Üks LoRaWAN infrastruktuur võib samaaegselt toetada mitut rakendust, vähendades juurutuskulusid ja lihtsustades võrguhaldust erinevates linna osakondades.',
      useCasesTitle: 'Kasutusjuhud',
      useCase1Title: 'Nutikas arvestus',
      useCase1Desc: 'Vesi, gaas ja elekter',
      useCase2Title: 'Õhukvaliteedi seire',
      useCase2Desc: 'CO2, osakesed, saasteained',
      useCase3Title: 'Parkimise seire',
      useCase3Desc: 'Täituvus ja suunamine',
      useCase4Title: 'Varade jälgimine',
      useCase4Desc: 'Sõidukid ja seadmed',
      useCase5Title: 'Jäätmekäitlus',
      useCase5Desc: 'Täitumistaseme seire',
      useCase6Title: 'Keskkonnaseire',
      useCase6Desc: 'Temperatuur, niiskus',
      useCase7Title: 'Nutikad hooned',
      useCase7Desc: 'Energia ja täituvus',
      // Multi-Network Section
      multiNetworkBadge: 'Mitmevõrgu strateegia',
      multiNetworkTitle: 'Loodud hübriidsele IoT infrastruktuurile',
      multiNetworkDesc: '0g Baltics ei ehita kunagi mono-ühenduvuse juurutusi. LoRaWAN on sageli IoT infrastruktuuri selgroog, kuid paljud juurutused ühendavad LoRaWAN-i Sigfoxi, NB-IoT, BLE või satelliitühenduvusega, et optimeerida kulusid, leviala ja usaldusväärsust.',
      benefit1Title: 'Kulude optimeerimine',
      benefit1Desc: 'Valige iga kasutusjuhu jaoks kõige efektiivsem ühenduvus',
      benefit2Title: 'Võrgu redundantsus',
      benefit2Desc: 'Varuühenduvus kriitiliste rakenduste jaoks',
      benefit3Title: 'Seadmete pikaealisus',
      benefit3Desc: 'Optimeerige aku eluiga õige võrguvalikuga',
      benefit4Title: 'Juurutuse paindlikkus',
      benefit4Desc: 'Kohandage infrastruktuuri muutuvate nõuetega',
      learnMultiNetwork: 'Lugege meie mitmevõrgu lähenemisest',
      connectivityTechnologies: 'Ühendustehnoloogiad',
      lorawanName: 'LoRaWAN',
      lorawanDesc: 'Pika ulatusega selgroo infrastruktuur',
      sigfoxName: 'Sigfox',
      sigfoxDesc: 'Ülimadala energiatarbega lihtsad sõnumid',
      nbiotName: 'NB-IoT',
      nbiotDesc: 'Mobiilside sügav siseruumide leviala',
      bleName: 'BLE',
      bleDesc: 'Siseruumide positsioneerimine ja jälgimine',
      satelliteName: 'Satelliit',
      satelliteDesc: 'Kaugpiirkondade ühenduvus',
      // Starter Kits Section
      kitsBadge: 'Kiire algus',
      kitsTitle: 'LoRaWAN starterikomplektid',
      kitsDesc: 'Alustage oma LoRaWAN juurutust kiiresti testitud infrastruktuuripakettidega, mis on loodud erinevatele kasutusjuhtudele ja mastaapidele.',
      pilotKitTitle: 'Pilootjuurutuse komplekt',
      pilotKitDesc: 'Ideaalne organisatsioonidele, kes alustavad oma esimest LoRaWAN pilootprojekti',
      pilotIncludes: 'Sisaldab:',
      pilotItem1: '1 Tektelic LoRaWAN lüüs',
      pilotItem2: 'Võrgu konfiguratsioon',
      pilotItem3: 'Seadmete integratsiooni tugi',
      pilotItem4: 'Põhilise töölaua ligipääs',
      pilotUseCases: 'Tüüpilised kasutusjuhud:',
      pilotTag1: 'Kontseptsiooni tõestus',
      pilotTag2: 'Väike linnaku',
      pilotTag3: 'Innovatsiooniprojektid',
      smartCityKitTitle: 'Nutika linna infrastruktuuri komplekt',
      smartCityKitDesc: 'Loodud omavalitsustele ja kommunaalettevõtetele, kes käivitavad nutika linna IoT infrastruktuuri',
      smartCityIncludes: 'Sisaldab:',
      smartCityItem1: 'Mitu Tektelic lüüsi',
      smartCityItem2: 'Võrguarhitektuuri disain',
      smartCityItem3: 'Leviala planeerimine',
      smartCityItem4: 'Integratsioon linnaplatvormidega',
      smartCityUseCases: 'Tüüpilised rakendused:',
      smartCityTag1: 'Nutikas arvestus',
      smartCityTag2: 'Keskkonnaseire',
      smartCityTag3: 'Nutikas parkimine',
      smartCityTag4: 'Avalik infrastruktuur',
      industrialKitTitle: 'Tööstuslik IoT võrgu komplekt',
      industrialKitDesc: 'Loodud logistikale, kommunaalettevõtetele ja tööstuskeskkondadele',
      industrialIncludes: 'Sisaldab:',
      industrialItem1: 'Tööstuslikud lüüsid',
      industrialItem2: 'Varade jälgimisseadmed',
      industrialItem3: 'Võrgu optimeerimine',
      industrialItem4: 'Integratsiooni tugi',
      industrialUseCases: 'Tüüpilised rakendused:',
      industrialTag1: 'Varade jälgimine',
      industrialTag2: 'Laoseire',
      industrialTag3: 'Energiainfrastruktuur',
      requestKitInfo: 'Taotle komplekti infot',
      popular: 'POPULAARNE',
      // Device Ecosystem Section
      devicesBadge: 'Seadmete ühilduvus',
      devicesTitle: 'Ühilduvad IoT seadmed',
      devicesDesc: 'Meie LoRaWAN infrastruktuur töötab laia sertifitseeritud seadmete ökosüsteemiga juhtivatelt tootjatelt, tagades, et saate valida parimad andurid oma konkreetsetele rakendusnõuetele.',
      viewDeviceEcosystem: 'Vaadake meie täielikku seadmete ökosüsteemi',
      device1Name: 'Adeunis',
      device1Desc: 'Tööstusandurid',
      device2Name: 'ATIM',
      device2Desc: 'Tööstuslikud IoT seadmed',
      device3Name: 'Digital Matter',
      device3Desc: 'Varade jälgijad',
      device4Name: 'Keskkonnaandurid',
      device4Desc: 'Õhukvaliteet, temperatuur',
      device5Name: 'Arvestusseadmed',
      device5Desc: 'Vesi, gaas, elekter',
      device6Name: 'Connected Inventions',
      device6Desc: 'Nutikad andurid',
      // Services Section
      servicesBadge: 'Meie teenused',
      servicesTitle: 'Infrastruktuuri disain ja juurutamine',
      servicesDesc: 'Algsest planeerimisest suuremahuliste juurutusteni pakub 0g Baltics põhjalikke LoRaWAN infrastruktuuriteenuseid.',
      service1: 'Võrgu disain',
      service2: 'Lüüside juurutamine',
      service3: 'Leviala optimeerimine',
      service4: 'Seadmete valik',
      service5: 'ROI analüüs',
      service6: 'Pilootjuurutused',
      service7: 'Suuremahulised juurutused',
      learnConsulting: 'Lugege lähemalt meie nõustamisteenustest',
      // Who We Work With Section
      clientsBadge: 'Meie kliendid',
      clientsTitle: 'Kes juurutab LoRaWAN infrastruktuuri',
      client1Title: 'Linnad ja omavalitsused',
      client1Desc: 'Nutika linna infrastruktuur',
      client2Title: 'Kommunaalettevõtted',
      client2Desc: 'Arvestus ja seire',
      client3Title: 'Tööstusettevõtted',
      client3Desc: 'Varade jälgimine ja seire',
      client4Title: 'Logistikaettevõtted',
      client4Desc: 'Autoparkide ja lasti jälgimine',
      client5Title: 'IoT integraatorid',
      client5Desc: 'Lahenduste arendus',
      // CTA Section
      ctaTitle: 'Alustage oma LoRaWAN infrastruktuuri projekti',
      ctaDesc: 'Arutage oma infrastruktuuriprojekti meie IoT spetsialistidega. Aitame teil projekteerida ja juurutada õige LoRaWAN lahendus teie nõuetele.',
      ctaButton: 'Võtke ühendust meie LoRaWAN ekspertidega',
    },

    common: {
      learnMore: 'Loe lähemalt',
      contactUs: 'Võtke meiega ühendust',
      getStarted: 'Alusta',
      requestDemo: 'Taotle demo',
      downloadBrochure: 'Laadi alla brošüür',
      viewCaseStudy: 'Vaata juhtumiuuringut',
      keyBenefits: 'Peamised eelised',
      features: 'Funktsioonid',
      useCases: 'Kasutusjuhud',
      industries: 'Tööstusharud',
      technologies: 'Tehnoloogiad',
      readMore: 'Loe edasi',
      backToHome: 'Tagasi avalehele',
    },
    realEstatePage: {
      metaTitle: 'IoT lahendused kinnisvarale ja infrastruktuurile | Nutikad hooned Baltikumis | 0g Baltics',
      metaDesc: 'Muutke kinnisvarad nutikateks, ühendatud, andmepõhisteks hooneteks. IoT kontoritele, hotellidele, elamutele, kaubandusele, tööstusparkidele ja infrastruktuurile Lätis, Leedus ja Eestis — energia, lekete tuvastamine, õhukvaliteet, hõivatus, nutikad kapid ja palju muud.',
      ogTitle: 'Nutikad hooned ja infrastruktuur — IoT lahendused 0g Baltics',
      ogDesc: 'Vähendage tegevuskulusid, parandage üürnike kogemust ja vähendage varade riski võtmete kätte IoT abil kinnisvara jaoks Baltikumis.',
      heroBadge: 'Kinnisvara ja infrastruktuur',
      heroTitle: 'Nutikad hooned. Ühendatud infrastruktuur. Mõõdetavad tulemused.',
      heroSubtitle: 'Muutke iga hoone ja ala ühendatud, andmepõhiseks varaks — energiast ja lekete tuvastamisest kuni õhukvaliteedi, hõivatuse ja juurdepääsuni.',
      heroDesc: 'Üks mitmeühenduse IoT komplekt kinnisvara arendajatele, omanikele, operaatoritele ja varahalduritele Lätis, Leedus ja Eestis.',
      ctaDemo: 'Broneeri 30-minutiline demo',
      ctaExplore: 'Uurige lahendusi',
      ctaPilot: 'Alusta pilootprojektiga',
      trustReady: 'Võtmed kätte ja piloodiks valmis',
      trustMulti: 'Mitmeühendus (LoRaWAN · NB-IoT · BLE · Satelliit)',
      trustLocal: 'Kohalik tugi Baltikumis',
      trustScalable: 'Ühest hoonest täisportfellideni',
      trustIntegration: 'BMS, PropTech ja ERP integratsioonid',
      solutionsBadge: 'Lahenduste ülevaade',
      solutionsTitle: 'Üks platvorm, iga hoone kasutusjuht',
      solutionsDesc: 'Paigaldage ainult see, mida te täna vajate — ja laiendage homme. Kõik lahendused töötavad sama 0g Baltics IoT selgroo peal, seega iga uus kasutusjuht kasutab teie olemasolevat võrku, seadmeid ja armatuurlaudu.',
      learnMore: 'Loe lähemalt',
      solEnergy: 'Energia ja alammõõtmine',
      solEnergyDesc: 'Alammõõdetud elekter, soojus, gaas ja vesi korruste, üürnike või vara lõikes — reaalajas tarbimise, võrdlusaluse ja kulude jaotusega.',
      solLeak: 'Veelekete tuvastamine',
      solLeakDesc: 'Juhtmevabad lekete ja voolu andurid püstikutes, seadmeruumides ja üksustes, et tuvastada probleeme minutitega, mitte nädalatega.',
      solAir: 'Siseõhu kvaliteet',
      solAirDesc: 'CO₂, temperatuuri, niiskuse, PM2.5 ja TVOC seire tervislike, nõuetele vastavate ja produktiivsete siseruumide jaoks.',
      solOccupancy: 'Hõivatus ja ruumi kasutamine',
      solOccupancyDesc: 'Laudade, ruumide ja tsoonide hõivatuse analüütika ruumi optimeerimiseks, kasutamata võimsuse vähendamiseks ja sisseseadete planeerimiseks andmetega.',
      solCleaning: 'Puhastuse ja tualettide analüütika',
      solCleaningDesc: 'Nõudmisel koristamine, mis põhineb reaalsel külastajate voolul ja tualettide kasutamisel — vähem tunde, paremad standardid.',
      solSafety: 'Ohutus ja keskkond',
      solSafetyDesc: 'Paanikanupud, temperatuuri, müra ja keskkonna hoiatused personalile, üürnikele ja kriitilistele ruumidele.',
      solLockers: 'Pakkide ja nutikad kapid',
      solLockersDesc: 'Hoonesse integreeritud kapid elanikele, töötajatele ja üürnikele — paketid, võtmed, puhastusvahendid ja palju muud.',
      solParking: 'Parkimise hõivatus',
      solParkingDesc: 'Reaalajas välis- ja siseparklate hõivatus, juhtimine ja kasutamise analüütika garaažidele ja parkidele.',
      solAsset: 'Vara ja seadmete jälgimine',
      solAssetDesc: 'Jälgige HVAC seadmeid, tööriistu, FM seadmeid ja väärtuslikku vara ühes hoones või täisportfellis.',
      solESG: 'ESG ja CO2 aruandlus',
      solESGDesc: 'Automatiseeritud, kontrollitavad energia ja keskkonnaandmed GRESB, CSRD, EL Taksonoomia ja sisemise ESG aruandluse jaoks.',
      detailEnergyBadge: 'Energia seire',
      detailEnergyTitle: 'Nägege, võrrelge ja vähendage energiat igas hoones',
      detailEnergyDesc: 'Enamik portfelle toetub endiselt ühele peamisele arvestile objekti kohta. Me lisame juhtmevabad alamarvestid ja keskkonna andurid, et saaksite näha tarbimist korrus-korruselt, üürnik-üürnikuhaaval ja siduda selle välis- ja siseruumide tingimustega — täpselt seal, kust säästud tulevad.',
      detailEnergyPoint1: 'Juhtmevaba elektri, soojuse, gaasi ja vee alammõõtmine tsooni või üürniku kohta',
      detailEnergyPoint2: 'Otseülekande armatuurlauad, võrdlusalused ja anomaaliahoiatused',
      detailEnergyPoint3: 'Üürnike kulude jaotus ja energia läbilaskmine',
      detailEnergyPoint4: 'Valmis BREEAM, LEED, GRESB ja CSRD andmete vajadusteks',
      statEnergySaving: 'Tüüpiline energiasääst jälgitavatel objektidel',
      statPayback: 'Aastat tüüpiline ROI',
      statMonitoring: 'Pidev seire',
      statAlerts: 'Reaalajas anomaaliate hoiatused',
      detailLeakBadge: 'Lekete tuvastamine',
      detailLeakTitle: 'Kaitske oma hooneid #1 välditavate kahjustuste kulude eest',
      detailLeakDesc: 'Veekahjustused on üks kallimaid, kõige välditavamaid kaotusi kinnisvaras. Meie juhtmevabad andurid jälgivad püstikuid, seadmeruume, vannitubasid ja haavatavaid tsoone 24/7 ja käivitavad hoiatused hetkel, kui vesi ilmub sinna, kuhu see ei peaks.',
      detailLeakPoint1: 'Patareidega juhtmevabad lekete, voolu ja rõhu andurid',
      detailLeakPoint2: 'Kohesed hoiatused FM-ile, turvale ja omanikele — SMS, e-post, BMS',
      detailLeakPoint3: 'Saadaval automaatne klapi sulgemise integratsioon',
      leakProblem: 'Jälgimata hoonete peidetud kulud',
      leakProblemDesc: 'Üks tuvastamata leke võib maksta kümneid tuhandeid eurosid remondi, seisakute ja kindlustusmaksete näol.',
      leakRisk1: 'Struktuursed ja viimistluskahjustused',
      leakRisk2: 'Üürnike häirimine ja kaebused',
      leakRisk3: 'Kasvavad kindlustusmaksed ja omavastutused',
      detailAirBadge: 'Siseõhu kvaliteet',
      detailAirTitle: 'Tervislikud, produktiivsed siseruumid — mõõdetud',
      detailAirDesc: 'Siseõhu kvaliteet mõjutab otseselt tootlikkust, üürnike rahulolu, puudumisi ja vastavust. Me paigaldame diskreetsed, patareitoitel andurid, mis mõõdavad pidevalt tervisliku siseõhu põhiparameetreid.',
      airTemp: 'Temperatuur',
      airHumidity: 'Niiskus',
      airBenefits: 'Mida te saate',
      airBenefit1: 'Tervislikumad, produktiivsemad üürnikud ja töötajad',
      airBenefit2: 'Parem HVAC kontroll ja energiasääst',
      airBenefit3: 'Tõendid WELL, BREEAM ja üürnike aruandluseks',
      airBenefit4: 'Ventilatsiooniprobleemide varajane avastamine',
      detailOccupancyBadge: 'Hõivatus',
      detailOccupancyTitle: 'Kujundage ruum tegeliku kasutamise ümber',
      detailOccupancyDesc: 'Mõõtke, kuidas laudu, ruume ja terveid korruseid tegelikult kasutatakse — ja kasutage neid andmeid üüride optimeerimiseks, sisseseadete planeerimiseks, puhastuse parandamiseks ja HVAC ajakavade optimeerimiseks.',
      occupancyUse1: 'Laudade, ruumide ja tsoonide hõivatuse analüütika',
      occupancyUse2: 'Koosolekuruumide kasutamine ja mitteilmumise tuvastamine',
      occupancyUse3: 'HVAC ja valgustuse automatiseerimine kohaloleku põhjal',
      occupancyUse4: 'Andmed üüride, sisseseade ja ruumistrateegia otsuste jaoks',
      detailLockersBadge: 'Nutikad kapid',
      detailLockersTitle: 'Nutikad kapid, mis on integreeritud igasse hoonesse',
      detailLockersDesc: 'Modulaarsed kapilahendused elanikele, töötajatele, üürnikele ja külalistele — integreeritud juurdepääsu kontrolli ja meie IoT platvormiga.',
      lockerUse1: 'Pakkide kohaletoimetamine elamu- ja kontorihoonetele',
      lockerUse2: 'Võtmete ja juurdepääsuseadmete haldus',
      lockerUse3: 'Tööriistade ja seadmete jaotus FM meeskondadele',
      lockerUse4: 'Click & collect kaubanduse ja segakasutuse jaoks',
      segmentsBadge: 'Kasutusjuhud segmenti kaupa',
      segmentsTitle: 'Loodud iga kinnisvara ja infrastruktuuri profiili jaoks',
      segmentsDesc: 'A-klassi kontoritest kuni tööstusparkideni on igal segmendil spetsiifilised prioriteedid. Me konfigureerime sama IoT komplekti, et see sobiks igaühega.',
      segOffice: 'Kontorid',
      segOfficeDesc: 'Energia, hõivatus, IAQ ja nutikas juurdepääs omanikele ja korporatiivsetele kasutajatele.',
      segHotel: 'Hotellid ja majutus',
      segHotelDesc: 'Toa mugavus, energia, minibaari ja tagaruumide seire kaubamärgi ja butiikobjektidele.',
      segResidential: 'Elamud ja mitmeperekorterid',
      segResidentialDesc: 'Alammõõtmine, lekete kaitse, pakikapid ja elanike mugavus BTR ja korterelamute jaoks.',
      segRetail: 'Kaubandus ja ostukeskused',
      segRetailDesc: 'Külastajate voolu, külmaahela, HVAC ja üldkasutatavate alade energia seire koos üürnike kulude jaotusega.',
      segBanks: 'Pangad ja avalikud hooned',
      segBanksDesc: 'Turvaline keskkonnaseire, energiatõhusus ja vastavusaruandlus reguleeritud ruumidele.',
      segParks: 'Tööstus- ja logistikapargid',
      segParksDesc: 'Mitmeüürnike alammõõtmine, vara jälgimine, värava- ja juurdepääsuseire suurtes objektides.',
      segDevelopers: 'Arendajad',
      segDevelopersDesc: 'Eelnevalt paigaldatud IoT komplekt, mis suurendab vara väärtust, ESG reitingut ja üürnike atraktiivsust esimesest päevast.',
      segAssetManagers: 'Vara- ja kinnisvarahaldurid',
      segAssetManagersDesc: 'Kogu portfelli armatuurlauad, võrdlusalused ja automatiseeritud ESG andmed iga halduses oleva vara kohta.',
      platformBadge: 'Ühenduvus ja platvorm',
      platformTitle: 'Üks ühendatud komplekt — andurist armatuurlauani',
      platformDesc: 'Me kavandame, paigaldame ja käitame teie hoonete jaoks täieliku IoT ahela: seadmed, ühenduvus, andmeplatvorm, armatuurlauad, hoiatused ja integratsioonid. Ei mingit tarnijalappi, üks partner kogu elutsükli jaoks.',
      platformDevices: 'Tööstusklassi seadmed',
      platformConnectivity: 'Mitmevõrgu ühenduvus',
      platformDashboard: 'Ühtsed armatuurlauad',
      platformAlerts: 'Nutikas teavitus',
      platformIntegration: 'BMS ja PropTech integratsioonid',
      platformSupport: 'Kohalik Balti tugi',
      techTitle: 'Õige võrk iga kasutusjuhu jaoks',
      techLoRaWAN: 'Pikkmaa, madala energiatarbega — ideaalne alammõõtmiseks, lekete, IAQ ja hõivatuse jaoks kõigis hoonetes.',
      techNBIoT: 'Sügav siseruumide mobiilikate — täiuslik kriitilise vara ja kaugemate üksikobjektide jaoks.',
      techBLE: 'Siseruumide mikroasukoha määramine ja vara jälgimine hoonete ja suurte objektide sees.',
      learnMoreConnectivity: 'Vaadake kõiki ühenduse võimalusi',
      bmsBadge: 'BMS ja BACnet integratsioon',
      bmsTitle: 'Ühendub teie olemasoleva BMS-iga — ilma väljakiskumise ja asendamiseta',
      bmsDesc: 'Meie juhtmevaba IoT kiht täiendab teie olemasolevat hoone haldussüsteemi reaalajas, detailsete andmetega. Andurid tarnitakse eelkonfigureeritud kujul, lüüsid töötavad kohapeal ja teie BMS saab struktureeritud andmeid protokollide kaudu, mida see juba räägib.',
      bmsProtocols: 'Toetatud protokollid',
      bmsBACnet: 'BACnet/IP',
      bmsBACnetDesc: 'Natiivne BACnet/IP ühendaja — punktid kaarduvad otse Niagara, Honeywell, Siemens ja teistesse BMS platvormidesse.',
      bmsModbus: 'Modbus TCP',
      bmsModbusDesc: 'Standardne Modbus TCP väljund pärandkontrolleritele, HVAC seadmeruumidele ja tööstuslikule automaatikale.',
      bmsRest: 'REST API ja MQTT',
      bmsRestDesc: 'Avatud REST API-d, webhookid ja MQTT PropTech platvormidele, kohandatud armatuurlaudadele ja andmejärvedele.',
      bmsLoRaWAN: 'LoRaWAN®',
      bmsLoRaWANDesc: 'Operaatoriklassi LoRaWAN võrk 5–10+ aastase patarei elueaga ja kogu hoone kate ühest lüüsist.',
      bmsEcosystemTitle: 'Täielik andurite ökosüsteem',
      bmsEcosystemDesc: 'Kasutage testitud TEKTELIC seadmete portfelli — mugavus, hõivatus, leke, CO₂, nutikas AC, külmaahel, personali ohutus — kõik samas võrgus.',
      bmsItWorks: 'See lihtsalt toimib',
      bmsItWorksDesc: 'Karbist välja töötavad seadmed, minimaalne inseneritöö, madal kogu omamiskulu ja partner, kes haldab elutsüklit — disain, paigaldus, käitamine ja hooldus.',
      bmsBenefit1: 'Ei ole vaja olemasolevat BMS-i välja kiskuda ega asendada',
      bmsBenefit2: '5–10 aastat seadme patarei eluiga',
      bmsBenefit3: 'Üks lüüs katab kogu hoone',
      bmsBenefit4: 'Niagara / OpenBlue / Honeywell valmis',
      fmBadge: 'Facility Management IoT komplekt',
      fmTitle: 'LoRaWAN® lahendused nutikaks hoonete haldamiseks',
      fmDesc: 'Tänapäevaste hoonete haldamine on keerulisem kui kunagi varem — kasvavad energiakulud, kõrgemad üürnike ootused, rangemad ESG nõuded. Meie LoRaWAN® IoT komplekt pakub reaalajas ülevaadet, mis vähendab tegevuskulusid ja automatiseerib vastavuse kogu portfellis.',
      fmStat1: 'Puhastuskulude vähendamine 60 päeva jooksul',
      fmStat2: 'Energiasääst HVAC ja hõivatuse optimeerimisel',
      fmStat3: 'Automatiseeritud ESG aruandlus — vaevata',
      fmStat4: 'Pidev õhu, lekete ja mugavuse seire',
      fmChallengesTitle: 'Lahendame teie hoonete haldamise väljakutseid',
      fmChallengesDesc: 'Igal hoonel on oma väljakutsed. Meie lahendused on ehitatud teie prioriteetide ümber.',
      fmCh1Title: 'Mugavuse ja õhukvaliteedi kaebused',
      fmCh1Desc: 'Halb temperatuur, CO₂ ja niiskuse kontroll vähendab üürnike rahulolu.',
      fmCh2Title: 'Peidetud riskid ja intsidendid',
      fmCh2Desc: 'Lekked ja seadmete rikked kiiresti eskaleeruvad ilma varase tuvastuseta.',
      fmCh3Title: 'Raskesti tõestatav ESG ja vastavus',
      fmCh3Desc: 'Jätkusuutlikkus ja regulatiivsed standardid on raske täita ilma pidevate andmeteta.',
      fmCh4Title: 'Aeglased, käsitsi tööprotsessid',
      fmCh4Desc: 'Ringkäigud ja reaktiivne hooldus raiskavad aega ja suurendavad kulusid.',
      fmCh5Title: 'Pilootist suurem kasvamine',
      fmCh5Desc: 'Paljud IoT projektid peatuvad pärast testimist. Laienemine tundub keeruline ja kulukas.',
      fmCh6Title: 'Integratsioon ja andmesilod',
      fmCh6Desc: 'Andmed on laiali eri süsteemides, raske saada ühtset ülevaadet.',
      fmAppsTitle: 'Kasutusvalmis rakendused',
      fmAppsDesc: 'Alustage IoT lahenduse kasutamist minutitega. Skannige QR-kood ja hallake kõike armatuurlaualt.',
      fmApp1Name: 'BREEZE',
      fmApp1Desc: 'Reaalajas siseõhu kvaliteedi armatuurlauad ja hoiatused.',
      fmApp2Name: 'LeapX',
      fmApp2Desc: 'Kiire ja sujuv IoT lüüside ja seadmete aktiveerimine.',
      fmApp3Name: 'TEMPO',
      fmApp3Desc: 'Nutikas koosolekuruumide broneerimine Outlook® / Google integratsiooniga.',
      fmApp4Name: 'LOCUS',
      fmApp4Desc: 'Visualiseerimine ja RTLS tööriistad hoonehalduse meeskondadele.',
      fmBmsTitle: 'Juhtmevaba integratsioon teie hoone haldussüsteemiga (BMS)',
      fmBmsDesc1: '0g Baltics muudab lihtsaks reaalajas ülevaadete — õhukvaliteedi, hõivatuse, energia, lekete ja külmaahela seire — sisestamise otse teie hoone haldussüsteemi (BMS).',
      fmBmsDesc2: 'Meie juhtmevabad lahendused kõrvaldavad keerulise juhtmestiku, vähendavad paigalduskulusid ja skaleeruvad vaevata üle iga objekti. Tänu toele tööstusstandardprotokollidele nagu BACnet, Modbus ja Niagara on integratsioon sujuv — andes teile ühe tervikliku ülevaate oma hoonetest, et teha targemaid otsuseid, vähendada kulusid ja luua tervislikumaid keskkondi.',
      fmBenefitsTitle: 'Efektiivsus, mugavus ja vastavus — ühes lahenduses',
      fmBen1Title: 'Madalamad tegevuskulud',
      fmBen1Desc: 'Vähendage puhastus-, hooldus- ja energiakulusid reaalajas ülevaadete abil.',
      fmBen2Title: 'Targem energia kasutamine',
      fmBen2Desc: 'Vähendage energia raiskamist optimeerides HVAC-i, valgustust ja ruumikasutust.',
      fmBen3Title: 'Tervislikumad, mugavamad ruumid',
      fmBen3Desc: 'Hoidke õhukvaliteet, temperatuur ja niiskus optimaalsel tasemel üürnike heaoluks.',
      fmBen4Title: 'Vaevata vastavus',
      fmBen4Desc: 'Koguge ja esitage ESG ja regulatiivseid andmeid automaatselt — ei mingit käsitsitööd.',
      fmBen5Title: 'Usaldusväärne ohutusseire',
      fmBen5Desc: 'Tuvastage lekked, keskkonnariskid või ebatavaline tegevus koheselt.',
      fmBen6Title: 'Skaleeruv lihtsus',
      fmBen6Desc: 'Alustage ühe hoonega ja laiendage kogu portfellile ilma keerukust lisamata.',
      relatedBadge: 'Seotud lahendused',
      relatedTitle: 'Seotud lahendused, mida saate kombineerida',
      relatedPakuskapis: 'Meie pakikapi võrk elamu- ja ärihoonetele Lätis.',
      relatedSmartLockers: 'Nutikad kapid',
      relatedSmartLockersDesc: 'Täielik modulaarne kapi valik pakkidele, võtmetele, tööriistadele ja click & collect jaoks.',
      relatedIAQ: 'Siseõhu kvaliteet',
      relatedIAQDesc: 'Pidev IAQ seire tervislikumate, nõuetele vastavate ja produktiivsete siseruumide jaoks.',
      relatedEnergy: 'Hoonete energiatõhusus',
      relatedEnergyDesc: 'Energia seire, HVAC optimeerimine ja ESG-valmis aruandlus hoonetele.',
      faqBadge: 'KKK',
      faqTitle: 'Küsimused, mida kinnisvara meeskonnad meilt küsivad',
      faq1Q: 'Kas peame asendama oma olemasolevad arvestid või BMS-i?',
      faq1A: 'Ei. Meie andurid on mitteinvasiivsed ja patareitoitel, ning integreeruvad teie olemasolevate arvestite, BMS-i ja ERP-ga. Te hoiate seda, mis toimib, ja laiendate nähtavust seal, kus teil on pimealasid.',
      faq2Q: 'Kas saame alustada ühe hoonega, enne kui laieneme kogu portfellile?',
      faq2A: 'Jah — enamik kliente alustab ühe pilootprojekti hoonega, kinnitab ROI 3–6 kuu jooksul, seejärel mastaapib ülejäänud portfellile, kasutades sama platvormi ja protsesse.',
      faq3Q: 'Kuidas on andmed kaitstud ja kus neid hoitakse?',
      faq3A: 'Andmed on krüpteeritud edastamisel ja puhkeolekus, hostitud EL andmekeskustes ja juurdepääs on rollipõhine. Oleme täielikult GDPR-nõuetele vastav ja saame integreeruda teie SSO-ga.',
      faq4Q: 'Milliseid võrke kasutate Baltikumis?',
      faq4A: 'Me ühendame LoRaWAN, NB-IoT, LTE-M, BLE ja satelliidi sõltuvalt hoonest, kasutusjuhust ja saadaolevast levist — nii et teid ei ole kunagi lukustatud ühe tehnoloogia külge.',
      faq5Q: 'Kas saame integreerida IoT andmed oma BMS, PropTech või ERP-ga?',
      faq5A: 'Jah. Me pakume REST API-sid, webhooks ja eelehitatud konnektoreid tavaliste BMS, PropTech platvormide, raamatupidamise ja ERP süsteemide jaoks.',
      faq6Q: 'Kuidas see toetab meie ESG ja CSRD aruandlust?',
      faq6A: 'Iga mõõdetud andmepunkt (energia, vesi, IAQ, hõivatus) on salvestatud ajatemplitega ja metaandmetega ning seda saab eksportida GRESB, BREEAM, LEED, CSRD ja EL Taksonoomia aruandluseks.',
      faq7Q: 'Kuidas näeb välja tüüpiline pilootprojekt?',
      faq7A: 'Tüüpiline piloot katab 1 hoone 20–100 seadmega 3–6 kuu jooksul, selgelt määratletud KPI-dega (nt energiasääst, ennetatud lekkesündmused, hõivatuse ülevaated). Me hoolitseme disaini, paigalduse ja koolituse eest.',
      ctaTitle: 'Muudame teie hooned andmepõhisteks varadeks',
      ctaDesc: 'Broneerige 30-minutiline kõne meie kinnisvara IoT meeskonnaga. Me kaardistame teie prioriteedid, näitame sarnaseid paigaldusi ja pakume konkreetset piloti.',
    },

  },
};
